import { A as StorageNotAvailableError, B as VertoPongError, C as OverconstrainedFallbackError, D as RequestError, E as RecoveryError, F as UnexpectedError, H as WebSocketTimeoutError, I as UnimplementedError, L as ValidationError, M as StorageWriteError, N as TokenRefreshError, O as RequestTimeoutError, P as TransportConnectionError, R as VertoAttachHandlerError, S as MessageParseError, T as RPCTimeoutError, V as WebSocketConnectionError, _ as DeviceTokenError, a as filterNull, b as JSONRPCError, c as setLogLevel, d as CallCreateError, f as CollectionFetchError, g as DeserializationError, h as DependencyError, i as getValueFrom, j as StorageReadError, k as SerializationError, l as setLogger, m as DPoPInitError, n as filterAs, o as getLogger, p as ConversationError, s as setDebugOptions, t as throwOnRPCError, u as AuthStateHandlerError, v as InvalidCredentialsError, w as PreflightError, x as MediaTrackError, y as InvalidParams, z as VertoInviteHandlerError } from "./operators-CX_lCCJm.mjs";
import { jwtDecode } from "jwt-decode";
import { BehaviorSubject, EMPTY, NEVER, Observable, ReplaySubject, Subject, TimeoutError, animationFrameScheduler, asapScheduler, auditTime, catchError, combineLatest, debounceTime, defer, distinctUntilChanged, exhaustMap, filter, first, firstValueFrom, from, interval, lastValueFrom, map, merge, mergeMap, observeOn, of, pipe, race, scan, share, shareReplay, skip, skipWhile, startWith, switchMap, take, takeUntil, tap, throwError, timeout, timer, toArray, withLatestFrom } from "rxjs";
import { v4 } from "uuid";
import { distinctUntilChanged as distinctUntilChanged$1, map as map$1 } from "rxjs/operators";

//#region src/behaviors/Destroyable.ts
var Destroyable = class {
	constructor() {
		this.subscriptions = [];
		this.subjects = [];
		this._destroyed$ = new Subject();
	}
	destroy() {
		this._observableCache?.clear();
		this.subscriptions.forEach((sub) => sub.unsubscribe());
		this.subjects.forEach((subject) => subject.complete());
		this._destroyed$.next();
		this._destroyed$.complete();
	}
	cachedObservable(key, factory) {
		this._observableCache ??= /* @__PURE__ */ new Map();
		let cached = this._observableCache.get(key);
		if (!cached) {
			cached = factory();
			this._observableCache.set(key, cached);
		}
		return cached;
	}
	/**
	* Like `cachedObservable`, but defers emissions to the microtask queue
	* via `observeOn(asapScheduler)`.
	*
	* Use ONLY for public-facing observable getters that external consumers
	* subscribe to. Prevents a class of bugs where `BehaviorSubject` or
	* `ReplaySubject` replays synchronously during `subscribe()`, before
	* the subscription variable is assigned in the caller's scope.
	*
	* Do NOT use for observables consumed internally by the SDK — internal
	* code using `subscribeTo()`, `firstValueFrom()`, or `withLatestFrom()`
	* depends on synchronous emission delivery.
	*/
	publicCachedObservable(key, factory) {
		const publicKey = `public:${key}`;
		this._observableCache ??= /* @__PURE__ */ new Map();
		let cached = this._observableCache.get(publicKey);
		if (!cached) {
			cached = factory().pipe(observeOn(asapScheduler));
			this._observableCache.set(publicKey, cached);
		}
		return cached;
	}
	/**
	* Wraps an observable so emissions are deferred to the microtask queue.
	*
	* Use ONLY for public-facing getters that expose a subject via
	* `.asObservable()` without going through `cachedObservable`.
	*
	* Do NOT use for observables consumed internally by the SDK.
	*/
	deferEmission(observable) {
		return observable.pipe(observeOn(asapScheduler));
	}
	subscribeTo(observable, observerOrNext) {
		const subscription = observable.subscribe(observerOrNext);
		this.subscriptions.push(subscription);
	}
	createSubject() {
		const subject = new Subject();
		this.subjects.push(subject);
		return subject;
	}
	createReplaySubject(bufferSize, windowTime) {
		const subject = new ReplaySubject(bufferSize, windowTime);
		this.subjects.push(subject);
		return subject;
	}
	createBehaviorSubject(initialValue) {
		const subject = new BehaviorSubject(initialValue);
		this.subjects.push(subject);
		return subject;
	}
	/**
	* Observable that emits when the instance is destroyed
	*/
	get destroyed$() {
		return this._destroyed$.asObservable();
	}
};

//#endregion
//#region src/utils/asyncRetry.ts
const DEFAULT_MAX_RETRIES = 10;
const DEFAULT_INITIAL_DELAY = 100;
const DEFAULT_DELAY_VARIATION = 1;
const increasingDelay = ({ delayLimit: upperDelayLimit = Number.MAX_SAFE_INTEGER, initialDelay = DEFAULT_INITIAL_DELAY, variation = DEFAULT_DELAY_VARIATION }) => {
	if (initialDelay < 0) throw new ValidationError("initialDelay must be gte 0");
	if (upperDelayLimit < 0) throw new ValidationError("upperDelayLimit must be gte 0");
	if (variation < 0) throw new ValidationError("variation must be gte 0");
	if (initialDelay > upperDelayLimit) throw new ValidationError("initialDelay must be lte delayLimit");
	let delay = Math.min(initialDelay, upperDelayLimit);
	return () => {
		if (delay === upperDelayLimit) return upperDelayLimit;
		const currentDelay = delay;
		delay = Math.min(delay + variation, upperDelayLimit);
		return currentDelay;
	};
};
const asyncRetry = async ({ asyncCallable, maxRetries: retries = DEFAULT_MAX_RETRIES, delayFn, validator, expectedErrorHandler }) => {
	let remainingAttempts = retries - 1;
	let wait = 0;
	const promiseAttempt = async () => {
		try {
			let result;
			if (wait <= 0) result = await asyncCallable();
			else result = await new Promise((resolve, reject) => setTimeout(() => {
				asyncCallable().then(resolve).catch(reject);
			}, wait));
			if (remainingAttempts) validator?.(result);
			return result;
		} catch (error) {
			if (remainingAttempts-- > 0 && !expectedErrorHandler?.(error)) {
				wait = delayFn?.() ?? 0;
				getLogger().debug(`Retrying request: ${retries - remainingAttempts} of ${retries}`);
				return promiseAttempt();
			} else throw error;
		}
	};
	return promiseAttempt();
};

//#endregion
//#region src/controllers/HTTPRequestController.ts
const logger$31 = getLogger();
const GET_PARAMS = {
	method: "GET",
	headers: { Accept: "application/json" }
};
const POST_PARAMS = {
	method: "POST",
	headers: {
		Accept: "application/json",
		"Content-Type": "application/json"
	}
};
var HTTPRequestController = class HTTPRequestController extends Destroyable {
	static {
		this.defaultMaxRetries = 3;
	}
	static {
		this.defaultRetryDelayMinMs = 1e3;
	}
	static {
		this.defaultRetryDelayMaxMs = 3e4;
	}
	static {
		this.defaultRequestTimeoutMs = 3e4;
	}
	static {
		this.SENSITIVE_BODY_FIELDS = new Set([
			"dpop_token",
			"token",
			"jwt_token"
		]);
	}
	constructor(baseURL, getCredential, options = {}) {
		super();
		this.baseURL = baseURL;
		this.getCredential = getCredential;
		this._responses$ = this.createSubject();
		this._errors$ = this.createSubject();
		this._status$ = this.createBehaviorSubject("idle");
		this.maxRetries = options.maxRetries ?? HTTPRequestController.defaultMaxRetries;
		this.retryDelayMin = options.retryDelayMin ?? HTTPRequestController.defaultRetryDelayMinMs;
		this.retryDelayMax = options.retryDelayMax ?? HTTPRequestController.defaultRetryDelayMaxMs;
		this.requestTimeout = options.requestTimeout ?? HTTPRequestController.defaultRequestTimeoutMs;
	}
	get status$() {
		return this._status$.asObservable();
	}
	get status() {
		return this._status$.value;
	}
	get responses$() {
		return this._responses$.asObservable();
	}
	get errors$() {
		return this._errors$.asObservable();
	}
	async request(request) {
		this._status$.next("requesting");
		try {
			const response = await this.executeWithRetry(request);
			this._status$.next("success");
			this._responses$.next(response);
			return response;
		} catch (error) {
			logger$31.error("[HTTPRequestController] Request error:", error);
			this._status$.next("error");
			const err = error instanceof Error ? error : new Error("HTTP request failed", { cause: error });
			this._errors$.next(err);
			throw err;
		}
	}
	async executeWithRetry(request) {
		const variation = Math.ceil((this.retryDelayMax - this.retryDelayMin) / Math.max(this.maxRetries - 1, 1));
		const delayFn = increasingDelay({
			initialDelay: this.retryDelayMin,
			variation,
			delayLimit: this.retryDelayMax
		});
		return asyncRetry({
			asyncCallable: async () => this.executeRequest(request),
			maxRetries: this.maxRetries,
			delayFn,
			validator: (response) => {
				if (response.status >= 500 && response.status < 600) throw new UnexpectedError(`Server error: ${response.status} ${response.statusText}`);
			}
		});
	}
	async executeRequest(request) {
		const url = this.buildURL(request.url);
		const headers = this.buildHeaders(request.headers);
		const timeout$1 = request.timeout ?? this.requestTimeout;
		logger$31.debug("[HTTPRequestController] Executing request:", {
			method: request.method,
			url,
			headers: Object.keys(headers).reduce((acc, key) => {
				acc[key] = key === "Authorization" ? `${headers[key].substring(0, 20)}...` : headers[key];
				return acc;
			}, {}),
			body: this.sanitizeBody(request.body)
		});
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout$1);
		try {
			const response = await fetch(url, {
				method: request.method,
				headers,
				body: request.body,
				signal: controller.signal
			});
			clearTimeout(timeoutId);
			const httpResponse = await this.convertResponse(response);
			logger$31.debug("[HTTPRequestController] Response received:", {
				status: response.status,
				statusText: response.statusText,
				headers: [...response.headers.entries()],
				body: httpResponse.body ? httpResponse.body.substring(0, 200) : "(empty)"
			});
			return httpResponse;
		} catch (error) {
			clearTimeout(timeoutId);
			if (error instanceof Error && error.name === "AbortError") throw new RequestTimeoutError(`Request timeout after ${timeout$1}ms`, { cause: error });
			logger$31.error("[HTTPRequestController] Request failed:", error);
			throw error;
		}
	}
	buildURL(url) {
		const urlString = typeof url === "string" ? url : url.toString();
		if (urlString.startsWith("http://") || urlString.startsWith("https://")) return urlString;
		return `${this.baseURL.endsWith("/") ? this.baseURL.slice(0, -1) : this.baseURL}${urlString.startsWith("/") ? urlString : `/${urlString}`}`;
	}
	buildHeaders(requestHeaders) {
		const headers = { ...requestHeaders ?? {} };
		const credential = this.getCredential();
		if (credential.token) {
			headers.Authorization = `Bearer ${credential.token}`;
			logger$31.debug("[HTTPRequestController] Using Bearer token auth, token length:", credential.token.length);
		} else logger$31.warn("[HTTPRequestController] No credentials available for authentication");
		return headers;
	}
	/**
	* Sanitizes a request body for debug logging by masking sensitive fields.
	*/
	sanitizeBody(body) {
		if (!body || typeof body !== "string") return body ? "(non-string body)" : void 0;
		try {
			const sanitized = { ...JSON.parse(body) };
			for (const key of Object.keys(sanitized)) if (HTTPRequestController.SENSITIVE_BODY_FIELDS.has(key) && typeof sanitized[key] === "string") sanitized[key] = `${sanitized[key].substring(0, 20)}...[redacted]`;
			return JSON.stringify(sanitized);
		} catch {
			return body.length > 200 ? `${body.substring(0, 200)}...` : body;
		}
	}
	async convertResponse(response) {
		const headers = {};
		response.headers.forEach((value, key) => {
			headers[key] = value;
		});
		const bodyText = await response.text();
		return {
			status: response.status,
			statusText: response.statusText,
			headers,
			body: bodyText,
			ok: response.ok,
			url: response.url
		};
	}
};

//#endregion
//#region src/controllers/DeviceHistoryManager.ts
/**
* DeviceHistoryManager - Maintains a per-kind stack of recently used devices.
*
* When a device disappears during a call, the SDK uses this history to fall
* back to the previously used device rather than picking an arbitrary default.
* This handles the common scenario of AirPods disconnecting and the user
* expecting to return to their built-in microphone.
*
* This is a pure data structure -- it does not extend Destroyable since it
* holds no subscriptions or subjects.
*
* @see Section 5.2 of the Implementation Guide
*/
/** Maximum number of entries retained per device kind. */
const MAX_HISTORY_SIZE = 5;
/**
* Converts a MediaDeviceInfo to a serializable HistoryEntry.
*/
const toHistoryEntry = (device, kind) => ({
	deviceId: device.deviceId,
	label: device.label,
	groupId: device.groupId,
	kind
});
var DeviceHistoryManager = class {
	constructor() {
		this._stacks = {
			audioinput: [],
			audiooutput: [],
			videoinput: []
		};
	}
	/**
	* Push a device onto the history stack for the given kind.
	*
	* If the device is already at the top of the stack, this is a no-op.
	* Duplicate entries deeper in the stack are removed to keep the stack clean.
	* The stack is capped at {@link MAX_HISTORY_SIZE} entries.
	*
	* @param kind - The device kind (audioinput, audiooutput, videoinput)
	* @param device - The MediaDeviceInfo to record
	*/
	push(kind, device) {
		const entry = toHistoryEntry(device, kind);
		const current = this._stacks[kind];
		if (current.length > 0 && current[0].deviceId === entry.deviceId) return;
		const filtered = current.filter((e) => e.deviceId !== entry.deviceId);
		this._stacks[kind] = [entry, ...filtered].slice(0, MAX_HISTORY_SIZE);
	}
	/**
	* Pop the most recent device from the history stack for the given kind.
	*
	* @param kind - The device kind
	* @returns The most recent HistoryEntry, or undefined if the stack is empty
	*/
	pop(kind) {
		const current = this._stacks[kind];
		if (current.length === 0) return;
		const [top, ...rest] = current;
		this._stacks[kind] = rest;
		return top;
	}
	/**
	* Find the most recent device in the history that is still present in the
	* available device list.
	*
	* Searches the history stack from most recent to oldest. A device is
	* considered a match if its deviceId or (groupId + label) match an
	* available device.
	*
	* @param kind - The device kind
	* @param availableDevices - The current list of available devices
	* @returns The matching MediaDeviceInfo from availableDevices, or undefined
	*/
	findInHistory(kind, availableDevices) {
		const history = this._stacks[kind];
		for (const entry of history) {
			const exactMatch = availableDevices.find((d) => d.deviceId === entry.deviceId);
			if (exactMatch) return exactMatch;
			const groupMatch = availableDevices.find((d) => d.groupId === entry.groupId && d.label === entry.label);
			if (groupMatch) return groupMatch;
		}
	}
	/**
	* Return a read-only snapshot of the history stack for a given kind.
	*
	* @param kind - The device kind
	* @returns A read-only array of HistoryEntry objects, most recent first
	*/
	getHistory(kind) {
		return this._stacks[kind];
	}
	/**
	* Clear all history stacks.
	*/
	clear() {
		this._stacks.audioinput = [];
		this._stacks.audiooutput = [];
		this._stacks.videoinput = [];
	}
};

//#endregion
//#region src/core/constants.ts
const INVITE_VERSION = 1e3;
const DEFAULT_ICE_CANDIDATE_TIMEOUT_MS = 600;
const DEFAULT_ICE_GATHERING_TIMEOUT_MS = 6e3;
const DEFAULT_RECONNECT_CALLS_TIMEOUT_MS = 300 * 1e3;
const DEFAULT_CONNECTION_TIMEOUT_MS = 1e4;
const DEFAULT_RECONNECT_DELAY_MIN_MS = 100;
const DEFAULT_RECONNECT_DELAY_MAX_MS = 3e3;
const DEFAULT_DEVICE_DEBOUNCE_TIME_MS = 1500;
const DEFAULT_DEVICE_POLLING_INTERVAL_MS = 0;
const PREFERENCES_STORAGE_KEY = "sw:preferences";
/** Scope value that enables automatic token refresh. */
const SAT_REFRESH_SCOPE = "sat:refresh";
/** API endpoints for device token operations. */
const DEVICE_TOKEN_ENDPOINT = "/api/fabric/subscriber/devices/token";
const DEVICE_REFRESH_ENDPOINT = "/api/fabric/subscriber/devices/refresh";
/** Default device token TTL in seconds (15 minutes). */
const DEVICE_TOKEN_DEFAULT_EXPIRE_IN = 900;
/** Buffer time in milliseconds before expiry to trigger refresh. */
const DEVICE_TOKEN_REFRESH_BUFFER_MS = 3e4;
/** Maximum retry attempts for device token refresh on transient failure. */
const DEVICE_TOKEN_REFRESH_MAX_RETRIES = 3;
/** Base delay in milliseconds for exponential backoff on refresh retry. */
const DEVICE_TOKEN_REFRESH_RETRY_BASE_MS = 1e3;
/** Maximum retry attempts for developer credential refresh on transient failure. */
const CREDENTIAL_REFRESH_MAX_RETRIES = 5;
/** Base delay in milliseconds for exponential backoff on credential refresh retry. */
const CREDENTIAL_REFRESH_RETRY_BASE_MS = 1e3;
/** Maximum delay in milliseconds for credential refresh backoff. */
const CREDENTIAL_REFRESH_MAX_DELAY_MS = 3e4;
/** Buffer in milliseconds before token expiry to trigger refresh. */
const CREDENTIAL_REFRESH_BUFFER_MS = 5e3;
/**
* Maximum time the coordinator will wait for `DeviceTokenManager.activate()`
* to resolve before treating the activation as failed and falling back to
* the developer-provided refresh path. Prevents a wedged HTTP layer from
* leaving the session with no active refresh mechanism.
*/
const CREDENTIAL_ACTIVATE_TIMEOUT_MS = 3e4;
/** JSON-RPC error code for requester validation failure (corrupted auth state). */
const RPC_ERROR_REQUESTER_VALIDATION_FAILED = -32003;
/** JSON-RPC error code for invalid params (e.g., missing authentication block). */
const RPC_ERROR_INVALID_PARAMS = -32602;
/** JSON-RPC error code for authentication failure (invalid token, missing DPoP, etc.). */
const RPC_ERROR_AUTHENTICATION_FAILED = -32002;
/** Default polling interval for RTCPeerConnection.getStats() in milliseconds. */
const DEFAULT_STATS_POLLING_INTERVAL_MS = 1e3;
/** Number of initial samples used to build a baseline for spike detection. */
const DEFAULT_STATS_BASELINE_SAMPLES = 10;
/** Duration in ms with no inbound audio packets before emitting a critical issue. */
const DEFAULT_STATS_NO_PACKET_THRESHOLD_MS = 2e3;
/** Multiplier applied to baseline RTT to detect a warning-level RTT spike. */
const DEFAULT_STATS_RTT_SPIKE_MULTIPLIER = 3;
/** Packet loss fraction (0-1) above which a warning is emitted. */
const DEFAULT_STATS_PACKET_LOSS_THRESHOLD = .05;
/** Multiplier applied to baseline jitter to detect a jitter spike. */
const DEFAULT_STATS_JITTER_SPIKE_MULTIPLIER = 4;
/** Number of seconds of metrics history to retain. */
const DEFAULT_STATS_HISTORY_SIZE = 30;
/** Maximum keyframe requests allowed within a single burst window. */
const DEFAULT_KEYFRAME_MAX_BURST$1 = 3;
/** Duration of the keyframe burst window in milliseconds. */
const DEFAULT_KEYFRAME_BURST_WINDOW_MS$1 = 3e3;
/** Cooldown period in ms after burst limit is reached before allowing more keyframes. */
const DEFAULT_KEYFRAME_COOLDOWN_MS$1 = 1e4;
/** Minimum time between re-INVITE attempts in milliseconds. */
const DEFAULT_REINVITE_DEBOUNCE_TIME_MS = 1e4;
/** Maximum number of re-INVITE attempts per call. */
const DEFAULT_REINVITE_MAX_ATTEMPTS = 3;
/** Timeout for a single re-INVITE attempt in milliseconds. */
const DEFAULT_REINVITE_TIMEOUT_MS = 5e3;
/** Debounce window in ms to collapse multiple detection signals into one trigger. */
const DEFAULT_RECOVERY_DEBOUNCE_TIME_MS = 2e3;
/** Cooldown period in ms between recovery attempts. */
const DEFAULT_RECOVERY_COOLDOWN_MS = 1e4;
/** Grace period in ms before treating ICE 'disconnected' as a failure. */
const DEFAULT_ICE_DISCONNECTED_GRACE_PERIOD_MS = 3e3;
/** Timeout for a single ICE restart attempt in milliseconds. */
const DEFAULT_ICE_RESTART_TIMEOUT_MS$1 = 5e3;
/** Maximum recovery attempts before emitting 'max_attempts_reached'. */
const DEFAULT_MAX_RECOVERY_ATTEMPTS = 3;
/** Upper bound in ms for waiting on iceGatheringState === 'complete' after an ICE restart. */
const ICE_GATHERING_COMPLETE_TIMEOUT_MS = 1e4;
/** Upper bound in ms for waiting on RTCPeerConnection.connectionState === 'connected' after a recovery ICE restart. */
const PEER_CONNECTION_RECOVERY_WAIT_MS = 5e3;
/** Polling interval in ms while waiting for RTCPeerConnection.connectionState to transition. */
const PEER_CONNECTION_RECOVERY_POLL_MS = 100;
/** Polling interval for LocalAudioPipeline.level$ (ms). ~30fps is smooth for meters. */
const AUDIO_LEVEL_POLL_INTERVAL_MS = 33;
/** RMS level threshold (0..1) above which the local participant is considered speaking. */
const VAD_THRESHOLD = .03;
/** Hold window in ms below the threshold before speaking$ flips back to false. */
const VAD_HOLD_MS = 250;
/** Whether to persist device selections to storage by default. */
const DEFAULT_PERSIST_DEVICE_SELECTION = true;
/** Whether to auto-apply device changes to active calls by default. */
const DEFAULT_SYNC_DEVICES_TO_ACTIVE_CALLS = true;
/** Storage keys for persisted device selections. */
const DEVICE_STORAGE_KEY_AUDIO_INPUT = "sw:device:audioinput";
const DEVICE_STORAGE_KEY_AUDIO_OUTPUT = "sw:device:audiooutput";
const DEVICE_STORAGE_KEY_VIDEO_INPUT = "sw:device:videoinput";
/** Whether to auto-mute video when the tab becomes hidden. */
const DEFAULT_AUTO_MUTE_VIDEO_ON_HIDDEN = false;
/** Whether to re-enumerate devices when the page becomes visible. */
const DEFAULT_REFRESH_DEVICES_ON_VISIBLE = true;
/** Whether to check peer connection health when the page becomes visible. */
const DEFAULT_CHECK_CONNECTION_ON_VISIBLE = true;
/** Whether automatic video degradation on low bandwidth is enabled. */
const DEFAULT_ENABLE_AUTO_DEGRADATION = true;
/** Bitrate in kbps below which video is automatically disabled. */
const DEFAULT_DEGRADATION_BITRATE_THRESHOLD_KBPS = 150;
/** Bitrate in kbps above which video is automatically re-enabled (hysteresis). */
const DEFAULT_DEGRADATION_RECOVERY_THRESHOLD_KBPS = 300;
/** Whether relay-only escalation is enabled as a last-resort recovery tier. */
const DEFAULT_ENABLE_RELAY_FALLBACK = true;
/** Whether to listen for browser online/offline/connection events. */
const DEFAULT_ENABLE_NETWORK_CHANGE_DETECTION = true;
/** Whether to intercept server-sent media-timeout hangups and attempt recovery. */
const DEFAULT_ENABLE_SERVER_HANGUP_INTERCEPTION = true;
/** Default video track constraints applied when video is enabled without explicit constraints. */
const DEFAULT_VIDEO_CONSTRAINTS = {
	width: { ideal: 1280 },
	height: { ideal: 720 },
	aspectRatio: 16 / 9
};
/** Whether stereo Opus is enabled by default. */
const DEFAULT_STEREO_AUDIO = false;
/** Max average bitrate for stereo Opus in bits per second. */
const DEFAULT_STEREO_MAX_AVERAGE_BITRATE = 51e4;

//#endregion
//#region src/utils/time.ts
function fromSecToMs(seconds) {
	return seconds * 1e3;
}
function fromMsToSec(milliseconds) {
	return Math.round(milliseconds / 100) / 10;
}

//#endregion
//#region src/containers/PreferencesContainer.ts
const logger$30 = getLogger();
var PreferencesContainer = class PreferencesContainer {
	static get instance() {
		this._instance ??= new PreferencesContainer();
		return this._instance;
	}
	constructor() {
		this.deviceDebounceTime = DEFAULT_DEVICE_DEBOUNCE_TIME_MS;
		this.devicePollingInterval = DEFAULT_DEVICE_POLLING_INTERVAL_MS;
		this.reconnectCallsTimeout = DEFAULT_RECONNECT_CALLS_TIMEOUT_MS;
		this.connectionTimeout = DEFAULT_CONNECTION_TIMEOUT_MS;
		this.reconnectDelayMin = DEFAULT_RECONNECT_DELAY_MIN_MS;
		this.reconnectDelayMax = DEFAULT_RECONNECT_DELAY_MAX_MS;
		this.disableUdpIceServers = false;
		this.relayOnly = false;
		this.iceCandidateTimeout = DEFAULT_ICE_CANDIDATE_TIMEOUT_MS;
		this.iceGatheringTimeout = DEFAULT_ICE_GATHERING_TIMEOUT_MS;
		this.defaultSignalWireOptions = {
			skipConnection: false,
			skipRegister: false,
			reconnectAttachedCalls: false,
			skipDeviceMonitoring: false,
			savePreferences: false
		};
		this.receiveVideo = false;
		this.receiveAudio = true;
		this.preferredAudioInput = null;
		this.preferredAudioOutput = null;
		this.preferredVideoInput = null;
		this.inviteSubscribeScreenshare = ["video.room.screenshare"];
		this.inviteSubscribeAdditionalDevice = [];
		this.inviteSubscribeMainDevice = [
			"track",
			"destroy",
			"member.updated.videoMuted",
			"layout.changed",
			"room.subscribed",
			"member.updated.audioMuted",
			"media.connected",
			"room.updated",
			"call.joined"
		];
		this.userVariables = {};
		this.statsPollingInterval = DEFAULT_STATS_POLLING_INTERVAL_MS;
		this.statsBaselineSamples = DEFAULT_STATS_BASELINE_SAMPLES;
		this.statsNoPacketThreshold = DEFAULT_STATS_NO_PACKET_THRESHOLD_MS;
		this.statsRttSpikeMultiplier = DEFAULT_STATS_RTT_SPIKE_MULTIPLIER;
		this.statsPacketLossThreshold = DEFAULT_STATS_PACKET_LOSS_THRESHOLD;
		this.statsJitterSpikeMultiplier = DEFAULT_STATS_JITTER_SPIKE_MULTIPLIER;
		this.statsHistorySize = DEFAULT_STATS_HISTORY_SIZE;
		this.keyframeMaxBurst = DEFAULT_KEYFRAME_MAX_BURST$1;
		this.keyframeBurstWindow = DEFAULT_KEYFRAME_BURST_WINDOW_MS$1;
		this.keyframeCooldown = DEFAULT_KEYFRAME_COOLDOWN_MS$1;
		this.reinviteDebounceTime = DEFAULT_REINVITE_DEBOUNCE_TIME_MS;
		this.reinviteMaxAttempts = DEFAULT_REINVITE_MAX_ATTEMPTS;
		this.reinviteTimeout = DEFAULT_REINVITE_TIMEOUT_MS;
		this.recoveryDebounceTime = DEFAULT_RECOVERY_DEBOUNCE_TIME_MS;
		this.recoveryCooldown = DEFAULT_RECOVERY_COOLDOWN_MS;
		this.iceDisconnectedGracePeriod = DEFAULT_ICE_DISCONNECTED_GRACE_PERIOD_MS;
		this.iceRestartTimeout = DEFAULT_ICE_RESTART_TIMEOUT_MS$1;
		this.maxRecoveryAttempts = DEFAULT_MAX_RECOVERY_ATTEMPTS;
		this.enableRelayFallback = DEFAULT_ENABLE_RELAY_FALLBACK;
		this.enableNetworkChangeDetection = DEFAULT_ENABLE_NETWORK_CHANGE_DETECTION;
		this.enableServerHangupInterception = DEFAULT_ENABLE_SERVER_HANGUP_INTERCEPTION;
		this.persistDeviceSelection = DEFAULT_PERSIST_DEVICE_SELECTION;
		this.syncDevicesToActiveCalls = DEFAULT_SYNC_DEVICES_TO_ACTIVE_CALLS;
		this.autoMuteVideoOnHidden = DEFAULT_AUTO_MUTE_VIDEO_ON_HIDDEN;
		this.refreshDevicesOnVisible = DEFAULT_REFRESH_DEVICES_ON_VISIBLE;
		this.checkConnectionOnVisible = DEFAULT_CHECK_CONNECTION_ON_VISIBLE;
		this.defaultAudioConstraints = void 0;
		this.defaultVideoConstraints = void 0;
		this.stereoAudio = DEFAULT_STEREO_AUDIO;
		this.enableAutoDegradation = DEFAULT_ENABLE_AUTO_DEGRADATION;
		this.degradationBitrateThreshold = DEFAULT_DEGRADATION_BITRATE_THRESHOLD_KBPS;
		this.degradationRecoveryThreshold = DEFAULT_DEGRADATION_RECOVERY_THRESHOLD_KBPS;
		this.preferredVideoCodecs = [];
		this.preferredAudioCodecs = [];
	}
	get preferredMediaOptions() {
		return {
			receiveVideo: this.receiveVideo,
			receiveAudio: this.receiveAudio,
			inputAudioDeviceConstraints: this.inputAudioDeviceConstraints,
			inputVideoDeviceConstraints: this.inputVideoDeviceConstraints
		};
	}
	get inputAudioDeviceConstraints() {
		return this._inputAudioDeviceConstraints;
	}
	set inputAudioDeviceConstraints(value) {
		this._inputAudioDeviceConstraints = value;
	}
	get inputVideoDeviceConstraints() {
		return this._inputVideoDeviceConstraints;
	}
	set inputVideoDeviceConstraints(value) {
		this._inputVideoDeviceConstraints = value;
	}
};
/** Keys of StoredPreferences that map to number fields on PreferencesContainer. */
const STORED_NUMBER_KEYS = [
	"connectionTimeout",
	"reconnectCallsTimeout",
	"reconnectDelayMin",
	"reconnectDelayMax",
	"iceCandidateTimeout",
	"iceGatheringTimeout",
	"deviceDebounceTime",
	"devicePollingInterval",
	"statsPollingInterval",
	"statsBaselineSamples",
	"statsNoPacketThreshold",
	"statsRttSpikeMultiplier",
	"statsPacketLossThreshold",
	"statsJitterSpikeMultiplier",
	"statsHistorySize",
	"keyframeMaxBurst",
	"keyframeBurstWindow",
	"keyframeCooldown",
	"reinviteDebounceTime",
	"reinviteMaxAttempts",
	"reinviteTimeout",
	"recoveryDebounceTime",
	"recoveryCooldown",
	"iceDisconnectedGracePeriod",
	"iceRestartTimeout",
	"maxRecoveryAttempts",
	"degradationBitrateThreshold",
	"degradationRecoveryThreshold"
];
/** Keys of StoredPreferences that map to boolean fields on PreferencesContainer. */
const STORED_BOOLEAN_KEYS = [
	"receiveVideo",
	"receiveAudio",
	"disableUdpIceServers",
	"relayOnly",
	"enableRelayFallback",
	"enableNetworkChangeDetection",
	"enableServerHangupInterception",
	"persistDeviceSelection",
	"syncDevicesToActiveCalls",
	"autoMuteVideoOnHidden",
	"refreshDevicesOnVisible",
	"checkConnectionOnVisible",
	"stereoAudio",
	"enableAutoDegradation"
];
/** Collects the serializable preferences from the container. */
function collectStoredPreferences() {
	const container = PreferencesContainer.instance;
	return {
		connectionTimeout: container.connectionTimeout,
		reconnectCallsTimeout: container.reconnectCallsTimeout,
		reconnectDelayMin: container.reconnectDelayMin,
		reconnectDelayMax: container.reconnectDelayMax,
		relayHost: container.relayHost,
		receiveVideo: container.receiveVideo,
		receiveAudio: container.receiveAudio,
		disableUdpIceServers: container.disableUdpIceServers,
		relayOnly: container.relayOnly,
		iceCandidateTimeout: container.iceCandidateTimeout,
		iceGatheringTimeout: container.iceGatheringTimeout,
		deviceDebounceTime: container.deviceDebounceTime,
		devicePollingInterval: container.devicePollingInterval,
		iceServers: container.iceServers,
		userVariables: container.userVariables,
		statsPollingInterval: container.statsPollingInterval,
		statsBaselineSamples: container.statsBaselineSamples,
		statsNoPacketThreshold: container.statsNoPacketThreshold,
		statsRttSpikeMultiplier: container.statsRttSpikeMultiplier,
		statsPacketLossThreshold: container.statsPacketLossThreshold,
		statsJitterSpikeMultiplier: container.statsJitterSpikeMultiplier,
		statsHistorySize: container.statsHistorySize,
		keyframeMaxBurst: container.keyframeMaxBurst,
		keyframeBurstWindow: container.keyframeBurstWindow,
		keyframeCooldown: container.keyframeCooldown,
		reinviteDebounceTime: container.reinviteDebounceTime,
		reinviteMaxAttempts: container.reinviteMaxAttempts,
		reinviteTimeout: container.reinviteTimeout,
		recoveryDebounceTime: container.recoveryDebounceTime,
		recoveryCooldown: container.recoveryCooldown,
		iceDisconnectedGracePeriod: container.iceDisconnectedGracePeriod,
		iceRestartTimeout: container.iceRestartTimeout,
		maxRecoveryAttempts: container.maxRecoveryAttempts,
		enableRelayFallback: container.enableRelayFallback,
		enableNetworkChangeDetection: container.enableNetworkChangeDetection,
		enableServerHangupInterception: container.enableServerHangupInterception,
		persistDeviceSelection: container.persistDeviceSelection,
		syncDevicesToActiveCalls: container.syncDevicesToActiveCalls,
		autoMuteVideoOnHidden: container.autoMuteVideoOnHidden,
		refreshDevicesOnVisible: container.refreshDevicesOnVisible,
		checkConnectionOnVisible: container.checkConnectionOnVisible,
		stereoAudio: container.stereoAudio,
		enableAutoDegradation: container.enableAutoDegradation,
		degradationBitrateThreshold: container.degradationBitrateThreshold,
		degradationRecoveryThreshold: container.degradationRecoveryThreshold,
		preferredVideoCodecs: container.preferredVideoCodecs,
		preferredAudioCodecs: container.preferredAudioCodecs
	};
}
/** Applies stored preferences to the container. */
function applyStoredPreferences(stored) {
	const container = PreferencesContainer.instance;
	for (const key of STORED_NUMBER_KEYS) if (stored[key] !== void 0) container[key] = stored[key];
	for (const key of STORED_BOOLEAN_KEYS) if (stored[key] !== void 0) container[key] = stored[key];
	if (stored.relayHost !== void 0) container.relayHost = stored.relayHost;
	if (stored.iceServers !== void 0) container.iceServers = stored.iceServers;
	if (stored.userVariables !== void 0) container.userVariables = stored.userVariables;
	if (stored.preferredVideoCodecs !== void 0) container.preferredVideoCodecs = stored.preferredVideoCodecs;
	if (stored.preferredAudioCodecs !== void 0) container.preferredAudioCodecs = stored.preferredAudioCodecs;
}
/**
* Public preferences API for configuring SDK behavior.
*
* Exposed as {@link SignalWire.preferences}. All timeout values
* are in seconds when accessed through this class.
*
* When {@link enableSavePreferences} is called, preferences are
* automatically loaded from storage and synced back on every change.
*/
var ClientPreferences = class {
	constructor() {
		this._storage = null;
	}
	/**
	* Enables persistence of preferences to storage.
	* Loads any previously saved preferences and syncs future changes.
	*/
	enableSavePreferences(storage) {
		this._storage = storage;
		this._loadFromStorage();
	}
	/** WebSocket connection timeout in seconds. */
	get connectionTimeout() {
		return fromMsToSec(PreferencesContainer.instance.connectionTimeout);
	}
	set connectionTimeout(seconds) {
		PreferencesContainer.instance.connectionTimeout = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Timeout for reconnecting to previously attached calls, in seconds. */
	get reconnectCallsTimeout() {
		return fromMsToSec(PreferencesContainer.instance.reconnectCallsTimeout);
	}
	set reconnectCallsTimeout(seconds) {
		PreferencesContainer.instance.reconnectCallsTimeout = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Minimum reconnection backoff delay in seconds. */
	get reconnectDelayMin() {
		return fromMsToSec(PreferencesContainer.instance.reconnectDelayMin);
	}
	set reconnectDelayMin(seconds) {
		PreferencesContainer.instance.reconnectDelayMin = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Maximum reconnection backoff delay in seconds. */
	get reconnectDelayMax() {
		return fromMsToSec(PreferencesContainer.instance.reconnectDelayMax);
	}
	set reconnectDelayMax(seconds) {
		PreferencesContainer.instance.reconnectDelayMax = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Custom relay host URL. Empty string uses the default. */
	get relayHost() {
		return PreferencesContainer.instance.relayHost ?? "";
	}
	set relayHost(value) {
		PreferencesContainer.instance.relayHost = value;
		this._saveToStorage();
	}
	/** Whether to receive remote video by default. */
	get receiveVideo() {
		return PreferencesContainer.instance.receiveVideo;
	}
	set receiveVideo(value) {
		PreferencesContainer.instance.receiveVideo = value;
		this._saveToStorage();
	}
	/** Whether to receive remote audio by default. */
	get receiveAudio() {
		return PreferencesContainer.instance.receiveAudio;
	}
	set receiveAudio(value) {
		PreferencesContainer.instance.receiveAudio = value;
		this._saveToStorage();
	}
	/** Preferred audio input device for new calls. */
	get preferredAudioInput() {
		return PreferencesContainer.instance.preferredAudioInput;
	}
	set preferredAudioInput(value) {
		PreferencesContainer.instance.preferredAudioInput = value;
	}
	/** Preferred audio output device for new calls. */
	get preferredAudioOutput() {
		return PreferencesContainer.instance.preferredAudioOutput;
	}
	set preferredAudioOutput(value) {
		PreferencesContainer.instance.preferredAudioOutput = value;
	}
	/** Preferred video input device for new calls. */
	get preferredVideoInput() {
		return PreferencesContainer.instance.preferredVideoInput;
	}
	set preferredVideoInput(value) {
		PreferencesContainer.instance.preferredVideoInput = value;
	}
	/** Default audio input track constraints. */
	get inputAudioConstraints() {
		return PreferencesContainer.instance.inputAudioDeviceConstraints;
	}
	set inputAudioConstraints(value) {
		PreferencesContainer.instance.inputAudioDeviceConstraints = value;
	}
	/** Default video input track constraints. */
	get inputVideoConstraints() {
		return PreferencesContainer.instance.inputVideoDeviceConstraints;
	}
	set inputVideoConstraints(value) {
		PreferencesContainer.instance.inputVideoDeviceConstraints = value;
	}
	/** Debounce time for device change events, in seconds. */
	get deviceDebounceTime() {
		return fromMsToSec(PreferencesContainer.instance.deviceDebounceTime);
	}
	set deviceDebounceTime(seconds) {
		PreferencesContainer.instance.deviceDebounceTime = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Polling interval for device enumeration, in seconds. */
	get devicePollingInterval() {
		return fromMsToSec(PreferencesContainer.instance.devicePollingInterval);
	}
	set devicePollingInterval(seconds) {
		PreferencesContainer.instance.devicePollingInterval = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Whether to filter out UDP-based ICE servers. */
	get disableUdpIceServers() {
		return PreferencesContainer.instance.disableUdpIceServers;
	}
	set disableUdpIceServers(value) {
		PreferencesContainer.instance.disableUdpIceServers = value;
		this._saveToStorage();
	}
	/** Whether to force TURN relay-only ICE candidates. */
	get relayOnly() {
		return PreferencesContainer.instance.relayOnly;
	}
	set relayOnly(value) {
		PreferencesContainer.instance.relayOnly = value;
		this._saveToStorage();
	}
	/** Timeout for individual ICE candidate gathering, in seconds. */
	get iceCandidateTimeout() {
		return fromMsToSec(PreferencesContainer.instance.iceCandidateTimeout);
	}
	set iceCandidateTimeout(seconds) {
		PreferencesContainer.instance.iceCandidateTimeout = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Timeout for the entire ICE gathering phase, in seconds. */
	get iceGatheringTimeout() {
		return fromMsToSec(PreferencesContainer.instance.iceGatheringTimeout);
	}
	set iceGatheringTimeout(seconds) {
		PreferencesContainer.instance.iceGatheringTimeout = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Custom ICE servers for TURN/STUN configuration. */
	get iceServers() {
		return PreferencesContainer.instance.iceServers;
	}
	set iceServers(value) {
		PreferencesContainer.instance.iceServers = value;
		this._saveToStorage();
	}
	/** Custom user variables attached to calls. */
	get userVariables() {
		return PreferencesContainer.instance.userVariables;
	}
	set userVariables(value) {
		PreferencesContainer.instance.userVariables = value;
		this._saveToStorage();
	}
	/** Stats polling interval in milliseconds. */
	get statsPollingInterval() {
		return PreferencesContainer.instance.statsPollingInterval;
	}
	set statsPollingInterval(value) {
		PreferencesContainer.instance.statsPollingInterval = value;
		this._saveToStorage();
	}
	/** Number of baseline samples for stats monitoring. */
	get statsBaselineSamples() {
		return PreferencesContainer.instance.statsBaselineSamples;
	}
	set statsBaselineSamples(value) {
		PreferencesContainer.instance.statsBaselineSamples = value;
		this._saveToStorage();
	}
	/** Duration in ms with no inbound packets before a critical issue is emitted. */
	get statsNoPacketThreshold() {
		return PreferencesContainer.instance.statsNoPacketThreshold;
	}
	set statsNoPacketThreshold(value) {
		PreferencesContainer.instance.statsNoPacketThreshold = value;
		this._saveToStorage();
	}
	/** Multiplier for RTT spike detection relative to baseline. */
	get statsRttSpikeMultiplier() {
		return PreferencesContainer.instance.statsRttSpikeMultiplier;
	}
	set statsRttSpikeMultiplier(value) {
		PreferencesContainer.instance.statsRttSpikeMultiplier = value;
		this._saveToStorage();
	}
	/** Packet loss fraction threshold (0-1) for issue detection. */
	get statsPacketLossThreshold() {
		return PreferencesContainer.instance.statsPacketLossThreshold;
	}
	set statsPacketLossThreshold(value) {
		PreferencesContainer.instance.statsPacketLossThreshold = value;
		this._saveToStorage();
	}
	/** Multiplier for jitter spike detection relative to baseline. */
	get statsJitterSpikeMultiplier() {
		return PreferencesContainer.instance.statsJitterSpikeMultiplier;
	}
	set statsJitterSpikeMultiplier(value) {
		PreferencesContainer.instance.statsJitterSpikeMultiplier = value;
		this._saveToStorage();
	}
	/** Number of seconds of metrics history to retain. */
	get statsHistorySize() {
		return PreferencesContainer.instance.statsHistorySize;
	}
	set statsHistorySize(value) {
		PreferencesContainer.instance.statsHistorySize = value;
		this._saveToStorage();
	}
	/** Maximum keyframe requests in a burst window. */
	get keyframeMaxBurst() {
		return PreferencesContainer.instance.keyframeMaxBurst;
	}
	set keyframeMaxBurst(value) {
		PreferencesContainer.instance.keyframeMaxBurst = value;
		this._saveToStorage();
	}
	/** Keyframe burst window duration in milliseconds. */
	get keyframeBurstWindow() {
		return PreferencesContainer.instance.keyframeBurstWindow;
	}
	set keyframeBurstWindow(value) {
		PreferencesContainer.instance.keyframeBurstWindow = value;
		this._saveToStorage();
	}
	/** Cooldown period in ms after keyframe burst limit is reached. */
	get keyframeCooldown() {
		return PreferencesContainer.instance.keyframeCooldown;
	}
	set keyframeCooldown(value) {
		PreferencesContainer.instance.keyframeCooldown = value;
		this._saveToStorage();
	}
	/** Minimum time in ms between re-INVITE attempts. */
	get reinviteDebounceTime() {
		return PreferencesContainer.instance.reinviteDebounceTime;
	}
	set reinviteDebounceTime(value) {
		PreferencesContainer.instance.reinviteDebounceTime = value;
		this._saveToStorage();
	}
	/** Maximum re-INVITE attempts per call. */
	get reinviteMaxAttempts() {
		return PreferencesContainer.instance.reinviteMaxAttempts;
	}
	set reinviteMaxAttempts(value) {
		PreferencesContainer.instance.reinviteMaxAttempts = value;
		this._saveToStorage();
	}
	/** Timeout in ms for a single re-INVITE attempt. */
	get reinviteTimeout() {
		return PreferencesContainer.instance.reinviteTimeout;
	}
	set reinviteTimeout(value) {
		PreferencesContainer.instance.reinviteTimeout = value;
		this._saveToStorage();
	}
	/** Recovery signal debounce window in seconds. */
	get recoveryDebounceTime() {
		return fromMsToSec(PreferencesContainer.instance.recoveryDebounceTime);
	}
	set recoveryDebounceTime(seconds) {
		PreferencesContainer.instance.recoveryDebounceTime = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Cooldown period between recovery attempts in seconds. */
	get recoveryCooldown() {
		return fromMsToSec(PreferencesContainer.instance.recoveryCooldown);
	}
	set recoveryCooldown(seconds) {
		PreferencesContainer.instance.recoveryCooldown = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Grace period before treating ICE 'disconnected' as failure, in seconds. */
	get iceDisconnectedGracePeriod() {
		return fromMsToSec(PreferencesContainer.instance.iceDisconnectedGracePeriod);
	}
	set iceDisconnectedGracePeriod(seconds) {
		PreferencesContainer.instance.iceDisconnectedGracePeriod = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Timeout for a single ICE restart attempt in seconds. */
	get iceRestartTimeout() {
		return fromMsToSec(PreferencesContainer.instance.iceRestartTimeout);
	}
	set iceRestartTimeout(seconds) {
		PreferencesContainer.instance.iceRestartTimeout = fromSecToMs(seconds);
		this._saveToStorage();
	}
	/** Maximum recovery attempts before giving up. */
	get maxRecoveryAttempts() {
		return PreferencesContainer.instance.maxRecoveryAttempts;
	}
	set maxRecoveryAttempts(value) {
		PreferencesContainer.instance.maxRecoveryAttempts = value;
		this._saveToStorage();
	}
	/** Whether relay-only escalation is enabled as a last-resort recovery tier. */
	get enableRelayFallback() {
		return PreferencesContainer.instance.enableRelayFallback;
	}
	set enableRelayFallback(value) {
		PreferencesContainer.instance.enableRelayFallback = value;
		this._saveToStorage();
	}
	/** Whether browser network change detection (online/offline) is enabled. */
	get enableNetworkChangeDetection() {
		return PreferencesContainer.instance.enableNetworkChangeDetection;
	}
	set enableNetworkChangeDetection(value) {
		PreferencesContainer.instance.enableNetworkChangeDetection = value;
		this._saveToStorage();
	}
	/** Whether server-sent media-timeout hangups are intercepted for recovery. */
	get enableServerHangupInterception() {
		return PreferencesContainer.instance.enableServerHangupInterception;
	}
	set enableServerHangupInterception(value) {
		PreferencesContainer.instance.enableServerHangupInterception = value;
		this._saveToStorage();
	}
	/** Whether device selections are persisted to storage. */
	get persistDeviceSelection() {
		return PreferencesContainer.instance.persistDeviceSelection;
	}
	set persistDeviceSelection(value) {
		PreferencesContainer.instance.persistDeviceSelection = value;
		this._saveToStorage();
	}
	/** Whether device changes are auto-applied to active calls. */
	get syncDevicesToActiveCalls() {
		return PreferencesContainer.instance.syncDevicesToActiveCalls;
	}
	set syncDevicesToActiveCalls(value) {
		PreferencesContainer.instance.syncDevicesToActiveCalls = value;
		this._saveToStorage();
	}
	/** Whether to auto-mute video when the tab becomes hidden. */
	get autoMuteVideoOnHidden() {
		return PreferencesContainer.instance.autoMuteVideoOnHidden;
	}
	set autoMuteVideoOnHidden(value) {
		PreferencesContainer.instance.autoMuteVideoOnHidden = value;
		this._saveToStorage();
	}
	/** Whether to re-enumerate devices when the page becomes visible. */
	get refreshDevicesOnVisible() {
		return PreferencesContainer.instance.refreshDevicesOnVisible;
	}
	set refreshDevicesOnVisible(value) {
		PreferencesContainer.instance.refreshDevicesOnVisible = value;
		this._saveToStorage();
	}
	/** Whether to check peer connection health when the page becomes visible. */
	get checkConnectionOnVisible() {
		return PreferencesContainer.instance.checkConnectionOnVisible;
	}
	set checkConnectionOnVisible(value) {
		PreferencesContainer.instance.checkConnectionOnVisible = value;
		this._saveToStorage();
	}
	/** Default audio track constraints applied when no explicit constraints are provided. */
	get defaultAudioConstraints() {
		return PreferencesContainer.instance.defaultAudioConstraints;
	}
	set defaultAudioConstraints(value) {
		PreferencesContainer.instance.defaultAudioConstraints = value;
	}
	/** Default video track constraints applied when video is enabled without explicit constraints. */
	get defaultVideoConstraints() {
		return PreferencesContainer.instance.defaultVideoConstraints;
	}
	set defaultVideoConstraints(value) {
		PreferencesContainer.instance.defaultVideoConstraints = value;
	}
	/** Whether stereo Opus is enabled globally. */
	get stereoAudio() {
		return PreferencesContainer.instance.stereoAudio;
	}
	set stereoAudio(value) {
		PreferencesContainer.instance.stereoAudio = value;
		this._saveToStorage();
	}
	/** Whether automatic video degradation on low bandwidth is enabled. */
	get enableAutoDegradation() {
		return PreferencesContainer.instance.enableAutoDegradation;
	}
	set enableAutoDegradation(value) {
		PreferencesContainer.instance.enableAutoDegradation = value;
		this._saveToStorage();
	}
	/** Bitrate in kbps below which video is automatically disabled. */
	get degradationBitrateThreshold() {
		return PreferencesContainer.instance.degradationBitrateThreshold;
	}
	set degradationBitrateThreshold(value) {
		PreferencesContainer.instance.degradationBitrateThreshold = value;
		this._saveToStorage();
	}
	/** Bitrate in kbps above which video is automatically re-enabled. */
	get degradationRecoveryThreshold() {
		return PreferencesContainer.instance.degradationRecoveryThreshold;
	}
	set degradationRecoveryThreshold(value) {
		PreferencesContainer.instance.degradationRecoveryThreshold = value;
		this._saveToStorage();
	}
	/** Preferred video codecs in priority order. */
	get preferredVideoCodecs() {
		return PreferencesContainer.instance.preferredVideoCodecs;
	}
	set preferredVideoCodecs(value) {
		PreferencesContainer.instance.preferredVideoCodecs = value;
		this._saveToStorage();
	}
	/** Preferred audio codecs in priority order. */
	get preferredAudioCodecs() {
		return PreferencesContainer.instance.preferredAudioCodecs;
	}
	set preferredAudioCodecs(value) {
		PreferencesContainer.instance.preferredAudioCodecs = value;
		this._saveToStorage();
	}
	/** Saves current preferences to storage (fire-and-forget). */
	_saveToStorage() {
		if (!this._storage) return;
		const data = collectStoredPreferences();
		this._storage.setItem(PREFERENCES_STORAGE_KEY, data, "local").catch((error) => {
			logger$30.error(`[ClientPreferences] Failed to save preferences: ${String(error)}`);
		});
	}
	/** Loads preferences from storage and applies them to the container. */
	_loadFromStorage() {
		if (!this._storage) return;
		this._storage.getItem(PREFERENCES_STORAGE_KEY, "local").then((stored) => {
			if (stored) applyStoredPreferences(stored);
		}).catch((error) => {
			logger$30.error(`[ClientPreferences] Failed to load preferences: ${String(error)}`);
		});
	}
};

//#endregion
//#region src/utils/toError.ts
/**
* Normalizes an unknown caught value into a proper Error instance.
*
* In catch blocks, the caught value is `unknown` — it could be an Error,
* string, number, or any other value. This utility ensures a consistent
* Error object is produced.
*/
function toError(value) {
	if (value instanceof Error) return value;
	return new Error(String(value));
}

//#endregion
//#region src/controllers/NavigatorDeviceController.ts
const logger$29 = getLogger();
/** Maps a device kind to its storage key. */
const DEVICE_STORAGE_KEYS = {
	audioinput: DEVICE_STORAGE_KEY_AUDIO_INPUT,
	audiooutput: DEVICE_STORAGE_KEY_AUDIO_OUTPUT,
	videoinput: DEVICE_STORAGE_KEY_VIDEO_INPUT
};
const initialDevicesState = {
	audioinput: [],
	audiooutput: [],
	videoinput: []
};
const initialSelectedDevicesState = {
	audioinput: null,
	audiooutput: null,
	videoinput: null
};
var NavigatorDeviceController = class extends Destroyable {
	constructor(webRTCApiProvider, storageManager) {
		super();
		this.webRTCApiProvider = webRTCApiProvider;
		this.deviceChangeHandler = () => {
			logger$29.debug("[DeviceController] Device change detected");
			this.enumerateDevices();
		};
		this._devicesState$ = this.createBehaviorSubject(initialDevicesState);
		this._selectedDevicesState$ = this.createBehaviorSubject(initialSelectedDevicesState);
		this._errors$ = this.createReplaySubject(1);
		this._deviceHistory = new DeviceHistoryManager();
		this._deviceRecovered$ = this.createSubject();
		this._audioInputDisabled$ = this.createBehaviorSubject(false);
		this._videoInputDisabled$ = this.createBehaviorSubject(false);
		this._lastAudioInputBeforeDisable = null;
		this._lastVideoInputBeforeDisable = null;
		this._persistedDevices = {};
		this._storageManager = storageManager;
		this.init();
	}
	/** Sets the storage manager for device persistence. */
	setStorageManager(storageManager) {
		this._storageManager = storageManager;
		this.loadPersistedDevices();
	}
	get selectedAudioInputDeviceConstraints() {
		if (this._audioInputDisabled$.value) return false;
		return this.deviceInfoToConstraints(this.selectedAudioInputDevice);
	}
	get selectedVideoInputDeviceConstraints() {
		if (this._videoInputDisabled$.value) return false;
		return this.deviceInfoToConstraints(this.selectedVideoInputDevice);
	}
	deviceInfoToConstraints(deviceInfo) {
		if (!deviceInfo?.deviceId || deviceInfo.deviceId.trim() === "") return {};
		const devices = deviceInfo.kind === "audioinput" ? this.audioInputDevices : this.videoInputDevices;
		const device = devices.find((device$1) => device$1.deviceId === deviceInfo.deviceId) ?? devices.find((device$1) => device$1.label === deviceInfo.label);
		if (device) return { deviceId: { exact: device.deviceId } };
		return {};
	}
	get errors$() {
		return this.cachedObservable("errors$", () => this._errors$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	/** Observable that emits when the SDK auto-switches a device. */
	get deviceRecovered$() {
		return this._deviceRecovered$.asObservable().pipe(takeUntil(this.destroyed$));
	}
	get videoInputDisabled$() {
		return this.cachedObservable("videoInputDisabled$", () => this._videoInputDisabled$.asObservable().pipe(distinctUntilChanged(), takeUntil(this.destroyed$)));
	}
	get audioInputDisabled$() {
		return this.cachedObservable("audioInputDisabled$", () => this._audioInputDisabled$.asObservable().pipe(distinctUntilChanged(), takeUntil(this.destroyed$)));
	}
	get videoInputDisabled() {
		return this._videoInputDisabled$.value;
	}
	get audioInputDisabled() {
		return this._audioInputDisabled$.value;
	}
	get audioInputDevices$() {
		return this.cachedObservable("audioInputDevices$", () => this._devicesState$.pipe(map((state) => state.audioinput), distinctUntilChanged(), takeUntil(this.destroyed$)));
	}
	get audioOutputDevices$() {
		return this.cachedObservable("audioOutputDevices$", () => this._devicesState$.pipe(map((state) => state.audiooutput), distinctUntilChanged(), takeUntil(this.destroyed$)));
	}
	get videoInputDevices$() {
		return this.cachedObservable("videoInputDevices$", () => this._devicesState$.pipe(map((state) => state.videoinput), distinctUntilChanged(), takeUntil(this.destroyed$)));
	}
	get selectedAudioInputDevice$() {
		return this.cachedObservable("selectedAudioInputDevice$", () => this._selectedDevicesState$.asObservable().pipe(map((state) => state.audioinput), distinctUntilChanged(), takeUntil(this.destroyed$), tap((info) => logger$29.debug("[DeviceController] Selected audio input device changed:", info))));
	}
	get selectedAudioOutputDevice$() {
		return this.cachedObservable("selectedAudioOutputDevice$", () => this._selectedDevicesState$.asObservable().pipe(map((state) => state.audiooutput), distinctUntilChanged(), takeUntil(this.destroyed$), tap((info) => logger$29.debug("[DeviceController] Selected audio output device changed:", info))));
	}
	get selectedVideoInputDevice$() {
		return this.cachedObservable("selectedVideoInputDevice$", () => this._selectedDevicesState$.asObservable().pipe(map((state) => state.videoinput), distinctUntilChanged(), takeUntil(this.destroyed$), tap((info) => logger$29.debug("[DeviceController] Selected video input device changed:", info))));
	}
	get selectedAudioInputDevice() {
		if (this._audioInputDisabled$.value) return null;
		return this._selectedDevicesState$.value.audioinput;
	}
	get selectedAudioOutputDevice() {
		return this._selectedDevicesState$.value.audiooutput;
	}
	get selectedVideoInputDevice() {
		if (this._videoInputDisabled$.value) return null;
		return this._selectedDevicesState$.value.videoinput;
	}
	get audioInputDevices() {
		return this._devicesState$.value.audioinput;
	}
	get audioOutputDevices() {
		return this._devicesState$.value.audiooutput;
	}
	get videoInputDevices() {
		return this._devicesState$.value.videoinput;
	}
	disableAudioInput() {
		if (!this._audioInputDisabled$.value) {
			this._lastAudioInputBeforeDisable = this._selectedDevicesState$.value.audioinput;
			this._audioInputDisabled$.next(true);
			this._selectedDevicesState$.next({
				...this._selectedDevicesState$.value,
				audioinput: null
			});
		}
	}
	enableAudioInput() {
		if (this._audioInputDisabled$.value) {
			this._audioInputDisabled$.next(false);
			const restored = this._lastAudioInputBeforeDisable ?? this.audioInputDevices[0];
			this._selectedDevicesState$.next({
				...this._selectedDevicesState$.value,
				audioinput: restored
			});
			this._lastAudioInputBeforeDisable = null;
		}
	}
	disableVideoInput() {
		if (!this._videoInputDisabled$.value) {
			this._lastVideoInputBeforeDisable = this._selectedDevicesState$.value.videoinput;
			this._videoInputDisabled$.next(true);
			this._selectedDevicesState$.next({
				...this._selectedDevicesState$.value,
				videoinput: null
			});
		}
	}
	enableVideoInput() {
		if (this._videoInputDisabled$.value) {
			this._videoInputDisabled$.next(false);
			const restored = this._lastVideoInputBeforeDisable ?? this.videoInputDevices[0];
			this._selectedDevicesState$.next({
				...this._selectedDevicesState$.value,
				videoinput: restored
			});
			this._lastVideoInputBeforeDisable = null;
		}
	}
	selectAudioInputDevice(device) {
		if (this._audioInputDisabled$.value && device) this._audioInputDisabled$.next(false);
		const previous = this._selectedDevicesState$.value.audioinput;
		if (previous && previous.deviceId !== device?.deviceId) this._deviceHistory.push("audioinput", previous);
		this._selectedDevicesState$.next({
			...this._selectedDevicesState$.value,
			audioinput: device
		});
		if (device) this.persistDeviceSelection("audioinput", device);
	}
	selectVideoInputDevice(device) {
		logger$29.debug("[DeviceController] Setting selected video input device:", device);
		if (this._videoInputDisabled$.value && device) this._videoInputDisabled$.next(false);
		const previous = this._selectedDevicesState$.value.videoinput;
		if (previous && previous.deviceId !== device?.deviceId) this._deviceHistory.push("videoinput", previous);
		this._selectedDevicesState$.next({
			...this._selectedDevicesState$.value,
			videoinput: device
		});
		if (device) this.persistDeviceSelection("videoinput", device);
	}
	selectAudioOutputDevice(device) {
		const previous = this._selectedDevicesState$.value.audiooutput;
		if (previous && previous.deviceId !== device?.deviceId) this._deviceHistory.push("audiooutput", previous);
		this._selectedDevicesState$.next({
			...this._selectedDevicesState$.value,
			audiooutput: device
		});
		if (device) this.persistDeviceSelection("audiooutput", device);
	}
	init() {
		this.loadPersistedDevices();
		this.subscribeTo(this._devicesState$.pipe(debounceTime(PreferencesContainer.instance.deviceDebounceTime)), (devicesState) => {
			const currentSelected = this._selectedDevicesState$.value;
			const newAudioInput = this._audioInputDisabled$.value ? null : this.resolveDevice("audioinput", devicesState.audioinput, currentSelected.audioinput, PreferencesContainer.instance.preferredAudioInput);
			const newAudioOutput = this.resolveDevice("audiooutput", devicesState.audiooutput, currentSelected.audiooutput, PreferencesContainer.instance.preferredAudioOutput);
			const newVideoInput = this._videoInputDisabled$.value ? null : this.resolveDevice("videoinput", devicesState.videoinput, currentSelected.videoinput, PreferencesContainer.instance.preferredVideoInput);
			if (newAudioInput !== currentSelected.audioinput || newAudioOutput !== currentSelected.audiooutput || newVideoInput !== currentSelected.videoinput) {
				const shouldSync = PreferencesContainer.instance.syncDevicesToActiveCalls;
				this._selectedDevicesState$.next({
					audioinput: shouldSync || !currentSelected.audioinput ? newAudioInput : currentSelected.audioinput,
					audiooutput: shouldSync || !currentSelected.audiooutput ? newAudioOutput : currentSelected.audiooutput,
					videoinput: shouldSync || !currentSelected.videoinput ? newVideoInput : currentSelected.videoinput
				});
			}
		});
		this.enumerateDevices();
	}
	/**
	* Resolves the best device for a given kind, using device history
	* for succession when the selected device disappears.
	* Section 5.1 adds persisted device resolution.
	* Section 5.4 adds duplicate-named device handling.
	*/
	resolveDevice(kind, devices, selected, preferred) {
		if (devices.length === 0) return null;
		if (selected) {
			if (devices.find((device) => device.deviceId === selected.deviceId)) return selected;
			const labelMatches = devices.filter((device) => device.label === selected.label);
			if (labelMatches.length === 1) return labelMatches[0];
			if (labelMatches.length > 1) {
				const groupMatch = labelMatches.find((device) => device.groupId === selected.groupId);
				if (groupMatch) return groupMatch;
				this.emitDeviceRecovered(kind, selected, null, "ambiguous_match");
				return null;
			}
			const fromHistory = this._deviceHistory.findInHistory(kind, devices);
			if (fromHistory) {
				logger$29.debug(`[DeviceController] Device disappeared, falling back to history: ${fromHistory.label}`);
				this.emitDeviceRecovered(kind, selected, fromHistory, "device_disconnected");
				return fromHistory;
			}
			const newDevice = (preferred ? devices.find((device) => device.deviceId === preferred.deviceId || device.label === preferred.label) : void 0) ?? devices[0];
			this.emitDeviceRecovered(kind, selected, newDevice, "fallback_to_default");
			return newDevice;
		}
		const persisted = this._persistedDevices[kind];
		if (persisted) {
			const fromPersisted = this.resolvePersistedDevice(kind, persisted, devices);
			if (fromPersisted) return fromPersisted;
		}
		if (preferred) {
			const preferredDevice = devices.find((device) => device.deviceId === preferred.deviceId || device.label === preferred.label);
			if (preferredDevice) return preferredDevice;
		}
		return devices[0];
	}
	/**
	* Section 5.1: Resolves a stored device preference against the current device list.
	* Priority: exact deviceId > groupId+label > label (single match only)
	*/
	resolvePersistedDevice(_kind, stored, devices) {
		const exactMatch = devices.find((d) => d.deviceId === stored.deviceId);
		if (exactMatch) return exactMatch;
		const groupLabelMatch = devices.find((d) => d.groupId === stored.groupId && d.label === stored.label);
		if (groupLabelMatch) return groupLabelMatch;
		const labelMatches = devices.filter((d) => d.label === stored.label);
		if (labelMatches.length === 1) return labelMatches[0];
		return null;
	}
	emitDeviceRecovered(kind, previousDevice, newDevice, reason) {
		try {
			this._deviceRecovered$.next({
				kind,
				previousDevice,
				newDevice,
				reason
			});
		} catch {}
	}
	async persistDeviceSelection(kind, device) {
		if (!this._storageManager || !PreferencesContainer.instance.persistDeviceSelection) return;
		const stored = {
			deviceId: device.deviceId,
			label: device.label,
			kind: device.kind,
			groupId: device.groupId
		};
		try {
			await this._storageManager.setItem(DEVICE_STORAGE_KEYS[kind], stored, "local");
		} catch (error) {
			logger$29.error(`[DeviceController] Failed to persist device selection for ${kind}:`, error);
		}
	}
	async loadPersistedDevices() {
		if (!this._storageManager || !PreferencesContainer.instance.persistDeviceSelection) return;
		for (const kind of [
			"audioinput",
			"audiooutput",
			"videoinput"
		]) try {
			const stored = await this._storageManager.getItem(DEVICE_STORAGE_KEYS[kind], "local");
			if (stored) this._persistedDevices = {
				...this._persistedDevices,
				[kind]: stored
			};
		} catch (error) {
			logger$29.error(`[DeviceController] Failed to load persisted device for ${kind}:`, error);
		}
	}
	/** Clears device history, persisted selections, and re-enumerates devices. */
	async clearDeviceState() {
		this._deviceHistory.clear();
		this._persistedDevices = {};
		this._lastAudioInputBeforeDisable = null;
		this._lastVideoInputBeforeDisable = null;
		this._audioInputDisabled$.next(false);
		this._videoInputDisabled$.next(false);
		this._selectedDevicesState$.next(initialSelectedDevicesState);
		await this.enumerateDevices();
	}
	enableDeviceMonitoring() {
		this.disableDeviceMonitoring();
		this.webRTCApiProvider.mediaDevices.addEventListener("devicechange", this.deviceChangeHandler);
		if (PreferencesContainer.instance.devicePollingInterval > 0) this._devicesPoolingSubscription = interval(PreferencesContainer.instance.devicePollingInterval).subscribe(() => {
			logger$29.debug("[DeviceController] Polling devices due to interval");
			this.enumerateDevices();
		});
		this.enumerateDevices();
	}
	disableDeviceMonitoring() {
		this.webRTCApiProvider.mediaDevices.removeEventListener("devicechange", this.deviceChangeHandler);
		if (this._devicesPoolingSubscription) {
			this._devicesPoolingSubscription.unsubscribe();
			this._devicesPoolingSubscription = void 0;
		}
	}
	async enumerateDevices() {
		try {
			const devicesByKind = (await this.webRTCApiProvider.mediaDevices.enumerateDevices()).reduce((acc, device) => {
				const kind = device.kind;
				return {
					...acc,
					[kind]: [...acc[kind], device]
				};
			}, {
				audioinput: [],
				audiooutput: [],
				videoinput: []
			});
			this._devicesState$.next(devicesByKind);
			logger$29.debug("[DeviceController] Devices enumerated:", {
				audioInputs: devicesByKind.audioinput.length,
				audioOutputs: devicesByKind.audiooutput.length,
				videoInputs: devicesByKind.videoinput.length
			});
		} catch (error) {
			logger$29.error("[DeviceController] Failed to enumerate devices:", error);
			this._errors$.next(toError(error));
		}
	}
	async getDeviceCapabilities(deviceInfo) {
		if (deviceInfo.kind === "audiooutput") return null;
		try {
			const constraints = this.deviceInfoToConstraints(deviceInfo);
			const stream = await this.webRTCApiProvider.mediaDevices.getUserMedia({
				audio: deviceInfo.kind === "audioinput" ? constraints : false,
				video: deviceInfo.kind === "videoinput" ? constraints : false
			});
			const capabilities = (deviceInfo.kind === "audioinput" ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0]).getCapabilities();
			stream.getTracks().forEach((t) => t.stop());
			return capabilities;
		} catch (error) {
			logger$29.error("[DeviceController] Failed to get device capabilities:", error);
			this._errors$.next(toError(error));
			throw error;
		}
	}
	async isValidDevice(deviceInfo) {
		if (!deviceInfo || deviceInfo.kind === "audiooutput") return false;
		try {
			return await this.getDeviceCapabilities(deviceInfo) !== null;
		} catch {
			return false;
		}
	}
	destroy() {
		this.disableDeviceMonitoring();
		super.destroy();
	}
};

//#endregion
//#region src/dependencies/DefaultLocalStorage.ts
/** Default storage implementation backed by browser `localStorage` and `sessionStorage`. */
var DefaultLocalStorage = class {
	constructor() {
		if (typeof localStorage === "undefined") throw new StorageNotAvailableError("localStorage");
		if (typeof sessionStorage === "undefined") throw new StorageNotAvailableError("sessionStorage");
		try {
			const testKey = "__storage_test__";
			localStorage.setItem(testKey, "test");
			localStorage.removeItem(testKey);
		} catch (error) {
			getLogger().error("LocalStorage is not accessible:", error);
			throw new StorageNotAvailableError("localStorage");
		}
	}
	storage(scope) {
		return scope === "local" ? localStorage : sessionStorage;
	}
	async setItem(key, value, scope = "session") {
		this.storage(scope).setItem(key, value);
		return Promise.resolve();
	}
	async getItem(key, scope = "session") {
		return Promise.resolve(this.storage(scope).getItem(key));
	}
	async removeItem(key, scope = "session") {
		this.storage(scope).removeItem(key);
		return Promise.resolve();
	}
	async clear(scope = "session") {
		const store = this.storage(scope);
		const keysToRemove = [];
		for (let i = 0; i < store.length; i++) {
			const key = store.key(i);
			if (key?.startsWith("sw:")) keysToRemove.push(key);
		}
		for (const key of keysToRemove) store.removeItem(key);
		return Promise.resolve();
	}
};

//#endregion
//#region src/managers/StorageManager.ts
var StorageManager = class {
	constructor(storageImpl = new DefaultLocalStorage()) {
		this.storageImpl = storageImpl;
	}
	/**
	* Validates that a value can be safely serialized to JSON
	* @throws SerializationError if value contains non-serializable types
	*/
	serialize(value, key) {
		if (value === void 0 || value === null) return null;
		try {
			return JSON.stringify(value);
		} catch (e) {
			throw new SerializationError(key ?? "unknown", e);
		}
	}
	/**
	* Stores a value in storage
	* @throws InvalidStorageValueError if value contains non-serializable types
	* @throws SerializationError if JSON serialization fails
	* @throws StorageWriteError if writing to storage fails
	*/
	async setItem(key, value, scope = "session") {
		const serialized = this.serialize(value, key);
		try {
			await this.storageImpl.setItem(key, serialized, scope);
		} catch (error) {
			throw new StorageWriteError(key, error);
		}
	}
	/**
	* Retrieves a value from storage
	*
	* This method distinguishes between:
	* - Storage errors (network, permission, etc.) - these are thrown
	* - JSON parse errors - these trigger onParseError and return raw string
	* - Missing keys - returns null
	*
	* @returns The parsed value, raw string (on parse error), or null
	* @throws StorageReadError
	*/
	async getItem(key, scope = "session") {
		let item;
		try {
			item = await this.storageImpl.getItem(key, scope);
		} catch (error) {
			throw new StorageReadError(key, error);
		}
		if (!item) return null;
		try {
			return JSON.parse(item);
		} catch (error) {
			throw new DeserializationError(key, error);
		}
	}
	/**
	* Removes a value from storage
	* @throws Error from underlying storage implementation
	*/
	async removeItem(key, scope = "session") {
		try {
			await this.storageImpl.removeItem(key, scope);
		} catch (error) {
			throw new StorageWriteError(key, error);
		}
	}
	/**
	* Clears all SDK keys from both 'local' and 'session' scopes.
	* @throws StorageWriteError if clearing fails
	*/
	async clearAll() {
		try {
			await this.storageImpl.clear("local");
			await this.storageImpl.clear("session");
		} catch (error) {
			throw new StorageWriteError("clearAll", error);
		}
	}
};

//#endregion
//#region src/containers/DependencyContainer.ts
var DependencyContainer = class {
	constructor() {
		this.persistSession = false;
		this._webSocketConstructor = typeof WebSocket !== "undefined" ? WebSocket : void 0;
		this._baseURL = this.apiHost;
		this._credential = {};
	}
	get userId() {
		return this.user.id;
	}
	get user() {
		if (!this._user) throw new DependencyError("User");
		return this._user;
	}
	set user(user) {
		this._user = user;
	}
	get storage() {
		if (!this._storageManager) {
			this._storageImpl ??= new DefaultLocalStorage();
			this._storageManager = new StorageManager(this._storageImpl);
		}
		return this._storageManager;
	}
	get http() {
		this._httpRequestController ??= new HTTPRequestController(this._baseURL, () => this._credential);
		return this._httpRequestController;
	}
	get conversationManager() {
		if (!this._conversationManager) throw new DependencyError("ConversationsManager");
		return this._conversationManager;
	}
	set conversationManager(conversationManager) {
		this._conversationManager = conversationManager;
	}
	get WebSocket() {
		if (!this._webSocketConstructor) throw new DependencyError("WebSocket constructor");
		return this._webSocketConstructor;
	}
	set WebSocket(WebSocketConstructor) {
		this._webSocketConstructor = WebSocketConstructor;
	}
	get deviceController() {
		this._deviceController ??= new NavigatorDeviceController(this.webRTCApiProvider, this.storage);
		return this._deviceController;
	}
	get webRTCApiProvider() {
		if (!this._webRTCApiProvider) {
			if (typeof RTCPeerConnection === "undefined" || typeof navigator === "undefined") throw new DependencyError("WebRTCApiProvider: RTCPeerConnection or navigator.mediaDevices is not available. Please provide a custom webRTCApiProvider in SignalWireOptions.");
			this._webRTCApiProvider = {
				RTCPeerConnection,
				mediaDevices: navigator.mediaDevices
			};
		}
		return this._webRTCApiProvider;
	}
	set webRTCApiProvider(webRTCApiProvider) {
		this._webRTCApiProvider = webRTCApiProvider;
		this._deviceController = void 0;
	}
	get authorizationStateKey() {
		return `sw:${this.userId}:as`;
	}
	get protocolKey() {
		return `sw:${this.userId}:pt`;
	}
	get attachedCallsKey() {
		return `sw:${this.userId}:att`;
	}
	getUserFromAddressId() {
		return this.user.addresses[0]?.id ?? "";
	}
	set baseURL(baseURL) {
		this._baseURL = baseURL;
		this._httpRequestController = void 0;
	}
	get credential() {
		return this._credential;
	}
	set credential(credential) {
		this._credential = credential;
	}
	set storageImpl(storageImpl) {
		this._storageImpl = storageImpl;
		this._storageManager = void 0;
	}
	set ch(ch) {
		if (!ch) return;
		const firstDot = ch.indexOf(".");
		if (firstDot !== -1) {
			this._host = ch.substring(0, firstDot);
			this._domain = ch.substring(firstDot + 1);
		}
		this._baseURL = this.apiHost;
		this._httpRequestController = void 0;
	}
	get relayHost() {
		return `wss://${this._host ?? "puc"}.${this._domain ?? "signalwire.com"}`;
	}
	get apiHost() {
		return `https://fabric.${this._domain ?? "signalwire.com"}`;
	}
};

//#endregion
//#region src/controllers/CryptoController.ts
const logger$28 = getLogger();
const DPOP_DB_NAME = "sw-dpop";
const DPOP_DB_VERSION = 1;
const DPOP_STORE_NAME = "keys";
const DPOP_KEY_ID = "dpop-keypair";
/**
* Base64url-encodes an ArrayBuffer (no padding, URL-safe).
*/
const base64url = (buffer) => {
	const bytes = new Uint8Array(buffer);
	let binary = "";
	for (const byte of bytes) binary += String.fromCharCode(byte);
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
/**
* Base64url-encodes a UTF-8 string.
*/
const base64urlEncode = (str) => {
	return base64url(new TextEncoder().encode(str).buffer);
};
/**
* Computes the JWK Thumbprint per RFC 7638.
*
* Only supports RSA keys — the canonical JSON uses lexicographic member ordering: { e, kty, n }.
* The thumbprint is SHA-256 of the canonical JSON, base64url-encoded.
*
* @throws {Error} If the JWK is not an RSA key.
*/
const computeJwkThumbprint = async (jwk) => {
	if (jwk.kty !== "RSA") throw new Error(`Unsupported key type for JWK Thumbprint: ${jwk.kty}. Only RSA is supported.`);
	const canonical = JSON.stringify({
		e: jwk.e,
		kty: jwk.kty,
		n: jwk.n
	});
	return base64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical)));
};
async function openDpopDB() {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DPOP_DB_NAME, DPOP_DB_VERSION);
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(DPOP_STORE_NAME)) db.createObjectStore(DPOP_STORE_NAME);
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error ?? /* @__PURE__ */ new Error("Failed to open IndexedDB"));
	});
}
async function loadKeyPairFromDB() {
	try {
		const db = await openDpopDB();
		return await new Promise((resolve, reject) => {
			const tx = db.transaction(DPOP_STORE_NAME, "readonly");
			const req = tx.objectStore(DPOP_STORE_NAME).get(DPOP_KEY_ID);
			req.onsuccess = () => resolve(req.result ?? null);
			req.onerror = () => reject(req.error ?? /* @__PURE__ */ new Error("Failed to load key pair from IndexedDB"));
			tx.oncomplete = () => db.close();
		});
	} catch (error) {
		logger$28.warn("[DPoP] Failed to load key pair from IndexedDB:", error);
		return null;
	}
}
async function saveKeyPairToDB(keyPair) {
	try {
		const db = await openDpopDB();
		await new Promise((resolve, reject) => {
			const tx = db.transaction(DPOP_STORE_NAME, "readwrite");
			tx.objectStore(DPOP_STORE_NAME).put(keyPair, DPOP_KEY_ID);
			tx.oncomplete = () => {
				db.close();
				resolve();
			};
			tx.onerror = () => {
				db.close();
				reject(tx.error ?? /* @__PURE__ */ new Error("Failed to save key pair to IndexedDB"));
			};
		});
	} catch (error) {
		logger$28.warn("[DPoP] Failed to save key pair to IndexedDB:", error);
	}
}
async function deleteKeyPairFromDB() {
	try {
		const db = await openDpopDB();
		await new Promise((resolve, reject) => {
			const tx = db.transaction(DPOP_STORE_NAME, "readwrite");
			tx.objectStore(DPOP_STORE_NAME).delete(DPOP_KEY_ID);
			tx.oncomplete = () => {
				db.close();
				resolve();
			};
			tx.onerror = () => {
				db.close();
				reject(tx.error ?? /* @__PURE__ */ new Error("Failed to delete key pair from IndexedDB"));
			};
		});
	} catch (error) {
		logger$28.warn("[DPoP] Failed to delete key pair from IndexedDB:", error);
	}
}
/**
* Controls DPoP (Demonstrating Proof-of-Possession) cryptographic operations.
*
* Generates an RSA-2048 key pair where the private key is non-extractable,
* computes the JWK Thumbprint (RFC 7638) as the fingerprint, and creates
* signed DPoP proof JWTs for both HTTP API requests and WebSocket RPC calls.
*
* The key pair is persisted in IndexedDB so the same fingerprint survives
* page reloads. This keeps the Client Bound SAT and stored authorization_state
* valid across reloads without needing to re-authenticate.
*
* @example
* ```typescript
* const crypto = new CryptoController();
* await crypto.init();
*
* // Get fingerprint for SAT issuance
* const fingerprint = crypto.fingerprint;
*
* // Create proof for HTTP endpoint
* const httpProof = await crypto.createHttpProof({
*   method: 'POST',
*   uri: '/api/fabric/subscriber/devices/token'
* });
*
* // Create proof for RPC call
* const rpcProof = await crypto.createRpcProof({
*   method: 'signalwire.connect'
* });
* ```
*/
var CryptoController = class {
	constructor() {
		this._keyPair = null;
		this._publicJwk = null;
		this._fingerprint = null;
		this._initialized = false;
	}
	/**
	* Initializes the DPoP key pair. Loads an existing key from IndexedDB
	* if available, otherwise generates a new one and persists it.
	*
	* The private key is non-extractable — IndexedDB stores the CryptoKey
	* handle via the structured clone algorithm without exposing key material.
	*
	* @returns The JWK Thumbprint (fingerprint) for the key.
	*/
	async init() {
		if (this._initialized) return this.fingerprint;
		const stored = await loadKeyPairFromDB();
		if (stored) try {
			const testData = new TextEncoder().encode("dpop-key-check");
			await crypto.subtle.sign("RSASSA-PKCS1-v1_5", stored.privateKey, testData);
			this._keyPair = stored;
			this._publicJwk = await crypto.subtle.exportKey("jwk", stored.publicKey);
			this._fingerprint = await computeJwkThumbprint(this._publicJwk);
			this._initialized = true;
			logger$28.debug("[DPoP] Key pair restored from IndexedDB, fingerprint:", this._fingerprint);
			return this._fingerprint;
		} catch (error) {
			logger$28.warn("[DPoP] Stored key pair unusable, generating new one:", error);
			await deleteKeyPairFromDB();
		}
		logger$28.debug("[DPoP] Generating RSA key pair");
		this._keyPair = await crypto.subtle.generateKey({
			name: "RSASSA-PKCS1-v1_5",
			modulusLength: 2048,
			publicExponent: new Uint8Array([
				1,
				0,
				1
			]),
			hash: "SHA-256"
		}, false, ["sign", "verify"]);
		this._publicJwk = await crypto.subtle.exportKey("jwk", this._keyPair.publicKey);
		this._fingerprint = await computeJwkThumbprint(this._publicJwk);
		this._initialized = true;
		await saveKeyPairToDB(this._keyPair);
		logger$28.debug("[DPoP] Key pair generated and persisted, fingerprint:", this._fingerprint);
		return this._fingerprint;
	}
	/**
	* The JWK Thumbprint (RFC 7638) of the public key.
	* Used as the `fingerprint` parameter when requesting scoped SATs.
	*
	* @throws {DPoPInitError} If {@link init} has not been called.
	*/
	get fingerprint() {
		if (!this._fingerprint) throw new DPoPInitError("CryptoController not initialized. Call init() first.");
		return this._fingerprint;
	}
	/**
	* Whether the controller has been initialized with a key pair.
	*/
	get initialized() {
		return this._initialized;
	}
	/**
	* Creates a DPoP proof JWT for an HTTP API request.
	*
	* Used for Prime API endpoints like `/api/fabric/subscriber/devices/token`
	* and `/api/fabric/subscriber/devices/refresh`.
	*
	* @param params - HTTP method and URI for the proof.
	* @returns Signed DPoP proof JWT string.
	*/
	async createHttpProof(params) {
		const payload = {
			jti: crypto.randomUUID(),
			htm: params.method,
			htu: params.uri,
			iat: Math.floor(Date.now() / 1e3)
		};
		if (params.accessToken) payload.ath = base64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(params.accessToken)));
		return this.signProof(payload);
	}
	/**
	* Creates a DPoP proof JWT for a WebSocket RPC call.
	*
	* Used for switchblade RPC methods like `signalwire.connect` and
	* `signalwire.reauthenticate`.
	*
	* @param params - RPC method name for the proof.
	* @returns Signed DPoP proof JWT string.
	*/
	async createRpcProof(params) {
		const payload = {
			jti: crypto.randomUUID(),
			rpc: "request",
			mth: params.method,
			iat: Math.floor(Date.now() / 1e3)
		};
		return this.signProof(payload);
	}
	/**
	* Releases the key pair references and removes the persisted key from IndexedDB.
	* After calling destroy, the controller must be re-initialized to be used again.
	*/
	destroy() {
		this._keyPair = null;
		this._publicJwk = null;
		this._fingerprint = null;
		this._initialized = false;
		deleteKeyPairFromDB();
		logger$28.debug("[DPoP] Controller destroyed");
	}
	get publicJwk() {
		if (!this._publicJwk) throw new DPoPInitError("CryptoController not initialized. Call init() first.");
		return this._publicJwk;
	}
	get privateKey() {
		if (!this._keyPair) throw new DPoPInitError("CryptoController not initialized. Call init() first.");
		return this._keyPair.privateKey;
	}
	async signProof(payload) {
		const header = {
			typ: "dpop+jwt",
			alg: "RS256",
			jwk: this.publicJwk
		};
		const signingInput = `${base64urlEncode(JSON.stringify(header))}.${base64urlEncode(JSON.stringify(payload))}`;
		return `${signingInput}.${base64url(await crypto.subtle.sign("RSASSA-PKCS1-v1_5", this.privateKey, new TextEncoder().encode(signingInput)))}`;
	}
};

//#endregion
//#region src/controllers/NetworkMonitor.ts
const logger$27 = getLogger();
/**
* Safely check whether we are running in a browser environment
* with `window` and the relevant event targets.
*/
function hasBrowserNetworkEvents() {
	return typeof window !== "undefined" && typeof window.addEventListener === "function";
}
/**
* Attempt to read the current effective type from the Network Information API.
* Returns `undefined` when the API is not available.
*/
function getNetworkType() {
	if (typeof navigator === "undefined") return;
	const { connection } = navigator;
	return connection?.effectiveType ?? void 0;
}
/**
* Returns the `NetworkInformation` object when available, or `undefined`.
*/
function getNetworkConnection() {
	if (typeof navigator === "undefined") return;
	return navigator.connection ?? void 0;
}
/**
* Monitors browser-level network connectivity events (online / offline)
* and, where supported, the Network Information API for connection type changes.
*
* Safe for non-browser environments — all event listeners are guarded by
* feature checks and no-op gracefully when `window` is unavailable.
*/
var NetworkMonitor = class extends Destroyable {
	constructor() {
		super();
		this._isOnline$ = this.createBehaviorSubject(typeof navigator !== "undefined" ? navigator.onLine : true);
		this._networkChange$ = this.createSubject();
		this._onOnline = this.handleOnline.bind(this);
		this._onOffline = this.handleOffline.bind(this);
		this._onConnectionChange = this.handleConnectionChange.bind(this);
		this._listenersAttached = false;
		this.attachListeners();
	}
	get isOnline$() {
		return this._isOnline$.asObservable().pipe(takeUntil(this._destroyed$));
	}
	get isOnline() {
		return this._isOnline$.value;
	}
	get networkChange$() {
		return this._networkChange$.asObservable().pipe(takeUntil(this._destroyed$));
	}
	destroy() {
		this.removeListeners();
		super.destroy();
	}
	attachListeners() {
		if (!hasBrowserNetworkEvents()) {
			logger$27.debug("NetworkMonitor: no browser environment detected, skipping event listeners");
			return;
		}
		window.addEventListener("online", this._onOnline);
		window.addEventListener("offline", this._onOffline);
		const connection = getNetworkConnection();
		if (connection) connection.addEventListener("change", this._onConnectionChange);
		this._listenersAttached = true;
		logger$27.debug("NetworkMonitor: event listeners attached");
	}
	removeListeners() {
		if (!this._listenersAttached) return;
		if (hasBrowserNetworkEvents()) {
			window.removeEventListener("online", this._onOnline);
			window.removeEventListener("offline", this._onOffline);
			const connection = getNetworkConnection();
			if (connection) connection.removeEventListener("change", this._onConnectionChange);
		}
		this._listenersAttached = false;
		logger$27.debug("NetworkMonitor: event listeners removed");
	}
	handleOnline() {
		logger$27.info("NetworkMonitor: browser went online");
		this._isOnline$.next(true);
		this._networkChange$.next({
			type: "online",
			timestamp: Date.now(),
			networkType: getNetworkType()
		});
	}
	handleOffline() {
		logger$27.info("NetworkMonitor: browser went offline");
		this._isOnline$.next(false);
		this._networkChange$.next({
			type: "offline",
			timestamp: Date.now()
		});
	}
	handleConnectionChange() {
		const networkType = getNetworkType();
		logger$27.info(`NetworkMonitor: connection changed — effectiveType=${networkType ?? "unknown"}`);
		this._networkChange$.next({
			type: "connection_change",
			timestamp: Date.now(),
			networkType
		});
	}
};

//#endregion
//#region src/controllers/PlatformCapabilities.ts
/**
* Detects the list of supported codecs for a given media kind
* by calling RTCRtpSender.getCapabilities when available.
*
* @param kind - 'audio' or 'video'
* @returns Array of unique codec names (e.g., ['VP8', 'VP9', 'H264'])
*/
function detectCodecs(kind) {
	if (typeof RTCRtpSender === "undefined" || typeof RTCRtpSender.getCapabilities !== "function") return [];
	const capabilities = RTCRtpSender.getCapabilities(kind);
	if (!capabilities) return [];
	const seen = /* @__PURE__ */ new Set();
	const codecs = [];
	for (const codec of capabilities.codecs) {
		const name = extractCodecName(codec.mimeType);
		if (name && !seen.has(name)) {
			seen.add(name);
			codecs.push(name);
		}
	}
	return codecs;
}
/**
* Extracts the codec name from a MIME type string (e.g., 'video/VP8' -> 'VP8').
*/
function extractCodecName(mimeType) {
	const slashIndex = mimeType.indexOf("/");
	return slashIndex >= 0 ? mimeType.substring(slashIndex + 1) : mimeType;
}
/**
* Checks whether insertable streams / encoded transforms are available.
*
* Tests for both the newer RTCRtpScriptTransform (Safari 15.4+, Chrome 128+)
* and the older MediaStreamTrackProcessor (Chrome 94+).
*/
function detectInsertableStreams() {
	if (typeof globalThis === "undefined") return false;
	return "RTCRtpScriptTransform" in globalThis || "MediaStreamTrackProcessor" in globalThis;
}
/**
* Checks whether setSinkId (audio output device selection) is available.
*/
function detectAudioOutputSelection() {
	if (typeof HTMLMediaElement === "undefined") return false;
	return "setSinkId" in HTMLMediaElement.prototype;
}
/**
* Checks whether simulcast is supported by checking for RTCRtpSender.setParameters
* and the presence of encoding parameters support.
*/
function detectSimulcast() {
	if (typeof RTCRtpSender === "undefined") return false;
	return typeof RTCRtpSender.prototype.setParameters === "function";
}
/**
* Detects platform WebRTC capabilities at call time.
*
* If a {@link WebRTCApiProvider} is supplied, its mediaDevices are used
* for `getUserMedia` / `getDisplayMedia` detection. Otherwise the
* standard `navigator.mediaDevices` is probed.
*
* @param provider - Optional custom WebRTC API provider
* @returns A frozen {@link PlatformCapabilities} object
*/
function detectPlatformCapabilities(provider) {
	const mediaDevices = provider?.mediaDevices ?? getNavigatorMediaDevices();
	const hasGetUserMedia = typeof mediaDevices?.getUserMedia === "function";
	const hasGetDisplayMedia = typeof mediaDevices?.getDisplayMedia === "function";
	const capabilities = {
		webrtc: provider ? typeof provider.RTCPeerConnection === "function" : typeof globalThis !== "undefined" && "RTCPeerConnection" in globalThis,
		getUserMedia: hasGetUserMedia,
		getDisplayMedia: hasGetDisplayMedia,
		screenShare: hasGetDisplayMedia,
		screenShareAudio: detectScreenShareAudio(),
		simulcast: detectSimulcast(),
		insertableStreams: detectInsertableStreams(),
		audioOutputSelection: detectAudioOutputSelection(),
		videoCodecs: detectCodecs("video"),
		audioCodecs: detectCodecs("audio")
	};
	return Object.freeze(capabilities);
}
/**
* Detects whether screen share with system audio is supported.
*
* This is primarily a Chromium feature. We detect it by checking
* whether getDisplayMedia exists and the browser is Chromium-based.
*/
function detectScreenShareAudio() {
	if (typeof navigator === "undefined") return false;
	const mediaDevices = getNavigatorMediaDevices();
	if (!mediaDevices || typeof mediaDevices.getDisplayMedia !== "function") return false;
	return navigator.userAgent.includes("Chrome/");
}
/**
* Safely retrieves navigator.mediaDevices, returning null if unavailable.
*/
function getNavigatorMediaDevices() {
	if (typeof navigator !== "undefined") return navigator.mediaDevices;
	return null;
}

//#endregion
//#region src/controllers/PreflightRunner.ts
const logger$26 = getLogger();
const DEFAULT_MEDIA_TEST_DURATION_S = 10;
const ICE_GATHERING_TIMEOUT_MS = 1e4;
const SIGNALING_RTT_TIMEOUT_MS = 5e3;
const DEVICE_TEST_TIMEOUT_MS = 5e3;
/**
* Runs a multi-phase connectivity test:
*   1. Signaling -- verify WebSocket is connected, measure RTT
*   2. Devices -- verify getUserMedia works with selected devices
*   3. ICE/TURN -- gather ICE candidates and check reachability
*   4. Media/bandwidth -- dial, collect getStats(), compute bandwidth
*
* Extends Destroyable so all resources are cleaned up when done.
*/
var PreflightRunner = class extends Destroyable {
	constructor(deviceController, iceServers, isConnected, transportRttMs, dialFn, options = {}) {
		super();
		this.deviceController = deviceController;
		this.iceServers = iceServers;
		this.isConnected = isConnected;
		this.transportRttMs = transportRttMs;
		this.dialFn = dialFn;
		this._options = {
			duration: options.duration ?? DEFAULT_MEDIA_TEST_DURATION_S,
			skipMediaTest: options.skipMediaTest ?? false,
			audioDevice: options.audioDevice,
			videoDevice: options.videoDevice
		};
	}
	/**
	* Execute the full preflight test and return the result.
	* Always cleans up resources on completion or error.
	*/
	async run(destination) {
		const warnings = [];
		try {
			const signaling = this.testSignaling();
			if (!signaling.reachable) warnings.push("WebSocket not connected");
			const devices = await this.testDevices();
			if (!devices.audioInput.working) warnings.push("Audio input device not working");
			if (!devices.videoInput.working) warnings.push("Video input device not working");
			const connectivity = await this.testIceConnectivity();
			if (connectivity.type === "failed") warnings.push("No ICE connectivity (neither STUN nor TURN reachable)");
			else if (connectivity.type === "relay") warnings.push("Only TURN relay connectivity available (no direct path)");
			if (!connectivity.turnReachable) warnings.push("TURN servers not reachable");
			let bandwidth = null;
			if (!this._options.skipMediaTest) try {
				bandwidth = await this.testMediaBandwidth(destination);
			} catch (error) {
				logger$26.warn("[PreflightRunner] Media bandwidth test failed:", error);
				warnings.push("Media bandwidth test failed");
			}
			return {
				ok: signaling.reachable && connectivity.type !== "failed" && devices.audioInput.working,
				signaling,
				connectivity,
				bandwidth,
				devices,
				warnings
			};
		} catch (error) {
			logger$26.error("[PreflightRunner] Preflight test failed:", error);
			throw new PreflightError("preflight", error instanceof Error ? error : new Error(String(error)));
		} finally {
			this.destroy();
		}
	}
	testSignaling() {
		return {
			reachable: this.isConnected,
			rttMs: this.transportRttMs
		};
	}
	async testDevices() {
		const audioDevice = this._options.audioDevice ?? this.deviceController.selectedAudioInputDevice;
		const videoDevice = this._options.videoDevice ?? this.deviceController.selectedVideoInputDevice;
		const audioOutputDevice = this.deviceController.selectedAudioOutputDevice;
		let audioWorking = false;
		let videoWorking = false;
		let audioStream;
		try {
			const constraints = {};
			if (audioDevice) constraints.audio = { deviceId: { exact: audioDevice.deviceId } };
			else constraints.audio = true;
			if (videoDevice) constraints.video = { deviceId: { exact: videoDevice.deviceId } };
			else constraints.video = true;
			audioStream = await Promise.race([navigator.mediaDevices.getUserMedia(constraints), new Promise((_, reject) => setTimeout(() => reject(/* @__PURE__ */ new Error("getUserMedia timeout")), DEVICE_TEST_TIMEOUT_MS))]);
			for (const track of audioStream.getTracks()) {
				if (track.kind === "audio" && track.readyState === "live") audioWorking = true;
				if (track.kind === "video" && track.readyState === "live") videoWorking = true;
			}
		} catch (error) {
			logger$26.warn("[PreflightRunner] Device test failed:", error);
		} finally {
			if (audioStream) audioStream.getTracks().forEach((t) => t.stop());
		}
		return {
			audioInput: {
				working: audioWorking,
				device: audioDevice
			},
			videoInput: {
				working: videoWorking,
				device: videoDevice
			},
			audioOutput: {
				available: !!audioOutputDevice,
				device: audioOutputDevice
			}
		};
	}
	async testIceConnectivity() {
		let pc;
		try {
			pc = new RTCPeerConnection({ iceServers: this.iceServers });
			const peerConnection = pc;
			const candidateTypes = /* @__PURE__ */ new Set();
			const startTime = Date.now();
			const gatheringComplete = new Promise((resolve) => {
				const timer$1 = setTimeout(resolve, ICE_GATHERING_TIMEOUT_MS);
				peerConnection.onicecandidate = (event) => {
					if (event.candidate) {
						const candidateStr = event.candidate.candidate;
						if (candidateStr.includes("typ host")) candidateTypes.add("host");
						if (candidateStr.includes("typ srflx")) candidateTypes.add("srflx");
						if (candidateStr.includes("typ relay")) candidateTypes.add("relay");
					} else {
						clearTimeout(timer$1);
						resolve();
					}
				};
			});
			pc.createDataChannel("preflight-test");
			const offer = await pc.createOffer();
			await pc.setLocalDescription(offer);
			await gatheringComplete;
			const rttMs = Date.now() - startTime;
			const stunReachable = candidateTypes.has("srflx");
			const turnReachable = candidateTypes.has("relay");
			const hasHost = candidateTypes.has("host");
			let type = "failed";
			if (hasHost || stunReachable) type = "direct";
			else if (turnReachable) type = "relay";
			return {
				type,
				turnReachable,
				stunReachable,
				rttMs
			};
		} catch (error) {
			logger$26.warn("[PreflightRunner] ICE connectivity test failed:", error);
			return {
				type: "failed",
				turnReachable: false,
				stunReachable: false,
				rttMs: 0
			};
		} finally {
			if (pc) pc.close();
		}
	}
	async testMediaBandwidth(destination) {
		let call;
		try {
			call = await this.dialFn(destination, {
				audio: true,
				video: false
			});
			await firstValueFrom(call.status$.pipe(filter((s) => s === "connected"), take(1), timeout(SIGNALING_RTT_TIMEOUT_MS)));
			const durationMs = this._options.duration * 1e3;
			await new Promise((resolve) => setTimeout(resolve, durationMs));
			const metrics = call.networkMetrics;
			let uploadKbps = 0;
			let downloadKbps = 0;
			if (metrics.length > 0) {
				const latest = metrics[metrics.length - 1];
				if (latest.availableOutgoingBitrate !== void 0) uploadKbps = Math.round(latest.availableOutgoingBitrate / 1e3);
				const totalPackets = latest.audio.packetsReceived + latest.video.packetsReceived;
				if (durationMs > 0 && totalPackets > 0) downloadKbps = uploadKbps;
			}
			return {
				uploadKbps,
				downloadKbps
			};
		} finally {
			if (call) try {
				await call.hangup();
			} catch {}
		}
	}
	destroy() {
		super.destroy();
	}
};

//#endregion
//#region src/controllers/VisibilityController.ts
const logger$25 = getLogger();
/**
* Checks whether the document visibility API is available.
*/
function isVisibilityApiAvailable() {
	try {
		return typeof document !== "undefined" && typeof document.addEventListener === "function" && typeof document.visibilityState === "string";
	} catch {
		return false;
	}
}
/**
* Returns the current document visibility state, defaulting to 'visible'
* in non-browser environments.
*/
function getCurrentVisibility() {
	try {
		if (typeof document !== "undefined" && typeof document.visibilityState === "string") return document.visibilityState === "visible" ? "visible" : "hidden";
	} catch {}
	return "visible";
}
/**
* VisibilityController tracks browser tab visibility state and exposes
* it as RxJS observables. Safe for use in non-browser environments
* where the document API is not available.
*
* Extends Destroyable for lifecycle management and automatic cleanup.
*/
var VisibilityController = class extends Destroyable {
	constructor() {
		super();
		this._visibility$ = this.createBehaviorSubject(getCurrentVisibility());
		this._visibilityChange$ = this.createSubject();
		this._hasVisibilityApi = isVisibilityApiAvailable();
		this._boundHandler = this._handleVisibilityChange.bind(this);
		if (this._hasVisibilityApi) {
			document.addEventListener("visibilitychange", this._boundHandler);
			logger$25.debug("VisibilityController: listening for visibilitychange events");
		} else logger$25.debug("VisibilityController: document visibility API not available, defaulting to visible");
	}
	/**
	* Observable of the current visibility state.
	* Emits 'visible' or 'hidden'. Always starts with the current state.
	*/
	get visibility$() {
		return this._visibility$.pipe(takeUntil(this._destroyed$));
	}
	/**
	* The current visibility state value.
	*/
	get visibility() {
		return this._visibility$.value;
	}
	/**
	* Observable that emits transition events when visibility changes.
	* Each event includes the previous state, new state, and timestamp.
	*/
	get visibilityChange$() {
		return this._visibilityChange$.pipe(takeUntil(this._destroyed$));
	}
	destroy() {
		if (this._hasVisibilityApi) {
			document.removeEventListener("visibilitychange", this._boundHandler);
			logger$25.debug("VisibilityController: removed visibilitychange listener");
		}
		super.destroy();
	}
	/**
	* Handle the browser's visibilitychange event.
	*/
	_handleVisibilityChange() {
		const newState = getCurrentVisibility();
		const previousState = this._visibility$.value;
		if (newState === previousState) return;
		this._visibility$.next(newState);
		const changeEvent = {
			from: previousState,
			to: newState,
			timestamp: Date.now()
		};
		this._visibilityChange$.next(changeEvent);
		logger$25.debug("VisibilityController: visibility changed", {
			from: previousState,
			to: newState
		});
	}
};

//#endregion
//#region src/behaviors/Fetchable.ts
var Fetchable = class extends Destroyable {
	constructor(fromPath, http) {
		super();
		this.fromPath = fromPath;
		this.http = http;
		this.fetched$ = defer(() => from(this.fetch())).pipe(shareReplay(1), takeUntil(this.destroyed$));
	}
	async fetch() {
		const response = await this.http.request({
			url: this.fromPath,
			method: "GET",
			headers: { Accept: "application/json" }
		});
		if (response.ok && response.body) {
			const data = JSON.parse(response.body);
			this.populateInstance(data);
			return true;
		}
		return false;
	}
};

//#endregion
//#region src/core/entities/User.ts
/**
* Authenticated user profile.
*
* Fetched automatically when a {@link SignalWire} connects.
* Contains identity, contact, and organization details.
*/
var User = class extends Fetchable {
	constructor(http) {
		super("/api/fabric/subscriber/info", http);
	}
	populateInstance(data) {
		this.id = data.id;
		this.email = data.email;
		this.firstName = data.first_name;
		this.lastName = data.last_name;
		this.displayName = data.display_name;
		this.jobTitle = data.job_title;
		this.timeZone = data.time_zone;
		this.country = data.country;
		this.region = data.region;
		this.companyName = data.company_name;
		this.pushNotificationKey = data.push_notification_key;
		this.appSettings = data.app_settings ? {
			displayName: data.app_settings.display_name,
			scopes: data.app_settings.scopes
		} : void 0;
		this.addresses = data.fabric_addresses;
		this.satClaims = data.sat_claims;
	}
};

//#endregion
//#region src/core/RPCMessages/helpers.ts
const buildRPCRequest = (params) => {
	return {
		jsonrpc: "2.0",
		id: params.id ?? v4(),
		...params
	};
};
const makeRPCResponse = (params) => {
	return {
		jsonrpc: "2.0",
		...params
	};
};

//#endregion
//#region src/core/RPCMessages/RPCConnect.ts
const DEFAULT_CONNECT_VERSION = {
	major: 4,
	minor: 0,
	revision: 0
};
const RPCConnect = (params) => {
	return buildRPCRequest({
		method: "signalwire.connect",
		params: {
			version: DEFAULT_CONNECT_VERSION,
			event_acks: true,
			...params
		}
	});
};

//#endregion
//#region src/core/RPCMessages/RPCReauthenticate.ts
const RPCReauthenticate = (params) => {
	const { dpop_token, ...authFields } = params;
	return buildRPCRequest({
		method: "signalwire.reauthenticate",
		params: {
			authentication: authFields,
			...dpop_token ? { dpop_token } : {}
		}
	});
};

//#endregion
//#region src/core/RPCMessages/RPCPing.ts
const RPCPingResponse = (id, timestamp) => {
	return makeRPCResponse({
		id,
		result: { timestamp: timestamp ?? Date.now() / 1e3 }
	});
};

//#endregion
//#region src/core/RPCMessages/RPCExecute.ts
const RPCExecute = ({ method, params }) => {
	return buildRPCRequest({
		method,
		params
	});
};

//#endregion
//#region src/core/RPCMessages/VertoMessages.ts
const SDK_TO_VERTO_FIELD_MAP = {
	id: "callID",
	destinationNumber: "destination_number",
	remoteCallerName: "remote_caller_id_name",
	remoteCallerNumber: "remote_caller_id_number",
	callerName: "caller_id_name",
	callerNumber: "caller_id_number",
	fromCallAddressId: "from_fabric_address_id"
};
const EXCLUDED_DIALOG_PARAMS = new Set([
	"remoteSdp",
	"localStream",
	"remoteStream"
]);
/**
* Translate SDK fields into verto variables.
* Returns a new object — the input is never mutated.
*/
/** @internal Exported for testing only. */
const filterVertoParams = (params) => {
	if (!Object.prototype.hasOwnProperty.call(params, "dialogParams")) return params;
	const sourceDialogParams = params.dialogParams;
	const filteredDialogParams = Object.entries(sourceDialogParams).reduce((acc, [key, value]) => {
		if (EXCLUDED_DIALOG_PARAMS.has(key)) return acc;
		const mappedKey = SDK_TO_VERTO_FIELD_MAP[key] ?? key;
		return {
			...acc,
			[mappedKey]: value
		};
	}, {});
	return {
		...params,
		dialogParams: filteredDialogParams
	};
};
const buildVertoRPCMessage = (method) => {
	return (params = {}) => {
		return buildRPCRequest({
			method,
			params: filterVertoParams(params)
		});
	};
};
const WebrtcVerto = (params) => {
	return buildRPCRequest({
		method: "webrtc.verto",
		params
	});
};
const VertoInvite = buildVertoRPCMessage("verto.invite");
const VertoBye = buildVertoRPCMessage("verto.bye");
const VertoAttach = buildVertoRPCMessage("verto.attach");
const VertoModify = buildVertoRPCMessage("verto.modify");
const VertoInfo = buildVertoRPCMessage("verto.info");
const VertoAnswer = buildVertoRPCMessage("verto.answer");
const VertoSubscribe = buildVertoRPCMessage("verto.subscribe");
const VertoPong = buildVertoRPCMessage("verto.pong");
const VertoByeCauseCodes = {
	NORMAL_CLEARING: "16",
	USER_BUSY: "17",
	MEDIA_TIMEOUT: "804"
};

//#endregion
//#region src/core/RPCMessages/RPCEventAck.ts
const RPCEventAckResponse = (id) => makeRPCResponse({
	id,
	result: {}
});

//#endregion
//#region src/managers/AttachManager.ts
const logger$24 = getLogger();
var AttachManager = class {
	constructor(storage, deviceController, reconnectCallsTimeout, attachKey) {
		this.storage = storage;
		this.deviceController = deviceController;
		this.reconnectCallsTimeout = reconnectCallsTimeout;
		this.attachKey = attachKey;
		this.writeQueue = Promise.resolve();
	}
	async detachAll() {
		await this.mutate((attached) => {
			return {};
		});
	}
	setSession(session) {
		this.session = session;
	}
	async readAttached() {
		try {
			return await this.storage.getItem(this.attachKey) ?? {};
		} catch (error) {
			logger$24.warn("[AttachManager] Failed to retrieve attached calls from storage", error);
			return {};
		}
	}
	async writeAttached(attached) {
		try {
			await this.storage.setItem(this.attachKey, attached);
		} catch (error) {
			logger$24.warn("[AttachManager] Failed to write attached calls to storage", error);
		}
	}
	/**
	* Serialize a read-modify-write operation against the attached-calls
	* storage. The mutator receives the current state and returns the new
	* state. Concurrent calls queue behind the in-flight one so writes never
	* interleave.
	*/
	async mutate(mutator) {
		const next = this.writeQueue.then(async () => {
			const updated = await mutator(await this.readAttached());
			await this.writeAttached(updated);
		});
		this.writeQueue = next.catch(() => void 0);
		return next;
	}
	async attach(call) {
		if (!call.to) {
			logger$24.warn("[AttachManager] Skip attach for calls with no destination");
			return;
		}
		const destination = call.to;
		const attachment = {
			nodeId: call.nodeId,
			destination,
			mediaDirections: call.mediaDirections,
			audioInputDevice: call.mediaDirections.audio !== "inactive" ? this.deviceController.selectedAudioInputDevice : null,
			videoInputDevice: call.mediaDirections.video !== "inactive" ? this.deviceController.selectedVideoInputDevice : null,
			attachedAt: Date.now()
		};
		await this.mutate((attached) => ({
			...attached,
			[call.id]: attachment
		}));
	}
	async detach(call) {
		await this.mutate((attached) => {
			const { [call.id]: _, ...remaining } = attached;
			return remaining;
		});
	}
	async flush() {
		await this.mutate(() => ({}));
	}
	/**
	* Reattach to previously active calls by sending verto.invite with
	* reattaching: true.
	*
	* NOTE: This currently fails with INVALID_CALL_REFERENCE because the
	* server's jsock UUID check rejects the new connection's UUID. A
	* server-side fix is needed: when reattaching: true is explicitly set
	* in dialogParams, FreeSWITCH's attempt_reattach() should update the
	* call's jsock reference to the new connection's UUID instead of
	* rejecting. Once that fix is deployed, this will work for both
	* page reloads and WebSocket reconnects.
	*
	* Failed reattach attempts are handled gracefully — the stale call
	* reference is cleaned up from storage.
	*/
	async reattachCalls() {
		const attached = await this.readAttached();
		await this.detachExpired();
		for (const [callId, attachment] of Object.entries(attached)) {
			const { destination } = attachment;
			const options = this.buildCallOptions(attachment);
			let succeeded = false;
			for (let attempt = 1; attempt <= 3; attempt++) try {
				await this.session.createOutboundCall(destination, {
					callId,
					...options
				});
				logger$24.info(`[AttachManager] Reattached call ${callId} (attempt ${attempt})`);
				succeeded = true;
				break;
			} catch (error) {
				logger$24.warn(`[AttachManager] Reattach attempt ${attempt}/3 failed for call ${callId}:`, error);
				if (attempt < 3) await new Promise((r) => setTimeout(r, (attempt + 1) * 1e3));
			}
			if (!succeeded) {
				logger$24.warn(`[AttachManager] Reattach failed after 3 attempts for call ${callId}, removing reference`);
				await this.detach({
					id: callId,
					mediaDirections: attachment.mediaDirections
				});
			}
		}
	}
	/**
	* Build CallOptions from stored attachment data for a call being reattached.
	* Also used by the session-level verto.attach handler.
	*/
	buildCallOptions(attachment) {
		const { audio: audioDirection, video: videoDirection } = attachment.mediaDirections;
		const { audioInputDevice, videoInputDevice, nodeId } = attachment;
		const receiveAudio = audioDirection.includes("recv");
		const receiveVideo = videoDirection.includes("recv");
		const sendAudio = audioDirection.includes("send");
		const sendVideo = videoDirection.includes("send");
		return {
			nodeId,
			receiveAudio,
			receiveVideo,
			inputAudioDeviceConstraints: sendAudio ? {
				audio: true,
				...this.deviceController.deviceInfoToConstraints(audioInputDevice)
			} : void 0,
			inputVideoDeviceConstraints: sendVideo ? {
				video: true,
				...this.deviceController.deviceInfoToConstraints(videoInputDevice)
			} : void 0,
			reattach: true
		};
	}
	/**
	* Look up stored attachment data for a call id and return CallOptions
	* suitable for rehydrating a reattached call. Returns undefined when no
	* matching entry exists in storage.
	*
	* Used by the session-level verto.attach handler when the server pushes
	* an attach event for a call the client doesn't have an object for yet
	* (e.g. after a reload).
	*/
	async consumePendingAttachment(callId) {
		const attached = await this.readAttached();
		if (!Object.hasOwn(attached, callId)) return;
		return this.buildCallOptions(attached[callId]);
	}
	async detachExpired() {
		const now = Date.now();
		const timeout$1 = this.reconnectCallsTimeout;
		await this.mutate((attached) => {
			const remaining = { ...attached };
			let changed = false;
			for (const [callId, attachment] of Object.entries(attached)) if (now - attachment.attachedAt > timeout$1) {
				delete remaining[callId];
				changed = true;
			}
			return changed ? remaining : attached;
		});
	}
};

//#endregion
//#region src/core/capabilities/types.ts
/**
* Default on/off state with no permissions
*/
const DEFAULT_ON_OFF = {
	on: false,
	off: false
};
/**
* Default member capabilities with no permissions
*/
const DEFAULT_MEMBER_CAPABILITIES = {
	muteAudio: DEFAULT_ON_OFF,
	muteVideo: DEFAULT_ON_OFF,
	deaf: DEFAULT_ON_OFF,
	raisehand: DEFAULT_ON_OFF,
	microphoneVolume: false,
	microphoneSensitivity: false,
	speakerVolume: false,
	position: false,
	meta: false,
	remove: false,
	audioFlags: false
};
/**
* Default call capabilities with no permissions
*/
const DEFAULT_CALL_CAPABILITIES = {
	self: DEFAULT_MEMBER_CAPABILITIES,
	member: DEFAULT_MEMBER_CAPABILITIES,
	end: false,
	setLayout: false,
	sendDigit: false,
	vmutedHide: DEFAULT_ON_OFF,
	lock: DEFAULT_ON_OFF,
	device: false,
	screenshare: false
};

//#endregion
//#region src/core/capabilities/computeCapabilities.ts
/**
* Computes an on/off capability state from filtered flags
*
* Logic:
* - `on` is true if any flag does NOT end with `.off`
* - `off` is true if any flag does NOT end with `.on`
*
* This means parent permissions (without .on/.off suffix) grant both on and off
*/
function computeOnOffCapability(filteredFlags) {
	if (filteredFlags.length === 0) return DEFAULT_ON_OFF;
	return {
		on: filteredFlags.some((flag) => !flag.endsWith(".off")),
		off: filteredFlags.some((flag) => !flag.endsWith(".on"))
	};
}
/**
* Filters flags for a specific member capability with on/off support
*
* Matches:
* - The member type itself (e.g., "self" grants all self capabilities)
* - The parent capability (e.g., "self.mute" grants all mute capabilities)
* - The specific capability and its on/off variants (e.g., "self.mute.audio*")
*/
function filterMemberOnOffFlags(flags, memberType, parent, capability) {
	return flags.filter((flag) => flag === memberType || flag === `${memberType}.${parent}` || flag.startsWith(`${memberType}.${parent}.${capability}`));
}
/**
* Checks if a boolean capability is granted
*
* Matches:
* - The member type itself (e.g., "self" grants all self capabilities)
* - The parent capability if provided (e.g., "self.microphone" grants volume and sensitivity)
* - The specific capability (e.g., "self.microphone.volume.set")
*/
function hasBooleanCapability(flags, memberType, parent, capability) {
	return flags.some((flag) => flag === memberType || parent !== null && flag === `${memberType}.${parent}` || flag.startsWith(`${memberType}.${parent ? `${parent}.` : ""}${capability}`));
}
/**
* Computes member-level capabilities (self or member) from raw flags
*/
function computeMemberCapabilities(flags, memberType) {
	if (flags.filter((flag) => flag.startsWith(memberType) || flag === memberType).length === 0) return DEFAULT_MEMBER_CAPABILITIES;
	return {
		muteAudio: computeOnOffCapability(filterMemberOnOffFlags(flags, memberType, "mute", "audio")),
		muteVideo: computeOnOffCapability(filterMemberOnOffFlags(flags, memberType, "mute", "video")),
		deaf: computeOnOffCapability(flags.filter((flag) => flag === memberType || flag.startsWith(`${memberType}.deaf`))),
		raisehand: computeOnOffCapability(flags.filter((flag) => flag === memberType || flag.startsWith(`${memberType}.raisehand`))),
		microphoneVolume: hasBooleanCapability(flags, memberType, "microphone", "volume"),
		microphoneSensitivity: hasBooleanCapability(flags, memberType, "microphone", "sensitivity"),
		speakerVolume: hasBooleanCapability(flags, memberType, "speaker", "volume"),
		position: hasBooleanCapability(flags, memberType, null, "position"),
		meta: hasBooleanCapability(flags, memberType, null, "meta"),
		remove: hasBooleanCapability(flags, memberType, null, "remove"),
		audioFlags: hasBooleanCapability(flags, memberType, null, "audioflags")
	};
}
/**
* Computes all call capabilities from raw capability strings
*
* This is a pure function that transforms the raw capability strings
* from the `call.joined` event into a structured capabilities state.
*
* @param capabilities - Raw capability strings from the server
* @returns Computed capabilities state
*/
function computeCapabilities(capabilities) {
	if (capabilities.length === 0) return DEFAULT_CALL_CAPABILITIES;
	return {
		self: computeMemberCapabilities(capabilities, "self"),
		member: computeMemberCapabilities(capabilities, "member"),
		end: capabilities.some((cap) => cap === "end"),
		setLayout: capabilities.some((cap) => cap.startsWith("layout")),
		sendDigit: capabilities.some((cap) => cap.startsWith("digit")),
		vmutedHide: computeOnOffCapability(capabilities.filter((flag) => flag.startsWith("vmuted"))),
		lock: computeOnOffCapability(capabilities.filter((flag) => flag.startsWith("lock"))),
		device: capabilities.some((cap) => cap === "device"),
		screenshare: capabilities.some((cap) => cap === "screenshare")
	};
}

//#endregion
//#region src/core/capabilities/SelfCapabilities.ts
/**
* SelfCapabilities manages the capability state for the self participant.
*
* Capabilities are received from the server via `call.joined` events and determine
* what actions the current participant is allowed to perform.
*
* Each capability is exposed as both:
* - An observable (e.g., `end$`) for reactive state management
* - A synchronous getter (e.g., `end`) for immediate access
*
* Member-level capabilities are accessed via the grouped `self` / `member` accessors:
* - `capabilities.self.muteAudio` (sync)
* - `capabilities.self$` (observable)
*
* When a new `call.joined` event is received, the capabilities state is
* completely replaced (not merged).
*/
var SelfCapabilities = class extends Destroyable {
	constructor(..._args) {
		super(..._args);
		this._state$ = this.createBehaviorSubject(DEFAULT_CALL_CAPABILITIES);
	}
	/**
	* Updates the capabilities state from raw capability strings.
	* This completely replaces the current state.
	*
	* @param capabilities - Raw capability strings from the server
	* @internal
	*/
	updateFromRaw(capabilities) {
		const newState = computeCapabilities(capabilities);
		this._state$.next(newState);
	}
	/** Observable for self member capabilities */
	get self$() {
		return this.cachedObservable("self$", () => this._state$.pipe(map((state) => state.self), distinctUntilChanged()));
	}
	/** Current self member capabilities */
	get self() {
		return this._state$.value.self;
	}
	/** Observable for other member capabilities */
	get member$() {
		return this.cachedObservable("member$", () => this._state$.pipe(map((state) => state.member), distinctUntilChanged()));
	}
	/** Current other member capabilities */
	get member() {
		return this._state$.value.member;
	}
	/** Observable for end call capability */
	get end$() {
		return this.cachedObservable("end$", () => this._state$.pipe(map((state) => state.end), distinctUntilChanged()));
	}
	/** Current end call capability */
	get end() {
		return this._state$.value.end;
	}
	/** Observable for set layout capability */
	get setLayout$() {
		return this.cachedObservable("setLayout$", () => this._state$.pipe(map((state) => state.setLayout), distinctUntilChanged()));
	}
	/** Current set layout capability */
	get setLayout() {
		return this._state$.value.setLayout;
	}
	/** Observable for send digit capability */
	get sendDigit$() {
		return this.cachedObservable("sendDigit$", () => this._state$.pipe(map((state) => state.sendDigit), distinctUntilChanged()));
	}
	/** Current send digit capability */
	get sendDigit() {
		return this._state$.value.sendDigit;
	}
	/** Observable for vmuted hide capability */
	get vmutedHide$() {
		return this.cachedObservable("vmutedHide$", () => this._state$.pipe(map((state) => state.vmutedHide), distinctUntilChanged()));
	}
	/** Current vmuted hide capability */
	get vmutedHide() {
		return this._state$.value.vmutedHide;
	}
	/** Observable for lock capability */
	get lock$() {
		return this.cachedObservable("lock$", () => this._state$.pipe(map((state) => state.lock), distinctUntilChanged()));
	}
	/** Current lock capability */
	get lock() {
		return this._state$.value.lock;
	}
	/** Observable for device capability */
	get device$() {
		return this.cachedObservable("device$", () => this._state$.pipe(map((state) => state.device), distinctUntilChanged()));
	}
	/** Current device capability */
	get device() {
		return this._state$.value.device;
	}
	/** Observable for screenshare capability */
	get screenshare$() {
		return this.cachedObservable("screenshare$", () => this._state$.pipe(map((state) => state.screenshare), distinctUntilChanged()));
	}
	/** Current screenshare capability */
	get screenshare() {
		return this._state$.value.screenshare;
	}
	/** Observable for the full capabilities state */
	get state$() {
		return this._state$.asObservable();
	}
	/** Current full capabilities state */
	get state() {
		return this._state$.value;
	}
};

//#endregion
//#region src/core/RPCMessages/utils.ts
function toggleDeafMethod(is) {
	return is ? "call.undeaf" : "call.deaf";
}
function toggleHandraiseMethod(is) {
	return is ? "call.lowerhand" : "call.raisehand";
}

//#endregion
//#region src/core/entities/Participant.ts
const logger$23 = getLogger();
const initialState = {};
/**
* Represents a participant in a call.
*
* Provides observable state (audio/video mute, hand raise, volume, etc.)
* and control methods for the participant. See {@link SelfParticipant} for
* the local participant with additional device control.
*/
var Participant = class extends Destroyable {
	constructor(id, executeMethod, deviceController) {
		super();
		this.executeMethod = executeMethod;
		this.deviceController = deviceController;
		this._state$ = this.createBehaviorSubject(initialState);
		this.id = id;
	}
	/** @internal */
	upnext(data) {
		this._state$.next({
			...this._state$.value,
			...data
		});
	}
	/** Observable of the participant's display name. */
	get name$() {
		return this.cachedObservable("name$", () => this._state$.pipe(map$1((state) => state.name), distinctUntilChanged$1()));
	}
	/** Observable of the participant type (e.g. `'member'`, `'screen'`). */
	get type$() {
		return this.cachedObservable("type$", () => this._state$.pipe(map$1((state) => state.type), distinctUntilChanged$1()));
	}
	/** Observable indicating whether the participant has raised their hand. */
	get handraised$() {
		return this.cachedObservable("handraised$", () => this._state$.pipe(map$1((state) => state.handraised), distinctUntilChanged$1()));
	}
	/** Observable indicating whether the participant is visible in the layout. */
	get visible$() {
		return this.cachedObservable("visible$", () => this._state$.pipe(map$1((state) => state.visible), distinctUntilChanged$1()));
	}
	/** Observable indicating whether the participant's audio is muted. */
	get audioMuted$() {
		return this.cachedObservable("audioMuted$", () => this._state$.pipe(map$1((state) => state.audio_muted), distinctUntilChanged$1()));
	}
	/** Observable indicating whether the participant's video is muted. */
	get videoMuted$() {
		return this.cachedObservable("videoMuted$", () => this._state$.pipe(map$1((state) => state.video_muted), distinctUntilChanged$1()));
	}
	/** Observable indicating whether the participant is deafened. */
	get deaf$() {
		return this.cachedObservable("deaf$", () => this._state$.pipe(map$1((state) => state.deaf), distinctUntilChanged$1()));
	}
	/**
	* Observable of the participant's **server-side** microphone input volume
	* as reported by the mix engine. This is gain applied on the bridged audio
	* leg (FreeSWITCH channel read volume), NOT the local browser mic. For a
	* local PC mic control, see {@link Call.setLocalMicrophoneGain}.
	*
	* @see {@link setAudioInputVolume}
	*/
	get inputVolume$() {
		return this.cachedObservable("inputVolume$", () => this._state$.pipe(map$1((state) => state.input_volume), distinctUntilChanged$1()));
	}
	/**
	* Observable of the participant's **server-side** speaker output volume as
	* reported by the mix engine (FreeSWITCH channel write volume). NOT the
	* local HTML `<audio>` element volume — set that on your own element.
	*
	* @see {@link setAudioOutputVolume}
	*/
	get outputVolume$() {
		return this.cachedObservable("outputVolume$", () => this._state$.pipe(map$1((state) => state.output_volume), distinctUntilChanged$1()));
	}
	/**
	* Observable of the **conference-only** microphone energy/gate sensitivity
	* level for this member. Routes through the conferencing mix engine and has
	* no effect on 1:1 WebRTC calls. Populated from `member.updated` events for
	* conference members.
	*
	* @see {@link setAudioInputSensitivity}
	*/
	get inputSensitivity$() {
		return this.cachedObservable("inputSensitivity$", () => this._state$.pipe(map$1((state) => state.input_sensitivity), distinctUntilChanged$1()));
	}
	/** Observable indicating whether echo cancellation is enabled. */
	get echoCancellation$() {
		return this.cachedObservable("echoCancellation$", () => this._state$.pipe(map$1((state) => state.echo_cancellation), distinctUntilChanged$1()));
	}
	/** Observable indicating whether auto-gain control is enabled. */
	get autoGain$() {
		return this.cachedObservable("autoGain$", () => this._state$.pipe(map$1((state) => state.auto_gain), distinctUntilChanged$1()));
	}
	/** Observable indicating whether noise suppression is enabled. */
	get noiseSuppression$() {
		return this.cachedObservable("noiseSuppression$", () => this._state$.pipe(map$1((state) => state.noise_suppression), distinctUntilChanged$1()));
	}
	/** Observable indicating whether low-bitrate mode is active. */
	get lowbitrate$() {
		return this.cachedObservable("lowbitrate$", () => this._state$.pipe(map$1((state) => state.lowbitrate), distinctUntilChanged$1()));
	}
	/** Observable indicating whether noise reduction is active. */
	get denoise$() {
		return this.cachedObservable("denoise$", () => this._state$.pipe(map$1((state) => state.denoise), distinctUntilChanged$1()));
	}
	/** Observable of custom metadata for this participant. */
	get meta$() {
		return this.cachedObservable("meta$", () => this._state$.pipe(map$1((state) => state.meta), distinctUntilChanged$1()));
	}
	/** Observable of the participant's user ID. */
	get userId$() {
		return this.cachedObservable("userId$", () => this._state$.pipe(map$1((state) => state.subscriber_id), distinctUntilChanged$1()));
	}
	/** Observable of the participant's address ID. */
	get addressId$() {
		return this.cachedObservable("addressId$", () => this._state$.pipe(map$1((state) => state.address_id), distinctUntilChanged$1()));
	}
	/** Observable of the server node ID for this participant. */
	get nodeId$() {
		return this.cachedObservable("nodeId$", () => this._state$.pipe(map$1((state) => state.node_id), distinctUntilChanged$1()));
	}
	/** Observable indicating whether the participant is currently speaking. */
	get isTalking$() {
		return this.cachedObservable("isTalking$", () => this._state$.pipe(map$1((state) => state.talking), distinctUntilChanged$1()));
	}
	/** Whether the participant is currently speaking. */
	get isTalking() {
		return this._state$.value.talking ?? false;
	}
	/** Observable of the participant's layout position. */
	get position$() {
		return this.cachedObservable("position$", () => this._state$.pipe(map$1((state) => state.position), distinctUntilChanged$1()));
	}
	/** Current layout position. */
	get position() {
		return this._state$.value.position;
	}
	/** Whether the participant is an audience member (view-only). */
	get isAudience() {
		return this._state$.value.isAudience ?? false;
	}
	/** Display name of this participant. */
	get name() {
		return this._state$.value.name;
	}
	/** Participant type (e.g. `'member'`, `'screen'`). */
	get type() {
		return this._state$.value.type;
	}
	/** Whether the participant has raised their hand. */
	get handraised() {
		return this._state$.value.handraised ?? false;
	}
	/** Whether the participant is visible in the layout. */
	get visible() {
		return this._state$.value.visible ?? false;
	}
	/** Whether the participant's audio is muted. */
	get audioMuted() {
		return this._state$.value.audio_muted ?? false;
	}
	/** Whether the participant's video is muted. */
	get videoMuted() {
		return this._state$.value.video_muted ?? false;
	}
	/** Whether the participant is deafened (incoming audio muted). */
	get deaf() {
		return this._state$.value.deaf ?? false;
	}
	/**
	* Current **server-side** microphone input volume as reported by the mix
	* engine, or `undefined` if not set. Not the local PC mic — see
	* {@link Call.setLocalMicrophoneGain} for browser-side control.
	*/
	get inputVolume() {
		return this._state$.value.input_volume;
	}
	/**
	* Current **server-side** speaker output volume from the mix engine, or
	* `undefined` if not set. Not the local `<audio>` element volume.
	*/
	get outputVolume() {
		return this._state$.value.output_volume;
	}
	/**
	* Current **conference-only** microphone sensitivity/gate level, or
	* `undefined` if not set. Applies only to conference members.
	*/
	get inputSensitivity() {
		return this._state$.value.input_sensitivity;
	}
	/** Whether echo cancellation is enabled. */
	get echoCancellation() {
		return this._state$.value.echo_cancellation ?? false;
	}
	/** Whether automatic gain control is enabled. */
	get autoGain() {
		return this._state$.value.auto_gain ?? false;
	}
	/** Whether noise suppression is enabled. */
	get noiseSuppression() {
		return this._state$.value.noise_suppression ?? false;
	}
	/** Whether low-bitrate mode is active. */
	get lowbitrate() {
		return this._state$.value.lowbitrate ?? false;
	}
	/** Whether noise reduction (denoise) is active. */
	get denoise() {
		return this._state$.value.denoise ?? false;
	}
	/** Custom metadata for this participant, or `undefined` if not set. */
	get meta() {
		return this._state$.value.meta;
	}
	/** User ID of this participant, or `undefined` if not available. */
	get userId() {
		return this._state$.value.subscriber_id;
	}
	/** Address ID of this participant, or `undefined` if not available. */
	get addressId() {
		return this._state$.value.address_id;
	}
	/** Server node ID for this participant, or `undefined` if not available. */
	get nodeId() {
		return this._state$.value.node_id;
	}
	/** @internal */
	get value() {
		return this._state$.value;
	}
	/** Toggles the deafened state (mutes/unmutes incoming audio). */
	async toggleDeaf() {
		const method = toggleDeafMethod(this.deaf);
		await this.executeMethod(this.id, method, {});
	}
	/** Toggles the hand-raised state. */
	async toggleHandraise() {
		await this.executeMethod(this.id, toggleHandraiseMethod(this.handraised), {});
	}
	/** Mutes the participant's audio. */
	async mute() {
		await this.executeMethod(this.id, "call.mute", { channels: ["audio"] });
	}
	/** Unmutes the participant's audio. */
	async unmute() {
		await this.executeMethod(this.id, "call.unmute", { channels: ["audio"] });
	}
	/** Toggles the participant's audio mute state. */
	async toggleMute() {
		return this.audioMuted ? this.unmute() : this.mute();
	}
	/** Mutes the participant's video. */
	async muteVideo() {
		await this.executeMethod(this.id, "call.mute", { channels: ["video"] });
	}
	/** Unmutes the participant's video. */
	async unmuteVideo() {
		await this.executeMethod(this.id, "call.unmute", { channels: ["video"] });
	}
	/** Toggles the participant's video mute state. */
	async toggleMuteVideo() {
		return this.videoMuted ? this.unmuteVideo() : this.muteVideo();
	}
	/** Toggles echo cancellation on the audio input. */
	async toggleEchoCancellation() {
		await this.executeMethod(this.id, "call.audioflags.set", {
			echo_cancellation: !this.echoCancellation,
			auto_gain: this.autoGain,
			noise_suppression: this.noiseSuppression
		});
	}
	/** Toggles automatic gain control on the audio input. */
	async toggleAudioInputAutoGain() {
		await this.executeMethod(this.id, "call.audioflags.set", {
			echo_cancellation: this.echoCancellation,
			auto_gain: !this.autoGain,
			noise_suppression: this.noiseSuppression
		});
	}
	/** Toggles noise suppression on the audio input. */
	async toggleNoiseSuppression() {
		await this.executeMethod(this.id, "call.audioflags.set", {
			echo_cancellation: this.echoCancellation,
			auto_gain: this.autoGain,
			noise_suppression: !this.noiseSuppression
		});
	}
	async toggleLowbitrate() {
		throw new UnimplementedError();
	}
	/**
	* Adjusts the **conference-only** microphone energy gate / sensitivity level
	* for this member. Routes through the conferencing mix engine
	* (`signalwire.conferencing member.set_input_sensitivity`) and has no effect
	* on 1:1 WebRTC calls — for those, use browser audio constraints via
	* {@link Call.setNoiseSuppression} / {@link Call.setAutoGainControl}.
	*
	* This is **not** a local PC mic gain control; it only changes how the
	* server-side mixer decides to open the mic gate on this participant.
	*
	* @param value - Sensitivity level as understood by the conference engine
	*   (integer, larger values are more sensitive).
	*/
	async setAudioInputSensitivity(value) {
		await this.executeMethod(this.id, "call.microphone.sensitivity.set", { sensitivity: value });
	}
	/**
	* Sets the **server-side** microphone volume on this participant's bridged
	* call leg. Applies a multiplier to the audio flowing through the mix
	* engine (FreeSWITCH channel read volume) — changes what other participants
	* hear, not what the local browser captures.
	*
	* For local PC mic gain, use {@link Call.setLocalMicrophoneGain} instead.
	*
	* @param value - Volume level (0-100).
	*/
	async setAudioInputVolume(value) {
		await this.executeMethod(this.id, "call.microphone.volume.set", { volume: value });
	}
	/**
	* Sets the **server-side** speaker volume on this participant's bridged call
	* leg (FreeSWITCH channel write volume) — what this participant hears from
	* the mix before it reaches their client.
	*
	* For local playback volume (the `<audio>` element the consumer attaches
	* `remoteStream` to), set `audioElement.volume` directly in the consumer's
	* code.
	*
	* @param value - Volume level (0-100).
	*/
	async setAudioOutputVolume(value) {
		await this.executeMethod(this.id, "call.speaker.volume.set", { volume: value });
	}
	/**
	* Sets the participant's position in the video layout.
	* @param value - The {@link VideoPosition} to assign (e.g. `'auto'`, `'reserved-0'`).
	*/
	async setPosition(value) {
		await this.executeMethod(this.id, "call.member.position.set", { position: value });
	}
	/** Removes this participant from the call. */
	async remove() {
		const state = this._state$.value;
		const target = {
			member_id: this.id,
			call_id: state.call_id ?? "",
			node_id: state.node_id ?? ""
		};
		await this.executeMethod(target, "call.member.remove", {});
	}
	/** Ends the call for this participant. */
	async end() {
		await this.executeMethod(this.id, "call.end", {});
	}
	/**
	* Replaces custom metadata for this participant.
	* @param _meta - Metadata object to set.
	* @throws {UnimplementedError} Not yet implemented.
	*/
	async setMeta(_meta) {
		throw new UnimplementedError();
	}
	/**
	* Merges values into custom metadata (unlike {@link setMeta} which replaces).
	* @param _meta - Metadata to merge.
	* @throws {UnimplementedError} Not yet implemented.
	*/
	async updateMeta(_meta) {
		throw new UnimplementedError();
	}
	/** Destroys the participant, releasing all subscriptions and references. */
	destroy() {
		this.executeMethod = void 0;
		super.destroy();
	}
};
/**
* The local participant in a call, with additional device and media control.
*
* Extends {@link Participant} with screen sharing, device selection,
* and local media stream management.
*/
var SelfParticipant = class extends Participant {
	/** @internal */
	constructor(id, executeMethod, vertoManager, deviceController) {
		super(id, executeMethod, deviceController);
		this.vertoManager = vertoManager;
		this._studioAudio$ = this.createBehaviorSubject(false);
		this.capabilities = new SelfCapabilities();
	}
	destroy() {
		this.capabilities.destroy();
		super.destroy();
	}
	/** Observable indicating whether studio audio (raw/unprocessed audio) mode is enabled. */
	get studioAudio$() {
		return this._studioAudio$.asObservable();
	}
	/** Whether studio audio (raw/unprocessed audio) mode is currently enabled. */
	get studioAudio() {
		return this._studioAudio$.value;
	}
	/**
	* Enables studio audio mode by disabling all audio processing.
	* Sets echoCancellation, noiseSuppression, and autoGainControl to false.
	*/
	async enableStudioAudio() {
		if (this._studioAudio$.value) return;
		this._studioAudio$.next(true);
		await this.executeMethod(this.id, "call.audioflags.set", {
			echo_cancellation: false,
			auto_gain: false,
			noise_suppression: false
		});
	}
	/**
	* Disables studio audio mode by restoring all audio processing to enabled.
	* Sets echoCancellation, noiseSuppression, and autoGainControl to true.
	*/
	async disableStudioAudio() {
		if (!this._studioAudio$.value) return;
		this._studioAudio$.next(false);
		await this.executeMethod(this.id, "call.audioflags.set", {
			echo_cancellation: true,
			auto_gain: true,
			noise_suppression: true
		});
	}
	/** Starts sharing the local screen. */
	async startScreenShare() {
		try {
			await this.vertoManager.addScreenMedia();
		} catch (error) {
			logger$23.error("[Participant.startScreenShare] Screen share error:", error);
		}
	}
	/** Observable of the current screen share status. */
	get screenShareStatus$() {
		return this.vertoManager.screenShareStatus$;
	}
	/** Current screen share status. */
	get screenShareStatus() {
		return this.vertoManager.screenShareStatus;
	}
	/** Stops the current screen share. */
	async stopScreenShare() {
		return this.vertoManager.removeScreenMedia();
	}
	/** Adds an additional media input device to the call. */
	async addAdditionalDevice(options) {
		try {
			await this.vertoManager.addInputDevice(options);
		} catch (error) {
			logger$23.error("[Participant.startScreenShare] Screen share error:", error);
		}
	}
	/** Removes an additional media input device by ID. */
	async removeAdditionalDevice(id) {
		return this.vertoManager.removeInputDevices(id);
	}
	/** Adds or replaces the primary audio input device with optional constraints or stream. */
	async addAudioInputDevice({ constraints, stream } = {}) {
		const audio = constraints ?? stream ? void 0 : true;
		return this.vertoManager.addMainInputDevices({
			audio,
			inputAudioDeviceConstraints: constraints,
			inputAudioStream: stream
		});
	}
	/** Adds or replaces the primary video input device with optional constraints or stream. */
	async addVideoInputDevice({ constraints, stream } = {}) {
		const video = constraints ?? stream ? void 0 : true;
		return this.vertoManager.addMainInputDevices({
			video,
			inputVideoDeviceConstraints: constraints,
			inputVideoStream: stream
		});
	}
	/** Adds or replaces primary input devices (audio and/or video). */
	async addInputDevices(options = {}) {
		await this.vertoManager.addMainInputDevices(options);
	}
	/** Selects the audio input device for future calls. Optionally saves as a preference. */
	selectAudioInputDevice(device, options = {}) {
		this.deviceController.selectAudioInputDevice(device);
		if (options.savePreference) PreferencesContainer.instance.preferredAudioInput = device;
	}
	/** Updates the audio input track constraints for the active call. */
	async setAudioInputDeviceConstraints(constraints) {
		await this.vertoManager.updateMediaConstraints({ audio: constraints });
	}
	/** Updates both audio and video input track constraints for the active call. */
	async setInputDevicesConstraints(constraints) {
		await this.vertoManager.updateMediaConstraints(constraints);
	}
	/** Selects the video input device for future calls. Optionally saves as a preference. */
	selectVideoInputDevice(device, options = {}) {
		this.deviceController.selectVideoInputDevice(device);
		if (options.savePreference) PreferencesContainer.instance.preferredVideoInput = device;
	}
	/** Updates the video input track constraints for the active call. */
	async setVideoInputDeviceConstraints(constraints) {
		await this.vertoManager.updateMediaConstraints({ video: constraints });
	}
	/** Selects the audio output device. Optionally saves as a preference. */
	selectAudioOutputDevice(device, options = {}) {
		this.deviceController.selectAudioOutputDevice(device);
		if (options.savePreference) PreferencesContainer.instance.preferredAudioOutput = device;
	}
	/**
	* Exits studio audio mode without restoring defaults.
	* Called internally before individual audio flag toggles.
	*/
	exitStudioModeIfActive() {
		if (this._studioAudio$.value) {
			logger$23.debug("[SelfParticipant] Exiting studio audio mode due to individual flag toggle");
			this._studioAudio$.next(false);
		}
	}
	/** Toggles echo cancellation. Exits studio mode if active. */
	async toggleEchoCancellation() {
		this.exitStudioModeIfActive();
		await super.toggleEchoCancellation();
	}
	/** Toggles automatic gain control. Exits studio mode if active. */
	async toggleAudioInputAutoGain() {
		this.exitStudioModeIfActive();
		await super.toggleAudioInputAutoGain();
	}
	/** Toggles noise suppression. Exits studio mode if active. */
	async toggleNoiseSuppression() {
		this.exitStudioModeIfActive();
		await super.toggleNoiseSuppression();
	}
	/** Mutes local audio. Falls back to local device mute if the server RPC fails. */
	async mute() {
		try {
			await super.mute();
		} catch (error) {
			logger$23.warn("[Participant.toggleAudioInput] Server Error while muting audio input, proceeding with local toggle anyway", error);
		} finally {
			this.vertoManager.muteMainAudioInputDevice();
		}
	}
	/** Unmutes local audio. Falls back to local device unmute if the server RPC fails. */
	async unmute() {
		try {
			await super.unmute();
		} catch (error) {
			logger$23.warn("[Participant.toggleAudioInput] Server Error while unmuting audio input, proceeding with local toggle anyway", error);
		} finally {
			await this.vertoManager.unmuteMainAudioInputDevice();
		}
	}
	/** Mutes local video. Falls back to local device mute if the server RPC fails. */
	async muteVideo() {
		try {
			await super.muteVideo();
		} catch (error) {
			logger$23.warn("[Participant.toggleVideoInput] Server Error while muting video input, proceeding with local toggle anyway", error);
		} finally {
			this.vertoManager.muteMainVideoInputDevice();
		}
	}
	/** Unmutes local video. Falls back to local device unmute if the server RPC fails. */
	async unmuteVideo() {
		try {
			await super.unmuteVideo();
		} catch (error) {
			logger$23.warn("[Participant.toggleVideoInput] Server Error while unmuting video input, proceeding with local toggle anyway", error);
		} finally {
			await this.vertoManager.unmuteMainVideoInputDevice();
		}
	}
};
/** Type guard that checks if a participant is the local {@link SelfParticipant}. */
const isSelfParticipant = (participant) => {
	return participant instanceof SelfParticipant;
};

//#endregion
//#region src/core/RPCMessages/guards/base.guards.ts
function isObject(value) {
	return typeof value === "object" && value !== null;
}
function hasProperty(obj, key) {
	return key in obj;
}
function isJSONRPCRequest(value) {
	return isObject(value) && hasProperty(value, "jsonrpc") && value.jsonrpc === "2.0" && hasProperty(value, "id") && typeof value.id === "string" && hasProperty(value, "method") && typeof value.method === "string";
}
function isJSONRPCResponse(value) {
	return isObject(value) && hasProperty(value, "jsonrpc") && value.jsonrpc === "2.0" && hasProperty(value, "id") && typeof value.id === "string" && (hasProperty(value, "result") || hasProperty(value, "error"));
}
function isJSONRPCErrorResponse(value) {
	return isObject(value) && hasProperty(value, "jsonrpc") && value.jsonrpc === "2.0" && hasProperty(value, "id") && typeof value.id === "string" && (hasProperty(value, "error") && isObject(value.error) && hasProperty(value.error, "code") && hasProperty(value.error, "message") || hasProperty(value, "result") && isObject(value.result) && hasProperty(value.result, "code") && value.result.code !== "200" && hasProperty(value.result, "message"));
}

//#endregion
//#region src/core/RPCMessages/guards/events.guards.ts
/** @internal @packageDocumentation */
/**
* Factory function to create Request-level type guards.
*/
function createEventRequestGuard(eventType) {
	return (value) => isSignalwireRequest(value) && value.params.event_type === eventType;
}
/**
* Factory function to create Metadata-level type guards.
*/
function createEventMetadataGuard(eventType) {
	return (value) => isSignalwireMetadata(value) && value.event_type === eventType;
}
function isSignalwireRequest(value) {
	return isJSONRPCRequest(value) && value.method === "signalwire.event" && isObject(value.params) && hasProperty(value.params, "event_type");
}
const isSignalwireAuthorizationStateRequest = createEventRequestGuard("signalwire.authorization.state");
const isWebrtcMessageRequest = createEventRequestGuard("webrtc.message");
const isCallJoinedRequest = createEventRequestGuard("call.joined");
const isCallLeftRequest = createEventRequestGuard("call.left");
const isCallUpdatedRequest = createEventRequestGuard("call.updated");
const isCallStateRequest = createEventRequestGuard("call.state");
const isCallPlayRequest = createEventRequestGuard("call.play");
const isCallConnectRequest = createEventRequestGuard("call.connect");
const isRoomUpdatedRequest = createEventRequestGuard("room.updated");
const isMemberUpdatedRequest = createEventRequestGuard("member.updated");
const isMemberJoinedRequest = createEventRequestGuard("member.joined");
const isMemberLeftRequest = createEventRequestGuard("member.left");
const isMemberTalkingRequest = createEventRequestGuard("member.talking");
const isLayoutChangedRequest = createEventRequestGuard("layout.changed");
const isConversationMessageRequest = createEventRequestGuard("conversation.message");
const isConversationMessageUpdatedRequest = createEventRequestGuard("conversation.message.updated");
function isSignalwireMetadata(value) {
	return isObject(value) && hasProperty(value, "event_type") && typeof value.event_type === "string" && hasProperty(value, "params");
}
function isSignalwireCallMetadata(value) {
	return isSignalwireMetadata(value) && (isCallJoinedMetadata(value) || isCallLeftMetadata(value) || isCallUpdatedMetadata(value) || isCallStateMetadata(value) || isCallPlayMetadata(value) || isCallConnectMetadata(value) || isRoomUpdatedMetadata(value) || isMemberUpdatedMetadata(value) || isMemberJoinedMetadata(value) || isMemberLeftMetadata(value) || isMemberTalkingMetadata(value) || isLayoutChangedMetadata(value) || isWebrtcMessageMetadata(value) || isConversationMessageMetadata(value) || isConversationMessageUpdatedMetadata(value));
}
const isSignalwireAuthorizationStateMetadata = createEventMetadataGuard("signalwire.authorization.state");
const isWebrtcMessageMetadata = createEventMetadataGuard("webrtc.message");
const isCallJoinedMetadata = createEventMetadataGuard("call.joined");
const isCallLeftMetadata = createEventMetadataGuard("call.left");
const isCallUpdatedMetadata = createEventMetadataGuard("call.updated");
const isCallStateMetadata = createEventMetadataGuard("call.state");
const isCallPlayMetadata = createEventMetadataGuard("call.play");
const isCallConnectMetadata = createEventMetadataGuard("call.connect");
const isRoomUpdatedMetadata = createEventMetadataGuard("room.updated");
const isMemberUpdatedMetadata = createEventMetadataGuard("member.updated");
const isMemberJoinedMetadata = createEventMetadataGuard("member.joined");
const isMemberLeftMetadata = createEventMetadataGuard("member.left");
const isMemberTalkingMetadata = createEventMetadataGuard("member.talking");
const isLayoutChangedMetadata = createEventMetadataGuard("layout.changed");
const isConversationMessageMetadata = createEventMetadataGuard("conversation.message");
const isConversationMessageUpdatedMetadata = createEventMetadataGuard("conversation.message.updated");
function isCallJoinedPayload(value) {
	return isObject(value) && hasProperty(value, "room_session") && hasProperty(value, "call_id") && hasProperty(value, "member_id") && hasProperty(value, "capabilities");
}
function isLayoutChangedPayload(value) {
	return isObject(value) && hasProperty(value, "room_id") && hasProperty(value, "room_session_id") && hasProperty(value, "layout");
}

//#endregion
//#region src/managers/CallEventsManager.ts
const logger$22 = getLogger();
const initialSessionState = {};
/** @internal */
var CallEventsManager = class extends Destroyable {
	constructor(webRtcCallSession, options = {}) {
		super();
		this.webRtcCallSession = webRtcCallSession;
		this.options = options;
		this.callIds = /* @__PURE__ */ new Set();
		this.roomSessionIds = /* @__PURE__ */ new Set();
		this._participants$ = this.createBehaviorSubject({});
		this._self$ = this.createBehaviorSubject(null);
		this._sessionState$ = this.createBehaviorSubject(initialSessionState);
		this.initSubscriptions();
	}
	get participants$() {
		return this.cachedObservable("participants$", () => this._participants$.asObservable().pipe(map((participantsRecord) => Object.values(participantsRecord))));
	}
	get participants() {
		return Object.values(this._participants$.value);
	}
	get self$() {
		return this.cachedObservable("self$", () => this._self$.asObservable().pipe(filterNull()));
	}
	isRoomSessionIdValid(roomSessionId) {
		return this.roomSessionIds.has(roomSessionId);
	}
	addCallId(callId) {
		this.callIds.add(callId);
	}
	isCallIdValid(callId) {
		return this.callIds.has(callId);
	}
	get recording$() {
		return this.cachedObservable("recording$", () => this._sessionState$.pipe(map((state) => state.recording), distinctUntilChanged(), filterNull()));
	}
	get recordings$() {
		return this.cachedObservable("recordings$", () => this._sessionState$.pipe(map((state) => state.recordings), distinctUntilChanged(), filterNull()));
	}
	get streaming$() {
		return this.cachedObservable("streaming$", () => this._sessionState$.pipe(map((state) => state.streaming), distinctUntilChanged(), filterNull()));
	}
	get streams$() {
		return this.cachedObservable("streams$", () => this._sessionState$.pipe(map((state) => state.streams), distinctUntilChanged(), filterNull()));
	}
	get playbacks$() {
		return this.cachedObservable("playbacks$", () => this._sessionState$.pipe(map((state) => state.playbacks), distinctUntilChanged(), filterNull()));
	}
	get raiseHandPriority$() {
		return this.cachedObservable("raiseHandPriority$", () => this._sessionState$.pipe(map((state) => state.prioritize_handraise), distinctUntilChanged(), filterNull()));
	}
	get locked$() {
		return this.cachedObservable("locked$", () => this._sessionState$.pipe(map((state) => state.locked), distinctUntilChanged(), filterNull()));
	}
	get meta$() {
		return this.cachedObservable("meta$", () => this._sessionState$.pipe(map((state) => state.meta), distinctUntilChanged(), filterNull()));
	}
	get capabilities$() {
		return this.cachedObservable("capabilities$", () => this._sessionState$.pipe(map((state) => state.capabilities), distinctUntilChanged(), filterNull()));
	}
	get layout$() {
		return this.cachedObservable("layout$", () => this._sessionState$.pipe(map((state) => state.layout_name), distinctUntilChanged(), filterNull()));
	}
	get layouts$() {
		return this.cachedObservable("layouts$", () => this._sessionState$.pipe(map((state) => state.layouts), distinctUntilChanged(), filterNull()));
	}
	get layoutLayers$() {
		return this.cachedObservable("layoutLayers$", () => this._sessionState$.pipe(map((state) => state.layout_layers), distinctUntilChanged(), filterNull()));
	}
	get self() {
		return this._self$.value;
	}
	get layoutLayers() {
		return this._sessionState$.value.layout_layers ?? [];
	}
	get recording() {
		return this._sessionState$.value.recording ?? false;
	}
	get streaming() {
		return this._sessionState$.value.streaming ?? false;
	}
	get raiseHandPriority() {
		return this._sessionState$.value.prioritize_handraise ?? false;
	}
	get locked() {
		return this._sessionState$.value.locked ?? false;
	}
	get meta() {
		return this._sessionState$.value.meta ?? {};
	}
	get layout() {
		return this._sessionState$.value.layout_name;
	}
	get layouts() {
		return this._sessionState$.value.layouts ?? [];
	}
	get capabilities() {
		return this._sessionState$.value.capabilities ?? [];
	}
	isSessionEvent(id) {
		return this.callIds.has(id) || this.roomSessionIds.has(id);
	}
	initSubscriptions() {
		this.subscribeTo(this.callJoinedEvent$, (callJoinedEvent) => {
			logger$22.debug("[CallEventsManager] Handling call.joined event for call/session IDs:", {
				callId: callJoinedEvent.call_id,
				roomSessionId: callJoinedEvent.room_session_id
			});
			const sessionState = callJoinedEvent.room_session;
			const capabilities = callJoinedEvent.capabilities;
			this.selfId = this.selfId ?? callJoinedEvent.member_id;
			this.originCallId = this.originCallId ?? callJoinedEvent.origin_call_id;
			this.callIds.add(callJoinedEvent.call_id);
			this.roomSessionIds.add(callJoinedEvent.room_session_id);
			this._sessionState$.next({
				...this._sessionState$.value,
				recording: sessionState.recording,
				recordings: sessionState.recordings,
				streaming: sessionState.streaming,
				streams: sessionState.streams,
				playbacks: sessionState.playbacks,
				prioritize_handraise: sessionState.prioritize_handraise,
				locked: sessionState.locked,
				meta: sessionState.meta,
				capabilities
			});
			this.updateParticipants(sessionState.members);
			this._self$.value?.capabilities.updateFromRaw(capabilities);
			if (this._self$.value?.capabilities.setLayout) this.updateLayouts();
		});
		this.subscribeTo(this.memberUpdates$, (member) => {
			logger$22.debug("[CallEventsManager] Handling member update event for member ID:", member);
			this.upsertParticipant(member);
		});
		this.subscribeTo(this.webRtcCallSession.memberLeft$, (memberLeftEvent) => {
			logger$22.debug("[CallEventsManager] Handling member.left event for member ID:", memberLeftEvent.member.member_id);
			const participants = { ...this._participants$.value };
			if (memberLeftEvent.member.member_id in participants) {
				delete participants[memberLeftEvent.member.member_id];
				this._participants$.next(participants);
			} else logger$22.warn(`[CallEventsManager] Received member.left event for unknown member ID: ${memberLeftEvent.member.member_id}`);
		});
		this.subscribeTo(this.webRtcCallSession.callUpdated$, (callUpdatedEvent) => {
			logger$22.debug("[CallEventsManager] Handling call.updated event:", callUpdatedEvent);
			const roomSession = callUpdatedEvent.room_session;
			this._sessionState$.next({
				...this._sessionState$.value,
				recording: roomSession.recording,
				recordings: roomSession.recordings,
				streaming: roomSession.streaming,
				streams: roomSession.streams,
				playbacks: roomSession.playbacks,
				prioritize_handraise: roomSession.prioritize_handraise,
				locked: roomSession.locked,
				meta: roomSession.meta
			});
		});
		this.subscribeTo(this.layoutChangedEvent$, (layoutChangedEvent) => {
			logger$22.debug("[CallEventsManager] Handling layout.changed event:", layoutChangedEvent);
			this._sessionState$.next({
				...this._sessionState$.value,
				layout_name: layoutChangedEvent.id,
				layout_layers: layoutChangedEvent.layers
			});
			this.updateParticipantPositions(layoutChangedEvent);
		});
	}
	updateParticipantPositions(layoutChangedEvent) {
		if (Object.keys(this._participants$.value).length > 0 && !layoutChangedEvent.layers.some((layer) => !!layer.member_id)) logger$22.warn("[CallEventsManager] No layers with member_id found in layout.changed event. Nothing to update.");
		layoutChangedEvent.layers.filter((layer) => !!layer.member_id).filter((layer) => {
			if (!(layer.member_id in this._participants$.value)) {
				logger$22.warn(`[CallEventsManager] Skipping layout layer for unknown member_id: ${layer.member_id}`);
				return false;
			}
			return true;
		}).map((layer) => {
			this._participants$.value[layer.member_id].upnext({ position: layer });
			return this._participants$.value[layer.member_id];
		}).forEach((participant) => {
			if (isSelfParticipant(participant)) this._self$.next(participant);
			this._participants$.next({
				...this._participants$.value,
				[participant.id]: participant
			});
		});
	}
	updateLayouts() {
		if (!this.selfId) return;
		this.webRtcCallSession.executeMethod(this.selfId, "call.layout.list", {}).then((response) => {
			this._sessionState$.next({
				...this._sessionState$.value,
				layouts: response.result.layouts
			});
		}).catch((error) => {
			logger$22.error("[CallEventsManager] Error fetching layouts:", error);
		});
	}
	updateParticipants(members) {
		members.forEach((member) => this.upsertParticipant(member));
	}
	upsertParticipant(member) {
		if (!(member.member_id in this._participants$.value)) {
			const newParticipant = this.webRtcCallSession.createParticipant(member.member_id, this.selfId);
			this._participants$.next({
				...this._participants$.value,
				[member.member_id]: newParticipant
			});
		}
		const participant = this._participants$.value[member.member_id];
		const oldValue = participant.value;
		logger$22.debug("[CallEventsManager] Updating participant:", member.member_id, {
			oldValue,
			newValue: member
		});
		participant.upnext({
			...oldValue,
			...member
		});
		if (isSelfParticipant(participant)) this._self$.next(participant);
		this._participants$.next(this._participants$.value);
	}
	get callJoinedEvent$() {
		return this.cachedObservable("callJoinedEvent$", () => this.webRtcCallSession.callEvent$.pipe(filter(isCallJoinedPayload), tap((event) => {
			logger$22.debug("[CallEventsManager] Call joined event:", event);
		})));
	}
	get layoutChangedEvent$() {
		return this.cachedObservable("layoutChangedEvent$", () => this.webRtcCallSession.callEvent$.pipe(filterAs(isLayoutChangedPayload, "layout"), tap((event) => {
			logger$22.debug("[CallEventsManager] Layout changed event:", event);
		})));
	}
	get memberUpdates$() {
		return this.cachedObservable("memberUpdates$", () => merge(this.webRtcCallSession.memberJoined$, this.webRtcCallSession.memberUpdated$, this.webRtcCallSession.memberTalking$).pipe(map((event) => event.member), tap((event) => {
			logger$22.debug("[CallEventsManager] Member update event:", event);
		})));
	}
	destroy() {
		Object.values(this._participants$.value).forEach((participant) => {
			participant.destroy();
		});
		this._participants$.next({});
		this._self$.next(null);
		this.callIds.clear();
		this.roomSessionIds.clear();
		this.selfId = void 0;
		this.originCallId = void 0;
		this.webRtcCallSession = void 0;
		this.callSession = void 0;
		super.destroy();
	}
};

//#endregion
//#region src/helpers/SDPHelper.ts
/**
* SDPHelper - Utility functions for SDP (Session Description Protocol) parsing and validation.
*
* This module provides helper functions to analyze and validate SDP content,
* particularly for ICE candidate validation in WebRTC connections.
*/
/** Valid SDP direction attribute values. */
const SDP_DIRECTIONS = new Set([
	"sendrecv",
	"sendonly",
	"recvonly",
	"inactive"
]);
/**
* Extracts the media directions (audio/video) from an SDP string.
*
* Parses each media section (`m=audio` / `m=video`) and reads the `a=` direction
* attribute (`sendrecv`, `sendonly`, `recvonly`, `inactive`).
* If no explicit direction attribute is found for a media section, defaults to `sendrecv`
* per RFC 4566.
*
* @param sdp - The SDP string to parse
* @returns The extracted audio and video directions
*
* @example
* ```typescript
* const sdp = `v=0\r\nm=audio 9 UDP/TLS/RTP/SAVPF 111\r\na=sendrecv\r\nm=video 9 UDP/TLS/RTP/SAVPF 96\r\na=recvonly`;
* extractMediaDirectionsFromSDP(sdp);
* // { audio: 'sendrecv', video: 'recvonly' }
* ```
*/
function extractMediaDirectionsFromSDP(sdp) {
	const result = {
		audio: "inactive",
		video: "inactive"
	};
	if (!sdp) return result;
	const lines = sdp.split(/\r?\n/);
	let currentMediaKind = null;
	let currentDirection = null;
	for (const line of lines) if (line.startsWith("m=")) {
		if (currentMediaKind) result[currentMediaKind] = currentDirection ?? "sendrecv";
		if (line.startsWith("m=audio")) currentMediaKind = "audio";
		else if (line.startsWith("m=video")) currentMediaKind = "video";
		else currentMediaKind = null;
		currentDirection = null;
	} else if (currentMediaKind && line.startsWith("a=")) {
		const attr = line.substring(2).trim();
		if (SDP_DIRECTIONS.has(attr)) currentDirection = attr;
	}
	if (currentMediaKind) result[currentMediaKind] = currentDirection ?? "sendrecv";
	return result;
}
/**
* Validates that an SDP string has at least one non-host ICE candidate
* for each media section (m= line).
*
* Non-host candidates (srflx, prflx, relay) indicate that the SDP has
* gathered candidates that can be used for connectivity through NAT
* traversal mechanisms.
*
* @param sdp - The SDP string to validate
* @returns true if the SDP is valid (has non-host candidates for all media sections,
*          or has no media sections), false otherwise
*
* @example
* ```typescript
* const sdp = `v=0
* m=audio 9 UDP/TLS/RTP/SAVPF 111
* a=candidate:1 1 UDP 1694498815 203.0.113.1 50001 typ srflx
* m=video 9 UDP/TLS/RTP/SAVPF 96
* a=candidate:2 1 UDP 1694498815 203.0.113.1 50002 typ relay`;
*
* isValidLocalDescription(sdp); // returns true
* ```
*/
function isValidLocalDescription(sdp) {
	if (!sdp) return false;
	const lines = sdp.split("\r\n");
	const mediaSectionsValidCandidates = [];
	let currentSection = -1;
	for (const line of lines) if (line.startsWith("m=")) {
		currentSection += 1;
		mediaSectionsValidCandidates[currentSection] = 0;
	} else if (line.startsWith("a=candidate:")) {
		const typeMatch = /\styp\s+(host|srflx|prflx|relay)/.exec(line);
		if (typeMatch && typeMatch[1] !== "host") mediaSectionsValidCandidates[currentSection] += 1;
	}
	return !mediaSectionsValidCandidates.length || mediaSectionsValidCandidates.every((count) => count > 0);
}
/**
* Reorders codec payload types in an SDP m= line to match the preferred order.
*
* For each media section (`m=audio` / `m=video`), this function parses the
* `a=rtpmap` lines to build a payload-type-to-codec-name map, then rewrites
* the m= line so preferred codecs appear first.
*
* Codecs not in the preferred list retain their original relative order after
* the preferred ones. Preferred codecs that do not appear in the SDP are ignored.
*
* @param sdp - The SDP string to modify
* @param preferredVideo - Preferred video codec names in priority order (e.g., ['VP9', 'VP8'])
* @param preferredAudio - Preferred audio codec names in priority order (e.g., ['opus'])
* @returns The modified SDP string with reordered codec payload types
*
* @example
* ```typescript
* const reordered = reorderCodecs(sdp, ['VP9', 'H264'], ['opus']);
* ```
*/
function reorderCodecs(sdp, preferredVideo = [], preferredAudio = []) {
	if (!sdp || preferredVideo.length === 0 && preferredAudio.length === 0) return sdp;
	const sections = splitIntoMediaSections(sdp);
	const result = [sections[0]];
	for (let i = 1; i < sections.length; i++) {
		const section = sections[i];
		const mLine = section.split(/\r?\n/)[0];
		if (mLine.startsWith("m=video") && preferredVideo.length > 0) result.push(reorderSectionCodecs(section, preferredVideo));
		else if (mLine.startsWith("m=audio") && preferredAudio.length > 0) result.push(reorderSectionCodecs(section, preferredAudio));
		else result.push(section);
	}
	return result.join("");
}
/**
* Adds stereo=1 and sprop-stereo=1 to the Opus fmtp line in the SDP.
*
* This enables stereo Opus negotiation for music/podcast use cases.
* Also sets maxaveragebitrate to accommodate stereo audio (default: 510000 bps).
*
* @param sdp - The SDP string to modify
* @param maxBitrate - Maximum average bitrate in bps (default: 510000)
* @returns The modified SDP string with stereo Opus parameters
*
* @example
* ```typescript
* const stereoSdp = enableStereoOpus(sdp);
* // a=fmtp:111 minptime=10;useinbandfec=1;stereo=1;sprop-stereo=1;maxaveragebitrate=510000
* ```
*/
function enableStereoOpus(sdp, maxBitrate = DEFAULT_STEREO_MAX_AVERAGE_BITRATE) {
	if (!sdp) return sdp;
	const opusPayloadType = findOpusPayloadType(sdp);
	if (opusPayloadType === null) return sdp;
	const lines = sdp.split(/\r?\n/);
	const updatedLines = [];
	const fmtpPrefix = `a=fmtp:${opusPayloadType} `;
	let foundFmtp = false;
	for (const line of lines) if (line.startsWith(fmtpPrefix)) {
		foundFmtp = true;
		updatedLines.push(appendStereoParams(line, maxBitrate));
	} else updatedLines.push(line);
	if (!foundFmtp) {
		const rtpmapLine = `a=rtpmap:${opusPayloadType} `;
		const withFmtp = [];
		for (const line of updatedLines) {
			withFmtp.push(line);
			if (line.startsWith(rtpmapLine)) withFmtp.push(`a=fmtp:${opusPayloadType} stereo=1;sprop-stereo=1;maxaveragebitrate=${maxBitrate}`);
		}
		return withFmtp.join("\r\n");
	}
	return updatedLines.join("\r\n");
}
/**
* Convenience wrapper that applies both codec reordering and stereo Opus.
*
* @param sdp - The SDP string to modify
* @param audioCodecs - Preferred audio codecs in priority order
* @param videoCodecs - Preferred video codecs in priority order
* @returns The modified SDP string
*/
function setCodecPreferences(sdp, audioCodecs = [], videoCodecs = []) {
	return reorderCodecs(sdp, videoCodecs, audioCodecs);
}
/**
* Splits an SDP string into session-level + per-media sections.
*
* The first element is the session-level content (before the first m= line).
* Subsequent elements each start with an m= line.
*/
function splitIntoMediaSections(sdp) {
	return sdp.split(/(?=m=)/);
}
/**
* Reorders codecs within a single media section based on preferred names.
*/
function reorderSectionCodecs(section, preferredNames) {
	const lines = section.split(/\r?\n/);
	const mLineParts = lines[0].split(" ");
	if (mLineParts.length < 4) return section;
	const payloadTypes = mLineParts.slice(3);
	const ptToCodec = /* @__PURE__ */ new Map();
	for (const line of lines) {
		const match = /^a=rtpmap:(\d+)\s+([^\s/]+)/.exec(line);
		if (match) ptToCodec.set(match[1], match[2]);
	}
	const preferredNamesUpper = preferredNames.map((n) => n.toUpperCase());
	const preferred = [];
	const remaining = [];
	for (const pt of payloadTypes) {
		const codecName = ptToCodec.get(pt)?.toUpperCase() ?? "";
		if (preferredNamesUpper.indexOf(codecName) >= 0) preferred.push(pt);
		else remaining.push(pt);
	}
	preferred.sort((a, b) => {
		const nameA = ptToCodec.get(a)?.toUpperCase() ?? "";
		const nameB = ptToCodec.get(b)?.toUpperCase() ?? "";
		return preferredNamesUpper.indexOf(nameA) - preferredNamesUpper.indexOf(nameB);
	});
	const reorderedPTs = [...preferred, ...remaining];
	return [[...mLineParts.slice(0, 3), ...reorderedPTs].join(" "), ...lines.slice(1)].join("\r\n");
}
/**
* Finds the Opus payload type number from a=rtpmap lines in the SDP.
*
* @returns The payload type number as a string, or null if Opus is not found
*/
function findOpusPayloadType(sdp) {
	const match = /a=rtpmap:(\d+)\s+opus\/48000/i.exec(sdp);
	return match ? match[1] : null;
}
/**
* Appends stereo and bitrate parameters to an existing Opus fmtp line.
*
* Avoids duplicating parameters if they already exist.
*/
function appendStereoParams(fmtpLine, maxBitrate) {
	let result = fmtpLine;
	if (!result.includes("stereo=")) result += ";stereo=1";
	if (!result.includes("sprop-stereo=")) result += ";sprop-stereo=1";
	if (!result.includes("maxaveragebitrate=")) result += `;maxaveragebitrate=${maxBitrate}`;
	return result;
}

//#endregion
//#region src/controllers/ICEGatheringController.ts
const logger$21 = getLogger();
var ICEGatheringController = class extends Destroyable {
	constructor(peerConnection, peerConnectionControllerNegotiating$, options = {}) {
		super();
		this.peerConnection = peerConnection;
		this.peerConnectionControllerNegotiating$ = peerConnectionControllerNegotiating$;
		this.onicegatheringstatechangeHandler = () => {
			const { iceGatheringState } = this.peerConnection;
			logger$21.debug(`[ICEGatheringController] ICE gathering state changed to: ${iceGatheringState}`);
			if (iceGatheringState === "gathering") this._iceCandidatesState.next({
				state: "gathering",
				validSDP: false
			});
		};
		this.onicecandidateHandler = (event) => {
			logger$21.debug("[ICEGatheringController] ICE candidate event received:", event.candidate);
			this.removeTimer("iceCandidateTimer");
			if (event.candidate) this.iceCandidateTimer = setTimeout(() => {
				if (this.peerConnection.iceGatheringState !== "complete") {
					logger$21.warn("[ICEGatheringController] ICE candidate timeout, using current SDP");
					this.handleICECandidateTimeout();
				}
			}, this.iceCandidateTimeout);
			else {
				logger$21.debug("[ICEGatheringController] ICE gathering completed: null candidate received");
				this.removeTimer("iceGatheringTimer");
				this.handleICEGatheringComplete();
			}
		};
		this._iceCandidatesState = this.createBehaviorSubject({
			state: "new",
			validSDP: false
		});
		this.iceCandidateTimeout = options.iceCandidateTimeout ?? DEFAULT_ICE_CANDIDATE_TIMEOUT_MS;
		this.iceGatheringTimeout = options.iceGatheringTimeout ?? DEFAULT_ICE_GATHERING_TIMEOUT_MS;
		this.relayOnly = options.relayOnly ?? false;
		this.setupEventListeners();
		this.subscribeTo(this.peerConnectionControllerNegotiating$.pipe(filter((isNegotiating) => isNegotiating)), (isNegotiating) => {
			if (isNegotiating) {
				this.setupEventListeners();
				this.iceGatheringTimer = setTimeout(() => {
					if (this.peerConnection.iceGatheringState !== "complete") {
						logger$21.warn("[ICEGatheringController] ICE gathering timeout, using current SDP");
						this.handleICEGatheringTimeout();
					}
				}, this.iceGatheringTimeout);
			}
		});
	}
	setupEventListeners() {
		this.peerConnection.removeEventListener("icecandidate", this.onicecandidateHandler);
		this.peerConnection.addEventListener("icecandidate", this.onicecandidateHandler);
		this.peerConnection.removeEventListener("icegatheringstatechange", this.onicegatheringstatechangeHandler);
		this.peerConnection.addEventListener("icegatheringstatechange", this.onicegatheringstatechangeHandler);
	}
	get iceCandidatesState$() {
		return this._iceCandidatesState.pipe(withLatestFrom(this.peerConnectionControllerNegotiating$), filter(([_, isNegotiating]) => isNegotiating), map(([state, _]) => state.state));
	}
	get hasValidLocalDescriptionSDP() {
		const sdp = this.peerConnection.localDescription?.sdp;
		return isValidLocalDescription(sdp ?? "");
	}
	get isRelayOnly() {
		return this.relayOnly;
	}
	setRelayOnly(value) {
		this.relayOnly = value;
	}
	handleICEGatheringComplete() {
		logger$21.debug("[ICEGatheringController] Handling ICE gathering complete");
		logger$21.debug(`[ICEGatheringController] Checking ICE gathering state: ${this.peerConnection.iceGatheringState}`);
		logger$21.debug("[ICEGatheringController] ICE gathering complete");
		this._iceCandidatesState.next({
			state: "complete",
			validSDP: this.hasValidLocalDescriptionSDP
		});
		this.stopGathering();
	}
	stopGathering() {
		this.peerConnection.removeEventListener("icegatheringstatechange", this.onicegatheringstatechangeHandler);
		this.peerConnection.removeEventListener("icecandidate", this.onicecandidateHandler);
		this.clearAllTimers();
	}
	handleICEGatheringTimeout() {
		this.removeTimer("iceGatheringTimer");
		const validSDP = this.hasValidLocalDescriptionSDP;
		if (validSDP) {
			logger$21.debug("[ICEGatheringController] Local SDP is valid");
			this._iceCandidatesState.next({
				state: "timeout",
				validSDP
			});
			this.stopGathering();
		} else logger$21.debug("### ICE gathering timeout\n", this.peerConnection.localDescription?.sdp);
	}
	handleICECandidateTimeout() {
		if (this.iceCandidateTimer) this.removeTimer("iceCandidateTimer");
		logger$21.warn("[ICEGatheringController] ICE candidate timeout");
		const validSDP = this.hasValidLocalDescriptionSDP;
		if (!validSDP && !this.relayOnly) this.restartICEGatheringWithRelayOnly();
		else {
			logger$21.debug("[ICEGatheringController] Using current SDP due to ICE candidate timeout");
			this._iceCandidatesState.next({
				state: "timeout",
				validSDP
			});
			this.stopGathering();
		}
	}
	restartICEGatheringWithRelayOnly() {
		logger$21.debug("[ICEGatheringController] Restarting ICE gathering with relay-only candidates");
		this.relayOnly = true;
		this.peerConnection.setConfiguration({
			...this.peerConnection.getConfiguration(),
			iceTransportPolicy: "relay"
		});
		this.peerConnection.restartIce();
	}
	removeTimer(timer$1) {
		if (this[timer$1]) {
			clearTimeout(this[timer$1]);
			this[timer$1] = void 0;
		}
	}
	clearAllTimers() {
		logger$21.debug("[ICEGatheringController] Clearing all timers");
		this.removeTimer("iceGatheringTimer");
		this.removeTimer("iceCandidateTimer");
	}
	removeEventListeners() {
		this.peerConnection.removeEventListener("icegatheringstatechange", this.onicegatheringstatechangeHandler);
		this.peerConnection.removeEventListener("icecandidate", this.onicecandidateHandler);
	}
	destroy() {
		logger$21.debug("[ICEGatheringController] Destroying ICEGatheringController");
		this.clearAllTimers();
		this.removeEventListeners();
		super.destroy();
	}
};

//#endregion
//#region src/controllers/LocalAudioPipeline.ts
const logger$20 = getLogger();
/**
* Web Audio pipeline for the local microphone stream.
*
* Wraps the raw mic `MediaStreamTrack` in a graph of:
*
* ```
*   MediaStreamAudioSourceNode  →  GainNode  →  AnalyserNode  →  MediaStreamAudioDestinationNode
* ```
*
* The {@link outputTrack} from the destination node is what callers should
* attach to the `RTCRtpSender` in place of the raw mic track. The same
* destination track is reused across input changes (device switch, mute /
* unmute track replacement) so the sender reference stays stable — only the
* source end of the graph is rebuilt.
*
* The pipeline owns a single {@link AudioContext}. Callers must invoke
* {@link destroy} to release it when the call ends.
*/
var LocalAudioPipeline = class extends Destroyable {
	constructor(options = {}) {
		super();
		this._inputSource = null;
		this._inputStream = null;
		this._lastSpokeAt = 0;
		this._gain$ = this.createBehaviorSubject(1);
		this._pttMultiplier = 1;
		this._audioContext = (options.audioContextFactory ?? (() => new AudioContext()))();
		this._gainNode = this._audioContext.createGain();
		this._analyser = this._audioContext.createAnalyser();
		this._analyser.fftSize = 2048;
		this._analyser.smoothingTimeConstant = .3;
		this._analyserBuffer = new Uint8Array(new ArrayBuffer(this._analyser.fftSize));
		this._destination = this._audioContext.createMediaStreamDestination();
		this._gainNode.connect(this._analyser);
		this._analyser.connect(this._destination);
		this._speakingThreshold = options.speakingThreshold ?? VAD_THRESHOLD;
		this._speakingHoldMs = options.speakingHoldMs ?? VAD_HOLD_MS;
		this._pollIntervalMs = options.pollIntervalMs ?? AUDIO_LEVEL_POLL_INTERVAL_MS;
		const initial = options.initialGain ?? 1;
		this._gain$.next(initial);
		this.applyEffectiveGain();
	}
	/** Observable of the current gain value (0..2). */
	get gain$() {
		return this._gain$.asObservable();
	}
	/** Current gain value (0..2). */
	get gain() {
		return this._gain$.value;
	}
	/**
	* Processed output track to attach to the RTCRtpSender. Stable reference
	* across input changes, so `sender.replaceTrack(pipeline.outputTrack)` only
	* needs to be called once.
	*/
	get outputTrack() {
		const [track] = this._destination.stream.getAudioTracks();
		return track;
	}
	/**
	* Root-mean-square audio level of the input signal, 0..1. Emits on a fixed
	* interval (~30fps by default).
	*/
	get level$() {
		return this.deferEmission(interval(this._pollIntervalMs, animationFrameScheduler).pipe(map(() => this.computeLevel())));
	}
	/**
	* Boolean VAD derived from {@link level$}. True while level ≥ threshold or
	* during the hold window after the last frame that crossed the threshold.
	*/
	get speaking$() {
		return this.deferEmission(this.level$.pipe(map((level) => this.evaluateSpeaking(level)), distinctUntilChanged()));
	}
	/**
	* Set gain multiplier applied to the input signal. 0 = silence,
	* 1 = unity, 2 = 2x. Values are clamped to [0, 2]. The effective gain on
	* the graph also respects the current PTT state.
	*/
	setGain(value) {
		const clamped = Math.max(0, Math.min(2, value));
		this._gain$.next(clamped);
		this.applyEffectiveGain();
	}
	/**
	* Silence the graph when `active = false`, otherwise restore the configured
	* gain. Use this from a PTT handler: released → `false`, held → `true`.
	* Orthogonal to {@link setGain} — once PTT returns to active, the last
	* configured gain reappears.
	*/
	setPTTActive(active) {
		this._pttMultiplier = active ? 1 : 0;
		this.applyEffectiveGain();
	}
	applyEffectiveGain() {
		this._gainNode.gain.value = this._gain$.value * this._pttMultiplier;
	}
	/**
	* Wire a new raw mic track as the pipeline's input. Replaces any previous
	* input source and reconnects the graph so {@link outputTrack} continues
	* to emit the processed audio. Pass `null` to disconnect the input (the
	* output track stays alive but emits silence).
	*
	* Also resumes the underlying AudioContext on attach — Chrome creates it
	* in a suspended state and the graph won't process (the destination
	* track emits silence) until resume() succeeds.
	*/
	setInputTrack(track) {
		if (this._inputSource) {
			try {
				this._inputSource.disconnect();
			} catch (error) {
				logger$20.debug("[LocalAudioPipeline] input disconnect warning:", error);
			}
			this._inputSource = null;
		}
		if (this._inputStream) this._inputStream = null;
		if (!track) return;
		this._inputStream = new MediaStream([track]);
		this._inputSource = this._audioContext.createMediaStreamSource(this._inputStream);
		this._inputSource.connect(this._gainNode);
		if (this._audioContext.state === "suspended") this._audioContext.resume().catch((error) => {
			logger$20.warn("[LocalAudioPipeline] AudioContext resume failed:", error);
		});
	}
	destroy() {
		if (this._inputSource) {
			try {
				this._inputSource.disconnect();
			} catch {}
			this._inputSource = null;
		}
		try {
			this._gainNode.disconnect();
			this._analyser.disconnect();
		} catch {}
		this._audioContext.close().catch((error) => {
			logger$20.debug("[LocalAudioPipeline] audio context close warning:", error);
		});
		super.destroy();
	}
	computeLevel() {
		if (!this._inputSource) return 0;
		this._analyser.getByteTimeDomainData(this._analyserBuffer);
		let sum = 0;
		for (const sample of this._analyserBuffer) {
			const normalized = (sample - 128) / 128;
			sum += normalized * normalized;
		}
		return Math.sqrt(sum / this._analyserBuffer.length);
	}
	evaluateSpeaking(level) {
		const now = Date.now();
		if (level >= this._speakingThreshold) {
			this._lastSpokeAt = now;
			return true;
		}
		return now - this._lastSpokeAt < this._speakingHoldMs;
	}
};

//#endregion
//#region src/controllers/LocalStreamController.ts
const logger$19 = getLogger();
var LocalStreamController = class extends Destroyable {
	constructor(options) {
		super();
		this.options = options;
		this.mediaTrackEndedHandler = (event) => {
			this._mediaTrackEnded$.next(event);
		};
		this._localStream$ = this.createBehaviorSubject(null);
		this._localAudioTracks$ = this.createBehaviorSubject([]);
		this._localVideoTracks$ = this.createBehaviorSubject([]);
		this._mediaTrackEnded$ = this.createSubject();
	}
	get localStream$() {
		return this._localStream$.asObservable().pipe(takeUntil(this.destroyed$));
	}
	get localAudioTracks$() {
		return this._localAudioTracks$.asObservable().pipe(takeUntil(this.destroyed$));
	}
	get localVideoTracks$() {
		return this._localVideoTracks$.asObservable().pipe(takeUntil(this.destroyed$));
	}
	get mediaTrackEnded$() {
		return this._mediaTrackEnded$.asObservable().pipe(takeUntil(this.destroyed$));
	}
	get localStream() {
		return this._localStream$.value;
	}
	get localAudioTracks() {
		return this._localAudioTracks$.value;
	}
	get localVideoTracks() {
		return this._localVideoTracks$.value;
	}
	/**
	* Build the local media stream based on the provided options.
	*/
	async buildLocalStream() {
		logger$19.debug("[LocalStreamController] Building local media stream.");
		let stream;
		if (this.options.inputAudioStream ?? this.options.inputVideoStream) {
			const tracks = [...this.options.inputAudioStream?.getTracks() ?? [], ...this.options.inputVideoStream?.getTracks() ?? []];
			stream = new MediaStream(tracks);
		} else if (this.options.propose === "screenshare") {
			logger$19.debug("[LocalStreamController] Requesting display media for screen sharing with audio:", Boolean(this.options.inputAudioDeviceConstraints));
			stream = await this.options.getDisplayMedia({
				video: true,
				audio: Boolean(this.options.inputAudioDeviceConstraints)
			});
			logger$19.debug("[LocalStreamController] Screen share media obtained:", stream);
		} else {
			const constraints = {
				audio: this.options.inputAudioDeviceConstraints,
				video: this.options.inputVideoDeviceConstraints
			};
			logger$19.debug("[LocalStreamController] Requesting user media with constraints:", constraints);
			stream = await this.options.getUserMedia(constraints);
			logger$19.debug("[LocalStreamController] User media obtained:", stream);
		}
		this._localStream$.next(stream);
		this._localAudioTracks$.next(stream.getAudioTracks());
		this._localVideoTracks$.next(stream.getVideoTracks());
		return stream;
	}
	/**
	* Add a local media track to the local stream.
	* @param track - The MediaStreamTrack to add
	* @returns The MediaStream (either existing or newly created)
	*/
	addTrack(track) {
		const localStream = this._localStream$.value ?? new MediaStream();
		track.addEventListener("ended", this.mediaTrackEndedHandler);
		localStream.addTrack(track);
		this._localStream$.next(localStream);
		if (track.kind === "video") this._localVideoTracks$.next(localStream.getVideoTracks());
		else this._localAudioTracks$.next(localStream.getAudioTracks());
		logger$19.debug(`[LocalStreamController] ${track.kind} track added:`, track.id);
		return localStream;
	}
	/**
	* Remove a local media track from the local stream.
	* @param trackId - The ID of the track to remove
	* @returns The removed track, or undefined if not found
	*/
	removeTrack(trackId) {
		const stream = this._localStream$.value;
		const track = stream?.getTracks().find((t) => t.id === trackId);
		if (!track) {
			logger$19.debug(`[LocalStreamController] track not found: ${trackId}`);
			return;
		}
		track.removeEventListener("ended", this.mediaTrackEndedHandler);
		stream?.removeTrack(track);
		track.stop();
		this._localStream$.next(stream);
		if (track.kind === "video") this._localVideoTracks$.next(stream?.getVideoTracks() ?? []);
		else this._localAudioTracks$.next(stream?.getAudioTracks() ?? []);
		logger$19.debug(`[LocalStreamController] ${track.kind} track removed:`, trackId);
		return track;
	}
	/**
	* Get or create a local stream.
	*/
	getOrCreateLocalStream() {
		return this._localStream$.value ?? new MediaStream();
	}
	/**
	* Set the local stream directly.
	*/
	setLocalStream(stream) {
		this._localStream$.next(stream);
	}
	/**
	* Add a track ended event listener to a track.
	*/
	addTrackEndedListener(track) {
		track.addEventListener("ended", this.mediaTrackEndedHandler);
	}
	/**
	* Update the controller options (e.g., when media overrides are applied).
	*/
	updateOptions(options) {
		this.options = {
			...this.options,
			...options
		};
	}
	/**
	* Stop all local tracks and clean up.
	*/
	stopAllTracks() {
		this._localStream$.value?.getTracks().forEach((track) => {
			logger$19.debug(`[LocalStreamController] Stopping local track: ${track.kind}`);
			track.removeEventListener("ended", this.mediaTrackEndedHandler);
			track.stop();
		});
	}
	/**
	* Clean up resources.
	*/
	destroy() {
		this.stopAllTracks();
		super.destroy();
	}
};

//#endregion
//#region src/controllers/TransceiverController.ts
const logger$18 = getLogger();
const getDirection = (send, recv) => {
	if (send && recv) return "sendrecv";
	else if (send && !recv) return "sendonly";
	else if (!send && recv) return "recvonly";
	return "inactive";
};
var TransceiverController = class extends Destroyable {
	constructor(options) {
		super();
		this.peerConnection = options.peerConnection;
		this.options = options;
	}
	get useAddTransceivers() {
		return typeof this.peerConnection.addTransceiver === "function";
	}
	get useAddTrack() {
		return typeof this.peerConnection.addTrack === "function";
	}
	get useAddStream() {
		return typeof this.peerConnection.addStream === "function" && !this.useAddTransceivers && !this.useAddTrack;
	}
	get propose() {
		return this.options.propose;
	}
	get isAdditionalDevice() {
		return this.propose === "additional-device";
	}
	get isScreenShare() {
		return this.propose === "screenshare";
	}
	get isSimulcast() {
		return Boolean(this.options.simulcast);
	}
	get isSFU() {
		return Boolean(this.options.sfu);
	}
	get receiveVideo() {
		return Boolean(this.options.receiveVideo);
	}
	get receiveAudio() {
		return Boolean(this.options.receiveAudio);
	}
	get localStream() {
		return this.options.localStreamController.localStream;
	}
	get inputAudioDeviceConstraints() {
		return this.options.getInputAudioDeviceConstraints();
	}
	get inputVideoDeviceConstraints() {
		return this.options.getInputVideoDeviceConstraints();
	}
	get audioDirection() {
		if (this.isAdditionalDevice) return "sendonly";
		const { localStream } = this;
		const hasAudioTrack = localStream?.getAudioTracks().some((track) => track.enabled);
		const wantsToSendAudio = Boolean(this.inputAudioDeviceConstraints);
		const wantsToReceiveAudio = Boolean(this.receiveAudio);
		return getDirection(hasAudioTrack || wantsToSendAudio, wantsToReceiveAudio);
	}
	get videoDirection() {
		if (this.isAdditionalDevice || this.isScreenShare) return "sendonly";
		if (this.isSFU) return "recvonly";
		const { localStream } = this;
		const hasVideoTrack = localStream?.getVideoTracks().some((track) => track.enabled);
		const wantsToSendVideo = Boolean(this.inputVideoDeviceConstraints);
		const wantsToReceiveVideo = Boolean(this.receiveVideo);
		return getDirection(hasVideoTrack || wantsToSendVideo, wantsToReceiveVideo);
	}
	get sendEncodings() {
		if (!this.isSimulcast) return;
		return [
			"0",
			"1",
			"2"
		].map((rid) => ({
			active: true,
			rid,
			scaleResolutionDownBy: Number(rid) * 6 || 1
		}));
	}
	/**
	* Resolve the current MediaTrackConstraints for an input kind, normalising
	* boolean shorthand to an empty object. Public so the surrounding
	* RTCPeerConnectionController can drive its own pipeline-aware getUserMedia
	* call with the same effective constraints the transceiver would have used.
	*/
	getConstraintsFor(kind) {
		const constraints = kind === "audio" ? this.inputAudioDeviceConstraints : this.inputVideoDeviceConstraints;
		return typeof constraints === "boolean" ? {} : constraints;
	}
	transceiverByKind(kind) {
		return this.peerConnection.getTransceivers().filter((t) => kind === "both" || t.receiver.track.kind === kind);
	}
	get audioTransceivers() {
		return this.transceiverByKind("audio");
	}
	get videoTransceivers() {
		return this.transceiverByKind("video");
	}
	async setupTransceiverSender(track, localStream, transceiver) {
		const isAudio = track.kind === "audio";
		const direction = isAudio ? this.audioDirection : this.videoDirection;
		const transceiverParams = {
			direction,
			sendEncodings: isAudio ? void 0 : this.sendEncodings,
			streams: direction === "recvonly" ? void 0 : [localStream]
		};
		logger$18.debug(`[TransceiverController] Setting up transceiver sender for local ${track.kind} track:`, {
			transceiver,
			transceiverParams
		});
		if (transceiverParams.direction && ["sendonly", "sendrecv"].includes(transceiverParams.direction)) if (transceiver) {
			await transceiver.sender.replaceTrack(track);
			transceiver.direction = transceiverParams.direction;
			if (transceiverParams.streams?.some((stream) => Boolean(stream))) {
				logger$18.debug(`[TransceiverController] Setting streams for transceiver sender for local ${track.kind} track:`, transceiverParams.streams);
				transceiver.sender.setStreams(...transceiverParams.streams);
			}
		} else {
			logger$18.debug(`[TransceiverController] Adding new transceiver for local ${track.kind} track:`, track.id);
			this.peerConnection.addTransceiver(track, transceiverParams);
		}
	}
	stopTrackSender(kind, options = { updateTransceiverDirection: false }) {
		try {
			const transceivers = this.transceiverByKind(kind);
			for (const transceiver of transceivers) if (transceiver.sender.track?.readyState === "live") {
				const trackId = transceiver.sender.track.id;
				transceiver.sender.track.stop();
				this.options.localStreamController.removeTrack(trackId);
				if (options.updateTransceiverDirection) transceiver.direction = "inactive";
			}
		} catch (error) {
			logger$18.error("[TransceiverController] stopTrackSender error", kind, error);
			this.options.onError?.(new MediaTrackError("stopTrackSender", kind, error));
		}
	}
	async restoreTrackSender(kind) {
		try {
			logger$18.debug("[TransceiverController] restoreTrackSender called", kind);
			const constraints = {};
			const transceivers = this.transceiverByKind(kind);
			for (const transceiver of transceivers) {
				const { track } = transceiver.sender;
				if (!track || track.readyState === "ended") {
					const trackKind = track?.kind ?? transceiver.receiver.track.kind;
					if (trackKind === "audio" || trackKind === "video") constraints[trackKind] = this.getConstraintsFor(trackKind);
				}
			}
			logger$18.debug("[TransceiverController] restoreTrackSender constraints:", constraints);
			if (Object.keys(constraints).length === 0) {
				logger$18.warn("[TransceiverController] restoreTrackSender: no tracks need restoration", kind);
				return;
			}
			const newTracks = (await this.options.getUserMedia(constraints)).getTracks();
			logger$18.debug("[TransceiverController] restoreTrackSender new tracks:", newTracks);
			for (const newTrack of newTracks) {
				this.options.localStreamController.addTrack(newTrack);
				const trackKind = newTrack.kind;
				const transceiverOfKind = this.transceiverByKind(trackKind)[0];
				transceiverOfKind.direction = trackKind === "audio" ? this.audioDirection : this.videoDirection;
				logger$18.debug("[TransceiverController] restoreTrackSender setting direction for", trackKind, transceiverOfKind.direction);
				await transceiverOfKind.sender.replaceTrack(newTrack);
			}
		} catch (error) {
			logger$18.error("[TransceiverController] restoreTrackSender error", kind, error);
			this.options.onError?.(new MediaTrackError("restoreTrackSender", kind, error));
		}
	}
	async replaceSenderTrack(kind, track) {
		const transceivers = kind === "audio" ? this.audioTransceivers : this.videoTransceivers;
		for (const transceiver of transceivers) await transceiver.sender.replaceTrack(track);
	}
	async setupRemoteTransceivers(type) {
		if (type === "answer") return;
		for (const kind of ["audio", "video"]) {
			const transceivers = kind === "audio" ? this.audioTransceivers : this.videoTransceivers;
			for (const transceiver of transceivers) {
				const direction = kind === "audio" ? this.audioDirection : this.videoDirection;
				if (["inactive", "recvonly"].includes(direction)) {
					transceiver.direction = direction;
					await transceiver.sender.replaceTrack(null);
					transceiver.sender.setStreams();
				}
			}
		}
		if (this.videoDirection === "recvonly" && this.isSFU && this.useAddTransceivers) {
			const { msStreamsNumber = 5 } = this.options;
			for (let i = 0; i < Number(msStreamsNumber); i++) this.peerConnection.addTransceiver("video", { direction: "recvonly" });
		}
	}
	async updateSendersConstraints(kind, constraints) {
		if (!constraints) {
			this.stopTrackSender(kind);
			return Promise.resolve();
		}
		const senders = this.peerConnection.getSenders().filter((sender) => sender.track?.kind === kind && sender.track.readyState === "live");
		for (const sender of senders) {
			const { track } = sender;
			if (track) {
				const constraintsToApply = {
					...track.getConstraints(),
					...constraints
				};
				try {
					await track.applyConstraints(constraintsToApply);
					logger$18.debug(`[TransceiverController] Updated ${kind} sender constraints:`, constraintsToApply);
					logger$18.debug(`[TransceiverController] Updated ${kind} sender constraints:`, track.getConstraints());
				} catch (error) {
					logger$18.warn(`[TransceiverController] applyConstraints failed for ${kind} track ${track.id}, attempting track replacement fallback:`, error);
					try {
						await this.replaceTrackFallback(sender, track, kind, constraintsToApply);
					} catch (fallbackError) {
						logger$18.warn(`[TransceiverController] Track replacement fallback also failed for ${kind} track:`, fallbackError);
						this.options.onError?.(new MediaTrackError("updateSendersConstraints", kind, fallbackError));
					}
				}
			}
		}
	}
	/**
	* Fallback when applyConstraints fails: stop the current track, acquire a new
	* one via getUserMedia with the merged constraints (preserving the current
	* deviceId), replace the sender track, and update the localStream.
	*
	* This is critical for iOS Safari where applyConstraints on audio tracks
	* silently fails or throws.
	*/
	async replaceTrackFallback(sender, oldTrack, kind, mergedConstraints) {
		const { deviceId } = oldTrack.getSettings();
		const constraintsWithDevice = {
			...mergedConstraints,
			...deviceId ? { deviceId: { exact: deviceId } } : {}
		};
		const trackId = oldTrack.id;
		oldTrack.stop();
		this.options.localStreamController.removeTrack(trackId);
		const newTrack = (await this.options.getUserMedia({ [kind]: constraintsWithDevice })).getTracks().find((t) => t.kind === kind);
		if (!newTrack) throw new MediaTrackError("replaceTrackFallback", kind, /* @__PURE__ */ new Error("getUserMedia returned no track of the requested kind"));
		await sender.replaceTrack(newTrack);
		this.options.localStreamController.addTrack(newTrack);
		logger$18.debug(`[TransceiverController] Track replacement fallback succeeded for ${kind}. New track: ${newTrack.id}`);
	}
	getMediaDirections() {
		if (this.peerConnection.connectionState === "connected") return this.peerConnection.getTransceivers().reduce((acc, transceiver) => {
			return {
				...acc,
				[transceiver.receiver.track.kind]: transceiver.direction
			};
		}, {
			audio: "inactive",
			video: "inactive"
		});
		return {
			audio: this.audioDirection,
			video: this.videoDirection
		};
	}
	updatePeerConnection(peerConnection) {
		this.peerConnection = peerConnection;
	}
	updateOptions(options) {
		this.options = {
			...this.options,
			...options
		};
	}
};

//#endregion
//#region src/controllers/RTCPeerConnectionController.ts
const logger$17 = getLogger();
var RTCPeerConnectionController = class extends Destroyable {
	constructor(options = {}, remoteSessionDescription, deviceController) {
		super();
		this.options = options;
		this.firstSDPExchangeCompleted = false;
		this.negotiationNeeded$ = this.createSubject();
		this.localDescription$ = defer(() => from(this.init())).pipe(switchMap(() => this.iceGatheringController.iceCandidatesState$.pipe(filter((iceCandidateState) => !["new", "gathering"].includes(iceCandidateState)), tap(() => {
			this.negotiationEnded();
		}), filter(() => this.shouldEmitLocalDescription), map(() => this.peerConnection?.localDescription), filterNull(), tap((desc) => {
			if (desc.type === "answer") this._type = "offer";
		}))), shareReplay(1), takeUntil(this.destroyed$));
		this.connectionTimeout = 3e3;
		this.oniceconnectionstatechangeHandler = () => {
			if (this.peerConnection) {
				const { iceConnectionState } = this.peerConnection;
				logger$17.debug(`[RTCPeerConnectionController] ICE connection state changed to: ${iceConnectionState}`);
				this._iceConnectionState$.next(this.peerConnection.iceConnectionState);
			}
		};
		this.onconnectionstatechangeHandler = () => {
			if (this.peerConnection) {
				const { connectionState } = this.peerConnection;
				logger$17.debug(`[RTCPeerConnectionController] Connection state changed to: ${connectionState}`);
				if (connectionState === "connected") this.removeConnectionTimer();
				this._connectionState$.next(this.peerConnection.connectionState);
			}
		};
		this.onsignalingstatechangeHandler = () => {
			logger$17.debug(`[RTCPeerConnectionController] Signaling state changed to: ${this.peerConnection?.signalingState}`);
		};
		this.onicegatheringstatechangeHandler = () => {
			if (this.peerConnection) this._iceGatheringState$.next(this.peerConnection.iceGatheringState);
		};
		this.onnegotiationneededHandler = (event) => {
			logger$17.debug("[RTCPeerConnectionController] Negotiation needed event received.", event);
			this.negotiationNeeded$.next();
		};
		this.updateSelectedInputDevice = async (kind, deviceInfo) => {
			try {
				const { localStream } = this;
				if (!localStream) {
					logger$17.warn("[RTCPeerConnectionController] No local stream available to update input device.");
					return;
				}
				logger$17.debug(`[RTCPeerConnectionController] Updating selected ${kind} input device:`, localStream.getTracks());
				const track = localStream.getTracks().find((track$1) => track$1.kind === kind);
				if (track) {
					this.transceiverController?.stopTrackSender(kind);
					this.localStreamController.removeTrack(track.id);
					logger$17.debug(`[RTCPeerConnectionController] Stopped existing ${kind} track: ${track.id}`, localStream.getTracks());
					if (!deviceInfo) {
						logger$17.debug(`[RTCPeerConnectionController] ${kind} input device selected: none`);
						return;
					}
					const streamTrack = (await this.getUserMedia({ [kind]: {
						...track.getConstraints(),
						...this.deviceController.deviceInfoToConstraints(deviceInfo)
					} })).getTracks().find((t) => t.kind === kind);
					if (streamTrack) {
						logger$17.debug(`[RTCPeerConnectionController] Adding new ${kind} track: ${streamTrack.id}`);
						this.localStreamController.addTrack(streamTrack);
						await this.transceiverController?.replaceSenderTrack(kind, streamTrack);
						logger$17.debug(`[RTCPeerConnectionController] Added new ${kind} track: ${streamTrack.id}`, this.localStream?.getTracks());
					}
				}
				logger$17.debug(`[RTCPeerConnectionController] ${kind} input device selected:`, deviceInfo?.label);
			} catch (error) {
				logger$17.error(`[RTCPeerConnectionController] Failed to select ${kind} input device:`, error);
				this._errors$.next(toError(error));
				throw error;
			}
		};
		this._isNegotiating$ = this.createBehaviorSubject(false);
		this._memberId = null;
		this._iceConnectionState$ = this.createReplaySubject(1);
		this._connectionState$ = this.createReplaySubject(1);
		this._signalingState$ = this.createReplaySubject(1);
		this._iceGatheringState$ = this.createReplaySubject(1);
		this._errors$ = this.createReplaySubject(1);
		this._iceCandidates$ = this.createReplaySubject(1);
		this._initialized$ = this.createReplaySubject(1);
		this._remoteDescription$ = this.createReplaySubject(1);
		this._remoteStream$ = this.createBehaviorSubject(null);
		this._remoteOfferMediaDirections = null;
		this._localAudioPipeline = null;
		this.deviceController = deviceController ?? {};
		this.id = options.callId ?? v4();
		this._type = remoteSessionDescription ? "answer" : "offer";
		this.sdpInit = remoteSessionDescription ? {
			type: "offer",
			sdp: remoteSessionDescription
		} : void 0;
		this._remoteOfferMediaDirections = remoteSessionDescription ? extractMediaDirectionsFromSDP(remoteSessionDescription) : null;
		const offerDefaults = this._remoteOfferMediaDirections ? {
			audio: this._remoteOfferMediaDirections.audio.includes("recv"),
			video: this._remoteOfferMediaDirections.video.includes("recv"),
			receiveAudio: this._remoteOfferMediaDirections.audio.includes("send"),
			receiveVideo: this._remoteOfferMediaDirections.video.includes("send")
		} : {};
		this.options = {
			...options,
			audio: options.audio ?? offerDefaults.audio,
			video: options.video ?? offerDefaults.video,
			receiveAudio: options.receiveAudio ?? offerDefaults.receiveAudio ?? PreferencesContainer.instance.receiveAudio,
			receiveVideo: options.receiveVideo ?? offerDefaults.receiveVideo ?? PreferencesContainer.instance.receiveVideo
		};
		this.localStreamController = new LocalStreamController({
			propose: this.propose,
			inputAudioStream: this.options.inputAudioStream,
			inputVideoStream: this.options.inputVideoStream,
			inputAudioDeviceConstraints: this.inputAudioDeviceConstraints,
			inputVideoDeviceConstraints: this.inputVideoDeviceConstraints,
			getUserMedia: async (constraints) => this.getUserMedia(constraints),
			getDisplayMedia: async (options$1) => this.getDisplayMedia(options$1)
		});
	}
	get iceGatheringController() {
		if (!this._iceGatheringController) throw new DependencyError("ICEGatheringController is not initialized");
		return this._iceGatheringController;
	}
	get shouldEmitLocalDescription() {
		if (!this.peerConnection) return false;
		const { localDescription, signalingState } = this.peerConnection;
		if (!localDescription || !isValidLocalDescription(localDescription.sdp)) return false;
		return localDescription.type === "offer" && signalingState === "have-local-offer" || localDescription.type === "answer" && signalingState === "stable";
	}
	removeConnectionTimer() {
		if (this.connectionTimer) {
			clearTimeout(this.connectionTimer);
			this.connectionTimer = void 0;
		}
	}
	setMemberId(memberId) {
		this._memberId = memberId;
	}
	get memberId() {
		return this._memberId;
	}
	stopTrackSender(kind, options = { updateTransceiverDirection: false }) {
		const audioCovered = kind === "audio" || kind === "both";
		if (audioCovered && this._localAudioPipeline) this.stopRawAudioInputForPipeline();
		if (!audioCovered) this.transceiverController?.stopTrackSender(kind, options);
		else if (kind === "both") this.transceiverController?.stopTrackSender("video", options);
		else if (!this._localAudioPipeline) this.transceiverController?.stopTrackSender(kind, options);
	}
	stopRawAudioInputForPipeline() {
		const rawTracks = this.localStreamController.localAudioTracks;
		for (const track of rawTracks) if (track.readyState === "live") {
			track.stop();
			this.localStreamController.removeTrack(track.id);
		}
		this._localAudioPipeline?.setInputTrack(null);
	}
	get isNegotiating$() {
		return this._isNegotiating$.asObservable();
	}
	get isNegotiating() {
		return this._isNegotiating$.value;
	}
	updateMediaDevicesOptions(options) {
		this.options = {
			...this.options,
			...options
		};
	}
	get iceGatheringState$() {
		return this.cachedObservable("iceGatheringState$", () => this._iceGatheringState$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	get mediaTrackEnded$() {
		return this.cachedObservable("mediaTrackEnded$", () => this.localStreamController.mediaTrackEnded$.pipe(takeUntil(this.destroyed$)));
	}
	get errors$() {
		return this.cachedObservable("errors$", () => this._errors$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	get iceCandidates$() {
		return this.cachedObservable("iceCandidates$", () => this._iceCandidates$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	get initialized$() {
		return this.cachedObservable("initialized$", () => this._initialized$.asObservable().pipe(filter((initialized) => initialized), takeUntil(this.destroyed$)));
	}
	get remoteDescription$() {
		return this.cachedObservable("remoteDescription$", () => this._remoteDescription$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	get localStream$() {
		return this.cachedObservable("localStream$", () => this.localStreamController.localStream$.pipe(takeUntil(this.destroyed$)));
	}
	get remoteStream$() {
		return this.cachedObservable("remoteStream$", () => this._remoteStream$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	get localAudioTracks$() {
		return this.cachedObservable("localAudioTracks$", () => this.localStreamController.localAudioTracks$.pipe(takeUntil(this.destroyed$)));
	}
	get localVideoTracks$() {
		return this.cachedObservable("localVideoTracks$", () => this.localStreamController.localVideoTracks$.pipe(takeUntil(this.destroyed$)));
	}
	get iceConnectionState$() {
		return this.cachedObservable("iceConnectionState$", () => this._iceConnectionState$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	get connectionState$() {
		return this.cachedObservable("connectionState$", () => this._connectionState$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	get signalingState$() {
		return this.cachedObservable("signalingState$", () => this._signalingState$.asObservable().pipe(takeUntil(this.destroyed$)));
	}
	get type() {
		return this._type;
	}
	get propose() {
		return this.options.propose ?? "main";
	}
	get isAdditionalDevice() {
		return this.propose === "additional-device";
	}
	get isMainDevice() {
		return this.propose === "main";
	}
	get isScreenShare() {
		return this.propose === "screenshare";
	}
	get iceServers() {
		if (!this.options.disableUdpIceServers) return this.options.iceServers ?? [];
		const tcpTransportParam = "transport=tcp";
		return (this.options.iceServers ?? []).map((server) => {
			const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
			return {
				...server,
				urls: urls.filter((url) => url.includes(tcpTransportParam))
			};
		});
	}
	get rtcConfiguration() {
		const { iceServers: _iceServers, ...restOptions } = this.options;
		return {
			bundlePolicy: "max-compat",
			iceCandidatePoolSize: 10,
			iceServers: this.iceServers,
			iceTransportPolicy: this.options.relayOnly ? "relay" : "all",
			sdpSemantics: "unified-plan",
			...restOptions
		};
	}
	get receiveVideo() {
		return Boolean(this.options.receiveVideo);
	}
	get receiveAudio() {
		return Boolean(this.options.receiveAudio);
	}
	get localStream() {
		return this.localStreamController.localStream;
	}
	get remoteStream() {
		return this._remoteStream$.value;
	}
	get inputAudioDeviceConstraints() {
		if (this.options.audio === false && !this.options.inputAudioDeviceConstraints) return false;
		const deviceConstraints = this.deviceController.selectedAudioInputDeviceConstraints;
		if (deviceConstraints === false) return false;
		const audioBase = typeof this.options.inputAudioDeviceConstraints === "object" ? this.options.inputAudioDeviceConstraints : {};
		const audioDevice = typeof deviceConstraints === "object" ? deviceConstraints : {};
		return {
			...audioBase,
			...audioDevice
		};
	}
	get inputVideoDeviceConstraints() {
		if (!this.options.video && !this.options.inputVideoDeviceConstraints) return false;
		const deviceConstraints = this.deviceController.selectedVideoInputDeviceConstraints;
		if (deviceConstraints === false) return false;
		const videoBase = typeof this.options.inputVideoDeviceConstraints === "object" ? this.options.inputVideoDeviceConstraints : {};
		const videoDevice = typeof deviceConstraints === "object" ? deviceConstraints : {};
		return {
			...videoBase,
			...videoDevice
		};
	}
	get WebRTCPeerConnectionConstructor() {
		return this.options.webRTCApiProvider?.RTCPeerConnection ?? RTCPeerConnection;
	}
	get offerOptions() {
		const options = { iceRestart: this.firstSDPExchangeCompleted ? true : void 0 };
		switch (this.propose) {
			case "screenshare":
			case "additional-device": return {
				...options,
				offerToReceiveAudio: false,
				offerToReceiveVideo: false
			};
			case "main":
			default: return {
				...options,
				offerToReceiveAudio: true,
				offerToReceiveVideo: this.options.receiveVideo ?? Boolean(this.inputVideoDeviceConstraints)
			};
		}
	}
	get answerOptions() {
		return { iceRestart: this.firstSDPExchangeCompleted ? true : void 0 };
	}
	/**
	* Initialize the RTCPeerConnection and setup event listeners.
	* Called automatically when localDescription$ is subscribed to (deferred pattern).
	* Uses Promise memoization to ensure initialization only happens once,
	* even if called concurrently.
	*/
	async init() {
		this.initPromise ??= this.doInit();
		return this.initPromise;
	}
	/**
	* Internal initialization implementation.
	* Should only be called via init() to ensure single execution.
	*/
	async doInit() {
		try {
			this.setupPeerConnection();
			this.subscribeTo(this.negotiationNeeded$.pipe(auditTime(0), exhaustMap(async () => this.startNegotiation())), {
				next: () => {
					logger$17.debug("[RTCPeerConnectionController] Start Negotiation completed successfully");
				},
				error: (error) => {
					logger$17.error("[RTCPeerConnectionController] Start Negotiation error:", error);
					this._errors$.next(toError(error));
				}
			});
			this.subscribeTo(merge(this.deviceController.selectedAudioInputDevice$.pipe(map((deviceInfo) => ["audio", deviceInfo])), this.deviceController.selectedVideoInputDevice$.pipe(map((deviceInfo) => ["video", deviceInfo]))).pipe(skipWhile(() => !this.localStreamController.localStream)), async ([kind, deviceInfo]) => {
				logger$17.debug(`[RTCPeerConnectionController] Selected input device changed for:`, {
					kind,
					deviceInfo
				});
				await this.updateSelectedInputDevice(kind, deviceInfo);
			});
			if (this.type === "answer" && this.sdpInit) {
				await this.setupRemoteTracks();
				this._initialized$.next(true);
				this.setupEventListeners();
				this._isNegotiating$.next(true);
				await this._setRemoteDescription(this.sdpInit);
			} else {
				await this.setupTrackHandling();
				this._initialized$.next(true);
			}
		} catch (error) {
			logger$17.error("[RTCPeerConnectionController] Initialization error:", error);
			this._errors$.next(toError(error));
			this.destroy();
		}
	}
	setupPeerConnection() {
		this.peerConnection = new this.WebRTCPeerConnectionConstructor(this.rtcConfiguration);
		this.peerConnection.addEventListener("negotiationneeded", this.onnegotiationneededHandler);
		this._iceGatheringController = new ICEGatheringController(this.peerConnection, this.isNegotiating$, {
			iceCandidateTimeout: this.options.iceCandidateTimeout,
			iceGatheringTimeout: this.options.iceGatheringTimeout,
			relayOnly: this.options.relayOnly
		});
		this.transceiverController = new TransceiverController({
			peerConnection: this.peerConnection,
			propose: this.propose,
			simulcast: this.options.simulcast,
			sfu: this.options.sfu,
			msStreamsNumber: this.options.msStreamsNumber,
			receiveAudio: this.receiveAudio,
			receiveVideo: this.receiveVideo,
			localStreamController: this.localStreamController,
			getInputAudioDeviceConstraints: () => this.inputAudioDeviceConstraints,
			getInputVideoDeviceConstraints: () => this.inputVideoDeviceConstraints,
			getUserMedia: async (constraints) => this.getUserMedia(constraints),
			onError: (error) => {
				this._errors$.next(error);
			}
		});
	}
	async startNegotiation() {
		if (this.isNegotiating) {
			logger$17.debug("[RTCPeerConnectionController] Negotiation already in progress, skipping.");
			return;
		}
		this.setupEventListeners();
		if (this.type === "answer") {
			logger$17.debug("[RTCPeerConnectionController] This is an answer type still, skipping offer creation.");
			return;
		}
		this._isNegotiating$.next(true);
		logger$17.debug("[RTCPeerConnectionController] Starting negotiation.");
		try {
			const { offerOptions } = this;
			logger$17.debug("[RTCPeerConnectionController] Creating offer with options:", offerOptions);
			await this.createOffer(offerOptions);
		} catch (error) {
			logger$17.error("[RTCPeerConnectionController] Error during negotiation:", error);
			this._errors$.next(toError(error));
		}
	}
	/**
	* Create an SDP offer and set it as local description.
	*/
	async createOffer(options) {
		if (!this.peerConnection) throw new DependencyError("RTCPeerConnection is not initialized");
		const offer = await this.peerConnection.createOffer(options);
		await this.setLocalDescription(offer);
	}
	async updateAnswerStatus({ status, sdp }) {
		let readyToConnect = status !== "failed";
		try {
			if (status === "received" && sdp) {
				logger$17.debug("[RTCPeerConnectionController] Received answer SDP:", sdp);
				await this._setRemoteDescription({
					type: "answer",
					sdp
				});
			}
		} catch (error) {
			logger$17.error("[RTCPeerConnectionController] Error updating answer status:", error);
			this._errors$.next(toError(error));
			readyToConnect = false;
		} finally {
			if (readyToConnect) this.readyToConnect();
			else this.iceGatheringController.restartICEGatheringWithRelayOnly();
		}
	}
	async updateOfferStatus({ status, sdp }) {
		switch (status) {
			case "received":
				this._type = "answer";
				this.sdpInit = {
					type: "offer",
					sdp
				};
				await this.handleOfferReceived();
				break;
			case "failed":
				logger$17.error("[RTCPeerConnectionController] Offer failed to be processed by remote.");
				break;
			case "sent":
			default:
		}
	}
	/**
	* Accept an inbound call by creating the SDP answer.
	* Optionally override media options before the answer is generated.
	* Must be called after initialization for inbound (answer-type) connections.
	*/
	async acceptInbound(mediaOverrides) {
		if (mediaOverrides) {
			const { audio, video, receiveAudio, receiveVideo } = mediaOverrides;
			this.options = {
				...this.options,
				...audio !== void 0 ? { audio } : {},
				...video !== void 0 ? { video } : {},
				...receiveAudio !== void 0 ? { receiveAudio } : {},
				...receiveVideo !== void 0 ? { receiveVideo } : {}
			};
			this.transceiverController?.updateOptions({
				receiveAudio: this.receiveAudio,
				receiveVideo: this.receiveVideo
			});
			this.localStreamController.updateOptions({
				inputAudioDeviceConstraints: this.inputAudioDeviceConstraints,
				inputVideoDeviceConstraints: this.inputVideoDeviceConstraints
			});
		}
		await this.setupLocalTracks();
		const { answerOptions } = this;
		logger$17.debug("[RTCPeerConnectionController] Creating inbound answer with options:", answerOptions);
		await this.createAnswer(answerOptions);
	}
	async handleOfferReceived() {
		if (!this.sdpInit) throw new DependencyError("SDP initialization parameters are not set");
		this._isNegotiating$.next(true);
		await this._setRemoteDescription(this.sdpInit);
		const { answerOptions } = this;
		logger$17.debug("[RTCPeerConnectionController] Creating answer with options:", answerOptions);
		await this.createAnswer(answerOptions);
	}
	readyToConnect() {
		this.firstSDPExchangeCompleted = true;
		this.connectionTimer = setTimeout(() => {
			this.removeConnectionTimer();
			if (this.peerConnection?.connectionState !== "connected") {
				logger$17.debug("[RTCPeerConnectionController] Connection timeout, restarting ICE gathering with relay only.");
				this.iceGatheringController.restartICEGatheringWithRelayOnly();
			}
		}, this.connectionTimeout);
	}
	async setRemoteDescriptionBefore(sdp = "") {
		return Promise.resolve(sdp);
	}
	async setLocalDescription(params) {
		const finalLocal = await this.setLocalDescriptionBefore(params.sdp);
		return this.peerConnection?.setLocalDescription({
			...params,
			sdp: finalLocal
		});
	}
	async setLocalDescriptionBefore(sdp = "") {
		let result = sdp;
		const preferredAudioCodecs = this.options.preferredAudioCodecs ?? PreferencesContainer.instance.preferredAudioCodecs;
		const preferredVideoCodecs = this.options.preferredVideoCodecs ?? PreferencesContainer.instance.preferredVideoCodecs;
		const stereo = this.options.stereo ?? PreferencesContainer.instance.stereoAudio;
		if (preferredAudioCodecs.length > 0 || preferredVideoCodecs.length > 0) {
			result = setCodecPreferences(result, preferredAudioCodecs, preferredVideoCodecs);
			logger$17.debug("[RTCPeerConnectionController] Applied codec preferences to SDP", {
				preferredAudioCodecs,
				preferredVideoCodecs
			});
		}
		if (stereo) {
			result = enableStereoOpus(result);
			logger$17.debug("[RTCPeerConnectionController] Applied stereo Opus to SDP");
		}
		return Promise.resolve(result);
	}
	/**
	* Create an SDP answer and set it as local description.
	*/
	async createAnswer(options) {
		if (!this.peerConnection) throw new DependencyError("RTCPeerConnection is not initialized");
		const answer = await this.peerConnection.createAnswer(options);
		await this.setLocalDescription(answer);
	}
	/**
	* Setup event listeners on RTCPeerConnection for state changes.
	*/
	setupEventListeners() {
		if (!this.peerConnection) throw new DependencyError("RTCPeerConnection is not initialized");
		this._iceConnectionState$.next(this.peerConnection.iceConnectionState);
		this._connectionState$.next(this.peerConnection.connectionState);
		this._signalingState$.next(this.peerConnection.signalingState);
		this._iceGatheringState$.next(this.peerConnection.iceGatheringState);
		this._remoteDescription$.next(this.peerConnection.remoteDescription);
		this.peerConnection.removeEventListener("icegatheringstatechange", this.onicegatheringstatechangeHandler);
		this.peerConnection.addEventListener("icegatheringstatechange", this.onicegatheringstatechangeHandler);
		this.peerConnection.removeEventListener("iceconnectionstatechange", this.oniceconnectionstatechangeHandler);
		this.peerConnection.addEventListener("iceconnectionstatechange", this.oniceconnectionstatechangeHandler);
		this.peerConnection.removeEventListener("connectionstatechange", this.onconnectionstatechangeHandler);
		this.peerConnection.addEventListener("connectionstatechange", this.onconnectionstatechangeHandler);
		this.peerConnection.removeEventListener("signalingstatechange", this.onsignalingstatechangeHandler);
		this.peerConnection.addEventListener("signalingstatechange", this.onsignalingstatechangeHandler);
	}
	negotiationEnded() {
		this._isNegotiating$.next(false);
	}
	/**
	* Trigger an ICE restart through the existing negotiation pipeline.
	*
	* This creates an offer with iceRestart: true and goes through the full
	* SDP pipeline (setLocalDescription → ICE gathering → localDescription$ emission).
	* The caller should NOT send the SDP manually — the existing
	* setupLocalDescriptionHandler in VertoManager will pick up the emission
	* from localDescription$ and send it as a verto.modify.
	*
	* Unlike calling pc.createOffer/setLocalDescription directly, this method:
	* - Sets _isNegotiating$ so ICEGatheringController arms its timers
	* - Waits for ICE gathering to complete before localDescription$ emits
	* - Goes through setLocalDescriptionBefore() for any SDP munging
	*/
	async triggerIceRestart(relayOnly) {
		if (!this.peerConnection) throw new DependencyError("RTCPeerConnection is not initialized");
		const policyChanged = relayOnly && !this.options.relayOnly;
		if (policyChanged) try {
			this.peerConnection.setConfiguration({
				...this.peerConnection.getConfiguration(),
				iceTransportPolicy: "relay"
			});
			logger$17.debug("[RTCPeerConnectionController] ICE transport policy set to relay-only");
		} catch (error) {
			logger$17.warn("[RTCPeerConnectionController] Failed to set relay-only policy:", error);
		}
		this.setupEventListeners();
		this._isNegotiating$.next(true);
		logger$17.debug(`[RTCPeerConnectionController] Triggering ICE restart${relayOnly ? " (relay-only)" : ""}.`);
		try {
			const offer = await this.peerConnection.createOffer({ iceRestart: true });
			await this.setLocalDescription(offer);
		} catch (error) {
			logger$17.error("[RTCPeerConnectionController] ICE restart offer failed:", error);
			this._errors$.next(toError(error));
			this.negotiationEnded();
			if (policyChanged) this.restoreIceTransportPolicy();
			throw error;
		}
		if (policyChanged) firstValueFrom(race(this._iceGatheringState$.pipe(filter((state) => state === "complete"), take(1)), timer(ICE_GATHERING_COMPLETE_TIMEOUT_MS).pipe(map(() => "timeout")))).then(() => this.restoreIceTransportPolicy()).catch((error) => {
			logger$17.warn("[RTCPeerConnectionController] Error waiting for ICE gathering to complete:", error);
			this.restoreIceTransportPolicy();
		});
	}
	restoreIceTransportPolicy() {
		try {
			this.peerConnection?.setConfiguration({
				...this.peerConnection.getConfiguration(),
				iceTransportPolicy: this.options.relayOnly ? "relay" : "all"
			});
			logger$17.debug("[RTCPeerConnectionController] ICE transport policy restored");
		} catch (error) {
			logger$17.warn("[RTCPeerConnectionController] Failed to restore ICE transport policy:", error);
		}
	}
	/**
	* Setup track handling for remote tracks.
	*/
	async setupTrackHandling() {
		if (!this.peerConnection) throw new DependencyError("RTCPeerConnection is not initialized");
		await this.setupLocalTracks();
		await this.setupRemoteTracks();
	}
	async setupLocalTracks() {
		logger$17.debug("[RTCPeerConnectionController] Setting up local tracks/transceivers.");
		const localStream = this.localStream ?? await this.localStreamController.buildLocalStream();
		if (this.transceiverController?.useAddStream ?? false) {
			logger$17.warn("[RTCPeerConnectionController] Using deprecated addStream API to add local stream.");
			this.peerConnection?.addStream(localStream);
			if (!this.isNegotiating) {
				logger$17.debug("[RTCPeerConnectionController] Forcing negotiationneeded after local tracks setup.");
				this.negotiationNeeded$.next();
			}
			return;
		}
		for (const kind of ["audio", "video"]) {
			const tracks = (kind === "audio" ? localStream.getAudioTracks() : localStream.getVideoTracks()).map((track, index) => ({
				index,
				track
			}));
			for (const { index, track } of tracks) {
				this.localStreamController.addTrackEndedListener(track);
				if (this.transceiverController?.useAddTransceivers ?? false) {
					const transceivers = (kind === "audio" ? this.transceiverController?.audioTransceivers : this.transceiverController?.videoTransceivers) ?? [];
					await this.transceiverController?.setupTransceiverSender(track, localStream, transceivers[index]);
				} else {
					logger$17.debug(`[RTCPeerConnectionController] Using addTrack for local ${kind} track:`, track.id);
					this.peerConnection?.addTrack(track, localStream);
				}
			}
		}
	}
	async getUserMedia(constraints) {
		return (this.options.webRTCApiProvider?.mediaDevices ?? navigator.mediaDevices).getUserMedia(constraints);
	}
	async getDisplayMedia(options) {
		const mediaDevices = this.options.webRTCApiProvider?.mediaDevices ?? navigator.mediaDevices;
		if (!mediaDevices.getDisplayMedia) throw new DependencyError("getDisplayMedia is not supported by the current WebRTC provider");
		return mediaDevices.getDisplayMedia(options);
	}
	async setupRemoteTracks() {
		if (!this.peerConnection) throw new DependencyError("RTCPeerConnection is not initialized");
		this.peerConnection.ontrack = (event) => {
			logger$17.debug("[RTCPeerConnectionController] Remote track received:", event.track.kind);
			if (event.streams[0]) this._remoteStream$.next(event.streams[0]);
			else {
				const existingTracks = this._remoteStream$.value?.getTracks() ?? [];
				const newStream = new MediaStream([...existingTracks, event.track]);
				this._remoteStream$.next(newStream);
			}
		};
		await this.transceiverController?.setupRemoteTransceivers(this.type);
	}
	async restoreTrackSender(kind) {
		const audioCovered = kind === "audio" || kind === "both";
		if (audioCovered && this._localAudioPipeline) await this.restoreRawAudioInputForPipeline();
		if (!audioCovered) await this.transceiverController?.restoreTrackSender(kind);
		else if (kind === "both") await this.transceiverController?.restoreTrackSender("video");
		else if (!this._localAudioPipeline) await this.transceiverController?.restoreTrackSender(kind);
	}
	async restoreRawAudioInputForPipeline() {
		if (!this._localAudioPipeline) return;
		const constraints = this.transceiverController?.getConstraintsFor("audio") ?? {};
		let stream;
		try {
			stream = await this.getUserMedia({ audio: constraints });
		} catch (error) {
			logger$17.error("[RTCPeerConnectionController] Failed to re-acquire mic for pipeline restore:", error);
			this._errors$.next(toError(error));
			return;
		}
		const newTrack = stream.getAudioTracks().at(0);
		if (!newTrack) return;
		this.localStreamController.addTrack(newTrack);
		this._localAudioPipeline.setInputTrack(newTrack);
	}
	/**
	* Return the lazily-created {@link LocalAudioPipeline}, constructing it on
	* first access. On creation the current audio sender's track is routed
	* through the pipeline (input → gain → analyser → destination) and the
	* sender is switched to emit the processed track. Returns `null` when no
	* audio sender exists yet (pre-negotiation).
	*/
	ensureLocalAudioPipeline() {
		if (this._localAudioPipeline) return this._localAudioPipeline;
		if (!this.peerConnection) return null;
		try {
			this._localAudioPipeline = new LocalAudioPipeline();
		} catch (error) {
			logger$17.warn("[RTCPeerConnectionController] Failed to create LocalAudioPipeline:", error);
			return null;
		}
		this.subscribeTo(this.localStreamController.localAudioTracks$, () => {
			this.applyLocalAudioPipelineToSender();
		});
		this.applyLocalAudioPipelineToSender();
		return this._localAudioPipeline;
	}
	/** The active LocalAudioPipeline, or null if it hasn't been created yet. */
	get localAudioPipeline() {
		return this._localAudioPipeline;
	}
	async applyLocalAudioPipelineToSender() {
		if (!this._localAudioPipeline || !this.peerConnection) return;
		const raw = this.localStreamController.localAudioTracks.at(0);
		this._localAudioPipeline.setInputTrack(raw ?? null);
		const sender = (this.transceiverController?.audioTransceivers.at(0))?.sender ?? this.peerConnection.getSenders().find((s) => s.track?.kind === "audio");
		if (!sender || !raw) return;
		try {
			await sender.replaceTrack(this._localAudioPipeline.outputTrack);
		} catch (error) {
			logger$17.warn("[RTCPeerConnectionController] Failed to route audio sender through pipeline:", error);
		}
	}
	/**
	* Add a local media track to the peer connection.
	* @param track - The MediaStreamTrack to add
	*/
	addLocalTrack(track) {
		if (!this.peerConnection) {
			const error = new DependencyError("RTCPeerConnection is not initialized");
			this._errors$.next(error);
			throw error;
		}
		try {
			const localStream = this.localStreamController.addTrack(track);
			this.peerConnection.addTrack(track, localStream);
			logger$17.debug(`[RTCPeerConnectionController] ${track.kind} track added:`, track.id);
		} catch (error) {
			logger$17.error(`[RTCPeerConnectionController] Failed to add ${track.kind} track:`, error);
			this._errors$.next(toError(error));
			throw error;
		}
	}
	/**
	* Remove a local media track from the peer connection.
	* @param trackId - The ID of the track to remove
	*/
	removeLocalTrack(trackId) {
		if (!this.peerConnection) {
			const error = new DependencyError("RTCPeerConnection is not initialized");
			this._errors$.next(error);
			throw error;
		}
		const sender = this.peerConnection.getSenders().find((sender$1) => sender$1.track?.id === trackId);
		if (!sender) {
			logger$17.debug(`[RTCPeerConnectionController] track not found: ${trackId}`);
			return;
		}
		try {
			this.peerConnection.removeTrack(sender);
			this.localStreamController.removeTrack(trackId);
			logger$17.debug(`[RTCPeerConnectionController] ${sender.track?.kind} track removed:`, trackId);
		} catch (error) {
			logger$17.error(`[RTCPeerConnectionController] Failed to remove ${sender.track?.kind} track:`, error);
			this._errors$.next(toError(error));
			throw error;
		}
	}
	/**
	* Replace all existing media tracks with a new media  track.
	* Convenience method for single-track scenarios.
	* @param track - The MediaStreamTrack to set
	*/
	setLocalTrack(track) {
		const existingTracks = [...track.kind === "audio" ? this.localStreamController.localAudioTracks : this.localStreamController.localVideoTracks];
		for (const existingTrack of existingTracks) this.removeLocalTrack(existingTrack.id);
		this.addLocalTrack(track);
	}
	async updateSendersConstraints(kind, constraints) {
		await this.transceiverController?.updateSendersConstraints(kind, constraints);
	}
	/**
	* Replace the current audio track with a new one using the given constraints.
	* Used for server-pushed audio constraint changes where applyConstraints
	* fails on iOS Safari. Stops the current track, acquires a new one via
	* getUserMedia, and replaces the sender track.
	*/
	async replaceAudioTrackWithConstraints(constraints) {
		const senders = this.peerConnection?.getSenders().filter((s) => s.track?.kind === "audio" && s.track.readyState === "live");
		if (!senders || senders.length === 0) {
			logger$17.warn("[RTCPeerConnectionController] No live audio sender to replace");
			return;
		}
		for (const sender of senders) {
			const oldTrack = sender.track;
			if (!oldTrack) continue;
			const { deviceId } = oldTrack.getSettings();
			const mergedConstraints = {
				...oldTrack.getConstraints(),
				...constraints,
				...deviceId ? { deviceId: { exact: deviceId } } : {}
			};
			const trackId = oldTrack.id;
			oldTrack.stop();
			this.localStreamController.removeTrack(trackId);
			const newTrack = (await this.getUserMedia({ audio: mergedConstraints })).getAudioTracks()[0];
			await sender.replaceTrack(newTrack);
			this.localStreamController.addTrack(newTrack);
			logger$17.debug(`[RTCPeerConnectionController] Audio track replaced for server-pushed params. New track: ${newTrack.id}`);
		}
	}
	/**
	* Clean up resources and close the peer connection.
	* Completes all observables to prevent memory leaks.
	*/
	destroy() {
		logger$17.debug(`[RTCPeerConnectionController] Destroying RTCPeerConnectionController. ${this.propose}`);
		this.removeConnectionTimer();
		this._iceGatheringController?.destroy();
		this._localAudioPipeline?.destroy();
		this._localAudioPipeline = null;
		this.localStreamController.destroy();
		this.transceiverController?.destroy();
		if (this.peerConnection) {
			this.stopRemoteTracks();
			this.removeAllListeners();
			this.peerConnection.close();
			this.peerConnection = void 0;
		}
		super.destroy();
	}
	removeAllListeners() {
		if (this.peerConnection) {
			this.peerConnection.removeEventListener("icegatheringstatechange", this.onicegatheringstatechangeHandler);
			this.peerConnection.removeEventListener("iceconnectionstatechange", this.oniceconnectionstatechangeHandler);
			this.peerConnection.removeEventListener("connectionstatechange", this.onconnectionstatechangeHandler);
			this.peerConnection.removeEventListener("signalingstatechange", this.onsignalingstatechangeHandler);
			this.peerConnection.removeEventListener("negotiationneeded", this.onnegotiationneededHandler);
		}
	}
	stopRemoteTracks() {
		this._remoteStream$.value?.getTracks().forEach((track) => {
			logger$17.debug(`[RTCPeerConnectionController] Stopping remote track: ${track.kind}`);
			track.stop();
		});
	}
	get mediaDirections() {
		return this.transceiverController?.getMediaDirections() ?? this._remoteOfferMediaDirections ?? {
			audio: "inactive",
			video: "inactive"
		};
	}
	async _setRemoteDescription(params) {
		if (!this.peerConnection) throw new DependencyError("RTCPeerConnection is not initialized");
		const finalRemote = await this.setRemoteDescriptionBefore(params.sdp);
		const answer = {
			...params,
			sdp: finalRemote
		};
		logger$17.debug("[RTCPeerConnectionController] Setting remote description:", answer);
		return this.peerConnection.setRemoteDescription(answer);
	}
};

//#endregion
//#region src/core/RPCMessages/guards/verto.guards.ts
function isVertoMethodMessage(value) {
	return isObject(value) && hasProperty(value, "jsonrpc") && value.jsonrpc === "2.0" && hasProperty(value, "id");
}
function isVertoInviteMessage(value) {
	if (!isVertoMethodMessage(value)) return false;
	const msg = value;
	return msg.method === "verto.invite" && isObject(msg.params) && hasProperty(msg.params, "sdp") && hasProperty(msg.params, "callID");
}
function isVertoByeMessage(value) {
	if (!isVertoMethodMessage(value)) return false;
	return value.method === "verto.bye";
}
function isVertoAttachMessage(value) {
	if (!isVertoMethodMessage(value)) return false;
	return value.method === "verto.attach";
}
function isVertoAnswerInnerParams(value) {
	return isObject(value) && hasProperty(value, "jsonrpc") && value.jsonrpc === "2.0" && hasProperty(value, "method") && value.method === "verto.answer" && isObject(value.params) && hasProperty(value.params, "callID");
}
function isVertoMediaInnerParams(value) {
	return isObject(value) && hasProperty(value, "jsonrpc") && value.jsonrpc === "2.0" && hasProperty(value, "method") && value.method === "verto.media" && isObject(value.params) && hasProperty(value.params, "callID") && hasProperty(value.params, "sdp");
}
function isVertoMediaParamsInnerParams(value) {
	return isObject(value) && hasProperty(value, "jsonrpc") && value.jsonrpc === "2.0" && hasProperty(value, "method") && value.method === "verto.mediaParams" && isObject(value.params) && hasProperty(value.params, "mediaParams");
}
function isVertoPingInnerParams(value) {
	return isObject(value) && hasProperty(value, "jsonrpc") && value.jsonrpc === "2.0" && hasProperty(value, "method") && value.method === "verto.ping";
}

//#endregion
//#region src/managers/VertoManager.ts
const logger$16 = getLogger();
/**
* Decide what value goes on the `node_id` field of a `webrtc.verto` envelope.
*
* - **Reattach invite:** must carry the persisted nodeId so the server routes
*   the new connection to the FreeSWITCH instance that holds the existing call.
* - **Fresh invite, caller-supplied `CallOptions.nodeId`:** carry the explicit
*   value as a steering hint (dev/staging traffic pinning). Server may honour
*   or ignore for placement reasons.
* - **Fresh invite, no caller nodeId:** strip to `''` = "server picks".
* - **Non-invite frames** (verto.modify, verto.bye, etc.): always carry the
*   current `_nodeId$.value` so the frame targets the node hosting the call.
*
* Pure function — exported for unit testing.
*/
function resolveInviteNodeId(args) {
	return args.isInvite && !args.reattach && !args.explicitNodeId ? "" : args.currentNodeId ?? "";
}
var VertoManager = class extends Destroyable {
	constructor(callSession) {
		super();
		this.callSession = callSession;
	}
	destroy() {
		this.callSession = void 0;
		super.destroy();
	}
};
var WebRTCVertoManager = class extends VertoManager {
	constructor(webRtcCallSession, attachManager, deviceController, webRTCApiProvider, options = {}) {
		super(webRtcCallSession);
		this.webRtcCallSession = webRtcCallSession;
		this.attachManager = attachManager;
		this.deviceController = deviceController;
		this.webRTCApiProvider = webRTCApiProvider;
		this._rtcPeerConnections$ = this.createBehaviorSubject([]);
		this._selfId$ = this.createBehaviorSubject(null);
		this._signalingStatus$ = this.createReplaySubject(1);
		this._screenShareStatus$ = this.createBehaviorSubject("none");
		this._rtcPeerConnectionsMap = /* @__PURE__ */ new Map();
		this._screenShareTimeoutMs = 5e4;
		this._nodeId$ = this.createBehaviorSubject(options.nodeId ?? null);
		this.onError = options.onError;
		this.onModifyFailed = options.onModifyFailed;
		this.initSubscriptions();
		this.initMainPeerConnection();
	}
	async hold() {
		const vertoModifyMessage = VertoModify({
			sessid: this.webRtcCallSession.id,
			dialogParams: { callID: this.webRtcCallSession.id },
			action: "hold"
		});
		try {
			await this.executeVerto(vertoModifyMessage);
		} catch (error) {
			logger$16.warn("[WebRTCManager] Call might already be disconnected, error sending Verto hold:", error);
			throw error;
		}
	}
	async unhold() {
		const vertoModifyMessage = VertoModify({
			sessid: this.webRtcCallSession.id,
			dialogParams: { callID: this.webRtcCallSession.id },
			action: "unhold"
		});
		try {
			await this.executeVerto(vertoModifyMessage);
		} catch (error) {
			logger$16.warn("[WebRTCManager] Call might already be disconnected, error sending Verto unhold:", error);
			throw error;
		}
	}
	get mediaDirections() {
		return this.mainPeerConnection.mediaDirections;
	}
	get rtcPeerConnections$() {
		return this._rtcPeerConnections$.asObservable();
	}
	get rtcPeerConnections() {
		return this._rtcPeerConnections$.value;
	}
	get nodeId$() {
		return this._nodeId$.asObservable();
	}
	get selfId$() {
		return this._selfId$.asObservable();
	}
	get localStream() {
		return this._rtcPeerConnectionsMap.get(this.webRtcCallSession.id)?.localStream ?? null;
	}
	get remoteStream() {
		return this._rtcPeerConnectionsMap.get(this.webRtcCallSession.id)?.remoteStream ?? null;
	}
	get nodeId() {
		return this._nodeId$.value;
	}
	get screenShareStatus() {
		return this._screenShareStatus$.value;
	}
	get screenShareStatus$() {
		return this._screenShareStatus$.asObservable();
	}
	get mainPeerConnection() {
		const rtcPeerConnection = this._rtcPeerConnectionsMap.get(this.webRtcCallSession.id);
		if (!rtcPeerConnection) throw new DependencyError("Main peer connection not found");
		return rtcPeerConnection;
	}
	get signalingStatus$() {
		return this.cachedObservable("signalingStatus$", () => merge(this._signalingStatus$.asObservable(), this.mainPeerConnection.connectionState$.pipe(filter((connectionState) => [
			"connected",
			"disconnected",
			"failed"
		].includes(connectionState)))));
	}
	initSubscriptions() {
		this.subscribeTo(this.callJoinedEvent$, (event) => {
			const memberNodeId = event.room_session.members.find((m) => m.call_id === event.call_id)?.node_id;
			if (memberNodeId) this.setNodeIdIfNull(memberNodeId);
			if (event.member_id) this.setSelfIdIfNull(event.member_id);
		});
		this.subscribeTo(this.vertoMedia$, (event) => {
			logger$16.debug("[WebRTCManager] Received Verto media event (early media SDP):", event);
			const { sdp, callID } = event;
			this.emitMainSignalingStatus(callID, "ringing");
			this._rtcPeerConnectionsMap.get(callID)?.updateAnswerStatus({
				status: "received",
				sdp
			});
		});
		this.subscribeTo(this.vertoAnswer$, (event) => {
			logger$16.debug("[WebRTCManager] Received Verto answer event:", event);
			const { sdp, callID } = event;
			this.emitMainSignalingStatus(callID, "connecting");
			this._rtcPeerConnectionsMap.get(callID)?.updateAnswerStatus({
				status: "received",
				sdp
			});
		});
		this.subscribeTo(this.vertoMediaParams$, (event) => {
			logger$16.debug("[WebRTCManager] Received Verto mediaParams event:", event);
			const { mediaParams, callID } = event;
			const rtcPeerConnController = this._rtcPeerConnectionsMap.get(callID);
			const { audio, video } = mediaParams;
			(async () => {
				try {
					if (audio && rtcPeerConnController) await rtcPeerConnController.replaceAudioTrackWithConstraints(audio);
					if (video) await rtcPeerConnController?.updateSendersConstraints("video", video);
					this.webRtcCallSession.emitMediaParamsUpdated({
						audio,
						video,
						timestamp: Date.now()
					});
				} catch (error) {
					logger$16.warn("[WebRTCManager] Error applying server-pushed media params:", error);
					this.onError?.(error instanceof Error ? error : new Error(String(error), { cause: error }));
				}
			})();
		});
		this.subscribeTo(this.vertoPing$, (vertoPing) => {
			this.attachManager.attach(this.buildAttachableCall());
			this.sendVertoPong(vertoPing);
		});
	}
	/**
	* Set node_id/selfId only when the current value is null.
	*
	* During reattach, `call.joined` and `verto.answer` events can deliver
	* these identifiers before the `verto.invite` RPC response (`CALL CREATED`)
	* arrives. These methods let early events populate them eagerly so that
	* downstream RPC calls (e.g. `call.layout.list`) don't fail with empty
	* identifiers. `processInviteResponse()` remains the authoritative source
	* and always overwrites unconditionally.
	*/
	setNodeIdIfNull(nodeId) {
		if (!this._nodeId$.value && nodeId) {
			logger$16.debug(`[WebRTCManager] Early node_id set: ${nodeId}`);
			this._nodeId$.next(nodeId);
		}
	}
	setSelfIdIfNull(selfId) {
		if (!this._selfId$.value && selfId) {
			logger$16.debug(`[WebRTCManager] Early selfId set: ${selfId}`);
			this._selfId$.next(selfId);
		}
	}
	async sendVertoPong(vertoPing) {
		try {
			const vertoPongMessage = VertoPong({ ...vertoPing });
			await this.executeVerto(vertoPongMessage);
		} catch (error) {
			logger$16.warn("[WebRTCManager] Call might disconnect, error sending Verto pong:", error);
			this.onError?.(new VertoPongError(error));
		}
	}
	async updateMediaConstraints(options = {}) {
		const { audio, video } = options;
		try {
			if (audio) await this.mainPeerConnection.updateSendersConstraints("audio", audio);
			if (video) await this.mainPeerConnection.updateSendersConstraints("video", video);
		} catch (error) {
			logger$16.warn("[WebRTCManager] Error updating media constraints:", error);
			this.onError?.(error instanceof Error ? error : new Error(String(error), { cause: error }));
			throw error;
		}
	}
	get selfId() {
		return this._selfId$.value;
	}
	/** Build an AttachableCall from the current call state. */
	buildAttachableCall(idOverride) {
		return {
			nodeId: this.nodeId ?? void 0,
			id: idOverride ?? this.webRtcCallSession.id,
			to: this.webRtcCallSession.to,
			mediaDirections: this.webRtcCallSession.mediaDirections
		};
	}
	/**
	* Request a video keyframe via RTCP PLI/FIR.
	*
	* Uses RTCRtpReceiver.requestKeyFrame() (Chrome 124+) to send a
	* Picture Loss Indication to the remote sender. This is a client-side
	* WebRTC operation — no server RPC is needed.
	*
	* Best-effort: logs a warning on failure, never emits on errors$.
	*/
	requestKeyframe() {
		try {
			const pc = this.mainPeerConnection.peerConnection;
			if (!pc) {
				logger$16.warn("[WebRTCManager] No peer connection for keyframe request");
				return;
			}
			const videoReceiver = pc.getReceivers().find((r) => r.track.kind === "video");
			if (!videoReceiver) {
				logger$16.warn("[WebRTCManager] No video receiver for keyframe request");
				return;
			}
			if (typeof videoReceiver.requestKeyFrame === "function") {
				videoReceiver.requestKeyFrame();
				logger$16.debug("[WebRTCManager] Keyframe requested via RTCRtpReceiver.requestKeyFrame()");
			} else logger$16.debug("[WebRTCManager] requestKeyFrame() not supported, skipping");
		} catch (error) {
			logger$16.warn("[WebRTCManager] Keyframe request failed (non-fatal):", error);
		}
	}
	/**
	* Request an ICE restart via the controller's negotiation pipeline.
	*
	* Triggers an ICE restart offer on the controller, which goes through the
	* full SDP pipeline: createOffer → setLocalDescription → ICE gathering →
	* localDescription$ emission → setupLocalDescriptionHandler sends verto.modify.
	*
	* This ensures the SDP sent to the server has fully gathered ICE candidates,
	* real ports/IPs, and any configured SDP munging applied — matching the
	* same pipeline used for the initial verto.invite.
	*/
	async requestIceRestart(relayOnly) {
		try {
			const controller = this.mainPeerConnection;
			if (!controller.peerConnection) {
				logger$16.warn("[WebRTCManager] No peer connection for ICE restart");
				return;
			}
			await controller.triggerIceRestart(relayOnly);
			logger$16.info(`[WebRTCManager] ICE restart initiated${relayOnly ? " (relay-only)" : ""}`);
		} catch (error) {
			logger$16.error("[WebRTCManager] ICE restart failed:", error);
			throw error;
		}
	}
	/**
	* Request an ICE restart on ALL active peer connections (main + additional legs).
	*
	* Screen share and additional device legs each get their own ICE restart
	* via the controller's negotiation pipeline. The SDP flows through
	* localDescription$ → setupLocalDescriptionHandler → verto.modify,
	* ensuring ICE gathering completes before the offer is sent.
	*
	* @param relayOnly - If true, constrain to TURN relay candidates only (Tier 3).
	*/
	async requestIceRestartAll(relayOnly) {
		const entries = Array.from(this._rtcPeerConnectionsMap.entries());
		for (const [id, controller] of entries) try {
			if (!controller.peerConnection) {
				logger$16.debug(`[WebRTCManager] No peer connection for leg ${id}, skipping ICE restart`);
				continue;
			}
			await controller.triggerIceRestart(relayOnly);
			logger$16.info(`[WebRTCManager] ICE restart initiated for leg ${id}${relayOnly ? " (relay-only)" : ""}`);
		} catch (error) {
			logger$16.warn(`[WebRTCManager] ICE restart failed for leg ${id}:`, error);
		}
	}
	/**
	* Request a keyframe on video-receiving legs only.
	*
	* Screen share legs are send-only (getDisplayMedia) so they have no
	* video receiver to request a keyframe from — they are skipped.
	*/
	requestKeyframeAll() {
		for (const [id, controller] of this._rtcPeerConnectionsMap) {
			if (controller.isScreenShare) {
				logger$16.debug(`[WebRTCManager] Skipping keyframe for send-only screen share leg ${id}`);
				continue;
			}
			try {
				const pc = controller.peerConnection;
				if (!pc) continue;
				const videoReceiver = pc.getReceivers().find((r) => r.track.kind === "video");
				if (!videoReceiver) continue;
				if (typeof videoReceiver.requestKeyFrame === "function") {
					videoReceiver.requestKeyFrame();
					logger$16.debug(`[WebRTCManager] Keyframe requested for leg ${id}`);
				}
			} catch (error) {
				logger$16.warn(`[WebRTCManager] Keyframe request failed for leg ${id} (non-fatal):`, error);
			}
		}
	}
	get callJoinedEvent$() {
		return this.webRtcCallSession.callEvent$.pipe(filter(isCallJoinedPayload), takeUntil(this.destroyed$));
	}
	get vertoMedia$() {
		return this.webRtcCallSession.webrtcMessages$.pipe(filterAs(isVertoMediaInnerParams, "params"), takeUntil(this.destroyed$));
	}
	get vertoAnswer$() {
		return this.cachedObservable("vertoAnswer$", () => this.webRtcCallSession.webrtcMessages$.pipe(filterAs(isVertoAnswerInnerParams, "params"), takeUntil(this.destroyed$)));
	}
	get vertoMediaParams$() {
		return this.cachedObservable("vertoMediaParams$", () => this.webRtcCallSession.webrtcMessages$.pipe(filterAs(isVertoMediaParamsInnerParams, "params"), takeUntil(this.destroyed$)));
	}
	get vertoBye$() {
		return this.cachedObservable("vertoBye$", () => this.webRtcCallSession.webrtcMessages$.pipe(filterAs(isVertoByeMessage, "params"), takeUntil(this.destroyed$)));
	}
	get vertoAttach$() {
		return this.cachedObservable("vertoAttach$", () => this.webRtcCallSession.webrtcMessages$.pipe(filterAs(isVertoAttachMessage, "params"), takeUntil(this.destroyed$)));
	}
	get vertoPing$() {
		return this.cachedObservable("vertoPing$", () => this.webRtcCallSession.webrtcMessages$.pipe(filterAs(isVertoPingInnerParams, "params"), takeUntil(this.destroyed$)));
	}
	async executeVerto(message, optionals = {}) {
		const webrtcVertoMessage = WebrtcVerto({
			callID: optionals.callID ?? this.webRtcCallSession.id,
			node_id: optionals.node_id ?? this._nodeId$.value ?? "",
			message,
			subscribe: optionals.subscribe
		});
		const response = await this.webRtcCallSession.execute(webrtcVertoMessage);
		if (response.error) {
			const error = new JSONRPCError(response.error.code, response.error.message, response.error.data);
			this.onError?.(error);
			return response;
		}
		const innerResult = getValueFrom(response, "result.result");
		if (innerResult?.error) {
			const error = new JSONRPCError(innerResult.error.code, innerResult.error.message, innerResult.error.data);
			this.onError?.(error);
			return response;
		}
		return response;
	}
	async sendLocalDescription(message, rtcPeerConnController) {
		const vertoMethod = message.method;
		const optionalsParams = this.getSendLocalSDPOptionalParams(rtcPeerConnController, message);
		try {
			const response = await this.executeVerto(message, optionalsParams);
			switch (vertoMethod) {
				case "verto.invite":
					this.processInviteResponse(response, rtcPeerConnController);
					break;
				case "verto.modify":
					await this.processModifyResponse(response, rtcPeerConnController);
					break;
				default:
			}
		} catch (error) {
			logger$16.error(`[WebRTCManager] Error sending Verto ${vertoMethod}:`, error);
			this.onError?.(error instanceof Error ? error : new Error(String(error), { cause: error }));
			if (vertoMethod === "verto.modify") this.onModifyFailed?.();
		}
	}
	async processModifyResponse(response, rtcPeerConnController) {
		if (!response.error) {
			const action = getValueFrom(response, "result.result.result.action");
			const sdp = getValueFrom(response, "result.result.result.sdp");
			if (action === "updateMedia" && !!sdp) try {
				await rtcPeerConnController.updateAnswerStatus({
					status: "received",
					sdp
				});
			} catch (error) {
				logger$16.warn("[WebRTCManager] Error processing modify response:", error);
				const modifyError = error instanceof Error ? error : new Error(String(error), { cause: error });
				this.onError?.(modifyError);
			}
		}
	}
	emitMainSignalingStatus(callId, status) {
		const rtcPeerConnController = this._rtcPeerConnectionsMap.get(callId);
		if (!rtcPeerConnController) {
			const signalingError = new DependencyError(`Cannot emit signaling status, RTCPeerConnectionController not found for callID: ${callId}`);
			logger$16.error("[WebRTCManager] Failed to emit signaling status:", {
				callId,
				status,
				signalingError
			});
			this.onError?.(signalingError);
			return;
		}
		if (rtcPeerConnController.isMainDevice) this._signalingStatus$.next(status);
	}
	processInviteResponse(response, rtcPeerConnController) {
		if (!response.error && getValueFrom(response, "result.result.result.message") === "CALL CREATED") {
			this.emitMainSignalingStatus(rtcPeerConnController.id, "trying");
			this._nodeId$.next(getValueFrom(response, "result.node_id") ?? null);
			const memberId = getValueFrom(response, "result.result.result.memberID") ?? null;
			const callId = getValueFrom(response, "result.result.result.callID") ?? null;
			logger$16.debug("[WebRTCManager] Verto invite response:", {
				callId,
				memberId,
				response
			});
			this._selfId$.next(memberId);
			rtcPeerConnController.setMemberId(memberId);
			if (callId) {
				this.webRtcCallSession.addCallId(callId);
				this.attachManager.attach(this.buildAttachableCall(callId));
			} else logger$16.warn("[WebRTCManager] Cannot attach call, missing callId:", {
				nodeId: this.nodeId,
				callId
			});
			logger$16.info("[WebRTCManager] Verto invite successful");
			logger$16.debug(`[WebRTCManager] nodeid: ${this._nodeId$.value}, selfId: ${this._selfId$.value}`);
		} else {
			logger$16.error("[WebRTCManager] Verto invite failed:", response);
			const inviteError = response.error ? new JSONRPCError(response.error.code, response.error.message, response.error.data) : /* @__PURE__ */ new Error("Verto invite failed: unexpected response");
			this.onError?.(inviteError);
		}
	}
	get RTCPeerConnectionConfig() {
		return {
			iceServers: this.webRtcCallSession.clientSession.iceServers ?? PreferencesContainer.instance.iceServers,
			relayOnly: PreferencesContainer.instance.relayOnly || PreferencesContainer.instance.disableUdpIceServers,
			disableUdpIceServers: PreferencesContainer.instance.disableUdpIceServers,
			iceCandidateTimeout: PreferencesContainer.instance.iceCandidateTimeout,
			iceGatheringTimeout: PreferencesContainer.instance.iceGatheringTimeout
		};
	}
	initMainPeerConnection() {
		const { options } = this.webRtcCallSession;
		const rtcPeerConnController = new RTCPeerConnectionController({
			propose: "main",
			callId: this.webRtcCallSession.id,
			audio: options.audio,
			video: options.video,
			inputAudioDeviceConstraints: options.inputAudioDeviceConstraints,
			inputVideoDeviceConstraints: options.inputVideoDeviceConstraints,
			inputAudioStream: options.inputAudioStream,
			inputVideoStream: options.inputVideoStream,
			receiveAudio: options.receiveAudio,
			receiveVideo: options.receiveVideo,
			webRTCApiProvider: this.webRTCApiProvider,
			preferredVideoCodecs: options.preferredVideoCodecs,
			preferredAudioCodecs: options.preferredAudioCodecs,
			stereo: options.stereo,
			...this.RTCPeerConnectionConfig
		}, options.initOffer, this.deviceController);
		this.setupLocalDescriptionHandler(rtcPeerConnController);
		this.setupVertoByeHandler();
		this.setupVertoAttachHandler();
		this.initObservables(rtcPeerConnController);
		this._rtcPeerConnectionsMap.set(rtcPeerConnController.id, rtcPeerConnController);
		this._rtcPeerConnections$.next(Array.from(this._rtcPeerConnectionsMap.values()));
		this.subscribeTo(rtcPeerConnController.errors$, (error) => {
			this.onError?.(error);
		});
		if (options.initOffer) this.handleInboundAnswer(rtcPeerConnController);
	}
	async handleInboundAnswer(rtcPeerConnController) {
		logger$16.debug("[WebRTCManager] Waiting for inbound call to be accepted or rejected");
		const vertoByeOrAccepted = await firstValueFrom(race(this.vertoBye$, this.webRtcCallSession.answered$).pipe(takeUntil(this.destroyed$))).catch(() => null);
		if (vertoByeOrAccepted === null) {
			logger$16.debug("[WebRTCManager] Inbound answer handler aborted (destroyed).");
			return;
		}
		if (isVertoByeMessage(vertoByeOrAccepted)) {
			logger$16.info("[WebRTCManager] Inbound call ended by remote before answer.");
			this.callSession?.destroy();
		} else if (!vertoByeOrAccepted) {
			logger$16.info("[WebRTCManager] Inbound call rejected by user.");
			try {
				await this.bye("USER_BUSY");
			} finally {
				this._signalingStatus$.next("disconnected");
				this.callSession?.destroy();
			}
		} else {
			logger$16.debug("[WebRTCManager] Inbound call accepted, creating SDP answer");
			const answerOptions = this.webRtcCallSession.answerMediaOptions;
			try {
				await rtcPeerConnController.acceptInbound(answerOptions);
			} catch (error) {
				logger$16.error("[WebRTCManager] Error creating inbound answer:", error);
				this.onError?.(error instanceof Error ? error : new Error(String(error), { cause: error }));
			}
		}
	}
	setupVertoAttachHandler() {
		this.subscribeTo(this.vertoAttach$, async (vertoAttach) => {
			logger$16.debug("[WebRTCManager] Received Verto attach event for existing call:", vertoAttach);
			const { callID } = vertoAttach;
			await this.attachManager.attach({
				nodeId: this.nodeId ?? void 0,
				id: callID,
				to: vertoAttach.callee_id_number,
				mediaDirections: {
					audio: "sendrecv",
					video: "inactive"
				}
			});
		});
	}
	initObservables(rtcPeerConnController) {
		this.mediaDirections$ = rtcPeerConnController.connectionState$.pipe(filter((state) => state === "connected"), map(() => rtcPeerConnController.mediaDirections), startWith(rtcPeerConnController.mediaDirections), takeUntil(this.destroyed$));
		this.localStream$ = rtcPeerConnController.localStream$.pipe(filterNull(), takeUntil(this.destroyed$));
		this.remoteStream$ = rtcPeerConnController.remoteStream$.pipe(filterNull(), takeUntil(this.destroyed$));
	}
	setupLocalDescriptionHandler(rtcPeerConnController) {
		this.subscribeTo(rtcPeerConnController.localDescription$.pipe(filter((description) => description !== null), takeUntil(this.destroyed$)), (description) => {
			const { type, sdp } = description;
			const dialogParams = this.dialogParams(rtcPeerConnController);
			const initial = !rtcPeerConnController.firstSDPExchangeCompleted;
			if (type === "answer") {
				const vertoMessageRequest = VertoAnswer({
					dialogParams,
					sdp
				});
				this.sendLocalDescriptionOnceAccepted(vertoMessageRequest, rtcPeerConnController);
			} else if (initial) {
				const vertoMessageRequest = VertoInvite({
					dialogParams,
					sdp
				});
				this.sendLocalDescription(vertoMessageRequest, rtcPeerConnController);
			} else {
				const vertoMessageRequest = VertoModify({
					dialogParams,
					sdp,
					action: "updateMedia"
				});
				this.sendLocalDescription(vertoMessageRequest, rtcPeerConnController);
			}
		});
	}
	setupVertoByeHandler() {
		this.subscribeTo(this.vertoBye$, () => {
			this._signalingStatus$.next("disconnected");
			this.attachManager.detach(this.buildAttachableCall());
			this.callSession?.destroy();
		});
	}
	getSendLocalSDPOptionalParams(rtcPeerConnController, vertoMessage) {
		let subscribe = void 0;
		if (!rtcPeerConnController.firstSDPExchangeCompleted) {
			subscribe = [];
			if (rtcPeerConnController.isMainDevice) subscribe.push(...PreferencesContainer.instance.inviteSubscribeMainDevice);
			else if (rtcPeerConnController.isAdditionalDevice) subscribe.push(...PreferencesContainer.instance.inviteSubscribeAdditionalDevice);
			else if (rtcPeerConnController.isScreenShare) subscribe.push(...PreferencesContainer.instance.inviteSubscribeScreenshare);
		}
		return {
			callID: rtcPeerConnController.id,
			node_id: resolveInviteNodeId({
				isInvite: isVertoInviteMessage(vertoMessage),
				reattach: this.webRtcCallSession.options.reattach === true,
				explicitNodeId: this.webRtcCallSession.options.nodeId,
				currentNodeId: this._nodeId$.value
			}),
			subscribe
		};
	}
	async sendLocalDescriptionOnceAccepted(vertoMessageRequest, rtcPeerConnectionController) {
		logger$16.debug("[WebRTCManager] Waiting for call to be accepted or ended before sending answer");
		const vertoByeOrAccepted = await firstValueFrom(race(this.vertoBye$, this.webRtcCallSession.answered$).pipe(takeUntil(this.destroyed$))).catch(() => null);
		if (vertoByeOrAccepted === null) {
			logger$16.debug("[WebRTCManager] Destroyed while waiting for call acceptance");
			return;
		}
		if (isVertoByeMessage(vertoByeOrAccepted)) {
			logger$16.info("[WebRTCManager] Call ended before answer was sent.");
			this.callSession?.destroy();
		} else if (!vertoByeOrAccepted) {
			logger$16.info("[WebRTCManager] Call was not accepted, sending verto.bye.");
			try {
				await this.bye("USER_BUSY");
			} finally {
				this._signalingStatus$.next("disconnected");
				this.callSession?.destroy();
			}
		} else {
			logger$16.debug("[WebRTCManager] Call accepted, sending answer");
			try {
				this.emitMainSignalingStatus(rtcPeerConnectionController.id, "connecting");
				await this.sendLocalDescription(vertoMessageRequest, rtcPeerConnectionController);
				await rtcPeerConnectionController.updateAnswerStatus({ status: "sent" });
				await this.attachManager.attach(this.buildAttachableCall());
			} catch (error) {
				logger$16.error("[WebRTCManager] Error sending Verto answer:", error);
				this.onError?.(error instanceof Error ? error : new Error(String(error), { cause: error }));
				await rtcPeerConnectionController.updateAnswerStatus({ status: "failed" });
			}
		}
	}
	dialogParams(rtcPeerConnectionController) {
		const memberId = rtcPeerConnectionController.memberId ?? this._selfId$.value ?? void 0;
		const attach = rtcPeerConnectionController.propose === "main" && !rtcPeerConnectionController.firstSDPExchangeCompleted && this.webRtcCallSession.options.reattach;
		return {
			id: rtcPeerConnectionController.isMainDevice ? this.webRtcCallSession.id : rtcPeerConnectionController.id,
			destinationNumber: this.webRtcCallSession.to ?? this.webRtcCallSession.from,
			attach,
			reattaching: attach,
			callerName: this.webRtcCallSession.fromName,
			callerNumber: this.webRtcCallSession.from,
			remoteCallerName: this.webRtcCallSession.toName,
			remoteCallerNumber: this.webRtcCallSession.to,
			userVariables: {
				memberCallId: this.webRtcCallSession.id,
				memberId,
				...this.webRtcCallSession.userVariables
			},
			screenShare: rtcPeerConnectionController.isScreenShare,
			additionalDevice: rtcPeerConnectionController.isAdditionalDevice,
			pingSupported: true,
			version: INVITE_VERSION
		};
	}
	muteMainAudioInputDevice() {
		return this.mainPeerConnection.stopTrackSender("audio");
	}
	muteMainVideoInputDevice() {
		return this.mainPeerConnection.stopTrackSender("video");
	}
	async unmuteMainAudioInputDevice() {
		return this.mainPeerConnection.restoreTrackSender("audio");
	}
	async unmuteMainVideoInputDevice() {
		return this.mainPeerConnection.restoreTrackSender("video");
	}
	/** Get or lazily create the local audio pipeline for the main peer connection. */
	ensureLocalAudioPipeline() {
		return this.mainPeerConnection.ensureLocalAudioPipeline();
	}
	/** The currently-active local audio pipeline, or null if it hasn't been created. */
	get localAudioPipeline() {
		return this.mainPeerConnection.localAudioPipeline;
	}
	async addInputDevice(options = {
		audio: false,
		video: true
	}) {
		return this.initAdditionalPeerConnection("additional-device", options);
	}
	/**
	* Add a new input device to the main peer connection,
	* only if a device of the same kind is not present already.
	*
	* @see selectAudioInputDevice
	* @see selectVideoInputDevice
	* @param options - Media options specifying which input devices to add (defaults to audio only).
	*/
	async addMainInputDevices(options = { audio: true }) {
		let deviceKind = void 0;
		const { mediaDirections } = this.mainPeerConnection;
		if (options.audio ?? options.inputAudioDeviceConstraints ?? (options.inputAudioStream && mediaDirections.audio.startsWith("send"))) deviceKind = "audio";
		if (options.video ?? options.inputVideoDeviceConstraints ?? (options.inputVideoStream && !mediaDirections.video.startsWith("send"))) deviceKind = deviceKind === "audio" ? "both" : "video";
		if (deviceKind) {
			this.mainPeerConnection.updateMediaDevicesOptions(options);
			await this.mainPeerConnection.restoreTrackSender(deviceKind);
		} else {
			const error = new InvalidParams("No valid device to be added");
			this.onError?.(error);
			throw error;
		}
	}
	async addScreenMedia(options = { audio: false }) {
		await this.initAdditionalPeerConnection("screenshare", options);
	}
	async initAdditionalPeerConnection(propose, options) {
		let rtcPeerConnController = null;
		try {
			this._screenShareStatus$.next("starting");
			rtcPeerConnController = new RTCPeerConnectionController({
				...options,
				...this.RTCPeerConnectionConfig,
				propose,
				webRTCApiProvider: this.webRTCApiProvider
			}, void 0, this.deviceController);
			this.setupLocalDescriptionHandler(rtcPeerConnController);
			if (propose === "screenshare") this._screenShareId = rtcPeerConnController.id;
			this._rtcPeerConnectionsMap.set(rtcPeerConnController.id, rtcPeerConnController);
			this._rtcPeerConnections$.next(Array.from(this._rtcPeerConnectionsMap.values()));
			this.subscribeTo(rtcPeerConnController.errors$, (error) => {
				this.onError?.(error);
			});
			await firstValueFrom(rtcPeerConnController.connectionState$.pipe(filter((state) => state === "connected"), take(1), timeout(this._screenShareTimeoutMs), takeUntil(this.destroyed$)));
			this._screenShareStatus$.next("started");
			logger$16.info("[WebRTCManager] Screen share started successfully.");
			return rtcPeerConnController.id;
		} catch (error) {
			logger$16.warn("[WebRTCManager] Error initializing additional peer connection:", error);
			this.onError?.(error instanceof Error ? error : new Error(String(error), { cause: error }));
			if (rtcPeerConnController) rtcPeerConnController.destroy();
			this._screenShareStatus$.next("none");
		}
	}
	async removeInputDevices(id) {
		return this.removeAdditionalPeerConnection(id);
	}
	removeMainInputDevice(options = {
		removeAudio: false,
		removeVideo: true
	}) {
		let removeTrack = void 0;
		if (options.removeAudio) removeTrack = "audio";
		if (options.removeVideo) removeTrack = removeTrack === "audio" ? "both" : "video";
		if (removeTrack) return this.mainPeerConnection.stopTrackSender(removeTrack, { updateTransceiverDirection: true });
	}
	async removeScreenMedia() {
		if (!["starting", "started"].includes(this._screenShareStatus$.value)) logger$16.warn("[WebRTCManager] No active screen share to stop.");
		if (!this._screenShareId) {
			logger$16.debug("[WebRTCManager] No screen share peer connection found.");
			return;
		}
		this._screenShareStatus$.next("stopping");
		await this.removeAdditionalPeerConnection(this._screenShareId);
		this._screenShareId = void 0;
		this._screenShareStatus$.next("none");
	}
	async removeAdditionalPeerConnection(id) {
		const rtcPeerConnController = this._rtcPeerConnectionsMap.get(id);
		try {
			if (rtcPeerConnController) await this.executeVertoBye(rtcPeerConnController);
		} finally {
			rtcPeerConnController?.destroy();
			this._rtcPeerConnectionsMap.delete(id);
			this._rtcPeerConnections$.next(Array.from(this._rtcPeerConnectionsMap.values()));
		}
	}
	async executeVertoBye(rtcPeerConnController, cause) {
		try {
			const causeParams = cause ? {
				cause,
				causeCode: VertoByeCauseCodes[cause]
			} : {};
			await this.executeVerto(VertoBye({
				...causeParams,
				dialogParams: this.dialogParams(rtcPeerConnController)
			}));
		} catch (error) {
			logger$16.warn("[WebRTCManager] Call might already be disconnected, error sending Verto bye:", error);
			throw error;
		}
	}
	async bye(cause) {
		this.attachManager.detach(this.buildAttachableCall());
		const rtcPeerConnController = this._rtcPeerConnectionsMap.get(this.webRtcCallSession.id);
		if (rtcPeerConnController) await this.executeVertoBye(rtcPeerConnController, cause);
	}
	async sendDigits(dtmf) {
		const vertoInfoMessage = VertoInfo({
			sessid: this.webRtcCallSession.id,
			dialogParams: { callID: this.webRtcCallSession.id },
			dtmf
		});
		try {
			await this.executeVerto(vertoInfoMessage);
		} catch (error) {
			logger$16.warn("[WebRTCManager] Error sending DTMF digits:", error);
			throw error;
		}
	}
	async transfer(options) {
		const message = VertoModify({
			...options,
			dialogParams: this.dialogParams(this.mainPeerConnection),
			action: "transfer"
		});
		try {
			logger$16.debug("[WebRTCManager] Transferring call with options:", options);
			await this.executeVerto(message);
		} catch (error) {
			logger$16.error("[WebRTCManager] Error transferring call:", error);
			throw error;
		}
	}
	destroy() {
		this._rtcPeerConnectionsMap.forEach((rtcPeerConnController) => {
			rtcPeerConnController.destroy();
		});
		this._rtcPeerConnectionsMap.clear();
		this._rtcPeerConnections$.complete();
		super.destroy();
	}
};

//#endregion
//#region src/controllers/RemoteAudioMeter.ts
const logger$15 = getLogger();
/**
* Read-only audio level meter for a remote MediaStream. Attaches an
* AnalyserNode to a MediaStreamAudioSourceNode so it observes the stream
* without affecting the caller's playback path (no GainNode, no destination).
*
* The server delivers all remote audio as a single mixed stream — there is
* no per-participant demux — so this meter reports the aggregate remote
* level, not per-member.
*/
var RemoteAudioMeter = class extends Destroyable {
	constructor(options = {}) {
		super();
		this._source = null;
		this._stream = null;
		this._audioContext = (options.audioContextFactory ?? (() => new AudioContext()))();
		this._analyser = this._audioContext.createAnalyser();
		this._analyser.fftSize = 2048;
		this._analyser.smoothingTimeConstant = .3;
		this._analyserBuffer = new Uint8Array(new ArrayBuffer(this._analyser.fftSize));
		this._pollIntervalMs = options.pollIntervalMs ?? AUDIO_LEVEL_POLL_INTERVAL_MS;
	}
	/** RMS level of the remote audio, 0..1. 0 when no stream is attached. */
	get level$() {
		return this.deferEmission(interval(this._pollIntervalMs, animationFrameScheduler).pipe(map(() => this.computeLevel())));
	}
	/**
	* Attach (or replace) the MediaStream whose audio track is being metered.
	* Pass null to detach without destroying the meter.
	*/
	setStream(stream) {
		if (this._source) {
			try {
				this._source.disconnect();
			} catch (error) {
				logger$15.debug("[RemoteAudioMeter] source disconnect warning:", error);
			}
			this._source = null;
			this._stream = null;
		}
		if (!stream || stream.getAudioTracks().length === 0) return;
		this._stream = new MediaStream(stream.getAudioTracks());
		this._source = this._audioContext.createMediaStreamSource(this._stream);
	}
	destroy() {
		if (this._source) {
			try {
				this._source.disconnect();
			} catch {}
			this._source = null;
		}
		this._audioContext.close().catch((error) => {
			logger$15.debug("[RemoteAudioMeter] audio context close warning:", error);
		});
		super.destroy();
	}
	computeLevel() {
		if (!this._source) return 0;
		this._analyser.getByteTimeDomainData(this._analyserBuffer);
		let sum = 0;
		for (const sample of this._analyserBuffer) {
			const normalized = (sample - 128) / 128;
			sum += normalized * normalized;
		}
		return Math.sqrt(sum / this._analyserBuffer.length);
	}
};

//#endregion
//#region src/controllers/RTCStatsMonitor.ts
/**
* RTCStatsMonitor — per-call WebRTC stats polling, baseline calculation,
* and network issue detection (audio/video packets, RTT, jitter, packet loss).
*
* Extends Destroyable for automatic RxJS cleanup.
*/
const logger$14 = getLogger();
const DEFAULT_POLLING_INTERVAL_MS = 1e3;
const DEFAULT_BASELINE_SAMPLES = 10;
const DEFAULT_NO_AUDIO_PACKET_THRESHOLD_MS = 2e3;
const DEFAULT_NO_VIDEO_PACKET_THRESHOLD_MS = 3e3;
const DEFAULT_RTT_SPIKE_WARNING_MULTIPLIER = 3;
const DEFAULT_RTT_SPIKE_CRITICAL_MULTIPLIER = 5;
const DEFAULT_PACKET_LOSS_WARNING_PERCENT = 5;
const DEFAULT_PACKET_LOSS_CRITICAL_PERCENT = 10;
const DEFAULT_JITTER_SPIKE_MULTIPLIER = 4;
const DEFAULT_HISTORY_WINDOW_SECONDS = 30;
function computePacketLossPercent(lost, received) {
	const total = lost + received;
	if (total === 0) return 0;
	return lost / total * 100;
}
function isInboundRtpStat(stat) {
	return typeof stat === "object" && stat !== null && "type" in stat && stat.type === "inbound-rtp";
}
function isCandidatePairStat(stat) {
	return typeof stat === "object" && stat !== null && "type" in stat && stat.type === "candidate-pair";
}
var RTCStatsMonitor = class extends Destroyable {
	constructor(peerConnection, config = {}) {
		super();
		this.peerConnection = peerConnection;
		this.running = false;
		this.lastAudioPacketsReceived = 0;
		this.lastAudioPacketChangeTime = 0;
		this.lastVideoPacketsReceived = 0;
		this.lastVideoPacketChangeTime = 0;
		this.lastRoundTripTime = 0;
		this.lastAvailableOutgoingBitrate = void 0;
		this._sample$ = this.createReplaySubject(1);
		this._baseline$ = this.createBehaviorSubject({
			rtt: 0,
			jitter: 0,
			ready: false
		});
		this._networkIssues$ = this.createBehaviorSubject([]);
		this._networkMetrics$ = this.createBehaviorSubject([]);
		this.pollingIntervalMs = config.pollingIntervalMs ?? DEFAULT_POLLING_INTERVAL_MS;
		this.baselineSampleCount = config.baselineSamples ?? DEFAULT_BASELINE_SAMPLES;
		this.noAudioPacketThresholdMs = config.noAudioPacketThresholdMs ?? DEFAULT_NO_AUDIO_PACKET_THRESHOLD_MS;
		this.noVideoPacketThresholdMs = config.noVideoPacketThresholdMs ?? DEFAULT_NO_VIDEO_PACKET_THRESHOLD_MS;
		this.rttSpikeWarningMultiplier = config.rttSpikeWarningMultiplier ?? DEFAULT_RTT_SPIKE_WARNING_MULTIPLIER;
		this.rttSpikeCriticalMultiplier = config.rttSpikeCriticalMultiplier ?? DEFAULT_RTT_SPIKE_CRITICAL_MULTIPLIER;
		this.packetLossWarningPercent = config.packetLossWarningPercent ?? DEFAULT_PACKET_LOSS_WARNING_PERCENT;
		this.packetLossCriticalPercent = config.packetLossCriticalPercent ?? DEFAULT_PACKET_LOSS_CRITICAL_PERCENT;
		this.jitterSpikeMultiplier = config.jitterSpikeMultiplier ?? DEFAULT_JITTER_SPIKE_MULTIPLIER;
		this.historyWindowSeconds = config.historyWindowSeconds ?? DEFAULT_HISTORY_WINDOW_SECONDS;
	}
	/** Current list of detected network issues (empty array = healthy). */
	get networkIssues$() {
		return this._networkIssues$.asObservable();
	}
	/** Snapshot of current issues (defensive copy). */
	get networkIssues() {
		return [...this._networkIssues$.value];
	}
	/** Simple boolean health indicator. */
	get isNetworkHealthy$() {
		return this.cachedObservable("isNetworkHealthy$", () => this._networkIssues$.pipe(map((issues) => issues.length === 0), distinctUntilChanged(), takeUntil(this.destroyed$)));
	}
	/** Whether the network is currently healthy. */
	get isNetworkHealthy() {
		return this._networkIssues$.value.length === 0;
	}
	/** Rolling metrics history. */
	get networkMetrics$() {
		return this._networkMetrics$.asObservable();
	}
	/** Snapshot of rolling metrics (defensive copy). */
	get networkMetrics() {
		return [...this._networkMetrics$.value];
	}
	/** Emits individual critical issues for the recovery pipeline. */
	get criticalIssue$() {
		return this.cachedObservable("criticalIssue$", () => this._networkIssues$.pipe(mergeMap((issues) => from(issues.filter((i) => i.severity === "critical"))), takeUntil(this.destroyed$)));
	}
	/** Emits each raw stats sample extracted from the peer connection. */
	get sample$() {
		return this._sample$.asObservable();
	}
	start() {
		if (this.running) return;
		this.running = true;
		const now = Date.now();
		this.lastAudioPacketChangeTime = now;
		this.lastVideoPacketChangeTime = now;
		logger$14.debug("[RTCStatsMonitor] Starting stats monitoring");
		this.subscribeTo(interval(this.pollingIntervalMs).pipe(filter(() => this.running), switchMap(() => from(this.peerConnection.getStats()).pipe(catchError((err) => {
			logger$14.warn("[RTCStatsMonitor] Failed to get stats:", err);
			return EMPTY;
		}))), filter(() => this.running), map((report) => this.extractSample(report))), (sample) => this._sample$.next(sample));
		this.subscribeTo(this._sample$.pipe(take(this.baselineSampleCount), toArray(), map((samples) => ({
			rtt: samples.reduce((s, b) => s + b.roundTripTime, 0) / samples.length,
			jitter: samples.reduce((s, b) => s + b.audioJitter, 0) / samples.length,
			ready: true
		}))), (baseline) => {
			logger$14.debug(`[RTCStatsMonitor] Baseline established: rtt=${baseline.rtt.toFixed(1)}ms, jitter=${baseline.jitter.toFixed(1)}ms`);
			this._baseline$.next(baseline);
		});
		this.subscribeTo(this._sample$.pipe(scan((acc, sample) => ({
			prev: acc.current,
			current: sample
		}), {
			prev: null,
			current: null
		}), filter((pair) => pair.current !== null)), ({ prev, current }) => {
			const now$1 = current.timestamp;
			this.updatePacketTracking(current, now$1);
			const issues = this.detectIssues(current, prev, now$1);
			this._networkIssues$.next(issues);
		});
		this.subscribeTo(this._sample$.pipe(scan((history, sample) => {
			const cutoff = sample.timestamp - this.historyWindowSeconds * 1e3;
			const metrics = {
				timestamp: sample.timestamp,
				audio: {
					packetsReceived: sample.audioPacketsReceived,
					packetsLost: sample.audioPacketsLost,
					jitter: sample.audioJitter
				},
				video: {
					packetsReceived: sample.videoPacketsReceived,
					packetsLost: sample.videoPacketsLost
				},
				roundTripTime: sample.roundTripTime,
				availableOutgoingBitrate: sample.availableOutgoingBitrate
			};
			return [...history.filter((m) => m.timestamp > cutoff), metrics];
		}, [])), (metrics) => this._networkMetrics$.next(metrics));
	}
	stop() {
		if (!this.running) return;
		this.running = false;
		logger$14.debug("[RTCStatsMonitor] Stopping stats monitoring");
	}
	destroy() {
		logger$14.debug("[RTCStatsMonitor] Destroying RTCStatsMonitor");
		this.stop();
		super.destroy();
	}
	extractSample(report) {
		let audioPacketsReceived = 0;
		let audioPacketsLost = 0;
		let audioJitter = 0;
		let videoPacketsReceived = 0;
		let videoPacketsLost = 0;
		let roundTripTime = 0;
		let availableOutgoingBitrate;
		report.forEach((stat) => {
			if (isInboundRtpStat(stat)) if (stat.kind === "audio") {
				audioPacketsReceived += stat.packetsReceived ?? 0;
				audioPacketsLost += stat.packetsLost ?? 0;
				audioJitter = Math.max(audioJitter, (stat.jitter ?? 0) * 1e3);
			} else {
				videoPacketsReceived += stat.packetsReceived ?? 0;
				videoPacketsLost += stat.packetsLost ?? 0;
			}
			if (isCandidatePairStat(stat) && stat.state === "succeeded" && stat.nominated) {
				roundTripTime = stat.currentRoundTripTime ? stat.currentRoundTripTime * 1e3 : this.lastRoundTripTime;
				availableOutgoingBitrate = stat.availableOutgoingBitrate ?? this.lastAvailableOutgoingBitrate;
			}
		});
		return {
			audioPacketsReceived,
			audioPacketsLost,
			audioJitter,
			videoPacketsReceived,
			videoPacketsLost,
			roundTripTime,
			availableOutgoingBitrate,
			timestamp: Date.now()
		};
	}
	updatePacketTracking(sample, now) {
		if (sample.audioPacketsReceived !== this.lastAudioPacketsReceived) {
			this.lastAudioPacketsReceived = sample.audioPacketsReceived;
			this.lastAudioPacketChangeTime = now;
		}
		if (sample.videoPacketsReceived !== this.lastVideoPacketsReceived) {
			this.lastVideoPacketsReceived = sample.videoPacketsReceived;
			this.lastVideoPacketChangeTime = now;
		}
		if (sample.roundTripTime > 0) this.lastRoundTripTime = sample.roundTripTime;
		if (sample.availableOutgoingBitrate !== void 0) this.lastAvailableOutgoingBitrate = sample.availableOutgoingBitrate;
	}
	detectIssues(sample, prevSample, now) {
		const issues = [];
		const baseline = this._baseline$.value;
		const audioSilenceMs = now - this.lastAudioPacketChangeTime;
		if (audioSilenceMs > this.noAudioPacketThresholdMs) issues.push({
			type: "no_inbound_audio",
			severity: "critical",
			timestamp: now,
			value: audioSilenceMs,
			threshold: this.noAudioPacketThresholdMs
		});
		const videoSilenceMs = now - this.lastVideoPacketChangeTime;
		if (videoSilenceMs > this.noVideoPacketThresholdMs) issues.push({
			type: "no_inbound_video",
			severity: "warning",
			timestamp: now,
			value: videoSilenceMs,
			threshold: this.noVideoPacketThresholdMs
		});
		if (baseline.ready) {
			const rtt = sample.roundTripTime;
			const baselineRtt = baseline.rtt;
			if (baselineRtt > 0) {
				const rttRatio = rtt / baselineRtt;
				if (rttRatio > this.rttSpikeCriticalMultiplier) issues.push({
					type: "high_rtt",
					severity: "critical",
					timestamp: now,
					value: rtt,
					threshold: baselineRtt * this.rttSpikeCriticalMultiplier
				});
				else if (rttRatio > this.rttSpikeWarningMultiplier) issues.push({
					type: "high_rtt",
					severity: "warning",
					timestamp: now,
					value: rtt,
					threshold: baselineRtt * this.rttSpikeWarningMultiplier
				});
			}
			const jitter = sample.audioJitter;
			const baselineJitter = baseline.jitter;
			if (baselineJitter > 0) {
				if (jitter / baselineJitter > this.jitterSpikeMultiplier) issues.push({
					type: "high_jitter",
					severity: "warning",
					timestamp: now,
					value: jitter,
					threshold: baselineJitter * this.jitterSpikeMultiplier
				});
			}
		}
		if (prevSample) {
			const deltaAudioReceived = Math.max(0, sample.audioPacketsReceived - prevSample.audioPacketsReceived);
			const deltaAudioLost = Math.max(0, sample.audioPacketsLost - prevSample.audioPacketsLost);
			const deltaVideoReceived = Math.max(0, sample.videoPacketsReceived - prevSample.videoPacketsReceived);
			const deltaVideoLost = Math.max(0, sample.videoPacketsLost - prevSample.videoPacketsLost);
			const totalDeltaReceived = deltaAudioReceived + deltaVideoReceived;
			const lossPercent = computePacketLossPercent(deltaAudioLost + deltaVideoLost, totalDeltaReceived);
			if (lossPercent > this.packetLossCriticalPercent) issues.push({
				type: "high_packet_loss",
				severity: "critical",
				timestamp: now,
				value: lossPercent,
				threshold: this.packetLossCriticalPercent
			});
			else if (lossPercent > this.packetLossWarningPercent) issues.push({
				type: "high_packet_loss",
				severity: "warning",
				timestamp: now,
				value: lossPercent,
				threshold: this.packetLossWarningPercent
			});
		}
		return issues;
	}
};

//#endregion
//#region src/managers/CallRecoveryManager.ts
const logger$13 = getLogger();
const DEFAULT_DEBOUNCE_TIME_MS = 2e3;
const DEFAULT_COOLDOWN_MS = 1e4;
const DEFAULT_ICE_GRACE_PERIOD_MS = 3e3;
const DEFAULT_ICE_RESTART_TIMEOUT_MS = 5e3;
const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_KEYFRAME_MAX_BURST = 3;
const DEFAULT_KEYFRAME_BURST_WINDOW_MS = 3e3;
const DEFAULT_KEYFRAME_COOLDOWN_MS = 1e4;
const DEFAULT_DEGRADATION_BITRATE_THRESHOLD = 150;
const DEFAULT_DEGRADATION_RECOVERY_THRESHOLD = 300;
const DEFAULT_PACKET_LOSS_RECOVERY_DELAY_SEC = 5;
/**
* Issue types that indicate quality degradation but NOT connectivity loss.
* These should only trigger Tier 1 (keyframe) — never ICE restart.
* The network path is slow or lossy, but media is still flowing.
*/
const DEGRADATION_ONLY_ISSUES = new Set([
	"high_rtt",
	"high_jitter",
	"high_packet_loss"
]);
/**
* Implements the tiered recovery pipeline described in Sections 2, 19, 22,
* and 25 of the implementation guide.
*
* Three detection signals (stats critical, ICE state, network events) are
* merged, debounced, and processed through tiered recovery:
*   Tier 1 - Request video keyframe (throttled burst)
*   Tier 2 - ICE restart via verto.modify
*   Tier 3 - Relay-only ICE restart
*
* Gate checks prevent recovery when the call is not in a recoverable state.
* A state machine tracks the pipeline: IDLE -> DEBOUNCING -> RECOVERING -> COOLDOWN.
*/
var CallRecoveryManager = class extends Destroyable {
	constructor(callbacks, inputs, config = {}) {
		super();
		this._recoveryState$ = this.createBehaviorSubject("idle");
		this._recoveryEvent$ = this.createSubject();
		this._bandwidthConstrained$ = this.createBehaviorSubject(false);
		this._hasPacketLoss$ = this.createBehaviorSubject(false);
		this._trigger$ = this.createSubject();
		this._attemptCount = 0;
		this._keyframeBurstCount = 0;
		this._keyframeBurstStart = 0;
		this._keyframeCooldownUntil = 0;
		this._cooldownUntil = 0;
		this._pipelineStop$ = this.createSubject();
		this._callbacks = callbacks;
		this._inputs = inputs;
		this._config = {
			debounceTimeMs: config.debounceTimeMs ?? DEFAULT_DEBOUNCE_TIME_MS,
			cooldownMs: config.cooldownMs ?? DEFAULT_COOLDOWN_MS,
			iceGracePeriodMs: config.iceGracePeriodMs ?? DEFAULT_ICE_GRACE_PERIOD_MS,
			iceRestartTimeoutMs: config.iceRestartTimeoutMs ?? DEFAULT_ICE_RESTART_TIMEOUT_MS,
			maxAttempts: config.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
			enableRelayFallback: config.enableRelayFallback ?? true,
			keyframeMaxBurst: config.keyframeMaxBurst ?? DEFAULT_KEYFRAME_MAX_BURST,
			keyframeBurstWindowMs: config.keyframeBurstWindowMs ?? DEFAULT_KEYFRAME_BURST_WINDOW_MS,
			keyframeCooldownMs: config.keyframeCooldownMs ?? DEFAULT_KEYFRAME_COOLDOWN_MS,
			degradationBitrateThreshold: config.degradationBitrateThreshold ?? DEFAULT_DEGRADATION_BITRATE_THRESHOLD,
			degradationRecoveryThreshold: config.degradationRecoveryThreshold ?? DEFAULT_DEGRADATION_RECOVERY_THRESHOLD,
			enableAutoDegradation: config.enableAutoDegradation ?? true,
			packetLossRecoveryDelaySec: config.packetLossRecoveryDelaySec ?? DEFAULT_PACKET_LOSS_RECOVERY_DELAY_SEC
		};
		this.initPipeline();
		this.initDegradationRecoveryPipeline();
	}
	get recoveryState$() {
		return this._recoveryState$.asObservable().pipe(takeUntil(this._destroyed$));
	}
	get recoveryState() {
		return this._recoveryState$.value;
	}
	get recoveryEvent$() {
		return this._recoveryEvent$.asObservable().pipe(takeUntil(this._destroyed$));
	}
	get bandwidthConstrained$() {
		return this._bandwidthConstrained$.asObservable().pipe(takeUntil(this._destroyed$));
	}
	get bandwidthConstrained() {
		return this._bandwidthConstrained$.value;
	}
	/**
	* Feed an external detection signal into the recovery pipeline.
	* Multiple signals are debounced and collapsed automatically.
	*/
	pushTrigger(trigger) {
		this._trigger$.next(trigger);
	}
	/**
	* Manual ICE restart request — bypasses cooldown and gate checks,
	* but skips if the pipeline is already recovering to avoid races.
	*/
	async requestIceRestart() {
		if (this._recoveryState$.value === "recovering") {
			logger$13.info("CallRecoveryManager: manual ICE restart skipped — recovery already in progress");
			return;
		}
		logger$13.info("CallRecoveryManager: manual ICE restart requested");
		this.transitionTo("recovering");
		await this.executeIceRestart(false);
		this.startCooldown();
	}
	/**
	* Manual keyframe request — subject to burst throttling only.
	*/
	requestKeyframe() {
		this.executeKeyframe("manual request");
	}
	/**
	* Reset all attempt counters and cooldowns. Should be called after
	* WebSocket reconnect or call state recovers to 'connected'.
	*/
	reset() {
		logger$13.info("CallRecoveryManager: resetting counters");
		this._attemptCount = 0;
		this._keyframeBurstCount = 0;
		this._keyframeBurstStart = 0;
		this._keyframeCooldownUntil = 0;
		this._cooldownUntil = 0;
		this.transitionTo("idle");
	}
	/**
	* Notify the recovery manager that a verto.modify (ICE restart SDP exchange)
	* failed at the signaling layer. Resets cooldown and pushes a new trigger
	* so recovery can re-attempt.
	*/
	notifyModifyFailed() {
		if (this._recoveryState$.value === "cooldown" || this._recoveryState$.value === "idle") {
			logger$13.info("CallRecoveryManager: verto.modify failed — re-entering recovery");
			this._cooldownUntil = 0;
			this.transitionTo("idle");
			this.pushTrigger({
				source: "network",
				detail: "modify_failed_during_recovery"
			});
		}
	}
	/**
	* Feed bandwidth information for graceful degradation (Section 22).
	* Call this from the stats monitor with current available outgoing bitrate.
	*/
	reportBandwidth(bitrateKbps) {
		if (!this._config.enableAutoDegradation) return;
		const wasConstrained = this._bandwidthConstrained$.value;
		if (!wasConstrained && bitrateKbps < this._config.degradationBitrateThreshold) {
			this._bandwidthConstrained$.next(true);
			this._callbacks.disableVideo();
			this.emitEvent({
				action: "video_disabled",
				reason: `bandwidth ${bitrateKbps}kbps below threshold ${this._config.degradationBitrateThreshold}kbps`,
				timestamp: Date.now()
			});
			logger$13.warn(`CallRecoveryManager: disabling video — bandwidth ${bitrateKbps}kbps < ${this._config.degradationBitrateThreshold}kbps`);
		} else if (wasConstrained && bitrateKbps >= this._config.degradationRecoveryThreshold) {
			this._bandwidthConstrained$.next(false);
			this._callbacks.enableVideo();
			this.emitEvent({
				action: "video_restored",
				reason: `bandwidth ${bitrateKbps}kbps recovered above ${this._config.degradationRecoveryThreshold}kbps`,
				timestamp: Date.now()
			});
			logger$13.info(`CallRecoveryManager: restoring video — bandwidth ${bitrateKbps}kbps >= ${this._config.degradationRecoveryThreshold}kbps`);
		}
	}
	/**
	* Feed current network issues for degradation recovery.
	* When video was disabled due to bandwidth constraints, this pipeline
	* monitors packet loss clearance to trigger video restoration.
	*/
	reportNetworkIssues(issues) {
		const hasPacketLoss = issues.some((i) => i.type === "high_packet_loss");
		if (hasPacketLoss !== this._hasPacketLoss$.value) this._hasPacketLoss$.next(hasPacketLoss);
	}
	/**
	* Signal-only vs full reconnect distinction (Section 25).
	* Call this when WebSocket reconnects to determine the appropriate action.
	*/
	handleWebSocketReconnect() {
		const pcState = this._callbacks.getPeerConnectionState();
		if (pcState === "connected" || pcState === "completed") {
			logger$13.info("CallRecoveryManager: signal-only reconnect — peer connection still alive");
			this.emitEvent({
				action: "signal_reconnect",
				reason: "WebSocket reconnected, peer connection still connected",
				timestamp: Date.now()
			});
		} else {
			logger$13.info("CallRecoveryManager: full reconnect — peer connection also down");
			this.emitEvent({
				action: "full_reconnect",
				reason: "WebSocket reconnected, peer connection not connected — ICE restart needed",
				timestamp: Date.now()
			});
			this.pushTrigger({
				source: "network",
				detail: "full_reconnect_after_ws"
			});
		}
		this.reset();
	}
	destroy() {
		this._pipelineStop$.next();
		this._pipelineStop$.complete();
		super.destroy();
	}
	initPipeline() {
		this.subscribeTo(this._trigger$.pipe(tap(() => {
			if (this._recoveryState$.value === "idle") this.transitionTo("debouncing");
		}), debounceTime(this._config.debounceTimeMs), withLatestFrom(this._inputs.signalingReady$), filter(([, signalingReady]) => this.passGateChecks(signalingReady)), map(([trigger]) => trigger), exhaustMap((trigger) => this.executeTieredRecovery(trigger)), takeUntil(merge(this._destroyed$, this._pipelineStop$))), {
			next: () => {},
			error: (err) => {
				logger$13.error("CallRecoveryManager: pipeline error", err);
				this.transitionTo("idle");
			}
		});
	}
	/**
	* When video was disabled due to bandwidth constraints, the browser's
	* bandwidth estimator loses its primary probe signal (video traffic).
	* This means availableOutgoingBitrate may never recover to the restoration
	* threshold. Instead, we watch for packet loss clearance as a proxy for
	* network recovery: if high_packet_loss stays absent for a configurable
	* duration while video is degraded, we restore the video track.
	*/
	initDegradationRecoveryPipeline() {
		if (!this._config.enableAutoDegradation) return;
		const delayMs = this._config.packetLossRecoveryDelaySec * 1e3;
		this.subscribeTo(combineLatest([this._bandwidthConstrained$, this._hasPacketLoss$]).pipe(switchMap(([constrained, hasPacketLoss]) => {
			if (constrained && !hasPacketLoss) return timer(delayMs);
			return EMPTY;
		}), takeUntil(this._destroyed$)), () => {
			this._bandwidthConstrained$.next(false);
			this._callbacks.enableVideo();
			this.emitEvent({
				action: "video_restored",
				reason: `no packet loss for ${this._config.packetLossRecoveryDelaySec}s — restoring video`,
				timestamp: Date.now()
			});
			logger$13.info(`CallRecoveryManager: restoring video — no packet loss for ${this._config.packetLossRecoveryDelaySec}s`);
		});
	}
	passGateChecks(signalingReady) {
		if (this._callbacks.isNegotiating()) {
			logger$13.debug("CallRecoveryManager: gate blocked — negotiation in progress");
			this.transitionTo("idle");
			return false;
		}
		if (!signalingReady) {
			logger$13.debug("CallRecoveryManager: gate blocked — signaling not ready");
			this.transitionTo("idle");
			return false;
		}
		if (!this._callbacks.isCallConnected()) {
			logger$13.debug("CallRecoveryManager: gate blocked — call not connected");
			this.transitionTo("idle");
			return false;
		}
		if (this.isCooldownActive()) {
			logger$13.debug("CallRecoveryManager: gate blocked — cooldown active");
			this.transitionTo("cooldown");
			return false;
		}
		return true;
	}
	isCooldownActive() {
		return Date.now() < this._cooldownUntil;
	}
	executeTieredRecovery(trigger) {
		this.transitionTo("recovering");
		logger$13.info(`CallRecoveryManager: starting tiered recovery — source=${trigger.source} detail=${trigger.detail}`);
		return from(this.runTiers(trigger)).pipe(tap(() => this.startCooldown()), catchError((err) => {
			logger$13.error("CallRecoveryManager: tiered recovery failed", err);
			this.startCooldown();
			return EMPTY;
		}));
	}
	async runTiers(trigger) {
		this.executeKeyframe(trigger.detail);
		if (trigger.issueType && DEGRADATION_ONLY_ISSUES.has(trigger.issueType)) {
			logger$13.debug(`CallRecoveryManager: degradation-only issue (${trigger.issueType}) — Tier 1 only, skipping ICE restart`);
			return;
		}
		if (this._attemptCount < this._config.maxAttempts) {
			if (await this.executeIceRestart(false)) return;
		}
		if (this._config.enableRelayFallback && this._attemptCount < this._config.maxAttempts) {
			if (await this.executeIceRestart(true)) return;
		}
		if (this._attemptCount >= this._config.maxAttempts) {
			this.emitEvent({
				action: "max_attempts_reached",
				reason: `all ${this._config.maxAttempts} recovery attempts exhausted`,
				attempt: this._attemptCount,
				maxAttempts: this._config.maxAttempts,
				timestamp: Date.now()
			});
			logger$13.warn("CallRecoveryManager: max recovery attempts reached");
		}
	}
	executeKeyframe(reason) {
		const now = Date.now();
		if (now < this._keyframeCooldownUntil) {
			logger$13.debug("CallRecoveryManager: keyframe request skipped — cooldown active");
			return;
		}
		if (now - this._keyframeBurstStart > this._config.keyframeBurstWindowMs) {
			this._keyframeBurstCount = 0;
			this._keyframeBurstStart = now;
		}
		if (this._keyframeBurstCount >= this._config.keyframeMaxBurst) {
			this._keyframeCooldownUntil = now + this._config.keyframeCooldownMs;
			logger$13.debug(`CallRecoveryManager: keyframe burst limit reached (${this._config.keyframeMaxBurst}), cooldown until ${this._keyframeCooldownUntil}`);
			return;
		}
		this._keyframeBurstCount += 1;
		this._callbacks.requestKeyframe();
		this.emitEvent({
			action: "keyframe_requested",
			reason,
			timestamp: now
		});
		logger$13.debug(`CallRecoveryManager: keyframe requested (burst ${this._keyframeBurstCount}/${this._config.keyframeMaxBurst})`);
	}
	async executeIceRestart(relayOnly) {
		this._attemptCount += 1;
		const tier = relayOnly ? "Tier 3 (relay-only)" : "Tier 2 (standard)";
		logger$13.info(`CallRecoveryManager: ${tier} ICE restart — attempt ${this._attemptCount}/${this._config.maxAttempts}`);
		this.emitEvent({
			action: "reinvite_started",
			reason: `${tier} ICE restart`,
			attempt: this._attemptCount,
			maxAttempts: this._config.maxAttempts,
			timestamp: Date.now()
		});
		try {
			if (await this.withTimeout(this._callbacks.requestIceRestart(relayOnly), this._config.iceRestartTimeoutMs)) {
				this.emitEvent({
					action: "reinvite_succeeded",
					reason: `${tier} ICE restart succeeded`,
					attempt: this._attemptCount,
					maxAttempts: this._config.maxAttempts,
					timestamp: Date.now()
				});
				logger$13.info(`CallRecoveryManager: ${tier} ICE restart succeeded`);
				this._attemptCount = 0;
				return true;
			}
			this.emitEvent({
				action: "reinvite_failed",
				reason: `${tier} ICE restart returned false`,
				attempt: this._attemptCount,
				maxAttempts: this._config.maxAttempts,
				timestamp: Date.now()
			});
			logger$13.warn(`CallRecoveryManager: ${tier} ICE restart failed`);
			return false;
		} catch {
			this.emitEvent({
				action: "reinvite_timeout",
				reason: `${tier} ICE restart timed out after ${this._config.iceRestartTimeoutMs}ms`,
				attempt: this._attemptCount,
				maxAttempts: this._config.maxAttempts,
				timestamp: Date.now()
			});
			logger$13.warn(`CallRecoveryManager: ${tier} ICE restart timed out`);
			return false;
		}
	}
	async withTimeout(promise, timeoutMs) {
		return new Promise((resolve, reject) => {
			const timeoutId = setTimeout(() => {
				reject(/* @__PURE__ */ new Error(`Timeout after ${timeoutMs}ms`));
			}, timeoutMs);
			promise.then((value) => {
				clearTimeout(timeoutId);
				resolve(value);
			}, (err) => {
				clearTimeout(timeoutId);
				reject(err instanceof Error ? err : new Error(String(err)));
			});
		});
	}
	transitionTo(state) {
		const prev = this._recoveryState$.value;
		if (prev !== state) {
			logger$13.debug(`CallRecoveryManager: state ${prev} -> ${state}`);
			this._recoveryState$.next(state);
		}
	}
	startCooldown() {
		this._cooldownUntil = Date.now() + this._config.cooldownMs;
		this.transitionTo("cooldown");
		if (this._cooldownSubscription) this._cooldownSubscription.unsubscribe();
		this._cooldownSubscription = timer(this._config.cooldownMs).pipe(take(1), takeUntil(this._destroyed$), filter(() => this._recoveryState$.value === "cooldown")).subscribe(() => this.transitionTo("idle"));
	}
	emitEvent(event) {
		this._recoveryEvent$.next(event);
	}
};

//#endregion
//#region src/managers/ParticipantFactory.ts
/**
* Factory for creating Participant instances with proper dependency injection
* Eliminates circular dependency between Call and Participant
*/
var ParticipantFactory = class {
	constructor(executeMethod, vertoManager, deviceController) {
		this.executeMethod = executeMethod;
		this.vertoManager = vertoManager;
		this.deviceController = deviceController;
	}
	/**
	* Create a self participant (the local user in the call)
	*/
	createSelfParticipant(id) {
		return new SelfParticipant(id, this.executeMethod, this.vertoManager, this.deviceController);
	}
	/**
	* Create a remote participant
	*/
	createParticipant(id) {
		return new Participant(id, this.executeMethod, this.deviceController);
	}
};

//#endregion
//#region src/utils/qualityScore.ts
/** E-model base R factor for a perfect channel. */
const E_MODEL_BASE_R = 93.2;
/** Delay/jitter weight factor. */
const E_MODEL_DELAY_WEIGHT = .024;
/** Packet-loss weight factor. */
const E_MODEL_LOSS_WEIGHT = 2.5;
/** R-factor valid range for the E-model polynomial. */
const R_FACTOR_MIN = 0;
const R_FACTOR_MAX = 100;
/** Lower bound for MOS. */
const MOS_MIN = 1;
/** Upper bound for MOS. */
const MOS_MAX = 5;
/** MOS threshold boundaries (lower bound of each level). */
const MOS_EXCELLENT_THRESHOLD = 4;
const MOS_GOOD_THRESHOLD = 3.5;
const MOS_FAIR_THRESHOLD = 3;
const MOS_POOR_THRESHOLD = 2;
/**
* Compute a Mean Opinion Score from network metrics using the simplified
* E-model formula (ITU-T G.107).
*
* ```
* R   = 93.2 - (rtt/2 + jitter) * 0.024 - packetLoss * 2.5
* MOS = 1 + 0.035 * R + R * (R - 60) * (100 - R) * 7e-6
* ```
*
* @param rtt         Round-trip time in milliseconds
* @param jitter      Jitter in milliseconds
* @param packetLoss  Packet loss as a percentage (e.g. 5 means 5%)
* @returns MOS value clamped to [1.0, 5.0]
*/
function computeMOS(rtt, jitter, packetLoss) {
	const rawR = E_MODEL_BASE_R - (rtt / 2 + jitter) * E_MODEL_DELAY_WEIGHT - packetLoss * E_MODEL_LOSS_WEIGHT;
	const r = Math.min(R_FACTOR_MAX, Math.max(R_FACTOR_MIN, rawR));
	const mos = 1 + .035 * r + r * (r - 60) * (100 - r) * 7e-6;
	return Math.min(MOS_MAX, Math.max(MOS_MIN, mos));
}
/**
* Map a numeric MOS score to a human-readable quality level.
*
* | MOS Range   | Level       |
* |-------------|-------------|
* | 4.0 - 5.0   | excellent   |
* | 3.5 - 4.0   | good        |
* | 3.0 - 3.5   | fair        |
* | 2.0 - 3.0   | poor        |
* | 1.0 - 2.0   | critical    |
*/
function mosToQualityLevel(mos) {
	if (mos >= MOS_EXCELLENT_THRESHOLD) return "excellent";
	if (mos >= MOS_GOOD_THRESHOLD) return "good";
	if (mos >= MOS_FAIR_THRESHOLD) return "fair";
	if (mos >= MOS_POOR_THRESHOLD) return "poor";
	return "critical";
}

//#endregion
//#region src/core/entities/Call.ts
const logger$12 = getLogger();
/**
* Ratio between the critical and warning RTT spike multipliers.
* Warning threshold = baseline * warningMultiplier (default 3x)
* Critical threshold = baseline * warningMultiplier * RTT_CRITICAL_TO_WARNING_RATIO
* With default 3x warning: critical = 3 * 5/3 = 5x baseline.
*/
const RTT_CRITICAL_TO_WARNING_RATIO = 5 / 3;
const fromDestinationParams = (destination) => {
	if (!destination) return {};
	try {
		const url = new URL(`destination:${destination}`);
		const params = {};
		url.searchParams.forEach((value, key) => {
			params[key] = value;
		});
		return params;
	} catch (error) {
		logger$12.warn(`Failed to parse destination URI: ${destination}`, error);
		return {};
	}
};
/**
* Concrete WebRTC call implementation.
*
* Manages the full lifecycle of a call including signaling, media streams,
* participants, layout, and event routing. Created via {@link SignalWire.dial}
* or received as an inbound call.
*/
var WebRTCCall = class extends Destroyable {
	constructor(clientSession, options, initialization, address) {
		super();
		this.clientSession = clientSession;
		this.options = options;
		this.address = address;
		this._errors$ = this.createReplaySubject(1);
		this._lastMergedStatus = "new";
		this._answered$ = this.createReplaySubject();
		this._holdState = false;
		this._userVariables$ = this.createBehaviorSubject({ ...PreferencesContainer.instance.userVariables });
		this._networkIssues$ = this.createBehaviorSubject([]);
		this._networkMetrics$ = this.createBehaviorSubject([]);
		this._isNetworkHealthy$ = this.createBehaviorSubject(true);
		this._qualityScore$ = this.createBehaviorSubject(5);
		this._qualityLevel$ = this.createBehaviorSubject("excellent");
		this._recoveryState$ = this.createBehaviorSubject("idle");
		this._recoveryEvent$ = this.createSubject();
		this._bandwidthConstrained$ = this.createBehaviorSubject(false);
		this._mediaParamsUpdated$ = this.createSubject();
		this._customSubscriptions = /* @__PURE__ */ new Map();
		this._pushToTalkEnabled = false;
		this._remoteAudioMeter = null;
		this.id = options.callId ?? v4();
		this.to = options.to;
		this._userVariables$.next({
			...this._userVariables$.value,
			...fromDestinationParams(options.to),
			...options.userVariables
		});
		this.subscribeTo(this.webrtcMessages$, (message) => {
			const userVars = getValueFrom(message, "params.userVariables");
			if (userVars) this._userVariables$.next({
				...this._userVariables$.value,
				...userVars
			});
		});
		const managers = initialization.initializeManagers(this);
		this.vertoManager = managers.vertoManager;
		this.callEventsManager = managers.callEventsManager;
		if (options.initOffer) {
			this._status$ = this.createBehaviorSubject("ringing");
			this._lastMergedStatus = "ringing";
		} else this._status$ = this.createBehaviorSubject("new");
		const { deviceController, networkChange$ } = initialization;
		this._networkChange$ = networkChange$;
		this.participantFactory = new ParticipantFactory(this.executeMethod.bind(this), this.vertoManager, deviceController);
		this.subscribeTo(merge(this._status$.asObservable(), this.vertoManager.signalingStatus$).pipe(distinctUntilChanged(), takeUntil(this._destroyed$)), (status) => {
			this._lastMergedStatus = status;
			if (status === "connected" && !this._statsMonitor) this.initResilienceSubsystems();
			else if (status === "disconnected") {
				this._statsMonitor?.destroy();
				this._statsMonitor = void 0;
			} else if (status === "destroyed" || status === "failed") this.stopResilienceSubsystems();
		});
	}
	/** Observable stream of errors from media, signaling, and peer connection layers. */
	get errors$() {
		return this.deferEmission(this._errors$.asObservable());
	}
	/**
	* @internal Push an error to the call's error stream.
	* Fatal errors automatically transition the call to `'failed'` and destroy it.
	*/
	emitError(callError) {
		if (this._status$.value === "destroyed" || this._status$.value === "failed") return;
		this._errors$.next(callError);
		if (callError.fatal) {
			this._status$.next("failed");
			this.destroy();
		}
	}
	/** Notify the recovery manager that a verto.modify signaling exchange failed. */
	notifyModifyFailed() {
		this._recoveryManager?.notifyModifyFailed();
	}
	/** Whether this call is `'inbound'` or `'outbound'`. */
	get direction() {
		return this.options.initOffer ? "inbound" : "outbound";
	}
	/** Observable of the address associated with this call. */
	get address$() {
		return this.deferEmission(from([this.address])).pipe(takeUntil(this._destroyed$));
	}
	/** Display name of the caller. */
	get fromName() {
		return this.options.fromName;
	}
	/** Address URI of the caller. */
	get from() {
		return this.options.from;
	}
	/** Display name of the callee. */
	get toName() {
		return this.options.toName;
	}
	/** Toggles whether incoming video is received. @throws {UnimplementedError} Not yet implemented. */
	async toggleIncomingVideo() {
		throw new UnimplementedError();
	}
	/** Toggles whether incoming audio is received. @throws {UnimplementedError} Not yet implemented. */
	async toggleIncomingAudio() {
		throw new UnimplementedError();
	}
	/** @internal Registers an additional call ID for event routing. */
	addCallId(callId) {
		this.callEventsManager.addCallId(callId);
	}
	/** List of capabilities available in the current call. */
	get capabilities() {
		return this.callEventsManager.capabilities;
	}
	/** Current snapshot of all participants in the call. */
	get participants() {
		return this.callEventsManager.participants;
	}
	/** The local participant, or `null` if not yet joined. */
	get self() {
		return this.callEventsManager.self;
	}
	/** Toggles the call lock state, preventing or allowing new participants from joining. */
	async toggleLock() {
		const method = this.locked ? "call.unlock" : "call.lock";
		await this.executeMethod(this.selfId ?? "", method, {});
	}
	/**
	* Toggles the hold state of the call (pauses/resumes local media transmission).
	*
	* Distinct from {@link Participant.toggleMute} which mutes individual tracks.
	*/
	async toggleHold() {
		if (this._holdState) await this.vertoManager.unhold();
		else await this.vertoManager.hold();
		this._holdState = !this._holdState;
	}
	/** @throws {UnimplementedError} Not yet implemented. Status tracked via {@link recording$}. */
	async startRecording() {
		throw new UnimplementedError();
	}
	/** @throws {UnimplementedError} Not yet implemented. Status tracked via {@link streaming$}. */
	async startStreaming() {
		throw new UnimplementedError();
	}
	/**
	* Replaces the call's custom metadata.
	* @param _meta - Metadata object to set.
	* @throws {UnimplementedError} Not yet implemented.
	*/
	async setMeta(_meta) {
		throw new UnimplementedError();
	}
	/**
	* Merges values into the call's custom metadata (unlike {@link setMeta} which replaces).
	* @param _meta - Metadata to merge.
	* @throws {UnimplementedError} Not yet implemented.
	*/
	async updateMeta(_meta) {
		throw new UnimplementedError();
	}
	/** Observable of layout layer positions for all participants. */
	get layoutLayers$() {
		return this.deferEmission(this.callEventsManager.layoutLayers$).pipe(takeUntil(this._destroyed$));
	}
	/** Current snapshot of layout layers. */
	get layoutLayers() {
		return this.callEventsManager.layoutLayers;
	}
	/**
	* Executes a Verto RPC method targeting a specific participant.
	*
	* Constructs call context (node_id, call_id, member_id) and sends the RPC request.
	*
	* @param target - Target member ID string, or a {@link MemberTarget} object.
	* @param method - Verto method name (e.g. `'call.mute'`, `'call.member.remove'`).
	* @param args - Parameters for the RPC method.
	* @returns The RPC response.
	* @throws {JSONRPCError} If the RPC call returns an error.
	*/
	async executeMethod(target, method, args) {
		const params = this.buildMethodParams(target, args);
		const request = buildRPCRequest({
			method,
			params
		});
		try {
			const response = await this.clientSession.execute(request);
			if (isJSONRPCErrorResponse(response)) throw new JSONRPCError(parseInt(response.result?.code ?? "0"), `Error response from method ${method}: ${response.result?.code} ${response.result?.message}`, void 0, void 0, request.id);
			return response;
		} catch (error) {
			logger$12.error(`[Call] Error executing method ${method} with params`, params, error);
			throw error;
		}
	}
	buildMethodParams(target, args) {
		const self = {
			node_id: this.nodeId ?? "",
			call_id: this.id,
			member_id: this.vertoManager.selfId ?? ""
		};
		if (typeof target === "object") return {
			...args,
			self,
			targets: [target]
		};
		return {
			...args,
			self,
			target: {
				node_id: this.nodeId ?? "",
				call_id: this.id,
				member_id: target
			}
		};
	}
	/** Observable of the current call status (e.g. `'ringing'`, `'connected'`). */
	get status$() {
		return this.publicCachedObservable("status$", () => merge(this._status$.asObservable(), this.vertoManager.signalingStatus$).pipe(distinctUntilChanged(), tap((status) => {
			this._lastMergedStatus = status;
		})));
	}
	/** Observable of the participants list, emits on join/leave/update. */
	get participants$() {
		return this.deferEmission(this.callEventsManager.participants$).pipe(takeUntil(this._destroyed$));
	}
	/** Observable of the local (self) participant. */
	get self$() {
		return this.deferEmission(this.callEventsManager.self$).pipe(takeUntil(this._destroyed$));
	}
	/** Observable indicating whether the call is being recorded. */
	get recording$() {
		return this.deferEmission(this.callEventsManager.recording$).pipe(takeUntil(this._destroyed$));
	}
	/** Observable indicating whether the call is being streamed. */
	get streaming$() {
		return this.deferEmission(this.callEventsManager.streaming$).pipe(takeUntil(this._destroyed$));
	}
	/** Observable indicating whether raise-hand priority is active. */
	get raiseHandPriority$() {
		return this.deferEmission(this.callEventsManager.raiseHandPriority$).pipe(takeUntil(this._destroyed$));
	}
	/** Observable indicating whether the call room is locked. */
	get locked$() {
		return this.deferEmission(this.callEventsManager.locked$).pipe(takeUntil(this._destroyed$));
	}
	/** Observable of custom metadata associated with the call. */
	get meta$() {
		return this.deferEmission(this.callEventsManager.meta$).pipe(takeUntil(this._destroyed$));
	}
	/** Observable of the call's capability flags. */
	get capabilities$() {
		return this.deferEmission(this.callEventsManager.capabilities$).pipe(takeUntil(this._destroyed$));
	}
	/** Observable of the current layout name. */
	get layout$() {
		return this.deferEmission(this.callEventsManager.layout$).pipe(takeUntil(this._destroyed$));
	}
	/** Current call status. */
	get status() {
		return this._lastMergedStatus;
	}
	/** Whether the call is currently being recorded. */
	get recording() {
		return this.callEventsManager.recording;
	}
	/** Whether the call is currently being streamed. */
	get streaming() {
		return this.callEventsManager.streaming;
	}
	/** Whether raise-hand priority is active. */
	get raiseHandPriority() {
		return this.callEventsManager.raiseHandPriority;
	}
	/** Whether the call room is locked. */
	get locked() {
		return this.callEventsManager.locked;
	}
	/** Current custom metadata of the call. */
	get meta() {
		return this.callEventsManager.meta;
	}
	/** Current layout name, or `undefined` if not set. */
	get layout() {
		return this.callEventsManager.layout;
	}
	/** Observable of available layout names. */
	get layouts$() {
		return this.deferEmission(this.callEventsManager.layouts$).pipe(takeUntil(this._destroyed$));
	}
	/** Current snapshot of available layout names. */
	get layouts() {
		return this.callEventsManager.layouts;
	}
	/** Observable of the local media stream (camera/microphone). */
	get localStream$() {
		return this.deferEmission(this.vertoManager.localStream$).pipe(takeUntil(this._destroyed$));
	}
	/** Current local media stream, or `null` if not available. */
	get localStream() {
		return this.vertoManager.localStream;
	}
	/** Observable of the remote media stream from the far end. */
	get remoteStream$() {
		return this.deferEmission(this.vertoManager.remoteStream$).pipe(takeUntil(this._destroyed$));
	}
	/** Current remote media stream, or `null` if not available. */
	get remoteStream() {
		return this.vertoManager.remoteStream;
	}
	/** Observable of custom user variables associated with the call. */
	get userVariables$() {
		return this.deferEmission(this._userVariables$.asObservable());
	}
	/** a copy of the current custom user variables of the call. */
	get userVariables() {
		return { ...this._userVariables$.value };
	}
	/** Merge current custom user variables of the call. */
	set userVariables(variables) {
		this._userVariables$.next({
			...this._userVariables$.value,
			...variables
		});
	}
	/** Observable of current network health issues (empty array = healthy). */
	get networkIssues$() {
		return this.deferEmission(this._networkIssues$.asObservable());
	}
	/** Current snapshot of network issues. */
	get networkIssues() {
		return this._networkIssues$.value;
	}
	/** Simple boolean health indicator derived from stats monitor. */
	get isNetworkHealthy$() {
		return this.deferEmission(this._isNetworkHealthy$.asObservable());
	}
	/** Whether the network is currently healthy. */
	get isNetworkHealthy() {
		return this._isNetworkHealthy$.value;
	}
	/** Rolling history of raw network metrics (RTT, jitter, packet loss, bitrate). */
	get networkMetrics$() {
		return this.deferEmission(this._networkMetrics$.asObservable());
	}
	/** Current snapshot of the metrics rolling window. */
	get networkMetrics() {
		return this._networkMetrics$.value;
	}
	/** Observable of MOS quality score (1-5) computed from stats metrics. */
	get qualityScore$() {
		return this.deferEmission(this._qualityScore$.asObservable());
	}
	/** Observable of simplified quality level (excellent/good/fair/poor/critical). */
	get qualityLevel$() {
		return this.deferEmission(this._qualityLevel$.asObservable());
	}
	/** Observable of the recovery pipeline state machine. */
	get recoveryState$() {
		return this.deferEmission(this._recoveryState$.asObservable());
	}
	/** Observable of recovery events (keyframe requested, ICE restart, etc.). */
	get recoveryEvent$() {
		return this.deferEmission(this._recoveryEvent$.asObservable());
	}
	/** Observable indicating whether the call is bandwidth-constrained. */
	get bandwidthConstrained$() {
		return this.deferEmission(this._bandwidthConstrained$.asObservable());
	}
	/** Observable that emits when server-pushed media params are applied. */
	get mediaParamsUpdated$() {
		return this.deferEmission(this._mediaParamsUpdated$.asObservable());
	}
	/**
	* @internal Emit a media params update event.
	* Called by the VertoManager when server-pushed media params are applied.
	*/
	emitMediaParamsUpdated(event) {
		this._mediaParamsUpdated$.next(event);
	}
	/** Request a video keyframe via RTCP PLI/FIR. */
	requestKeyframe() {
		this.vertoManager.requestKeyframe?.();
	}
	/** Force an ICE restart / re-INVITE. */
	async requestIceRestart() {
		await this.vertoManager.requestIceRestart?.();
	}
	/**
	* @internal Initialize resilience subsystems when the call reaches 'connected'.
	* Called from within the status subscription to wire stats and recovery.
	*/
	initResilienceSubsystems() {
		const pc = this.rtcPeerConnection;
		logger$12.debug(`[Call] initResilienceSubsystems: pc=${pc ? "exists" : "undefined"}, connectionState=${pc?.connectionState}`);
		if (!pc) {
			logger$12.warn("[Call] No peer connection available, skipping resilience init");
			return;
		}
		try {
			const prefs = PreferencesContainer.instance;
			this._statsMonitor = new RTCStatsMonitor(pc, {
				pollingIntervalMs: prefs.statsPollingInterval,
				baselineSamples: prefs.statsBaselineSamples,
				noAudioPacketThresholdMs: prefs.statsNoPacketThreshold,
				rttSpikeWarningMultiplier: prefs.statsRttSpikeMultiplier,
				rttSpikeCriticalMultiplier: prefs.statsRttSpikeMultiplier * RTT_CRITICAL_TO_WARNING_RATIO,
				packetLossWarningPercent: prefs.statsPacketLossThreshold * 100,
				packetLossCriticalPercent: prefs.statsPacketLossThreshold * 200,
				jitterSpikeMultiplier: prefs.statsJitterSpikeMultiplier,
				historyWindowSeconds: prefs.statsHistorySize
			});
			this._recoveryManager = new CallRecoveryManager({
				requestKeyframe: () => {
					try {
						if (this.vertoManager.requestKeyframeAll) this.vertoManager.requestKeyframeAll();
						else this.vertoManager.requestKeyframe?.();
					} catch {}
				},
				requestIceRestart: async (relayOnly) => {
					try {
						if (this.vertoManager.requestIceRestartAll) await this.vertoManager.requestIceRestartAll(relayOnly);
						else await this.vertoManager.requestIceRestart?.(relayOnly);
					} catch {
						return false;
					}
					return this.waitForPeerConnectionConnected();
				},
				disableVideo: () => {
					try {
						this.vertoManager.muteMainVideoInputDevice();
						logger$12.debug("[Call] Recovery manager disabled video");
					} catch {
						logger$12.debug("[Call] Recovery manager failed to disable video");
					}
				},
				enableVideo: () => {
					this.vertoManager.unmuteMainVideoInputDevice().catch(() => {
						logger$12.debug("[Call] Recovery manager failed to enable video");
					});
				},
				isNegotiating: () => this.vertoManager.mainPeerConnection.isNegotiating,
				isCallConnected: () => this._lastMergedStatus === "connected",
				getPeerConnectionState: () => pc.connectionState
			}, { signalingReady$: this.clientSession.authenticated$ }, {
				debounceTimeMs: prefs.recoveryDebounceTime,
				cooldownMs: prefs.recoveryCooldown,
				iceGracePeriodMs: prefs.iceDisconnectedGracePeriod,
				iceRestartTimeoutMs: prefs.iceRestartTimeout,
				maxAttempts: prefs.maxRecoveryAttempts,
				enableRelayFallback: prefs.enableRelayFallback,
				keyframeMaxBurst: prefs.keyframeMaxBurst,
				keyframeBurstWindowMs: prefs.keyframeBurstWindow,
				keyframeCooldownMs: prefs.keyframeCooldown
			});
			this.subscribeTo(this._statsMonitor.networkIssues$, (issues) => {
				this._networkIssues$.next(issues);
				this._recoveryManager?.reportNetworkIssues(issues);
			});
			this.subscribeTo(this._statsMonitor.isNetworkHealthy$, (healthy) => {
				this._isNetworkHealthy$.next(healthy);
			});
			this.subscribeTo(this._statsMonitor.networkMetrics$, (metrics) => {
				this._networkMetrics$.next(metrics);
				if (metrics.length > 0) {
					const latest = metrics[metrics.length - 1];
					const totalRecv = latest.audio.packetsReceived + latest.video.packetsReceived;
					const totalLost = latest.audio.packetsLost + latest.video.packetsLost;
					const lossPct = totalRecv + totalLost > 0 ? totalLost / (totalRecv + totalLost) * 100 : 0;
					const mos = computeMOS(latest.roundTripTime, latest.audio.jitter, lossPct);
					this._qualityScore$.next(mos);
					this._qualityLevel$.next(mosToQualityLevel(mos));
					if (latest.availableOutgoingBitrate !== void 0) this._recoveryManager?.reportBandwidth(latest.availableOutgoingBitrate / 1e3);
				}
			});
			this.subscribeTo(this._statsMonitor.criticalIssue$, (issue) => {
				this._recoveryManager?.pushTrigger({
					source: "stats",
					detail: `${issue.type}: ${issue.severity}`,
					issueType: issue.type
				});
			});
			this.subscribeTo(this._recoveryManager.recoveryState$, (state) => {
				this._recoveryState$.next(state);
			});
			this.subscribeTo(this._recoveryManager.recoveryEvent$, (event) => {
				this._recoveryEvent$.next(event);
				if (event.action === "max_attempts_reached") {
					logger$12.warn("[Call] All recovery attempts exhausted, terminating call");
					this.emitError({
						kind: "network",
						fatal: true,
						error: /* @__PURE__ */ new Error("Call recovery failed: all attempts exhausted"),
						callId: this.id
					});
				}
			});
			this.subscribeTo(this._recoveryManager.bandwidthConstrained$, (constrained) => {
				this._bandwidthConstrained$.next(constrained);
			});
			if (this._networkChange$) this.subscribeTo(this._networkChange$, (event) => {
				if (event.type === "offline") this._recoveryManager?.pushTrigger({
					source: "network",
					detail: "browser went offline"
				});
				else if (event.type === "online") this._recoveryManager?.handleWebSocketReconnect();
			});
			this.subscribeTo(this.clientSession.authenticated$.pipe(skip(1), filter(Boolean)), () => {
				logger$12.debug("[Call] WebSocket reconnected — notifying recovery manager");
				this._recoveryManager?.handleWebSocketReconnect();
			});
			this._statsMonitor.start();
			logger$12.debug("[Call] Resilience subsystems initialized for call", this.id);
		} catch (error) {
			logger$12.warn("[Call] Failed to initialize resilience subsystems:", error);
		}
	}
	/**
	* Wait for the underlying RTCPeerConnection to reach 'connected' after
	* triggering an ICE restart. Resolves true on success, false on failure
	* or if the state doesn't transition within the configured timeout.
	*
	* Polls connectionState directly because the recovery manager already
	* wraps this call in its own withTimeout(); a separate listener-based
	* implementation would race the outer timeout in subtle ways.
	*/
	async waitForPeerConnectionConnected() {
		const pc = this.rtcPeerConnection;
		if (!pc) return false;
		const deadline = Date.now() + PEER_CONNECTION_RECOVERY_WAIT_MS;
		for (;;) {
			const state = pc.connectionState;
			if (state === "connected") return true;
			if (state === "failed" || state === "closed") return false;
			if (Date.now() >= deadline) return false;
			await new Promise((resolve) => setTimeout(resolve, PEER_CONNECTION_RECOVERY_POLL_MS));
		}
	}
	/**
	* @internal Stop and destroy resilience subsystems (on disconnect/destroy).
	* Clears references so they can be re-created on reconnect.
	*/
	stopResilienceSubsystems() {
		try {
			this._statsMonitor?.destroy();
			this._recoveryManager?.destroy();
		} catch {}
		this._statsMonitor = void 0;
		this._recoveryManager = void 0;
	}
	/** @internal */
	createParticipant(memberId, selfId) {
		if (memberId === (selfId ?? this.vertoManager.selfId)) return this.participantFactory.createSelfParticipant(memberId);
		return this.participantFactory.createParticipant(memberId);
	}
	/** Observable of the current audio/video send/receive directions. */
	get mediaDirections$() {
		return this.deferEmission(this.vertoManager.mediaDirections$).pipe(takeUntil(this._destroyed$));
	}
	/** Current audio/video send/receive directions. */
	get mediaDirections() {
		return this.vertoManager.mediaDirections;
	}
	get participantsId$() {
		return this.cachedObservable("participantsId$", () => this.participants$.pipe(map((participants) => participants.map((participant) => participant.id))));
	}
	/**
	* Executes a raw JSON-RPC request on the client session.
	*
	* Lower-level than {@link executeMethod} — allows full control over the RPC request structure.
	*
	* @param request - Complete JSON-RPC request object.
	* @param options - Optional RPC execution options (timeout, etc.).
	* @returns The RPC response.
	* @throws {JSONRPCError} If the RPC call returns an error response.
	*/
	async execute(request, options) {
		return this.clientSession.execute(request, options);
	}
	/** Observable of the local participant's member ID. */
	get selfId$() {
		return this.vertoManager.selfId$;
	}
	/** Local participant's member ID, or `null` if not joined. */
	get selfId() {
		return this.vertoManager.selfId;
	}
	/** Observable of the server node ID handling this call. */
	get nodeId$() {
		return this.vertoManager.nodeId$;
	}
	/** Server node ID handling this call, or `null`. */
	get nodeId() {
		return this.vertoManager.nodeId;
	}
	isCallSessionEvent(event) {
		try {
			logger$12.debug("[Call] Checking if event is for this call session:", event);
			const callId = getValueFrom(event, "params.params.callID") ?? getValueFrom(event, "params.call_id");
			const roomSessionId = getValueFrom(event, "params.room_session_id");
			logger$12.debug(`[Call] Extracted session identifiers callID: ${callId} and roomSessionID: ${roomSessionId} from event:`);
			return callId === this.id || !!callId && this.callEventsManager.isCallIdValid(callId) || !!roomSessionId && this.callEventsManager.isRoomSessionIdValid(roomSessionId);
		} catch (error) {
			logger$12.error("[Call] Error checking if event is for this call session:", error);
			return false;
		}
	}
	get callSessionEvents$() {
		return this.cachedObservable("callSessionEvents$", () => this.clientSession.signalingEvent$.pipe(filter((event) => this.isCallSessionEvent(event)), tap((event) => {
			logger$12.debug("[Call] Received call session event:", event);
		}), takeUntil(this.destroyed$), share()));
	}
	/** Observable of call-updated events. */
	get callUpdated$() {
		return this.publicCachedObservable("callUpdated$", () => this.callSessionEvents$.pipe(filterAs(isCallUpdatedMetadata, "params"), takeUntil(this.destroyed$)));
	}
	/** Observable of member-joined events, emitted when a remote participant joins the call. */
	get memberJoined$() {
		return this.publicCachedObservable("memberJoined$", () => this.callSessionEvents$.pipe(filterAs(isMemberJoinedMetadata, "params"), takeUntil(this.destroyed$)));
	}
	/** Observable of member-left events, emitted when a participant leaves the call. */
	get memberLeft$() {
		return this.publicCachedObservable("memberLeft$", () => this.callSessionEvents$.pipe(filterAs(isMemberLeftMetadata, "params"), takeUntil(this.destroyed$)));
	}
	/** Observable of member-updated events (mute, volume, etc.). */
	get memberUpdated$() {
		return this.publicCachedObservable("memberUpdated$", () => this.callSessionEvents$.pipe(filterAs(isMemberUpdatedMetadata, "params"), takeUntil(this.destroyed$)));
	}
	/** Observable of member-talking events (speech start/stop). */
	get memberTalking$() {
		return this.publicCachedObservable("memberTalking$", () => this.callSessionEvents$.pipe(filterAs(isMemberTalkingMetadata, "params"), takeUntil(this.destroyed$)));
	}
	/** Observable of call state-change events. */
	get callStates$() {
		return this.publicCachedObservable("callStates$", () => this.callSessionEvents$.pipe(filterAs(isCallStateMetadata, "params"), takeUntil(this.destroyed$)));
	}
	/** Observable of layout-changed events. */
	get layoutUpdates$() {
		return this.publicCachedObservable("layoutUpdates$", () => this.callSessionEvents$.pipe(filterAs(isLayoutChangedMetadata, "params"), takeUntil(this.destroyed$)));
	}
	/** Underlying `RTCPeerConnection`, for advanced use cases. */
	get rtcPeerConnection() {
		return this.vertoManager.mainPeerConnection.peerConnection;
	}
	/** Observable of raw signaling events as plain objects. */
	get signalingEvent$() {
		return this.publicCachedObservable("signalingEvent$", () => this.callEvent$.pipe(map((event) => JSON.parse(JSON.stringify(event)))));
	}
	/**
	* Subscribe to a custom signaling event type on this call.
	*
	* Returns a cached observable that filters `callSessionEvents$` for events
	* whose `event_type` matches the given string. The observable completes
	* when the call is destroyed.
	*
	* Unlike `signalingEvent$` (which only emits known call-level event types),
	* this method also matches custom/user-defined event types.
	*
	* The SDK does not validate event type strings --- the server decides
	* whether a given type is valid.
	*
	* @param eventType - The event type to subscribe to (e.g. `'my.custom.event'`).
	* @returns An observable that emits matching signaling events.
	*
	* @example
	* ```ts
	* call.subscribe('my.custom.event').subscribe(event => {
	*   console.log('Custom event:', event);
	* });
	* ```
	*/
	subscribe(eventType) {
		const cached = this._customSubscriptions.get(eventType);
		if (cached) return cached;
		const filtered$ = this.callSessionEvents$.pipe(filter((event) => event.event_type === eventType), map((event) => JSON.parse(JSON.stringify(event))), takeUntil(this._destroyed$));
		this._sendVertoSubscribe(eventType).then(() => {
			this._customSubscriptions.set(eventType, filtered$);
		}, (error) => {
			this._customSubscriptions.delete(eventType);
			logger$12.warn(`[Call] verto.subscribe for '${eventType}' failed, not caching:`, error);
		});
		this._customSubscriptions.set(eventType, filtered$);
		return filtered$;
	}
	get webrtcMessages$() {
		return this.cachedObservable("webrtcMessages$", () => this.callSessionEvents$.pipe(filterAs(isWebrtcMessageMetadata, "params"), tap((event) => logger$12.debug("[Call] Event is a WebRTC message event:", event)), takeUntil(this.destroyed$), share()));
	}
	get callEvent$() {
		return this.cachedObservable("callEvent$", () => this.callSessionEvents$.pipe(filterAs(isSignalwireCallMetadata, "params"), tap((event) => logger$12.debug("[Call] Event is a call event:", event)), takeUntil(this.destroyed$), share()));
	}
	get layoutEvent$() {
		return this.cachedObservable("layoutEvent$", () => this.callEvent$.pipe(filterAs(isLayoutChangedMetadata, "params")));
	}
	/**
	* Hangs up the call and releases all resources.
	*
	* Sends a Verto `bye` to the server, transitions status to `'disconnecting'`,
	* then destroys the call. After this, the call instance is no longer usable.
	*
	* @example
	* ```ts
	* await call.hangup();
	* ```
	*/
	async hangup() {
		this._status$.next("disconnecting");
		try {
			await this.vertoManager.bye();
		} finally {
			this.destroy();
		}
	}
	/**
	* Sends DTMF digits on the call.
	*
	* @param dtmf - The digit string to send (e.g. `'1234#'`).
	*
	* @example
	* ```ts
	* await call.sendDigits('1234#');
	* ```
	*/
	async sendDigits(dtmf) {
		return this.vertoManager.sendDigits(dtmf);
	}
	/** Observable of WebRTC-specific signaling messages. */
	/** Observable of call-level signaling events. */
	/** Observable of layout-changed signaling events. */
	/**
	* Accepts an inbound call, optionally overriding media options for the answer.
	*
	* @param options - Optional media constraints for the answer (audio/video).
	*
	* @example
	* ```ts
	* // Accept with defaults
	* call.answer();
	*
	* // Accept audio-only
	* call.answer({ audio: true, video: false });
	* ```
	* @see {@link reject} to decline the call instead.
	* @see {@link answered$} to observe the acceptance state.
	*/
	answer(options) {
		this._answerMediaOptions = options;
		this._answered$.next(true);
	}
	/** Media options provided when answering. Used internally by the VertoManager. */
	get answerMediaOptions() {
		return this._answerMediaOptions;
	}
	/**
	* Rejects an inbound call, preventing media negotiation.
	*
	* @see {@link answer} to accept the call instead.
	* @see {@link answered$} to observe the rejection state.
	*/
	reject() {
		this._answered$.next(false);
	}
	/** Observable that emits `true` when answered, `false` when rejected. */
	get answered$() {
		return this.deferEmission(this._answered$.asObservable());
	}
	/**
	* Sets the call layout and participant positions.
	*
	* @param layout - Layout name (must be one of {@link layouts}).
	* @param positions - Map of member IDs to {@link VideoPosition} values.
	* @throws {InvalidParams} If the layout is not in the available {@link layouts}.
	*
	* @example
	* ```ts
	* await call.setLayout('grid-responsive', {
	*   [participantId]: 'reserved-0',
	* });
	* ```
	*/
	async setLayout(layout, positions) {
		if (!this.layouts.includes(layout)) throw new InvalidParams(`Layout ${layout} is not available in the current call layouts: ${this.layouts.join(", ")}`);
		const selfId = await firstValueFrom(this.selfId$.pipe(filter((id) => id !== null)));
		await this.executeMethod(selfId, "call.layout.set", {
			layout,
			positions
		});
	}
	/**
	* Transfers the call to another destination.
	*
	* @param options - Transfer configuration including the target destination.
	* @see {@link status$} to observe the transfer progress.
	*/
	async transfer(options) {
		return this.vertoManager.transfer(options);
	}
	/**
	* Set the local microphone gain as a percentage applied before transmission.
	*
	*   - `0`   = silent
	*   - `100` = unity (no change, default)
	*   - `200` = 2× digital boost (max; expect clipping / noise amplification)
	*
	* Values are clamped to [0, 200]. Engages the local audio pipeline on
	* first use (one-time cost).
	*
	* Note: this is a **digital** multiplier applied in a Web Audio GainNode
	* between your mic track and the RTCRtpSender — it does not change the
	* physical mic's hardware sensitivity. Browsers' autoGainControl can
	* fight the setting; call {@link setAutoGainControl}(false) for
	* predictable behaviour.
	*
	* @param value - Gain percentage (0..200; 100 = unity).
	*/
	setLocalMicrophoneGain(value) {
		const pipeline = this.vertoManager.ensureLocalAudioPipeline();
		if (!pipeline) {
			logger$12.warn("[Call] setLocalMicrophoneGain: audio pipeline unavailable");
			return;
		}
		const percent = Math.max(0, Math.min(200, value));
		pipeline.setGain(percent / 100);
	}
	/** Observable of the current local microphone gain (0..200, where 100 = unity). */
	get localMicrophoneGain$() {
		const pipeline = this.vertoManager.ensureLocalAudioPipeline();
		if (!pipeline) return of(100).pipe(takeUntil(this._destroyed$));
		return this.publicCachedObservable("localMicrophoneGain$", () => pipeline.gain$.pipe(map((multiplier) => multiplier * 100), takeUntil(this._destroyed$)));
	}
	/**
	* Observable of the RMS audio level of the local microphone, 0..1.
	* Emits at ~30fps while a mic track is active. Engages the local audio
	* pipeline on first subscription.
	*/
	get localAudioLevel$() {
		const pipeline = this.vertoManager.ensureLocalAudioPipeline();
		if (!pipeline) return of(0).pipe(takeUntil(this._destroyed$));
		return this.publicCachedObservable("localAudioLevel$", () => pipeline.level$.pipe(takeUntil(this._destroyed$), share()));
	}
	/**
	* Observable that is `true` while the local participant is speaking
	* (RMS level above the VAD threshold, with hold time to avoid flicker).
	*/
	get localSpeaking$() {
		const pipeline = this.vertoManager.ensureLocalAudioPipeline();
		if (!pipeline) return of(false).pipe(takeUntil(this._destroyed$));
		return this.publicCachedObservable("localSpeaking$", () => pipeline.speaking$.pipe(takeUntil(this._destroyed$), share()));
	}
	/**
	* Enable push-to-talk: while {@link setPushToTalkActive} has been called
	* with `false`, the microphone gain is forced to 0; calling
	* {@link setPushToTalkActive} with `true` restores the configured gain.
	* Use this instead of mute/unmute for instant talk/silence transitions
	* because it doesn't rebuild the track.
	*
	* This method installs the pipeline but does not attach any keyboard
	* listener — consumers bind the key themselves and call
	* {@link setPushToTalkActive} on keydown/keyup.
	*/
	enablePushToTalk() {
		const pipeline = this.vertoManager.ensureLocalAudioPipeline();
		if (!pipeline) {
			logger$12.warn("[Call] enablePushToTalk: audio pipeline unavailable");
			return;
		}
		pipeline.setPTTActive(false);
		this._pushToTalkEnabled = true;
	}
	/** Disable push-to-talk; mic gain returns to the configured value. */
	disablePushToTalk() {
		this.vertoManager.localAudioPipeline?.setPTTActive(true);
		this._pushToTalkEnabled = false;
	}
	/**
	* While push-to-talk is enabled, sets the talk state. `true` = transmitting,
	* `false` = silent. No-op if push-to-talk has not been enabled.
	*/
	setPushToTalkActive(active) {
		if (!this._pushToTalkEnabled) return;
		this.vertoManager.localAudioPipeline?.setPTTActive(active);
	}
	/**
	* Toggle echo cancellation on the local mic at runtime. Applied via
	* `track.applyConstraints`; browsers that don't honour runtime constraints
	* (notably iOS Safari) fall back to re-acquiring the track with the new
	* constraint set and plumbing the replacement through the local audio
	* pipeline if one is active.
	*/
	async setEchoCancellation(enabled) {
		await this.vertoManager.updateMediaConstraints({ audio: { echoCancellation: enabled } });
	}
	/** Toggle browser noise suppression on the local mic at runtime. */
	async setNoiseSuppression(enabled) {
		await this.vertoManager.updateMediaConstraints({ audio: { noiseSuppression: enabled } });
	}
	/** Toggle browser automatic gain control on the local mic at runtime. */
	async setAutoGainControl(enabled) {
		await this.vertoManager.updateMediaConstraints({ audio: { autoGainControl: enabled } });
	}
	/**
	* Observable of the aggregate remote audio level, 0..1 RMS. The server
	* delivers a single mixed audio stream for all remote participants — this
	* meter reports that mix. Per-participant audio is not available client-side.
	*
	* Engages a shared AudioContext on first subscription (cheap — one
	* AnalyserNode, no GainNode, no destination) so it does not affect the
	* caller's audio element playback.
	*/
	get remoteAudioLevel$() {
		return this.publicCachedObservable("remoteAudioLevel$", () => {
			this._remoteAudioMeter ??= new RemoteAudioMeter();
			const meter = this._remoteAudioMeter;
			this.subscribeTo(this.vertoManager.remoteStream$, (stream) => {
				meter.setStream(stream);
			});
			return meter.level$.pipe(takeUntil(this._destroyed$), share());
		});
	}
	/** Destroys the call, releasing all resources and subscriptions. */
	destroy() {
		if (this._status$.value === "destroyed") return;
		this._status$.next("destroyed");
		this.stopResilienceSubsystems();
		this._remoteAudioMeter?.destroy();
		this._remoteAudioMeter = null;
		this.vertoManager.destroy();
		this.callEventsManager.destroy();
		super.destroy();
	}
	/**
	* @internal Send a verto.subscribe message to add an event type to the
	* server's subscription list for this call. Returns the underlying RPC
	* promise so callers can decide whether to cache the observable on success
	* or retry on failure.
	*/
	async _sendVertoSubscribe(eventType) {
		const message = VertoSubscribe({
			sessid: this.id,
			eventChannel: [eventType]
		});
		const params = {
			callID: this.id,
			node_id: this.vertoManager.nodeId ?? "",
			message
		};
		await this.clientSession.execute(WebrtcVerto(params));
	}
};

//#endregion
//#region src/managers/CallFactory.ts
/**
* Infers the semantic error category from a raw Error thrown by VertoManager
* or an RTCPeerConnection layer.
*/
function inferCallErrorKind(error) {
	if (error instanceof RPCTimeoutError) return "timeout";
	if (error instanceof JSONRPCError) return "signaling";
	if (error instanceof MediaTrackError) return "media";
	if (error instanceof WebSocketConnectionError || error instanceof TransportConnectionError) return "network";
	return "internal";
}
/** JSON-RPC error codes that ClientSessionManager treats as recoverable at the
* session level. Surfacing one of these against an in-flight call should not
* destroy the call, because the session will reauthenticate and any pending
* RPC can then be retried. */
const RECOVERABLE_RPC_CODES = new Set([
	RPC_ERROR_REQUESTER_VALIDATION_FAILED,
	RPC_ERROR_AUTHENTICATION_FAILED,
	RPC_ERROR_INVALID_PARAMS
]);
/** Determines whether an error should be fatal (destroy the call). */
function isFatalError(error) {
	if (error instanceof VertoPongError) return false;
	if (error instanceof MediaTrackError) return false;
	if (error instanceof RPCTimeoutError) return false;
	if (error instanceof JSONRPCError && RECOVERABLE_RPC_CODES.has(error.code)) return false;
	return true;
}
/**
* Factory for creating WebRTCCall instances with proper manager wiring.
* Eliminates circular dependencies by centralizing Call and Manager creation.
*/
var CallFactory = class {
	constructor(sessionManager, deviceController, attachManager, webRTCApiProvider, networkChange$) {
		this.sessionManager = sessionManager;
		this.deviceController = deviceController;
		this.attachManager = attachManager;
		this.webRTCApiProvider = webRTCApiProvider;
		this.networkChange$ = networkChange$;
	}
	/**
	* Create a new WebRTCCall with properly initialized managers
	*/
	createCall(address, options) {
		return new WebRTCCall(this.sessionManager, options, {
			initializeManagers: (callInstance) => {
				return {
					vertoManager: new WebRTCVertoManager(callInstance, this.attachManager, this.deviceController, this.webRTCApiProvider, {
						nodeId: options.nodeId,
						onError: (error) => {
							const callError = {
								kind: inferCallErrorKind(error),
								fatal: isFatalError(error),
								error,
								callId: callInstance.id
							};
							callInstance.emitError(callError);
						},
						onModifyFailed: () => {
							callInstance.notifyModifyFailed();
						}
					}),
					callEventsManager: new CallEventsManager(callInstance)
				};
			},
			deviceController: this.deviceController,
			networkChange$: this.networkChange$
		}, address);
	}
};

//#endregion
//#region src/behaviors/Collection.ts
const logger$11 = getLogger();
var Fetcher = class {
	constructor(endpoint, params, http) {
		this.endpoint = endpoint;
		this.http = http;
		this.filter = (_item) => true;
		this.mapper = (item) => item;
		this.nextUrl = `${this.endpoint}?${params}`;
	}
	async next() {
		if (!this.nextUrl) {
			this.hasMore = false;
			return [];
		}
		const response = await this.http.request({
			...GET_PARAMS,
			url: this.nextUrl
		});
		if (response.ok && !!response.body) {
			const result = JSON.parse(response.body);
			this.nextUrl = result.links.next;
			this.hasMore = !!this.nextUrl;
			return result.data.filter(this.filter).map(this.mapper);
		}
		logger$11.error("Failed to fetch entity");
		return [];
	}
	async id(v) {
		const response = await this.http.request({
			...GET_PARAMS,
			url: `${this.endpoint}/${String(v)}`
		});
		if (response.ok && !!response.body) return JSON.parse(response.body);
	}
};
var EntityCollection = class extends Destroyable {
	constructor(fetchController, update$, onError) {
		super();
		this.fetchController = fetchController;
		this.update$ = update$;
		this.onError = onError;
		this.collectionData = /* @__PURE__ */ new Map();
		this.observablesRegistry = /* @__PURE__ */ new Map();
		this.upsertData = (data) => {
			if (!data.id) return;
			const existing = this.collectionData.get(data.id) ?? {};
			const updated = {};
			const allKeys = new Set([...Object.keys(existing), ...Object.keys(data)]);
			for (const key of allKeys) {
				const existingVal = existing[key];
				const newVal = data[key];
				if (newVal !== void 0 && existingVal !== void 0 && typeof existingVal === "object" && existingVal !== null && !Array.isArray(existingVal) && typeof newVal === "object" && newVal !== null && !Array.isArray(newVal)) updated[key] = {
					...existingVal,
					...newVal
				};
				else if (newVal !== void 0) updated[key] = newVal;
				else updated[key] = existingVal;
			}
			this.collectionData.set(data.id, updated);
			this.observablesRegistry.get(data.id)?.next(updated);
			this._values$.next(Array.from(this.collectionData.values()));
		};
		this._loading$ = this.createBehaviorSubject(false);
		this._values$ = this.createReplaySubject(1);
		this._hasMore$ = this.createBehaviorSubject(true);
		this.subscribeTo(this.update$, this.upsertData);
		this.hasMore$ = defer(() => from(this.init())).pipe(switchMap(() => this._hasMore$), distinctUntilChanged(), shareReplay(1), takeUntil(this.destroyed$));
	}
	get loading$() {
		return this._loading$.asObservable();
	}
	get loading() {
		return this._loading$.value;
	}
	get values$() {
		return this._values$.asObservable();
	}
	get hasMore() {
		return this.fetchController.hasMore ?? true;
	}
	get updated$() {
		return this.cachedObservable("updated$", () => this._loading$.pipe(distinctUntilChanged(), skip(1), filter((loading) => !loading), map(() => void 0), takeUntil(this.destroyed$)));
	}
	get values() {
		return Array.from(this.collectionData.values());
	}
	async init() {
		if (this.fetchController.hasMore === false) {
			this._hasMore$.next(false);
			return;
		}
		await this.fetchMore();
	}
	async fetchMore() {
		try {
			this._loading$.next(true);
			(await this.fetchController.next()).forEach(this.upsertData);
			this._hasMore$.next(this.fetchController.hasMore ?? false);
			this._loading$.next(false);
		} catch (error) {
			logger$11.error(`Failed to fetch initial collection data`, error);
			this._hasMore$.next(this.fetchController.hasMore ?? false);
			this._loading$.next(false);
			this.onError?.(new CollectionFetchError("fetchMore", error));
		}
	}
	async tryFetch(key, value) {
		try {
			this._loading$.next(true);
			const data = await this.fetchController[key]?.(value);
			this._loading$.next(false);
			if (data) this.upsertData(data);
			return data;
		} catch (error) {
			logger$11.error(`Failed to fetch data for (${String(key)}:${String(value)}) :`, error);
			this._loading$.next(false);
			this.onError?.(new CollectionFetchError(`tryFetch(${String(key)})`, error));
		}
	}
	get$(id) {
		if (!this.observablesRegistry.has(id)) {
			this.observablesRegistry.set(id, new ReplaySubject(1));
			const data = this.collectionData.get(id);
			if (data) this.observablesRegistry.get(id)?.next(data);
			else this.tryFetch("id", id);
		}
		return this.observablesRegistry.get(id)?.asObservable();
	}
	async find$(key, value) {
		const data = Array.from(this.collectionData.values()).find((item) => item[key] === value) ?? await this.tryFetch(key, value);
		return data ? this.get$(data.id) : void 0;
	}
	loadMore() {
		if (this.fetchController.hasMore !== false) this.fetchMore();
	}
	destroy() {
		this.observablesRegistry.forEach((subject) => subject.complete());
		this.observablesRegistry.clear();
		super.destroy();
	}
};
var EntityCollectionTransformed = class {
	constructor(originalCollection, filter$2 = (i) => !!i, mapper = (item) => item) {
		this.originalCollection = originalCollection;
		this.filter = filter$2;
		this.mapper = mapper;
	}
	get loading$() {
		return this.originalCollection.loading$;
	}
	get loading() {
		return this.originalCollection.loading;
	}
	get hasMore$() {
		return this.originalCollection.hasMore$;
	}
	get hasMore() {
		return this.originalCollection.hasMore;
	}
	get values() {
		return this.originalCollection.values.filter(this.filter).map(this.mapper);
	}
	get values$() {
		return this._values$ ??= this.originalCollection.values$.pipe(map((values) => values.filter(this.filter).map(this.mapper)));
	}
	get$(id) {
		const original$ = this.originalCollection.get$(id);
		return !original$ ? original$ : original$.pipe(pipe(filter(this.filter), map(this.mapper)));
	}
	async find$(key, value) {
		const original$ = await this.originalCollection.find$(key, value);
		return !original$ ? original$ : original$.pipe(pipe(filter(this.filter), map(this.mapper)));
	}
	loadMore() {
		this.originalCollection.loadMore();
	}
	destroy() {
		this.originalCollection.destroy();
	}
};

//#endregion
//#region src/core/entities/Address.ts
/**
* Represents a contact or room in the directory.
*
* Provides identity metadata, conversation history, text messaging,
* and activity state for an address entry.
*/
var Address = class extends Destroyable {
	constructor(addressId, conversationManager, addressProvider) {
		super();
		this.addressId = addressId;
		this.conversationManager = conversationManager;
		this.addressProvider = addressProvider;
		this.initConversationMessages = async () => {
			this._conversationMessages = this._conversationMessages ?? await this.conversationManager.getConversationMessageCollection(this.id);
			if (this._conversationMessages.hasMore) this._conversationMessages.loadMore();
			return this._conversationMessages;
		};
		this.textMessages$ = defer(this.initConversationMessages).pipe(map(() => this.textMessage), shareReplay(1), takeUntil(this.destroyed$));
		this.history$ = defer(this.initConversationMessages).pipe(map(() => this.history), shareReplay(1), takeUntil(this.destroyed$));
		this._state$ = this.createBehaviorSubject(null);
	}
	/** @internal */
	upnext(state) {
		const update = {
			...this._state$.value,
			...state
		};
		this._state$.next(update);
	}
	/** @internal */
	get state() {
		return this._state$.value;
	}
	/** Unique address identifier. */
	get id() {
		return this.addressId;
	}
	/** Address name (resource identifier). */
	get name() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.name;
	}
	/** ISO timestamp of when the address was created. */
	get createdAt() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.created_at;
	}
	/** Default communication channel URI (video for rooms, audio otherwise). */
	get defaultChannel() {
		return this.type === "room" ? this.channels.video : this.channels.audio;
	}
	/** Observable of the human-readable display name. */
	get displayName$() {
		return this.cachedObservable("displayName$", () => this._state$.pipe(filterNull(), map((state) => state.display_name), takeUntil(this.destroyed$)));
	}
	/** Human-readable display name. */
	get displayName() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.display_name;
	}
	/** Observable of the preview image URL. */
	get previewUrl$() {
		return this.cachedObservable("previewUrl$", () => this._state$.pipe(filterNull(), map((state) => state.preview_url), takeUntil(this.destroyed$)));
	}
	/** Preview image URL. */
	get previewUrl() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.preview_url;
	}
	/** Observable of the cover image URL. */
	get coverUrl$() {
		return this.cachedObservable("coverUrl$", () => this._state$.pipe(filterNull(), shareReplay(1), map((state) => state.cover_url), takeUntil(this.destroyed$)));
	}
	/** Cover image URL. */
	get coverUrl() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.cover_url;
	}
	/** Observable of the underlying resource ID. */
	get resourceId$() {
		return this.cachedObservable("resourceId$", () => this._state$.pipe(filterNull(), shareReplay(1), map((state) => state.resource_id), takeUntil(this.destroyed$)));
	}
	/** Underlying resource ID. */
	get resourceId() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.resource_id;
	}
	/** Observable of the resource type (e.g. `'room'`, `'subscriber'`). */
	get type$() {
		return this.cachedObservable("type$", () => this._state$.pipe(filterNull(), shareReplay(1), map((state) => state.type), takeUntil(this.destroyed$)));
	}
	/** Resource type (e.g. `'room'`, `'subscriber'`). */
	get type() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.type;
	}
	/** Observable of available communication channels (audio, video, messaging). */
	get channels$() {
		return this.cachedObservable("channels$", () => this._state$.pipe(filterNull(), shareReplay(1), map((state) => state.channels), takeUntil(this.destroyed$)));
	}
	/** Available communication channels. */
	get channels() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.channels;
	}
	/** Whether the address (room) is locked. */
	get locked() {
		if (!this._state$.value) throw new DependencyError("state not initialized");
		return this._state$.value.locked;
	}
	/** Observable indicating whether the address (room) is locked. */
	get locked$() {
		return this.cachedObservable("locked$", () => this._state$.pipe(filterNull(), shareReplay(1), map((state) => state.locked), takeUntil(this.destroyed$)));
	}
	/**
	* Sends a text message to this address.
	*
	* @param text - The message text to send.
	*
	* @example
	* ```ts
	* await address.sendText('Hello!');
	* ```
	*/
	async sendText(text) {
		return this.conversationManager.sendText(text, this.id);
	}
	/**
	* Collection of text messages for this address, with pagination support.
	*
	* Returns `undefined` until {@link textMessages$} has been subscribed to (lazy-loaded).
	* Filters to `'chat'` subtype messages from the conversation.
	*
	* @see {@link textMessages$} to trigger lazy loading.
	* @see {@link sendText} to send a new message.
	*/
	get textMessage() {
		if (!this._conversationMessages) return;
		this._textMessages$ = this._textMessages$ ?? new EntityCollectionTransformed(this._conversationMessages, (item) => item.subtype === "chat", (item) => ({
			id: item.id,
			text: item.text,
			created: item.ts,
			fromAddress$: this.addressProvider.get$(item.from_fabric_address_id)
		}));
		return this._textMessages$;
	}
	/**
	* Collection of call history entries for this address, with pagination support.
	*
	* Returns `undefined` until {@link history$} has been subscribed to (lazy-loaded).
	* Filters to `'log'` subtype messages including kind, status, start/end times.
	*
	* @see {@link history$} to trigger lazy loading.
	*/
	get history() {
		if (!this._conversationMessages) return;
		this._history$ = this._history$ ?? new EntityCollectionTransformed(this._conversationMessages, (item) => item.subtype === "log", (item) => ({
			id: item.id,
			kind: item.kind,
			status: item.details.status,
			started: item.details.start_time,
			ended: item.details.end_time,
			fromAddress$: this.addressProvider.get$(item.from_fabric_address_id)
		}));
		return this._history$;
	}
	/** Observable of active call states for this address. @throws {UnimplementedError} Requires presence support. */
	get activity$() {
		throw new UnimplementedError();
	}
	/** Active call states for this address. @throws {UnimplementedError} Requires presence support. */
	get activity() {
		throw new UnimplementedError();
	}
};

//#endregion
//#region src/core/utils.ts
const logger$10 = getLogger();
const isRPCConnectResult = (e) => {
	logger$10.debug("isRPCConnectResult check:", e);
	if (!e || typeof e !== "object") return false;
	const result = e;
	const is = typeof result.identity === "string" && typeof result.protocol === "string" && typeof result.authorization === "object" && typeof result.authorization.jti === "string" && typeof result.authorization.project_id === "string" && typeof result.authorization.fabric_subscriber === "object";
	logger$10.debug("isRPCConnectResult check result:", is);
	return is;
};
var PendingRPC = class PendingRPC {
	static {
		this.defaultTimeoutMs = 5e3;
	}
	constructor(request, responses$, options) {
		this.id = v4();
		logger$10.debug(`[PendingRPC(${this.id}) request:${request.id}: method:${request.method}] Creating PendingRPC`);
		this.request = request;
		const timeoutMs = options?.timeoutMs ?? PendingRPC.defaultTimeoutMs;
		const signal = options?.signal;
		this.promise = new Promise((resolve, reject) => {
			if (signal?.aborted) {
				reject(new DOMException("The operation was aborted", "AbortError"));
				return;
			}
			let isSettled = false;
			const subscription = race(responses$.pipe(filter((result) => result.id === request.id), take(1)), new Observable((subscriber) => {
				const timer$1 = setTimeout(() => {
					subscriber.error(new RPCTimeoutError(request.id, timeoutMs));
				}, timeoutMs);
				return () => clearTimeout(timer$1);
			}), signal ? new Observable((subscriber) => {
				const abortHandler = () => {
					subscriber.error(new DOMException("The operation was aborted", "AbortError"));
				};
				signal.addEventListener("abort", abortHandler);
				return () => signal.removeEventListener("abort", abortHandler);
			}) : NEVER).subscribe({
				next: (response) => {
					isSettled = true;
					if (response.error) {
						const rpcError = new JSONRPCError(response.error.code, response.error.message, response.error.data, void 0, request.id);
						logger$10.debug(`[PendingRPC(${this.id}) request:${request.id}] Rejecting promise with RPC error:`, rpcError);
						reject(rpcError);
					} else {
						logger$10.debug(`[PendingRPC(${this.id}) request:${request.id}] Resolving promise with response:`, response);
						resolve(response);
					}
					subscription.unsubscribe();
				},
				error: (error) => {
					logger$10.debug(`[PendingRPC(${this.id}) request:${request.id}] Rejecting promise with error:`, error);
					isSettled = true;
					reject(error);
					subscription.unsubscribe();
				},
				complete: () => {
					logger$10.debug(`[PendingRPC(${this.id}) request:${request.id}] Observable completed`);
					if (!isSettled) reject(new RPCTimeoutError(request.id, timeoutMs));
					subscription.unsubscribe();
				}
			});
		});
	}
	async then(onfulfilled, onrejected) {
		return this.promise.then(onfulfilled, onrejected);
	}
	async catch(onrejected) {
		return this.promise.catch(onrejected);
	}
	async finally(onfinally) {
		return this.promise.finally(onfinally);
	}
};

//#endregion
//#region src/managers/ClientSessionManager.ts
const logger$9 = getLogger();
const getAddressSearchURI = (options) => {
	const to = options.to?.split("?")[0];
	const from$1 = options.from?.startsWith("subscriber://") ? options.from.replace("subscriber://", "") : options.from;
	const name = to ?? from$1;
	if (!name) throw new UnexpectedError("Error building Address name");
	return name;
};
var ClientSessionManager = class extends Destroyable {
	constructor(getCredential, transport, storage, authorizationStateKey, deviceController, attachManager, webRTCApiProvider, dpopManager, networkChange$) {
		super();
		this.getCredential = getCredential;
		this.transport = transport;
		this.storage = storage;
		this.authorizationStateKey = authorizationStateKey;
		this.attachManager = attachManager;
		this.dpopManager = dpopManager;
		this.callCreateTimeout = 6e3;
		this.agent = `signalwire-js/4.0.0`;
		this.eventAcks = true;
		this.authorizationState$ = this.createReplaySubject(1);
		this.connectVersion = {
			major: 4,
			minor: 0,
			revision: 0
		};
		this._authorization$ = this.createBehaviorSubject(void 0);
		this._errors$ = this.createReplaySubject(1);
		this._authState$ = this.createBehaviorSubject({ kind: "unauthenticated" });
		this._wasClientBound = false;
		this._userInfo$ = this.createBehaviorSubject(null);
		this._calls$ = this.createBehaviorSubject({});
		this._iceServers$ = this.createBehaviorSubject([]);
		attachManager.setSession(this);
		this.callFactory = new CallFactory(this, deviceController, attachManager, webRTCApiProvider, networkChange$);
		this.initialized$ = defer(() => from(this.init())).pipe(shareReplay(1), takeUntil(this.destroyed$));
	}
	get incomingCalls$() {
		return this.cachedObservable("incomingCalls$", () => this.calls$.pipe(map((calls) => calls.filter((call) => call.direction === "inbound"))));
	}
	get incomingCalls() {
		return Object.values(this._calls$.value).filter((call) => call.direction === "inbound");
	}
	get userInfo$() {
		return this._userInfo$.asObservable();
	}
	get userInfo() {
		return this._userInfo$.value;
	}
	get calls$() {
		return this.cachedObservable("calls$", () => this._calls$.pipe(map((calls) => Object.values(calls))));
	}
	get calls() {
		return Object.values(this._calls$.value);
	}
	get iceServers() {
		return this._iceServers$.value;
	}
	get authorization$() {
		return this._authorization$.asObservable();
	}
	get authorization() {
		return this._authorization$.value;
	}
	get errors$() {
		return this._errors$.asObservable();
	}
	get authenticated$() {
		return this._authState$.pipe(map((state) => state.kind === "authenticated"), distinctUntilChanged());
	}
	get authenticated() {
		return this._authState$.value.kind === "authenticated";
	}
	/**
	* Whether this session is client-bound (using a Client Bound SAT).
	* When client-bound, DPoP proof creation failures are treated as
	* authentication errors rather than silently degraded.
	* @internal
	*/
	get clientBound() {
		return this._wasClientBound;
	}
	/** @internal Current auth state for debugging/testing. */
	get authState() {
		return this._authState$.value;
	}
	/**
	* Set the directory instance
	* Called by SignalWire after directory is created
	* @internal
	*/
	setDirectory(directory) {
		this._directory = directory;
	}
	async execute(request, options) {
		try {
			return await this.transport.execute(request, options);
		} catch (error) {
			logger$9.debug("[Session] Execute Error", error);
			this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
			throw error;
		}
	}
	send(message) {
		this.transport.send(message);
	}
	async init() {
		await this.loadAuthorizationStateFromStorage();
		this.setupMessageHandlers();
		return true;
	}
	setupMessageHandlers() {
		logger$9.debug("[Session] Setting up message handlers");
		this.subscribeTo(this.authStateEvent$, async (authStateEvent) => {
			logger$9.debug("[Session] Authorization state event received:", authStateEvent);
			try {
				await this.updateAuthorizationStateInStorage(authStateEvent.authorization_state);
			} catch (error) {
				logger$9.error("[Session] Failed to handle authorization state update:", error);
				this._errors$.next(new AuthStateHandlerError(error));
			}
		});
		this.subscribeTo(this.transport.connectionStatus$.pipe(filter((status) => status === "disconnected" || status === "reconnecting")), () => {
			if (this._authState$.value.kind === "authenticated") this._authState$.next({ kind: "unauthenticated" });
		});
		this.subscribeTo(this.transport.connectionStatus$.pipe(filter((status) => status === "connected"), exhaustMap(() => {
			logger$9.debug("[Session] Connection established, initiating authentication");
			return from(this.authenticate()).pipe(catchError((error) => {
				this.handleAuthenticationError(error).catch((err) => {
					logger$9.error("[Session] Error handling authentication failure:", err);
				});
				return EMPTY;
			}));
		})), void 0);
		this.subscribeTo(this.vertoInvite$, async (invite) => {
			logger$9.debug("[Session] Verto invite received:", invite);
			try {
				await this.createInboundCall(invite);
			} catch (error) {
				logger$9.error("[Session] Error handling Verto invite:", error);
				this._errors$.next(new VertoInviteHandlerError(error));
			}
		});
		this.subscribeTo(this.vertoAttach$, async (attach) => {
			logger$9.debug("[Session] Verto attach received:", attach);
			try {
				await this.handleVertoAttach(attach);
			} catch (error) {
				logger$9.error("[Session] Error handling Verto attach:", error);
				this._errors$.next(new VertoAttachHandlerError(error));
			}
		});
	}
	async loadAuthorizationStateFromStorage() {
		try {
			const storedState = await this.storage.getItem(this.authorizationStateKey);
			this.authorizationState$.next(storedState ?? void 0);
		} catch (error) {
			logger$9.error("Failed to retrieve authorization state from storage:", error);
			this.authorizationState$.next(void 0);
		}
	}
	async updateAuthorizationStateInStorage(authorizationState) {
		if (!authorizationState) {
			logger$9.debug("[Session] Removing authorization state from storage");
			try {
				await this.storage.removeItem(this.authorizationStateKey);
				this.authorizationState$.next(void 0);
			} catch (error) {
				logger$9.error("Failed to remove authorization state from storage:", error);
				throw error;
			}
			return;
		}
		try {
			logger$9.debug("[Session] Updating authorization state in storage");
			await this.storage.setItem(this.authorizationStateKey, authorizationState);
			this.authorizationState$.next(authorizationState);
		} catch (error) {
			logger$9.error("Failed to retrieve authorization state from storage:", error);
			throw error;
		}
	}
	get authStateEvent$() {
		return this.cachedObservable("authStateEvent$", () => this.signalingEvent$.pipe(tap((msg) => {
			logger$9.debug("[Session] Received incoming message:", msg);
		}), filterAs(isSignalwireAuthorizationStateMetadata, "params"), tap((event) => {
			logger$9.debug("[Session] Authorization state event received:", event.authorization_state);
		})));
	}
	get signalingEvent$() {
		return this.cachedObservable("signalingEvent$", () => this.transport.incomingEvent$.pipe(filterAs(isSignalwireRequest, "params"), share()));
	}
	get vertoInvite$() {
		return this.cachedObservable("vertoInvite$", () => this.signalingEvent$.pipe(filter(isWebrtcMessageMetadata), filter((event) => isVertoInviteMessage(event.params)), map((event) => ({
			node_id: event.node_id,
			...event.params.params
		}))));
	}
	get vertoAttach$() {
		return this.cachedObservable("vertoAttach$", () => this.signalingEvent$.pipe(filter(isWebrtcMessageMetadata), filter((event) => isVertoAttachMessage(event.params)), map((event) => ({
			node_id: event.node_id,
			...event.params.params
		}))));
	}
	get contexts() {
		return [];
	}
	get eventing() {
		return [];
	}
	get topics() {
		return [];
	}
	get authentication() {
		const credential = this.getCredential();
		if (!credential.token) throw new DependencyError("Credential token is undefined");
		return { jwt_token: credential.token };
	}
	async connect() {
		await firstValueFrom(this.initialized$);
		await this.transport.connect();
		await firstValueFrom(this.authenticated$.pipe(takeUntil(this.destroyed$), filter(Boolean), take(1), timeout({ first: 15e3 })));
	}
	async handleAuthenticationError(error) {
		logger$9.error("Authentication error:", error);
		const isRecoverableAuthError = error instanceof JSONRPCError && (error.code === RPC_ERROR_REQUESTER_VALIDATION_FAILED || error.code === RPC_ERROR_INVALID_PARAMS || error.code === RPC_ERROR_AUTHENTICATION_FAILED);
		const hasStoredState = await firstValueFrom(this.authorizationState$.pipe(take(1))) !== void 0;
		if (isRecoverableAuthError && hasStoredState) {
			logger$9.debug("[Session] Recoverable auth error — cleaning up stored state and reconnecting fresh");
			try {
				await this.cleanupStoredConnectionParams();
			} catch (cleanupError) {
				logger$9.error("Failed to cleanup stored connection params:", cleanupError);
			} finally {
				this.transport.reconnect();
			}
		} else this._errors$.next(error);
	}
	async cleanupStoredConnectionParams() {
		await this.transport.setProtocol(void 0);
		await this.updateAuthorizationStateInStorage(void 0);
		this._authorization$.next(void 0);
	}
	async updateAuthState(authorization_state) {
		try {
			await this.storage.setItem(this.authorizationStateKey, authorization_state);
		} catch (error) {
			logger$9.error("Failed to update authorization state in storage:", error);
			this._errors$.next(new AuthStateHandlerError(error));
		}
	}
	async reauthenticate(token, dpopToken, options) {
		logger$9.debug("[Session] Re-authenticating session");
		try {
			let resolvedDpopToken = dpopToken;
			if (!resolvedDpopToken && this.dpopManager?.initialized) try {
				resolvedDpopToken = await this.dpopManager.createRpcProof({ method: "signalwire.reauthenticate" });
			} catch (error) {
				if (this.clientBound) throw error;
				logger$9.warn("[Session] Failed to create DPoP proof for reauthenticate:", error);
			}
			const request = RPCReauthenticate({
				project: this._authorization$.value?.project_id ?? "",
				jwt_token: token,
				...resolvedDpopToken ? { dpop_token: resolvedDpopToken } : {}
			});
			await lastValueFrom(from(this.transport.execute(request)).pipe(throwOnRPCError(), take(1), catchError((err) => {
				logger$9.error("[Session] Re-authentication RPC failed:", err);
				throw err;
			})));
			if (options?.clientBound) this._wasClientBound = true;
			logger$9.debug("[Session] Re-authentication successful, updating stored auth state");
		} catch (error) {
			logger$9.error("[Session] Re-authentication failed:", error);
			this._errors$.next(new AuthStateHandlerError(error));
			throw error;
		}
	}
	async authenticate() {
		logger$9.debug("[Session] Starting authentication process");
		const persistedParams = await firstValueFrom(combineLatest({
			protocol: this.transport.protocol$,
			authorization_state: this.authorizationState$
		}).pipe(take(1)));
		logger$9.debug("[Session] Persisted params:\n", {
			protocol: persistedParams.protocol,
			authStateLength: persistedParams.authorization_state?.length
		});
		const hasReconnectState = persistedParams.authorization_state && persistedParams.protocol;
		const storedToken = this.getCredential().token;
		const isReconnect = hasReconnectState && storedToken;
		let dpopToken;
		if (isReconnect) logger$9.debug("[Session] Reconnecting with stored jwt_token + authorization_state");
		else if (this.onBeforeReconnect && this.clientBound) {
			logger$9.debug("[Session] Refreshing credentials before fresh connect");
			await this.onBeforeReconnect();
		}
		if ((!isReconnect || this.clientBound) && this.dpopManager?.initialized) try {
			dpopToken = await this.dpopManager.createRpcProof({ method: "signalwire.connect" });
		} catch (error) {
			if (this.clientBound) throw error;
			logger$9.warn("[Session] Failed to create DPoP proof for connect, proceeding without:", error);
		}
		const rpcConnectRequest = RPCConnect({
			authentication: isReconnect ? { jwt_token: storedToken } : this.authentication,
			version: this.connectVersion,
			agent: this.agent,
			contexts: this.contexts,
			eventing: this.eventing,
			topics: this.topics,
			event_acks: this.eventAcks,
			...dpopToken ? { dpop_token: dpopToken } : {},
			...isReconnect ? {
				authorization_state: persistedParams.authorization_state,
				protocol: persistedParams.protocol
			} : {}
		});
		const response = await lastValueFrom(from(this.transport.execute(rpcConnectRequest)).pipe(throwOnRPCError(), map((res) => res.result), filter(isRPCConnectResult), tap(() => {
			logger$9.debug("[Session] Response passed filter, processing authentication result");
		}), take(1), catchError((err) => {
			logger$9.error("[Session] Authentication RPC failed:", err);
			throw err;
		})));
		logger$9.debug("[Session] Processing authentication result:", {
			hasProtocol: !!response.protocol,
			hasAuthorization: !!response.authorization,
			hasIceServers: !!response.ice_servers
		});
		if (response.protocol) await this.transport.setProtocol(response.protocol);
		this._authorization$.next(response.authorization);
		this._iceServers$.next(response.ice_servers ?? []);
		this._authState$.next({ kind: "authenticated" });
		logger$9.debug("[Session] Authentication completed successfully");
	}
	async disconnect() {
		this.transport.disconnect();
		this._authState$.next({ kind: "unauthenticated" });
		await this.cleanupStoredConnectionParams();
	}
	async createInboundCall(invite) {
		const callSession = await this.createCall({
			nodeId: invite.node_id,
			callId: invite.callID,
			initOffer: invite.sdp,
			toName: invite.callee_id_name,
			to: invite.callee_id_number,
			fromName: invite.caller_id_name,
			from: invite.caller_id_number,
			displayDirection: invite.display_direction,
			userVariables: invite.userVariables
		});
		this._calls$.next({
			[`${callSession.id}`]: callSession,
			...this._calls$.value
		});
	}
	/**
	* Handle a server-pushed verto.attach event at the session level.
	*
	* On page reload the server detects the reconnected session and pushes
	* verto.attach for any active calls. If a call object already exists
	* (network blip, no reload), the per-call handler in VertoManager deals
	* with it. This method only creates a new call object when no existing
	* one matches the callID.
	*/
	async handleVertoAttach(attach) {
		const { callID } = attach;
		if (callID in this._calls$.value) {
			logger$9.debug(`[Session] Verto attach for existing call ${callID}, deferring to per-call handler`);
			return;
		}
		const storedOptions = await this.attachManager.consumePendingAttachment(callID);
		logger$9.debug(`[Session] Creating reattached call for callID: ${callID}`);
		const callSession = await this.createCall({
			nodeId: attach.node_id,
			callId: callID,
			toName: attach.callee_id_name,
			to: attach.callee_id_number,
			fromName: attach.caller_id_name,
			from: attach.caller_id_number,
			reattach: true,
			...storedOptions
		});
		this._calls$.next({
			[`${callSession.id}`]: callSession,
			...this._calls$.value
		});
	}
	async createOutboundCall(destination, options = {}) {
		const destinationURI = destination instanceof Address ? destination.defaultChannel : destination;
		let callSession;
		try {
			callSession = await this.createCall({
				to: destinationURI,
				...options
			});
			await firstValueFrom(race(callSession.selfId$.pipe(filter((id) => Boolean(id)), take(1), timeout(this.callCreateTimeout)), callSession.errors$.pipe(take(1), switchMap((callError) => throwError(() => callError.error)))));
			this._calls$.next({
				[`${callSession.id}`]: callSession,
				...this._calls$.value
			});
			return callSession;
		} catch (error) {
			logger$9.error("[Session] Error creating outbound call:", error);
			callSession?.destroy();
			const callError = new CallCreateError(error instanceof TimeoutError ? "Call create timeout" : "Call creation failed", error, "outbound");
			this._errors$.next(callError);
			throw callError;
		}
	}
	async createCall(options = {}) {
		try {
			const addressURI = getAddressSearchURI(options);
			let address;
			try {
				if (!this._directory) throw new DependencyError("Directory not initialized");
				const addressId = await this._directory.findAddressIdByURI(addressURI);
				if (!addressId) throw new DependencyError(`Address name: ${addressURI} not found`);
				address = this._directory.get(addressId);
				if (!address) throw new DependencyError(`Address ID: ${addressId} not found`);
			} catch {
				logger$9.warn(`[Session] Directory lookup failed for ${addressURI}, proceeding with raw URI`);
			}
			const callSession = this.callFactory.createCall(address, { ...options });
			this.subscribeTo(callSession.status$.pipe(filter((status) => status === "destroyed"), take(1)), () => {
				const { [`${callSession.id}`]: _, ...remainingCalls } = this._calls$.value;
				this._calls$.next(remainingCalls);
			});
			return callSession;
		} catch (error) {
			logger$9.error("[Session] Error creating call session:", error);
			throw new CallCreateError("Call create error", error, options.initOffer ? "inbound" : "outbound");
		}
	}
	destroy() {
		for (const call of Object.values(this._calls$.value)) call.hangup();
		super.destroy();
	}
};
var ClientSessionWrapper = class {
	constructor(clientSessionManager) {
		this.clientSessionManager = clientSessionManager;
	}
	get authenticated$() {
		return this.clientSessionManager.authenticated$;
	}
	get authenticated() {
		return this.clientSessionManager.authenticated;
	}
	get signalingEvent$() {
		return this.clientSessionManager.signalingEvent$;
	}
	get iceServers() {
		return this.clientSessionManager.iceServers;
	}
	async execute(request, options) {
		return this.clientSessionManager.execute(request, options);
	}
	get incomingCalls$() {
		return this.clientSessionManager.incomingCalls$;
	}
	get incomingCalls() {
		return this.clientSessionManager.incomingCalls;
	}
	get calls$() {
		return this.clientSessionManager.calls$;
	}
	get calls() {
		return this.clientSessionManager.calls;
	}
};

//#endregion
//#region src/utils/isString.ts
const isString = (obj) => typeof obj === "string";

//#endregion
//#region src/managers/ConversationsManager.ts
const logger$8 = getLogger();
var ConversationMessagesFetcher = class extends Fetcher {
	constructor(groupId, http) {
		super(`/api/fabric/conversations/${groupId}/messages`, "page_size=100", http);
		this.groupId = groupId;
	}
};
var ConversationMessageCollection = class extends EntityCollection {
	constructor(groupId, update$, http, onError) {
		super(new ConversationMessagesFetcher(groupId, http), update$, onError);
	}
};
var ConversationsManager = class {
	constructor(clientSession, http, getUserAddressId, onError) {
		this.clientSession = clientSession;
		this.http = http;
		this.getUserAddressId = getUserAddressId;
		this.onError = onError;
		this.groupIds = /* @__PURE__ */ new Map();
	}
	async join(addressId) {
		const userFromAddressId = this.getUserAddressId();
		try {
			const response = await this.http.request({
				...POST_PARAMS,
				url: `/api/fabric/conversations/join`,
				body: JSON.stringify({
					from_fabric_address_id: userFromAddressId,
					fabric_address_ids: [addressId, userFromAddressId]
				})
			});
			if (response.ok && !!response.body) {
				const data = JSON.parse(response.body);
				if (isString(data.group_id)) {
					this.groupIds.set(addressId, data.group_id);
					return data.group_id;
				}
			}
			throw new ConversationError("Join Failed - Unexpected response");
		} catch (error) {
			logger$8.error("[ConversationsManager] Failed to join conversation:", error);
			throw error;
		}
	}
	async getConversationMessageCollection(addressId) {
		const groupId = this.groupIds.get(addressId) ?? await this.join(addressId);
		return Promise.resolve(new ConversationMessageCollection(groupId, this.clientSession.signalingEvent$.pipe(filterAs(isConversationMessageMetadata, "params"), tap((event) => logger$8.debug("[ConversationsManager ] Conversation Event:", event)), map((params) => ({ ...params }))), this.http, this.onError));
	}
	async sendText(text, destinationAddressId) {
		const groupId = this.groupIds.get(destinationAddressId) ?? await this.join(destinationAddressId);
		const userFromAddressId = this.getUserAddressId();
		try {
			if ((await this.http.request({
				...POST_PARAMS,
				url: "/api/fabric/messages",
				body: JSON.stringify({
					group_id: groupId,
					from_fabric_address_id: userFromAddressId,
					text
				})
			})).ok) return;
			throw new ConversationError("Send Text Failed - Unexpected response");
		} catch (error) {
			logger$8.error("[ConversationsManager] Failed to send text message:", error);
			throw error;
		}
	}
};

//#endregion
//#region src/managers/DeviceTokenManager.ts
const logger$7 = getLogger();
/**
* Resolves the token expiry timestamp (epoch seconds) using a 3-tier priority chain:
* 1. `data.expires_at` — server-provided absolute timestamp
* 2. `data.expires_in` — server-provided relative lifetime
* 3. Fallback to `DEVICE_TOKEN_DEFAULT_EXPIRE_IN` with a warning
*/
function resolveExpiresAt(data) {
	if (data.expires_at) return data.expires_at;
	if (data.expires_in) return Math.floor(Date.now() / 1e3) + data.expires_in;
	logger$7.warn("[DeviceToken] Could not determine token expiry, using default");
	return Math.floor(Date.now() / 1e3) + DEVICE_TOKEN_DEFAULT_EXPIRE_IN;
}
/**
* Resolves the token TTL in seconds from a fresh response.
* Called at token receive time so `expires_at - now` reflects the original lifetime.
*
* 1. `data.expires_in` — server-provided TTL directly
* 2. `data.expires_at - now` — derive TTL from absolute timestamp
* 3. Fallback to `DEVICE_TOKEN_DEFAULT_EXPIRE_IN`
*/
function resolveExpireIn(data) {
	if (data.expires_in) return data.expires_in;
	if (data.expires_at) return Math.max(data.expires_at - Math.floor(Date.now() / 1e3), 1);
	return DEVICE_TOKEN_DEFAULT_EXPIRE_IN;
}
/**
* Manages the Client Bound SAT lifecycle: activation, token exchange,
* reauthentication, and automatic refresh scheduling.
*
* Extends {@link Destroyable} for automatic RxJS subscription and subject cleanup.
* Uses a reactive pipeline (`BehaviorSubject` + `switchMap(timer())`) instead of
* raw `setTimeout` for refresh scheduling.
*/
var DeviceTokenManager = class extends Destroyable {
	constructor(dpopManager, http, errorHandler, getCredential) {
		super();
		this.dpopManager = dpopManager;
		this.http = http;
		this.errorHandler = errorHandler;
		this.getCredential = getCredential;
		this._currentToken$ = this.createBehaviorSubject(null);
		this._refreshInProgress = false;
		this._paused = false;
		this._effectiveExpireIn = DEVICE_TOKEN_DEFAULT_EXPIRE_IN;
		this.subscribeTo(this._currentToken$.pipe(filter(Boolean), switchMap((tokenData) => {
			const expiresAt = resolveExpiresAt(tokenData);
			const refreshIn = Math.max(expiresAt * 1e3 - Date.now() - DEVICE_TOKEN_REFRESH_BUFFER_MS, 1e3);
			logger$7.debug(`[DeviceToken] Scheduling Client Bound SAT refresh in ${refreshIn}ms`);
			return timer(refreshIn);
		})), () => {
			this.executeRefresh();
		});
	}
	/** Current token TTL in milliseconds. Used to extend cached credential expiry on refresh. */
	get effectiveExpireIn() {
		return this._effectiveExpireIn;
	}
	/**
	* Activates the Client Bound SAT flow when the user's token has
	* `sat:refresh` scope.
	*
	* Returns an {@link ActivationResult} indicating whether the manager
	* took ownership of refresh duties. The caller must use the `activated`
	* boolean to decide whether to keep its own refresh path armed — when
	* `activated` is `false`, the caller is responsible for refresh.
	*
	* Steps on success:
	* 1. Check user's `sat_claims` for `sat:refresh` scope
	* 2. Call `/api/fabric/subscriber/devices/token` with a DPoP proof
	* 3. Reauthenticate the session with the Client Bound SAT + DPoP proof
	* 4. Emit token to trigger the reactive refresh pipeline
	*/
	async activate(user, session, updateCredential) {
		const { satClaims } = user;
		if (!satClaims?.scope?.includes(SAT_REFRESH_SCOPE)) {
			logger$7.debug("[DeviceToken] No sat:refresh scope, skipping Client Bound SAT activation");
			return {
				activated: false,
				reason: "no-scope"
			};
		}
		this._session = session;
		this._updateCredential = updateCredential;
		try {
			const cached = this._currentToken$.value;
			if (cached && this.isTokenFresh(cached)) {
				logger$7.debug("[DeviceToken] Reusing cached Client Bound SAT — skipping /devices/token");
				const rpcProof$1 = await this.dpopManager.createRpcProof({ method: "signalwire.reauthenticate" });
				await session.reauthenticate(cached.token, rpcProof$1, { clientBound: true });
				updateCredential({ token: cached.token });
				return { activated: true };
			}
			const tokenData = await this.obtainToken();
			if (!tokenData.expires_at && !tokenData.expires_in && satClaims.expires_at) tokenData.expires_at = satClaims.expires_at;
			this._effectiveExpireIn = resolveExpireIn(tokenData);
			const rpcProof = await this.dpopManager.createRpcProof({ method: "signalwire.reauthenticate" });
			await session.reauthenticate(tokenData.token, rpcProof, { clientBound: true });
			updateCredential({ token: tokenData.token });
			logger$7.info("[DeviceToken] Client Bound SAT activated successfully");
			this._currentToken$.next(tokenData);
			return { activated: true };
		} catch (error) {
			logger$7.error("[DeviceToken] Failed to activate Client Bound SAT:", error);
			this.errorHandler(new DPoPInitError(error, "Failed to activate Client Bound SAT"));
			return {
				activated: false,
				reason: "endpoint-failed"
			};
		}
	}
	/**
	* Returns true when the cached token has enough headroom before expiry to
	* be safely reused on reactivation. The headroom matches the refresh
	* buffer, so a token within the refresh window is treated as stale (the
	* reactive pipeline is about to refresh it anyway).
	*/
	isTokenFresh(token) {
		return resolveExpiresAt(token) * 1e3 - Date.now() > DEVICE_TOKEN_REFRESH_BUFFER_MS;
	}
	/**
	* Obtains a Client Bound SAT from `/api/fabric/subscriber/devices/token`.
	* Returns the full {@link DeviceTokenResponse} including expiry metadata.
	*/
	async obtainToken() {
		const dpopProof = await this.dpopManager.createHttpProof({
			method: "POST",
			uri: DEVICE_TOKEN_ENDPOINT
		});
		const response = await this.http.request({
			url: DEVICE_TOKEN_ENDPOINT,
			...POST_PARAMS,
			body: JSON.stringify({
				dpop_token: dpopProof,
				expire_in: DEVICE_TOKEN_DEFAULT_EXPIRE_IN
			})
		});
		if (!response.ok || !response.body) throw new DeviceTokenError(`Failed to obtain device token: ${response.status} ${response.statusText}`);
		const data = JSON.parse(response.body);
		if (!data.token) throw new DeviceTokenError("Device token response missing token field");
		return data;
	}
	/**
	* Refreshes the Client Bound SAT via `/api/fabric/subscriber/devices/refresh`.
	*
	* Creates a fresh DPoP proof, calls the refresh endpoint, reauthenticates
	* the WebSocket session, and returns the new token data (scheduling is
	* handled by the reactive pipeline).
	*/
	async refreshToken(session, currentToken, updateCredential) {
		logger$7.debug("[DeviceToken] Refreshing Client Bound SAT");
		const dpopProof = await this.dpopManager.createHttpProof({
			method: "POST",
			uri: DEVICE_REFRESH_ENDPOINT,
			accessToken: currentToken
		});
		const response = await this.http.request({
			url: DEVICE_REFRESH_ENDPOINT,
			...POST_PARAMS,
			body: JSON.stringify({
				dpop_token: dpopProof,
				expire_in: this._effectiveExpireIn
			})
		});
		if (!response.ok || !response.body) throw new TokenRefreshError(`Failed to refresh device token: ${response.status} ${response.statusText}`);
		const data = JSON.parse(response.body);
		if (!data.token) throw new TokenRefreshError("Device token refresh response missing token field");
		if (!data.expires_at && !data.expires_in) data.expires_in = this._effectiveExpireIn;
		this._effectiveExpireIn = resolveExpireIn(data);
		const rpcProof = await this.dpopManager.createRpcProof({ method: "signalwire.reauthenticate" });
		await session.reauthenticate(data.token, rpcProof);
		updateCredential({ token: data.token });
		logger$7.info("[DeviceToken] Client Bound SAT refreshed successfully");
		return data;
	}
	/**
	* Executes a refresh with retry and exponential backoff.
	* On success, emits to `_currentToken$` to schedule the next refresh.
	* On all retries exhausted, emits to `errorHandler`.
	*/
	async executeRefresh() {
		if (this._paused) {
			logger$7.debug("[DeviceToken] Manager paused, skipping refresh");
			return;
		}
		if (this._refreshInProgress) {
			logger$7.debug("[DeviceToken] Refresh already in progress, skipping");
			return;
		}
		const session = this._session;
		const updateCredential = this._updateCredential;
		if (!session || !updateCredential) {
			logger$7.warn("[DeviceToken] Cannot refresh: session or updateCredential not set");
			return;
		}
		if (!session.authenticated) {
			logger$7.debug("[DeviceToken] Session not authenticated, deferring refresh");
			return;
		}
		this._refreshInProgress = true;
		try {
			const currentToken = this.getCredential().token;
			if (!currentToken) throw new TokenRefreshError("No current token available for refresh");
			const newTokenData = await this.retryRefresh(session, currentToken, updateCredential);
			this._currentToken$.next(newTokenData);
		} catch (error) {
			logger$7.error("[DeviceToken] Automatic Client Bound SAT refresh failed:", error);
			this.errorHandler(error instanceof TokenRefreshError ? error : new TokenRefreshError("Automatic token refresh failed", error));
		} finally {
			this._refreshInProgress = false;
		}
	}
	/**
	* Retries `refreshToken()` up to `DEVICE_TOKEN_REFRESH_MAX_RETRIES` times
	* with exponential backoff (1s, 2s, 4s).
	*/
	async retryRefresh(session, currentToken, updateCredential) {
		let lastError;
		for (let attempt = 0; attempt < DEVICE_TOKEN_REFRESH_MAX_RETRIES; attempt++) try {
			return await this.refreshToken(session, currentToken, updateCredential);
		} catch (error) {
			lastError = error;
			if (attempt < DEVICE_TOKEN_REFRESH_MAX_RETRIES - 1) {
				const delay = DEVICE_TOKEN_REFRESH_RETRY_BASE_MS * Math.pow(2, attempt);
				logger$7.warn(`[DeviceToken] Refresh attempt ${attempt + 1} failed, retrying in ${delay}ms`);
				await new Promise((resolve) => setTimeout(resolve, delay));
			}
		}
		throw lastError instanceof Error ? lastError : new TokenRefreshError("All refresh retries exhausted", lastError);
	}
	/**
	* Stops the reactive refresh pipeline from firing. Use when the underlying
	* session is being torn down (e.g., during {@link SignalWire.disconnect})
	* so a scheduled refresh cannot fire against a destroyed session.
	*
	* The manager's state (cached token, effective TTL, subscriptions) is
	* preserved — call {@link resume} to re-enable firing after reconnect.
	*/
	pause() {
		this._paused = true;
	}
	/** Re-enables the reactive refresh pipeline after a previous {@link pause}. */
	resume() {
		this._paused = false;
	}
	/** Cleans up the manager, cancelling the reactive pipeline and all subscriptions. */
	destroy() {
		super.destroy();
	}
};

//#endregion
//#region src/managers/CredentialRefreshCoordinator.ts
const logger$6 = getLogger();
const defaultDeviceTokenManagerFactory = (dpopManager, http, errorHandler, getCredential) => new DeviceTokenManager(dpopManager, http, errorHandler, getCredential);
/**
* Centralizes credential-refresh ownership across the two competing
* mechanisms — developer-provided `CredentialProvider.refresh()` and the
* Client Bound SAT path via {@link DeviceTokenManager}.
*
* Maintains the invariant: **at most one refresh mechanism is armed at a
* time, and at least one is armed whenever the current credential has an
* `expiry_at`**.
*
* Replaces the previous design where refresh state was distributed across
* `SignalWire` (timer field, scheduler method, activation helper) and
* `DeviceTokenManager` (reactive pipeline). Centralizing the invariant in
* one component eliminates the bug class that produced issue #19074.
*
* Race-safety:
* - `_activating` flag prevents overlapping `activate()` calls from racing.
* - `_activationGeneration` lets late resolutions detect they've been
*   preempted by a newer activation (e.g., reconnect during in-flight
*   `obtainToken`).
*/
var CredentialRefreshCoordinator = class extends Destroyable {
	constructor(dpopManager, deps) {
		super();
		this.deps = deps;
		this._activating = false;
		this._activationGeneration = 0;
		if (dpopManager?.initialized) this._deviceTokenManager = (deps.deviceTokenManagerFactory ?? defaultDeviceTokenManagerFactory)(dpopManager, deps.http, (error) => deps.notifier.onError(error), () => deps.store.read());
	}
	/** True when the Client Bound SAT path is available (DPoP initialized). */
	get clientBoundSATAvailable() {
		return this._deviceTokenManager !== void 0;
	}
	/** True when the developer-provided refresh timer is currently armed. */
	get developerRefreshArmed() {
		return this._developerTimerId !== void 0;
	}
	/**
	* Arms the developer-provided refresh timer to fire shortly before
	* `expiresAt`. Replaces any previously scheduled developer refresh.
	*
	* Idempotent — multiple calls just reschedule. On retry exhaustion,
	* invokes `deps.onRefreshExhausted` so the orchestrator can disconnect.
	*/
	scheduleDeveloperRefresh(provider, expiresAt, attempt = 0) {
		if (this._developerTimerId !== void 0) clearTimeout(this._developerTimerId);
		const refreshInterval = attempt === 0 ? Math.max(expiresAt - Date.now() - CREDENTIAL_REFRESH_BUFFER_MS, 1e3) : Math.min(CREDENTIAL_REFRESH_RETRY_BASE_MS * Math.pow(2, attempt) * (.5 + Math.random() * .5), CREDENTIAL_REFRESH_MAX_DELAY_MS);
		this._developerTimerId = setTimeout(async () => {
			try {
				if (!provider.refresh) throw new InvalidCredentialsError("Credential provider does not support refresh");
				const newCredentials = await provider.refresh();
				this.deps.store.write(newCredentials);
				this.deps.store.persist(newCredentials);
				logger$6.info("[Coordinator] Credentials refreshed successfully.");
				if (newCredentials.expiry_at) this.scheduleDeveloperRefresh(provider, newCredentials.expiry_at, 0);
			} catch (error) {
				const nextAttempt = attempt + 1;
				logger$6.error(`[Coordinator] Credential refresh failed (attempt ${nextAttempt}/${CREDENTIAL_REFRESH_MAX_RETRIES}):`, error);
				this.deps.notifier.onError(error instanceof Error ? error : new Error(String(error), { cause: error }));
				if (nextAttempt < CREDENTIAL_REFRESH_MAX_RETRIES) this.scheduleDeveloperRefresh(provider, expiresAt, nextAttempt);
				else {
					logger$6.error("[Coordinator] Credential refresh exhausted all retries. Disconnecting.");
					this.deps.notifier.onError(new TokenRefreshError("Credential refresh failed after max retries"));
					this.deps.notifier.onRefreshExhausted();
				}
			}
		}, refreshInterval);
	}
	/**
	* Cancels any scheduled developer-provided refresh. Idempotent.
	*
	* @internal Used by the coordinator's own activation flow. External
	* callers should use {@link suspend} for disconnect-time quiescence —
	* `suspend()` also pauses the internal Client Bound SAT pipeline.
	*/
	cancelDeveloperRefresh() {
		if (this._developerTimerId !== void 0) {
			clearTimeout(this._developerTimerId);
			this._developerTimerId = void 0;
		}
	}
	/**
	* Suspends both refresh paths — cancels the developer timer and pauses
	* the internal reactive pipeline. Use when the underlying session is
	* being torn down (e.g., {@link SignalWire.disconnect}). The next
	* {@link activate} call re-enables the internal pipeline.
	*
	* The internal manager's cached token survives — see
	* {@link DeviceTokenManager.pause} — so a subsequent reconnect can
	* skip the `/devices/token` exchange entirely.
	*/
	suspend() {
		this.cancelDeveloperRefresh();
		this._deviceTokenManager?.pause();
	}
	/**
	* Asks the Client Bound SAT path to take over refresh. If it accepts,
	* the developer-provided timer (if any) is cancelled. If it declines, the
	* developer timer remains armed and a `credential_refresh_fallback`
	* warning is emitted.
	*
	* **Idempotent** — re-entrant calls during an in-flight activation are
	* dropped. Use `_activationGeneration` to detect stale resolutions
	* (e.g., a reconnect-triggered activate() that races with an earlier one).
	*/
	async activate(user, session) {
		if (this._activating) {
			logger$6.debug("[Coordinator] activate() in flight; ignoring re-entrant call");
			return;
		}
		if (!this._deviceTokenManager) return;
		this._deviceTokenManager.resume();
		const generation = ++this._activationGeneration;
		this._activating = true;
		try {
			const result = await this.withActivationTimeout(this._deviceTokenManager.activate(user, session, (cred) => this.deps.store.merge(cred)));
			if (generation !== this._activationGeneration) {
				logger$6.debug("[Coordinator] activate() result discarded (preempted by newer activation)");
				return;
			}
			if (result.activated) {
				this.cancelDeveloperRefresh();
				logger$6.debug("[Coordinator] Developer refresh disabled — Client Bound SAT owns refresh");
				return;
			}
			logger$6.warn(`[SignalWire] [SW-REFRESH-FALLBACK] Client Bound SAT declined (reason=${result.reason}); using developer-provided refresh handler.`);
			this.deps.notifier.onWarning({
				code: "credential_refresh_fallback",
				source: "CredentialProvider",
				reason: result.reason,
				message: `Client Bound SAT activation declined (${result.reason}); using developer-provided refresh.`
			});
		} finally {
			this._activating = false;
		}
	}
	destroy() {
		this.cancelDeveloperRefresh();
		this._deviceTokenManager?.destroy();
		this._deviceTokenManager = void 0;
		super.destroy();
	}
	/**
	* Races the manager's `activate()` against a hard timeout. A wedged HTTP
	* layer (e.g., proxy issues) could otherwise hang the activation
	* indefinitely, leaving the session with no refresh mechanism while the
	* `_activating` guard blocks subsequent retries.
	*
	* On timeout, the manager is paused immediately. The inner `activate()`
	* may still complete in the background and emit to `_currentToken$`,
	* which would normally arm the reactive refresh pipeline; pausing
	* prevents that pipeline from firing while the developer refresh path
	* is the active mechanism — preserving the "at most one mechanism
	* armed" invariant. The next `activate()` call resumes the manager.
	*/
	async withActivationTimeout(inner) {
		return new Promise((resolve) => {
			const timer$1 = setTimeout(() => {
				this._deviceTokenManager?.pause();
				resolve({
					activated: false,
					reason: "activation-timeout"
				});
			}, CREDENTIAL_ACTIVATE_TIMEOUT_MS);
			inner.then((result) => {
				clearTimeout(timer$1);
				resolve(result);
			}, (error) => {
				clearTimeout(timer$1);
				this.deps.notifier.onError(error instanceof Error ? error : new Error(String(error)));
				resolve({
					activated: false,
					reason: "endpoint-failed"
				});
			});
		});
	}
};

//#endregion
//#region src/managers/DiagnosticsCollector.ts
const logger$5 = getLogger();
/** Default maximum number of diagnostic events to retain in the ring buffer. */
const DEFAULT_MAX_EVENTS = 1e3;
/**
* Returns the user agent string, safely handling non-browser environments.
*/
function getUserAgent() {
	try {
		if (typeof navigator !== "undefined" && navigator.userAgent) return navigator.userAgent;
	} catch {}
	return "unknown";
}
/**
* DiagnosticsCollector maintains a ring buffer of diagnostic events
* for structured export. It records connection, call, device, recovery,
* and error events during a session, and can serialize them into a
* plain object for support tickets or debugging.
*
* Extends Destroyable for lifecycle management.
*/
var DiagnosticsCollector = class extends Destroyable {
	constructor(options) {
		super();
		this._events = [];
		this._calls = [];
		this._deviceChanges = [];
		this._eventRecorded$ = this.createSubject();
		this._sdkVersion = options.sdkVersion;
		this._maxEvents = options.maxEvents ?? DEFAULT_MAX_EVENTS;
		logger$5.debug("DiagnosticsCollector initialized", {
			sdkVersion: this._sdkVersion,
			maxEvents: this._maxEvents
		});
	}
	/**
	* Observable that emits each time a diagnostic event is recorded.
	*/
	get eventRecorded$() {
		return this._eventRecorded$.asObservable();
	}
	/**
	* Record a diagnostic event into the ring buffer.
	*
	* @param category - The event category (connection, call, device, recovery, error).
	* @param event - A short description of the event.
	* @param details - Optional additional details as a key-value map.
	*/
	record(category, event, details) {
		const entry = {
			timestamp: Date.now(),
			category,
			event,
			...details !== void 0 ? { details } : {}
		};
		this._events = this._appendToBuffer(this._events, entry);
		this._eventRecorded$.next(entry);
	}
	/**
	* Shorthand to record a device change event.
	*
	* @param event - Description of the device change.
	* @param details - Optional additional details.
	*/
	recordDeviceChange(event, details) {
		const entry = {
			timestamp: Date.now(),
			category: "device",
			event,
			...details !== void 0 ? { details } : {}
		};
		this._deviceChanges = this._appendToBuffer(this._deviceChanges, entry);
		this._events = this._appendToBuffer(this._events, entry);
		this._eventRecorded$.next(entry);
	}
	/**
	* Record a call summary for diagnostic export.
	*
	* @param summary - The call diagnostic summary to add.
	*/
	recordCallSummary(summary) {
		this._calls = this._appendCallToBuffer(this._calls, summary);
		this.record("call", "call_summary", {
			callId: summary.callId,
			direction: summary.direction,
			duration: summary.duration,
			finalStatus: summary.finalStatus
		});
	}
	/**
	* Export the current diagnostic buffer as a structured plain object.
	* This can be JSON.stringify'd and attached to support tickets.
	*/
	export() {
		return {
			sdkVersion: this._sdkVersion,
			userAgent: getUserAgent(),
			exportedAt: Date.now(),
			events: [...this._events],
			calls: [...this._calls],
			deviceChanges: [...this._deviceChanges]
		};
	}
	/**
	* Clear all diagnostic buffers, resetting to empty state.
	*/
	clear() {
		this._events = [];
		this._calls = [];
		this._deviceChanges = [];
		logger$5.debug("DiagnosticsCollector buffers cleared");
	}
	destroy() {
		logger$5.debug("DiagnosticsCollector destroyed");
		super.destroy();
	}
	/**
	* Append an entry to a buffer array, enforcing the max size via ring buffer semantics.
	* Returns a new array (immutable pattern).
	*/
	_appendToBuffer(buffer, entry) {
		const updated = [...buffer, entry];
		if (updated.length > this._maxEvents) return updated.slice(updated.length - this._maxEvents);
		return updated;
	}
	/**
	* Append a call summary to the calls buffer, enforcing max size.
	* Returns a new array (immutable pattern).
	*/
	_appendCallToBuffer(buffer, entry) {
		const updated = [...buffer, entry];
		if (updated.length > this._maxEvents) return updated.slice(updated.length - this._maxEvents);
		return updated;
	}
};

//#endregion
//#region src/utils/arrays.ts
const isEmptyArray = (a) => {
	return (a?.length ?? 0) === 0;
};

//#endregion
//#region src/utils/warmup.ts
const warmup = (observable) => {
	observable.pipe(take(1)).subscribe();
};

//#endregion
//#region src/managers/DirectoryManager.ts
const logger$4 = getLogger();
var AddressFetcher = class extends Fetcher {
	constructor(http) {
		super("/api/fabric/addresses", "sort_by=name&sort_order=asc", http);
	}
	async name(name) {
		const response = await this.http.request({
			...GET_PARAMS,
			url: `${this.endpoint}?name=${encodeURIComponent(name)}`
		});
		if (response.ok && !!response.body) {
			const result = JSON.parse(response.body);
			if (!isEmptyArray(result.data)) return result.data[0];
		}
		logger$4.error("Failed to fetch addresses");
	}
};
/** Collection of address states with reactive updates and pagination. */
var AddressStateCollection = class extends EntityCollection {
	constructor(update$, http, onError) {
		super(new AddressFetcher(http), update$, onError);
	}
};
/**
* Manages the directory of {@link Address} entries with paginated loading.
*
* Implements the {@link Directory} interface and provides reactive access to
* addresses via observables, along with pagination and lookup methods.
*/
var DirectoryManager = class extends Destroyable {
	constructor(http, clientSession, conversationManager, onError) {
		super();
		this.http = http;
		this.conversationManager = conversationManager;
		this.onError = onError;
		this.addNewAddress = (id) => {
			const address = new Address(id, this.conversationManager, this);
			const observable = this._statesCollection.get$(id)?.pipe(filterNull(), map((data) => {
				address.upnext(data);
				return address;
			}));
			if (observable) {
				warmup(observable);
				this._observableRegistry.set(id, observable);
			}
			this._addressesInstances.set(id, address);
		};
		this._addresses$ = this.createBehaviorSubject([]);
		this._addressesInstances = /* @__PURE__ */ new Map();
		this._observableRegistry = /* @__PURE__ */ new Map();
		this._statesCollection = new AddressStateCollection(clientSession.signalingEvent$.pipe(filterAs(isConversationMessageUpdatedMetadata, "params"), map((_) => ({}))), this.http, this.onError);
		this.initSubscriptions();
	}
	/** Whether addresses are currently being loaded from the server. */
	get loading() {
		return this._statesCollection.loading;
	}
	initSubscriptions() {
		this.subscribeTo(this._statesCollection.updated$, () => {
			const existing = Array.from(this._addressesInstances.values().map((address) => address.id));
			const newStates = this._statesCollection.values.filter((state) => !existing.includes(state.id));
			if (!isEmptyArray(newStates)) {
				newStates.forEach((state) => this.addNewAddress(state.id));
				this._addresses$.next(Array.from(this._addressesInstances.values()));
			}
		});
	}
	/** Observable stream of all addresses in the directory. */
	get addresses$() {
		return this._addresses$.asObservable();
	}
	/** Current snapshot of all loaded addresses. */
	get addresses() {
		return this._addresses$.value;
	}
	/** Observable indicating whether more addresses can be loaded. */
	get hasMore$() {
		return this._statesCollection.hasMore$;
	}
	/** Observable of the current loading state. */
	get loading$() {
		return this._statesCollection.loading$;
	}
	/**
	* Loads the next page of addresses from the server.
	*
	* No-op if {@link hasMore$} is `false`. Loading state is observable via {@link loading$}.
	* New addresses are appended to {@link addresses$} when the page loads.
	*/
	loadMore() {
		if (this._statesCollection.hasMore) this._statesCollection.loadMore();
	}
	/**
	* Returns a reactive observable for a specific address by ID.
	* @param id - The address ID to observe.
	* @returns An observable of the address, or `undefined` if not found.
	*/
	get$(id) {
		if (!this._observableRegistry.has(id)) this.addNewAddress(id);
		return this._observableRegistry.get(id);
	}
	/**
	* Returns an address by ID from the local cache.
	* @param addressId - The address ID to look up.
	* @returns The address, or `undefined` if not found.
	*/
	get(addressId) {
		return this._addressesInstances.get(addressId);
	}
	/**
	* Finds an address ID by its resource name (URI).
	*
	* Searches locally loaded addresses first, then queries the server if not found.
	* The resource name is the {@link Address.name} identifier, distinct from display name.
	*
	* @param name - The resource name to search for (exact match, e.g. `'/public/conference'`).
	* @returns The address ID, or `undefined` if no match is found locally or on server.
	*/
	async findAddressIdByURI(name) {
		let addressId = this._addressesInstances.values().find((addr) => addr.name === name)?.id;
		if (!addressId) {
			const found$ = await this._statesCollection.find$("name", name);
			if (found$) {
				const state = await firstValueFrom(found$);
				this.addNewAddress(state.id);
				addressId = state.id;
			}
		}
		return addressId;
	}
};

//#endregion
//#region src/controllers/WebSocketController.ts
const logger$3 = getLogger();
var WebSocketController = class WebSocketController extends Destroyable {
	static {
		this.DEFAULT_RECONNECT_DELAY_MIN_MS = 1e3;
	}
	static {
		this.DEFAULT_RECONNECT_DELAY_MAX_MS = 3e4;
	}
	static {
		this.DEFAULT_CONNECTION_TIMEOUT_MS = 1e4;
	}
	constructor(WebSocketConstructor, endpoint, outgoingMessages$, options = {}) {
		super();
		this.WebSocketConstructor = WebSocketConstructor;
		this.endpoint = endpoint;
		this.outgoingMessages$ = outgoingMessages$;
		this.messageQueue = [];
		this.shouldReconnect = false;
		this.boundHandleOpen = () => this.handleOpen();
		this.boundHandleClose = (event) => this.handleClose(event);
		this.boundHandleError = () => this.handleError();
		this.boundHandleMessage = (event) => this.handleMessage(event);
		this._status$ = this.createBehaviorSubject("disconnected");
		this._incomingMessages$ = this.createSubject();
		this._errors$ = this.createReplaySubject(1);
		this.reconnectDelayMin = options.reconnectDelayMin ?? WebSocketController.DEFAULT_RECONNECT_DELAY_MIN_MS;
		this.reconnectDelayMax = options.reconnectDelayMax ?? WebSocketController.DEFAULT_RECONNECT_DELAY_MAX_MS;
		this.connectionTimeout = options.connectionTimeout ?? WebSocketController.DEFAULT_CONNECTION_TIMEOUT_MS;
		this.currentReconnectDelay = this.reconnectDelayMin;
		this.subscriptions.push(this.outgoingMessages$.subscribe((data) => {
			this.send(data);
		}));
	}
	get status$() {
		return this._status$.asObservable();
	}
	get incomingMessages$() {
		return this._incomingMessages$.asObservable();
	}
	get errors$() {
		return this._errors$.asObservable();
	}
	connect() {
		if (this._status$.value === "connecting" || this._status$.value === "connected") return;
		this.shouldReconnect = true;
		this._status$.next("connecting");
		this.createWebSocket();
	}
	disconnect() {
		this.shouldReconnect = false;
		this.clearReconnectTimer();
		this.clearConnectionTimeout();
		const currentStatus = this._status$.value;
		if (currentStatus === "connected" || currentStatus === "connecting" || currentStatus === "reconnecting") if (this.socket) {
			this._status$.next("disconnecting");
			this.socket.close();
		} else this._status$.next("disconnected");
		else this._status$.next("disconnected");
	}
	reconnect() {
		this.shouldReconnect = true;
		this._status$.next("reconnecting");
		this.scheduleReconnection();
	}
	send(data) {
		if (this._status$.value === "connected" && this.socket?.readyState === 1) {
			logger$3.wsTraffic({
				type: "send",
				raw: data
			});
			this.socket.send(data);
		} else this.messageQueue.push(data);
	}
	createWebSocket() {
		try {
			this.closeExistingSocket();
			this.socket = new this.WebSocketConstructor(this.endpoint);
			this.setupWebSocketListeners();
			this.startConnectionTimeout();
		} catch (error) {
			const err = error instanceof Error ? error : new UnexpectedError("Failed to create WebSocket");
			this._errors$.next(err);
			this.handleConnectionError();
		}
	}
	/**
	* Closes the existing socket and removes its event listeners to prevent
	* phantom 'open'/'close' events from firing on the orphaned socket.
	*/
	closeExistingSocket() {
		if (!this.socket) return;
		const oldSocket = this.socket;
		this.socket = void 0;
		this.removeWebSocketListeners(oldSocket);
		try {
			oldSocket.close();
		} catch {}
	}
	setupWebSocketListeners() {
		if (!this.socket) return;
		this.socket.addEventListener("open", this.boundHandleOpen);
		this.socket.addEventListener("close", this.boundHandleClose);
		this.socket.addEventListener("error", this.boundHandleError);
		this.socket.addEventListener("message", this.boundHandleMessage);
	}
	removeWebSocketListeners(socket) {
		try {
			socket.removeEventListener("open", this.boundHandleOpen);
			socket.removeEventListener("close", this.boundHandleClose);
			socket.removeEventListener("error", this.boundHandleError);
			socket.removeEventListener("message", this.boundHandleMessage);
		} catch {}
	}
	handleOpen() {
		this.clearConnectionTimeout();
		this._status$.next("connected");
		this.currentReconnectDelay = this.reconnectDelayMin;
		this.flushMessageQueue();
	}
	handleClose(_event) {
		this.clearConnectionTimeout();
		if (this.shouldReconnect) {
			this._status$.next("reconnecting");
			this.scheduleReconnection();
		} else this._status$.next("disconnected");
	}
	handleError() {
		const error = new WebSocketConnectionError("WebSocket connection error");
		this._errors$.next(error);
		this.handleConnectionError();
	}
	handleMessage(event) {
		logger$3.wsTraffic({
			type: "recv",
			raw: event.data
		});
		this._incomingMessages$.next(event);
	}
	handleConnectionError() {
		this.reconnect();
	}
	scheduleReconnection() {
		this.clearReconnectTimer();
		const jitteredDelay = this.currentReconnectDelay * (.5 + Math.random() * .5);
		this.reconnectTimer = setTimeout(() => {
			if (this.shouldReconnect) {
				this._status$.next("connecting");
				this.createWebSocket();
				this.increaseReconnectDelay();
			}
		}, jitteredDelay);
	}
	increaseReconnectDelay() {
		this.currentReconnectDelay = Math.min(this.currentReconnectDelay * 2, this.reconnectDelayMax);
	}
	startConnectionTimeout() {
		this.clearConnectionTimeout();
		this.connectionTimeoutTimer = setTimeout(() => {
			if (this._status$.value === "connecting") {
				const error = new WebSocketTimeoutError("WebSocket connection timeout");
				this._errors$.next(error);
				if (this.socket) this.socket.close();
			}
		}, this.connectionTimeout);
	}
	clearConnectionTimeout() {
		if (this.connectionTimeoutTimer) {
			clearTimeout(this.connectionTimeoutTimer);
			this.connectionTimeoutTimer = void 0;
		}
	}
	clearReconnectTimer() {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = void 0;
		}
	}
	flushMessageQueue() {
		while (this.messageQueue.length > 0 && this.socket?.readyState === 1) {
			const message = this.messageQueue.shift();
			if (message !== void 0) this.socket.send(message);
		}
	}
};

//#endregion
//#region src/core/RPCMessages/guards/methods.guards.ts
function isSignalwirePingRequest(value) {
	return isJSONRPCRequest(value) && value.method === "signalwire.ping";
}

//#endregion
//#region src/managers/TransportManager.ts
const logger$2 = getLogger();
var TransportManager = class extends Destroyable {
	constructor(storage, protocolKey, webSocketConstructor, relayHost, onError) {
		super();
		this.storage = storage;
		this.protocolKey = protocolKey;
		this.onError = onError;
		this.protocol$ = this.createReplaySubject(1);
		this.isConnecting = false;
		this.isConnected = false;
		this.ackEvent = () => {
			return tap((message) => {
				if (isSignalwireRequest(message)) try {
					logger$2.debug("[Transport] Sending event ack", { eventId: message.id });
					this.send(RPCEventAckResponse(message.id));
				} catch (error) {
					logger$2.error("[Transport] Failed to send event acknowledgment:", error);
				}
			});
		};
		this.replySignalwirePing = () => {
			return filter((message) => {
				if (isSignalwirePingRequest(message)) {
					try {
						logger$2.debug("[Transport] Received ping, sending pong", { pingId: message.id });
						this.send(RPCPingResponse(message.id));
					} catch (error) {
						logger$2.error("[Transport] Failed to send ping response:", error);
					}
					return false;
				}
				return true;
			});
		};
		this.discardStaleEvents = () => {
			return filter((message) => {
				if (!isSignalwireRequest(message)) return true;
				const eventChannel = message.params.event_channel;
				if (!eventChannel) return true;
				const currentProtocol = this._currentProtocol;
				if (!currentProtocol) return true;
				if (!eventChannel.includes(currentProtocol)) {
					const eventType = message.params.event_type;
					logger$2.warn(`[Transport] Discarding stale event: ${eventType} (event_channel does not match current protocol)`);
					return false;
				}
				return true;
			});
		};
		this._outgoingMessages$ = this.createSubject();
		this._webSocketConnections = new WebSocketController(webSocketConstructor, relayHost, this._outgoingMessages$.asObservable(), {
			connectionTimeout: PreferencesContainer.instance.connectionTimeout,
			reconnectDelayMin: PreferencesContainer.instance.reconnectDelayMin,
			reconnectDelayMax: PreferencesContainer.instance.reconnectDelayMax
		});
		this.subscribeTo(this._webSocketConnections.errors$, (error) => {
			this.onError?.(error);
		});
		this.initialized$ = defer(() => from(this._init())).pipe(shareReplay(1), takeUntil(this.destroyed$));
		this._jsonRPCMessage$ = this._webSocketConnections.incomingMessages$.pipe(map((event) => {
			try {
				return JSON.parse(event.data);
			} catch (error) {
				logger$2.error("[Transport] Failed to parse incoming message:", error);
				this.onError?.(new MessageParseError(error));
				return null;
			}
		}), filter((message) => message !== null && (isJSONRPCResponse(message) || isJSONRPCRequest(message))), catchError((error) => {
			logger$2.error("[Transport] Message processing error:", error);
			this.onError?.(error instanceof Error ? error : new Error(String(error), { cause: error }));
			return EMPTY;
		}), share(), takeUntil(this.destroyed$));
		this._jsonRPCResponse$ = this._jsonRPCMessage$.pipe(filter(isJSONRPCResponse));
		this._incomingEvent$ = this._jsonRPCMessage$.pipe(this.ackEvent(), this.replySignalwirePing(), filter((message) => !isJSONRPCResponse(message)), this.discardStaleEvents(), share(), takeUntil(this.destroyed$));
	}
	async setProtocol(protocol) {
		this._currentProtocol = protocol;
		this.protocol$.next(protocol);
		await this._updateProtocolInStorage(protocol);
	}
	get incomingEvent$() {
		return this._incomingEvent$;
	}
	get connectionStatus$() {
		return this._webSocketConnections.status$;
	}
	async connect() {
		if (this.isConnecting || this.isConnected) {
			logger$2.warn("[Transport] Already connecting or connected");
			return Promise.resolve();
		}
		return new Promise((resolve, reject) => {
			this.isConnecting = true;
			this.subscribeTo(this.initialized$, () => {
				this._webSocketConnections.connect();
				const connectionSub = this._webSocketConnections.status$.pipe(filter((status) => status === "connected" || status === "disconnected"), take(1), timeout(1e4)).subscribe({
					next: (status) => {
						if (status === "connected") {
							this.isConnecting = false;
							this.isConnected = true;
							logger$2.debug("[Transport] Connection established");
							resolve();
						} else {
							this.isConnecting = false;
							const error = new TransportConnectionError("Failed to connect");
							logger$2.error("[Transport] Connection failed");
							this.onError?.(error);
							reject(error);
						}
					},
					error: (err) => {
						this.isConnecting = false;
						logger$2.error("[Transport] Connection error:", err);
						this.onError?.(err instanceof Error ? err : new Error(String(err), { cause: err }));
						reject(err);
					}
				});
				this.subscriptions.push(connectionSub);
				this.subscribeTo(this._webSocketConnections.status$.pipe(filter((status) => status === "disconnected")), () => {
					logger$2.debug("[Transport] Disconnected");
					this.isConnected = false;
				});
			});
		});
	}
	reconnect() {
		this._webSocketConnections.reconnect();
	}
	async execute(request, options) {
		this.send(request);
		return new PendingRPC(request, this._jsonRPCResponse$, options).promise;
	}
	send(message) {
		const payload = JSON.stringify(message);
		this._outgoingMessages$.next(payload);
	}
	disconnect() {
		logger$2.debug("[Transport] Disconnecting");
		this.isConnected = false;
		this.isConnecting = false;
		this._webSocketConnections.disconnect();
	}
	destroy() {
		logger$2.debug("[Transport] Destroying");
		this.disconnect();
		super.destroy();
		this._webSocketConnections.destroy();
	}
	async _loadProtocolFromStorage() {
		try {
			const storedProtocol = await this.storage.getItem(this.protocolKey);
			this._currentProtocol = storedProtocol ?? void 0;
			this.protocol$.next(storedProtocol ?? void 0);
		} catch (error) {
			logger$2.error("Failed to retrieve protocol from storage:", error);
			throw error;
		}
	}
	async _updateProtocolInStorage(protocol) {
		if (!protocol) {
			try {
				await this.storage.removeItem(this.protocolKey);
			} catch (error) {
				logger$2.error("Failed to remove protocol from storage:", error);
				throw error;
			}
			return;
		}
		try {
			const storedProtocol = await this.storage.getItem(this.protocolKey);
			if (!storedProtocol || storedProtocol !== protocol) await this.storage.setItem(this.protocolKey, protocol);
		} catch (error) {
			logger$2.error("Failed to update protocol in storage:", error);
			throw error;
		}
	}
	async _init() {
		await this._loadProtocolFromStorage();
		return true;
	}
};

//#endregion
//#region src/clients/SignalWire.ts
const logger$1 = getLogger();
const buildOptionsFromDestination = (destination) => {
	if (typeof destination === "string") {
		const queryStartIndex = destination.indexOf("?");
		if (queryStartIndex !== -1) {
			const queryString = destination.substring(queryStartIndex + 1);
			const channel = new URLSearchParams(queryString).get("channel");
			if (channel === "video") return {
				audio: true,
				video: true,
				receiveVideo: true
			};
			else if (channel === "audio") return {
				audio: true,
				video: false
			};
		}
	}
	return {};
};
/**
* Main entry point for the SignalWire Browser SDK.
*
* Manages authentication, WebSocket transport, call creation, and media devices.
*
* @example
* ```ts
* const client = new SignalWire(credentialProvider);
* client.isConnected$.subscribe(connected => console.log('Connected:', connected));
* const call = await client.dial('/public/my-room');
* ```
*/
var SignalWire = class extends Destroyable {
	/**
	* Creates a new SignalWire client and begins connecting.
	*
	* @param credentialProvider - Provider that supplies authentication credentials.
	* @param options - Configuration options (connection, device monitoring, preferences).
	*/
	constructor(credentialProvider, options = {}) {
		super();
		this.preferences = new ClientPreferences();
		this._user$ = this.createBehaviorSubject(void 0);
		this._directory$ = this.createBehaviorSubject(void 0);
		this._isConnected$ = this.createBehaviorSubject(false);
		this._isRegistered$ = this.createBehaviorSubject(false);
		this._errors$ = this.createReplaySubject(1);
		this._warnings$ = this.createReplaySubject(10);
		this._options = {};
		this._deps = new DependencyContainer();
		this._credentialProvider = credentialProvider;
		this._options = {
			...PreferencesContainer.instance.defaultSignalWireOptions,
			...options
		};
		if (this._options.storageImplementation) this._deps.storageImpl = this._options.storageImplementation;
		if (this._options.persistSession) this._deps.persistSession = true;
		if (this._options.webSocketConstructor) this._deps.WebSocket = this._options.webSocketConstructor;
		if (this._options.savePreferences) this.preferences.enableSavePreferences(this._deps.storage);
		if (this._options.webRTCApiProvider) this._deps.webRTCApiProvider = this._options.webRTCApiProvider;
		if (this._options.logger !== void 0) setLogger(this._options.logger);
		if (this._options.logLevel) setLogLevel(this._options.logLevel);
		if (this._options.debug) setDebugOptions(this._options.debug);
		this._deviceController = this._deps.deviceController;
		if (!this._options.skipDeviceMonitoring) this._deviceController.enableDeviceMonitoring();
		this.subscribeTo(this._deviceController.errors$, (error) => {
			this._errors$.next(error);
		});
		this.initResilienceSubsystems();
		this.resolveCredentials().then(() => {
			this.init().catch((error) => {
				logger$1.error("[SignalWire] Initialization error:", error);
				this._deps.storage.removeItem("sw:cached_credential");
				this._deps.storage.removeItem("sw:cached_credential", "local");
				this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
			});
		}).catch((error) => {
			logger$1.error("[SignalWire] Initialization error:", error);
			this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
		});
	}
	/**
	* Initializes DPoP if not already set up. Returns the fingerprint on success.
	*/
	async initDPoP() {
		if (this._dpopManager?.initialized) return this._dpopManager.fingerprint;
		try {
			this._dpopManager = new CryptoController();
			const fingerprint = await this._dpopManager.init();
			logger$1.debug("[SignalWire] DPoP initialized, fingerprint available");
			return fingerprint;
		} catch (error) {
			logger$1.warn("[SignalWire] DPoP initialization failed, proceeding without DPoP:", error);
			this._dpopManager = void 0;
			return;
		}
	}
	/**
	* Resolves credentials using cache-first strategy when persistSession is enabled.
	*
	* 1. If persistSession → check localStorage for cached credential
	* 2. If cached and not expired → use it (skip provider.authenticate())
	* 3. If no cache or expired → call provider.authenticate()
	* 4. If no provider AND no cache → throw
	*/
	async resolveCredentials() {
		const fingerprint = await this.initDPoP();
		this._refreshCoordinator = new CredentialRefreshCoordinator(this._dpopManager, {
			http: this._deps.http,
			notifier: {
				onError: (error) => this._errors$.next(error),
				onWarning: (warning) => this._warnings$.next(warning),
				onRefreshExhausted: () => void this.disconnect()
			},
			store: {
				read: () => this._deps.credential,
				write: (credential) => {
					this._deps.credential = credential;
				},
				merge: (partial) => {
					this._deps.credential = {
						...this._deps.credential,
						...partial
					};
				},
				persist: (credential) => this.persistCredential(credential)
			}
		});
		if (this._credentialProvider) return this.validateCredentials(this._credentialProvider, void 0, fingerprint);
		for (const scope of this._deps.persistSession ? ["local", "session"] : ["session"]) try {
			const cached = await this._deps.storage.getItem("sw:cached_credential", scope);
			if (cached?.token) {
				logger$1.debug(`[SignalWire] Using cached credential from ${scope}Storage`);
				return await this.validateCredentials(void 0, cached);
			}
		} catch {}
		throw new InvalidCredentialsError("No credential provider and no cached session. Provide a CredentialProvider or enable persistSession with a prior login.");
	}
	async validateCredentials(credentialProvider, credentials, fingerprint) {
		const _fingerprint = fingerprint ?? (this._dpopManager?.initialized ? this._dpopManager.fingerprint : void 0);
		const _credentials = credentials ?? (credentialProvider ? await credentialProvider.authenticate(_fingerprint ? { fingerprint: _fingerprint } : void 0) : void 0);
		if (!_credentials) throw new InvalidCredentialsError("No credentials available.");
		if (_credentials.token) try {
			const decodeHeader = jwtDecode(_credentials.token, { header: true });
			this._deps.ch = decodeHeader.ch;
		} catch (error) {
			logger$1.error("[SignalWire] Invalid JWT token provided in credentials:", error);
			throw new InvalidCredentialsError("Invalid JWT token provided in credentials.", { cause: error });
		}
		if (!_credentials.token && !_credentials.authorizationState) {
			logger$1.error("[SignalWire] No valid authentication credentials provided.");
			throw new InvalidCredentialsError("No valid authentication credentials provided.");
		}
		if (!this._deps.persistSession && _credentials.expiry_at && _credentials.expiry_at < Date.now()) {
			logger$1.error("[SignalWire] Provided credentials have expired.");
			throw new InvalidCredentialsError("Provided credentials have expired.");
		}
		if (_credentials.expiry_at && credentialProvider?.refresh) this._refreshCoordinator?.scheduleDeveloperRefresh(credentialProvider, _credentials.expiry_at);
		else if (_credentials.expiry_at && !credentialProvider?.refresh) {
			logger$1.warn(`[SignalWire] [SW-NO-REFRESH-HANDLER] Credential has expiry_at=${_credentials.expiry_at} but no refresh handler. Session will terminate at expiry unless the SAT has 'sat:refresh' scope.`);
			this._warnings$.next({
				code: "credential_no_refresh_handler",
				source: "CredentialProvider",
				message: "Credential has expiry_at but no refresh handler. Session will terminate at expiry unless the SAT carries 'sat:refresh' scope.",
				expiresAt: _credentials.expiry_at
			});
		}
		this._deps.credential = _credentials;
		this.persistCredential(_credentials);
		if (this.isConnected && this._clientSession.authenticated && _credentials.token) try {
			await this._clientSession.reauthenticate(_credentials.token);
			logger$1.info("[SignalWire] Session refreshed with new credentials.");
		} catch (error) {
			logger$1.error("[SignalWire] Failed to refresh session with new credentials:", error);
			this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
		}
	}
	/** Persist credential to localStorage when persistSession is enabled. */
	persistCredential(credential) {
		if (!credential.token) return;
		this._deps.storage.setItem("sw:cached_credential", credential);
		if (this._deps.persistSession) this._deps.storage.setItem("sw:cached_credential", credential, "local");
	}
	async init() {
		this._user$.next(new User(this._deps.http));
		if (!this._options.skipConnection) await this.connect();
		if (!this._options.reconnectAttachedCalls && this._attachManager) await this._attachManager.flush();
		if (!this._options.skipRegister) try {
			await this.register();
		} catch (error) {
			logger$1.error("[SignalWire] Registration failed:", error);
			this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
		}
		this.handleAttachments();
	}
	async handleAttachments() {
		if (!this._attachManager) {
			logger$1.error("[SignalWire] AttachManager not initialized");
			return;
		}
		if (!this._options.reconnectAttachedCalls) return;
		try {
			await this._attachManager.reattachCalls();
		} catch (error) {
			logger$1.error("[SignalWire] Failed to reattach calls:", error);
			this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
		}
	}
	/**
	* Establishes the WebSocket connection and authenticates the session.
	*
	* ## Reconnection behavior
	*
	* After a successful connection the underlying {@link WebSocketController}
	* automatically attempts to reconnect whenever the socket closes
	* unexpectedly (e.g. network change, server restart). Reconnection uses an
	* **exponential back-off** strategy:
	*
	* - First retry after `reconnectDelayMin` (default **0.1 s**).
	* - Each subsequent retry doubles the delay up to `reconnectDelayMax`
	*   (default **3 s**).
	* - The delay resets to `reconnectDelayMin` once a connection succeeds.
	* - A per-attempt `connectionTimeout` (default **10 s**) aborts the
	*   attempt and schedules the next retry if the server does not respond.
	*
	* Calling {@link disconnect} stops the reconnection loop entirely.
	*
	* ## Message handling during temporary disconnections
	*
	* While the socket is not in the `connected` state, **outgoing messages
	* are queued** in an internal buffer. Once the connection is
	* re-established the queue is flushed in order so no outgoing RPC call is
	* lost.
	*
	* **Incoming** server-to-client messages that arrive while the socket is
	* down are *not* buffered by the SDK — they are expected to be
	* re-delivered by the server after the session is re-authenticated.
	* Active RPC calls that were awaiting a response will time out
	* (default **5 s**) and reject with an `RPCTimeoutError`; callers should
	* handle this and retry if appropriate.
	*
	* The connection status can be observed via the `status$` observable on
	* the transport layer, which emits `'connecting'`, `'connected'`,
	* `'reconnecting'`, `'disconnecting'`, or `'disconnected'`.
	*/
	async connect() {
		await this.teardownTransportAndSession();
		try {
			const user = this._user$.value;
			if (!user) throw new UnexpectedError("User not initialized before connect");
			if (!await firstValueFrom(user.fetched$)) throw new UnexpectedError("Failed to fetch user information - fetched$ emitted false");
			this._deps.user = user;
		} catch (error) {
			logger$1.error(`[SignalWire] Failed to fetch user information: ${error instanceof Error ? error.message : "Unknown error"}. This usually means the user token is invalid or expired.`);
			throw new UnexpectedError("Error fetching user information", { cause: error });
		}
		const errorHandler = (error) => {
			this._errors$.next(error);
		};
		this._transport = new TransportManager(this._deps.storage, this._deps.protocolKey, this._deps.WebSocket, PreferencesContainer.instance.relayHost ?? this._deps.relayHost, errorHandler);
		this._attachManager = new AttachManager(this._deps.storage, this._deps.deviceController, PreferencesContainer.instance.reconnectCallsTimeout, this._deps.attachedCallsKey);
		this._clientSession = new ClientSessionManager(() => this._deps.credential, this._transport, this._deps.storage, this._deps.authorizationStateKey, this._deps.deviceController, this._attachManager, this._deps.webRTCApiProvider, this._dpopManager, this._networkMonitor?.networkChange$);
		this._publicSession = new ClientSessionWrapper(this._clientSession);
		this._clientSession.onBeforeReconnect = async () => {
			if (!this._credentialProvider) return;
			try {
				const fingerprint = this._dpopManager?.initialized ? this._dpopManager.fingerprint : void 0;
				logger$1.debug("[SignalWire] Credential expired, refreshing before reconnect");
				const newCredentials = await this._credentialProvider.authenticate(fingerprint ? { fingerprint } : void 0);
				this._deps.credential = newCredentials;
				if (newCredentials.expiry_at && this._credentialProvider.refresh) this._refreshCoordinator?.scheduleDeveloperRefresh(this._credentialProvider, newCredentials.expiry_at);
				logger$1.debug("[SignalWire] Credential refreshed successfully for reconnect");
			} catch (error) {
				logger$1.error("[SignalWire] Failed to refresh credentials for reconnect:", error);
				this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
				throw error;
			}
		};
		this.subscribeTo(this._clientSession.errors$, (error) => {
			this._errors$.next(error);
		});
		await this._clientSession.connect();
		await this._refreshCoordinator?.activate(this._deps.user, this._clientSession);
		this.subscribeTo(this._clientSession.authenticated$.pipe(skip(1), filter(Boolean)), async () => {
			try {
				await this._refreshCoordinator?.activate(this._deps.user, this._clientSession);
			} catch (error) {
				logger$1.error("[SignalWire] Refresh re-arm after reconnect failed (non-fatal):", error);
				this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
			}
			try {
				logger$1.debug("[SignalWire] Re-registering user after reconnect");
				await this.register();
				logger$1.debug("[SignalWire] User re-registered successfully after reconnect");
			} catch (error) {
				logger$1.error("[SignalWire] Re-registration failed after reconnect:", error);
				this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
			}
		});
		const conversationManager = new ConversationsManager(this._clientSession, this._deps.http, () => this._deps.getUserFromAddressId(), errorHandler);
		const directory = new DirectoryManager(this._deps.http, this._clientSession, conversationManager, errorHandler);
		this._directory$.next(directory);
		this._clientSession.setDirectory(directory);
		this._isConnected$.next(true);
		this._diagnosticsCollector?.record("connection", "connected");
		this.subscribeTo(this._clientSession.authenticated$.pipe(skip(2), filter(Boolean)), () => {
			this._diagnosticsCollector?.record("connection", "reconnected");
		});
	}
	/**
	* Observable that emits the {@link User} profile once fetched,
	* or `undefined` before authentication completes.
	*
	* @example
	* ```ts
	* client.user$.subscribe(u => {
	*   if (u) console.log('Logged in as', u.email);
	* });
	* ```
	*/
	get user$() {
		return this.deferEmission(this._user$.asObservable());
	}
	/** Current user snapshot, or `undefined` if not yet authenticated. */
	get user() {
		return this._user$.value;
	}
	/**
	* Observable that emits the {@link Directory} instance once the client is connected,
	* or `undefined` while disconnected. Subscribe to this to safely wait for the directory
	* to become available without risking an error.
	*
	* @example
	* ```ts
	* client.directory$.subscribe(dir => {
	*   if (dir) dir.addresses$.subscribe(console.log);
	* });
	* ```
	*/
	get directory$() {
		return this.deferEmission(this._directory$.asObservable());
	}
	/**
	* Current directory snapshot, or `undefined` if the client is not yet connected.
	* Prefer {@link directory$} when you need to react to the directory becoming available.
	*/
	get directory() {
		return this._directory$.value;
	}
	/** Observable that emits when the user registration state changes. */
	get isRegistered$() {
		return this.deferEmission(this._isRegistered$.asObservable());
	}
	/** Whether the user is currently registered. */
	get isRegistered() {
		return this._isRegistered$.value;
	}
	/** Whether the client is currently connected. */
	get isConnected() {
		return this._isConnected$.value;
	}
	/** Observable that emits when the connection state changes. */
	get isConnected$() {
		return this.deferEmission(this._isConnected$.asObservable());
	}
	/** Observable that emits `true` when the client is both connected and authenticated. */
	get ready$() {
		return this.publicCachedObservable("ready$", () => this._isConnected$.pipe(switchMap((connected) => connected ? this._clientSession.authenticated$ : of(false))));
	}
	/** Observable stream of errors from transport, authentication, and devices. */
	get errors$() {
		return this.deferEmission(this._errors$.asObservable());
	}
	/**
	* Observable stream of non-fatal SDK warnings.
	*
	* Subscribe to detect SDK behaviors that affect session liveness or developer-facing
	* contracts but do not warrant disconnection — e.g., a fallback from Client Bound SAT
	* refresh to the developer-provided `refresh()` because the SAT lacks `sat:refresh`
	* scope. Discriminated by `code`.
	*
	* Independent from {@link errors$}: existing error consumers are not notified.
	*/
	get warnings$() {
		return this.deferEmission(this._warnings$.asObservable());
	}
	/** Platform WebRTC capabilities detected at construction time. */
	get platformCapabilities() {
		this._platformCapabilities ??= detectPlatformCapabilities(this._options.webRTCApiProvider);
		return this._platformCapabilities;
	}
	/** Observable that emits when the SDK auto-switches a device. */
	get deviceRecovered$() {
		return this.deferEmission(this._deviceController.deviceRecovered$);
	}
	/**
	* Export a structured diagnostic bundle for support/debugging.
	* Includes connection events, call summaries, and device changes.
	*/
	exportDiagnostics() {
		const devices = {
			audioInput: this.audioInputDevices,
			audioOutput: this.audioOutputDevices,
			videoInput: this.videoInputDevices
		};
		const base = {
			sdkVersion: "unknown",
			userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
			capabilities: this.platformCapabilities,
			events: [],
			calls: [],
			deviceChanges: [],
			devices
		};
		if (!this._diagnosticsCollector) return base;
		const raw = this._diagnosticsCollector.export();
		return {
			...base,
			sdkVersion: raw.sdkVersion,
			userAgent: raw.userAgent,
			events: raw.events,
			calls: raw.calls,
			deviceChanges: raw.deviceChanges,
			devices
		};
	}
	/**
	* Initialize resilience subsystems. Non-fatal: any failure is logged and
	* the SDK continues working without the failing subsystem.
	*/
	initResilienceSubsystems() {
		try {
			this._platformCapabilities = detectPlatformCapabilities(this._options.webRTCApiProvider);
		} catch (error) {
			logger$1.warn("[SignalWire] Failed to detect platform capabilities:", error);
		}
		try {
			this._networkMonitor = new NetworkMonitor();
		} catch (error) {
			logger$1.warn("[SignalWire] Failed to initialize NetworkMonitor:", error);
		}
		try {
			this._visibilityController = new VisibilityController();
			this.subscribeTo(this._visibilityController.visibilityChange$.pipe(filter((event) => event.to === "visible" && PreferencesContainer.instance.refreshDevicesOnVisible)), () => {
				logger$1.debug("[SignalWire] Page visible, re-enumerating devices");
				try {
					this._deviceController.disableDeviceMonitoring();
					this._deviceController.enableDeviceMonitoring();
				} catch {}
			});
		} catch (error) {
			logger$1.warn("[SignalWire] Failed to initialize VisibilityController:", error);
		}
		try {
			this._diagnosticsCollector = new DiagnosticsCollector({ sdkVersion: "4.0.0-rc.0" });
		} catch (error) {
			logger$1.warn("[SignalWire] Failed to initialize DiagnosticsCollector:", error);
		}
	}
	/**
	* Disconnects the WebSocket and tears down the current session.
	*
	* The client can be reconnected by calling {@link connect} again,
	* which creates a fresh transport and session.
	*/
	async disconnect() {
		this._refreshCoordinator?.suspend();
		this._diagnosticsCollector?.record("connection", "disconnected");
		await this.teardownTransportAndSession();
		this._isConnected$.next(false);
	}
	/**
	* Tear down the current transport / session / attach manager. Safe to call
	* when nothing has been initialized yet (e.g. first connect()).
	*/
	async teardownTransportAndSession() {
		const session = this._clientSession;
		const transport = this._transport;
		if (session) {
			try {
				await session.disconnect();
			} catch (error) {
				logger$1.warn("[SignalWire] Error disconnecting previous session:", error);
			}
			session.destroy();
		}
		if (transport) transport.destroy();
		this._clientSession = void 0;
		this._publicSession = void 0;
		this._transport = void 0;
		this._attachManager = void 0;
	}
	async waitAuthentication() {
		await firstValueFrom(this.ready$.pipe(filter((ready$1) => ready$1 === true)));
	}
	/**
	* Registers the user as online to receive inbound calls and events.
	*
	* Waits for authentication to complete before sending the registration.
	* If the initial attempt fails, reauthentication is attempted automatically.
	*
	* @throws {InvalidCredentialsError} If registration and reauthentication both fail.
	*/
	async register() {
		try {
			await this.waitAuthentication();
			await this._transport.execute(RPCExecute({
				method: "subscriber.online",
				params: {}
			}));
			this._isRegistered$.next(true);
			return;
		} catch (error) {
			if (!this._deps.credential.token) {
				this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
				throw error;
			}
			logger$1.debug("[SignalWire] Failed to register user, trying reauthentication...");
			try {
				await this._clientSession.reauthenticate(this._deps.credential.token);
				logger$1.debug("[SignalWire] Reauthentication successful, retrying register()");
				await this._transport.execute(RPCExecute({
					method: "subscriber.online",
					params: {}
				}));
				this._isRegistered$.next(true);
			} catch (reauthError) {
				logger$1.error("[SignalWire] Reauthentication failed during register():", reauthError);
				const registerError = new InvalidCredentialsError("Failed to register user, and reauthentication attempt also failed. Please check your credentials.", { cause: reauthError instanceof Error ? reauthError : new Error(String(reauthError), { cause: reauthError }) });
				this._errors$.next(registerError);
				throw registerError;
			}
		}
	}
	/**
	* Unregisters the user, going offline for inbound calls.
	*
	* The WebSocket connection remains open; use {@link disconnect} to fully close it.
	*/
	async unregister() {
		try {
			await this._transport.execute(RPCExecute({
				method: "subscriber.offline",
				params: {}
			}));
			this._isRegistered$.next(false);
		} catch (error) {
			logger$1.error("[SignalWire] Failed to unregister user:", error);
			this._errors$.next(error instanceof Error ? error : new Error(String(error), { cause: error }));
			throw error;
		}
	}
	/**
	* Places an outbound call to the given destination.
	*
	* Waits for authentication before dialing. Media options are merged from
	* saved preferences, destination query parameters (e.g. `?channel=video`),
	* and the provided `options` (highest priority).
	*
	* Returns a {@link Call} in `'ringing'` state. Subscribe to {@link Call.status$}
	* to track progression through `'connected'` → `'disconnected'`.
	*
	* @param destination - Address URI string (e.g. `'/public/my-room'`) or {@link Address} instance.
	* @param options - Media and dial options (audio/video, device constraints). Overrides defaults.
	* @returns The created {@link Call} instance.
	* @throws {Error} If authentication is not complete or call creation fails.
	*
	* @example
	* ```ts
	* const call = await client.dial('/public/conference', {
	*   audio: true,
	*   video: true,
	* });
	* call.status$.subscribe(status => console.log('Call:', status));
	* ```
	*/
	async dial(destination, options = {}) {
		const computed_options = {
			...PreferencesContainer.instance.preferredMediaOptions,
			...buildOptionsFromDestination(destination),
			...options
		};
		await this.waitAuthentication();
		logger$1.debug("[SignalWire] Dialing with options:", computed_options);
		return this._clientSession.createOutboundCall(destination, computed_options);
	}
	/**
	* Runs a multi-phase connectivity test against the given destination.
	*
	* The test checks:
	*   1. **Signaling** -- WebSocket connected, RTT measurement
	*   2. **Devices** -- getUserMedia succeeds with selected (or specified) devices
	*   3. **ICE/TURN** -- gathers ICE candidates to verify STUN/TURN reachability
	*   4. **Media/bandwidth** (unless `skipMediaTest`) -- dials the destination,
	*      collects getStats() for `duration` seconds, computes bandwidth estimates
	*
	* @param destination - A destination to dial for the media test (e.g. `'/private/network-test'`).
	* @param options - Preflight options (duration, skipMediaTest, device overrides).
	* @returns A {@link PreflightResult} describing connectivity health.
	*
	* @example
	* ```ts
	* const result = await client.preflight('/private/network-test', { duration: 5 });
	* if (!result.ok) console.warn('Connectivity issues:', result.warnings);
	* ```
	*/
	async preflight(destination, options) {
		const iceServers = this._clientSession.iceServers ?? PreferencesContainer.instance.iceServers ?? [];
		const isConnected = this._isConnected$.value;
		return new PreflightRunner(this._deviceController, iceServers, isConnected, 0, async (dest, opts) => this.dial(dest, opts), options).run(destination);
	}
	/** The underlying client session for advanced RPC operations. */
	get session() {
		return this._publicSession;
	}
	/** Observable list of available audio input (microphone) devices. */
	get audioInputDevices$() {
		return this.deferEmission(this._deviceController.audioInputDevices$);
	}
	/** Current snapshot of available audio input devices. */
	get audioInputDevices() {
		return this._deviceController.audioInputDevices;
	}
	/** Observable list of available audio output (speaker) devices. */
	get audioOutputDevices$() {
		return this.deferEmission(this._deviceController.audioOutputDevices$);
	}
	/** Current snapshot of available audio output devices. */
	get audioOutputDevices() {
		return this._deviceController.audioOutputDevices;
	}
	/** Observable list of available video input (camera) devices. */
	get videoInputDevices$() {
		return this.deferEmission(this._deviceController.videoInputDevices$);
	}
	/** Current snapshot of available video input devices. */
	get videoInputDevices() {
		return this._deviceController.videoInputDevices;
	}
	/** Observable of the currently selected audio input device. */
	get selectedAudioInputDevice$() {
		return this.deferEmission(this._deviceController.selectedAudioInputDevice$);
	}
	/** Observable of the currently selected audio output device. */
	get selectedAudioOutputDevice$() {
		return this.deferEmission(this._deviceController.selectedAudioOutputDevice$);
	}
	/** Observable of the currently selected video input device. */
	get selectedVideoInputDevice$() {
		return this.deferEmission(this._deviceController.selectedVideoInputDevice$);
	}
	/** Currently selected audio input device, or `null` if none. */
	get selectedAudioInputDevice() {
		return this._deviceController.selectedAudioInputDevice;
	}
	/** Currently selected audio output device, or `null` if none. */
	get selectedAudioOutputDevice() {
		return this._deviceController.selectedAudioOutputDevice;
	}
	/** Currently selected video input device, or `null` if none. */
	get selectedVideoInputDevice() {
		return this._deviceController.selectedVideoInputDevice;
	}
	/** Media track constraints for the selected audio input device. Returns `false` when disabled. */
	get selectedAudioInputDeviceConstraints() {
		return this._deviceController.selectedAudioInputDeviceConstraints;
	}
	/** Media track constraints for the selected video input device. Returns `false` when disabled. */
	get selectedVideoInputDeviceConstraints() {
		return this._deviceController.selectedVideoInputDeviceConstraints;
	}
	/** Converts a `MediaDeviceInfo` to track constraints suitable for `getUserMedia`. */
	deviceInfoToConstraints(deviceInfo) {
		return this._deviceController.deviceInfoToConstraints(deviceInfo);
	}
	/** Sets the preferred audio input device. */
	selectAudioInputDevice(device) {
		this._deviceController.selectAudioInputDevice(device);
	}
	/** Sets the preferred video input device. */
	selectVideoInputDevice(device) {
		this._deviceController.selectVideoInputDevice(device);
	}
	/** Sets the preferred audio output device. */
	selectAudioOutputDevice(device) {
		this._deviceController.selectAudioOutputDevice(device);
	}
	/**
	* Apply the currently selected audio output device to an HTMLMediaElement
	* (e.g. the `<audio>` or `<video>` element the consumer attached the
	* remote stream to). Uses `HTMLMediaElement.setSinkId` under the hood.
	* Returns a `Promise<boolean>`: `true` if the sink was applied,
	* `false` if the browser doesn't support `setSinkId` or no device is
	* selected.
	*
	* @example
	* ```ts
	* audioEl.srcObject = call.remoteStream;
	* await client.applySelectedAudioOutputDevice(audioEl);
	* ```
	*/
	async applySelectedAudioOutputDevice(element) {
		const device = this._deviceController.selectedAudioOutputDevice;
		if (!device?.deviceId) return false;
		const withSink = element;
		if (typeof withSink.setSinkId !== "function") {
			logger$1.warn("[SignalWire] setSinkId not supported on this element / browser");
			return false;
		}
		try {
			await withSink.setSinkId(device.deviceId);
			return true;
		} catch (error) {
			logger$1.warn("[SignalWire] Failed to apply audio output device:", error);
			return false;
		}
	}
	/** Starts monitoring for media device changes (connect/disconnect). */
	enableDeviceMonitoring() {
		this._deviceController.enableDeviceMonitoring();
	}
	/** Stops monitoring for media device changes. */
	disableDeviceMonitoring() {
		this._deviceController.disableDeviceMonitoring();
	}
	/**
	* Returns the capabilities of a media device.
	* @param deviceInfo - The device to query.
	* @returns The device capabilities, or `null` if unavailable.
	*/
	async getDeviceCapabilities(deviceInfo) {
		return this._deviceController.getDeviceCapabilities(deviceInfo);
	}
	/**
	* Checks whether a device is still available and usable.
	* @param deviceInfo - The device to validate, or `null`.
	* @returns `true` if the device is valid and available. Returns `false` for `null`, audio output devices, or unavailable devices.
	*/
	async isValidDevice(deviceInfo) {
		return this._deviceController.isValidDevice(deviceInfo);
	}
	/** Injects a storage manager into the device controller for persistence. */
	setStorageManager(storageManager) {
		this._deviceController.setStorageManager(storageManager);
	}
	/** Clears all device state and re-enumerates. */
	async clearDeviceState() {
		return this._deviceController.clearDeviceState();
	}
	/** Forces a device re-enumeration. */
	async enumerateDevices() {
		return this._deviceController.enumerateDevices();
	}
	/** Disables audio input (receive-only mode). No audio track will be acquired. */
	disableAudioInput() {
		this._deviceController.disableAudioInput();
	}
	/** Re-enables audio input, restoring the last selection or auto-selecting. */
	enableAudioInput() {
		this._deviceController.enableAudioInput();
	}
	/** Disables video input (receive-only mode). No video track will be acquired. */
	disableVideoInput() {
		this._deviceController.disableVideoInput();
	}
	/** Re-enables video input, restoring the last selection or auto-selecting. */
	enableVideoInput() {
		this._deviceController.enableVideoInput();
	}
	/** Observable that emits `true` when video input is disabled (receive-only). */
	get videoInputDisabled$() {
		return this.deferEmission(this._deviceController.videoInputDisabled$);
	}
	/** Observable that emits `true` when audio input is disabled (receive-only). */
	get audioInputDisabled$() {
		return this.deferEmission(this._deviceController.audioInputDisabled$);
	}
	/** Whether video input is currently disabled. */
	get videoInputDisabled() {
		return this._deviceController.videoInputDisabled;
	}
	/** Whether audio input is currently disabled. */
	get audioInputDisabled() {
		return this._deviceController.audioInputDisabled;
	}
	/**
	* Triggers the browser's media permission dialog and captures the user's device selections.
	*
	* @param options - Which permissions to request.
	* @param options.audio - Whether to request audio permission.
	* @param options.video - Whether to request video permission.
	* @returns The permission result with selected devices.
	*/
	async requestMediaPermissions(options = {
		audio: true,
		video: true
	}) {
		const constraints = {
			audio: options.audio ?? false,
			video: options.video ?? false
		};
		let audioGranted = false;
		let videoGranted = false;
		let selectedAudioDevice;
		let selectedVideoDevice;
		try {
			const tracks = (await this._deps.webRTCApiProvider.mediaDevices.getUserMedia(constraints)).getTracks();
			for (const track of tracks) {
				const settings = track.getSettings();
				if (track.kind === "audio") {
					audioGranted = true;
					if (settings.deviceId) selectedAudioDevice = this.audioInputDevices.find((d) => d.deviceId === settings.deviceId);
				} else if (track.kind === "video") {
					videoGranted = true;
					if (settings.deviceId) selectedVideoDevice = this.videoInputDevices.find((d) => d.deviceId === settings.deviceId);
				}
				track.stop();
			}
		} catch (error) {
			logger$1.warn("[SignalWire] Media permission request failed:", error);
		}
		await this._deviceController.enumerateDevices();
		if (audioGranted && selectedAudioDevice) {
			const audioDeviceId = selectedAudioDevice.deviceId;
			selectedAudioDevice = this.audioInputDevices.find((d) => d.deviceId === audioDeviceId) ?? selectedAudioDevice;
		}
		if (videoGranted && selectedVideoDevice) {
			const videoDeviceId = selectedVideoDevice.deviceId;
			selectedVideoDevice = this.videoInputDevices.find((d) => d.deviceId === videoDeviceId) ?? selectedVideoDevice;
		}
		if (audioGranted && selectedAudioDevice && !this.selectedAudioInputDevice) this.selectAudioInputDevice(selectedAudioDevice);
		if (videoGranted && selectedVideoDevice && !this.selectedVideoInputDevice) this.selectVideoInputDevice(selectedVideoDevice);
		return {
			audio: audioGranted,
			video: videoGranted,
			selectedAudioDevice,
			selectedVideoDevice
		};
	}
	/**
	* Clears all SDK-persisted state and resets to defaults.
	*
	* This clears device preferences, device history, authorization state,
	* attached call IDs, and all SDK storage keys, then re-enumerates devices.
	*/
	async resetToDefaults() {
		await this._deps.storage.clearAll();
		const prefs = PreferencesContainer.instance;
		prefs.preferredAudioInput = null;
		prefs.preferredAudioOutput = null;
		prefs.preferredVideoInput = null;
		await this._deviceController.clearDeviceState();
	}
	/** Destroys the client, clearing timers and releasing all resources. */
	destroy() {
		this._refreshCoordinator?.destroy();
		this._refreshCoordinator = void 0;
		this._dpopManager?.destroy();
		if (this._attachManager) this._attachManager.detachAll();
		this._transport.destroy();
		this._clientSession.destroy();
		try {
			this._networkMonitor?.destroy();
		} catch {}
		try {
			this._visibilityController?.destroy();
		} catch {}
		try {
			this._diagnosticsCollector?.destroy();
		} catch {}
		this._networkMonitor = void 0;
		this._visibilityController = void 0;
		this._diagnosticsCollector = void 0;
		super.destroy();
	}
};

//#endregion
//#region src/dependencies/EmbedTokenCredentialProvider.ts
const logger = getLogger();
/** Credential provider that exchanges an embed token for a SAT via the host's token endpoint. */
var EmbedTokenCredentialProvider = class {
	constructor(host, embedToken) {
		this.host = host;
		this.embedToken = embedToken;
	}
	async fetchSAT() {
		const url = `https://${this.host}/api/fabric/embeds/tokens`;
		const timeout$1 = 1e4;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout$1);
		try {
			const response = await fetch(url, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token: this.embedToken }),
				signal: controller.signal
			});
			clearTimeout(timeoutId);
			if (response.ok) return (await response.json()).token;
			throw new RequestError(`Failed to fetch SAT using embed token: ${response.status} ${response.statusText}`);
		} catch (error) {
			clearTimeout(timeoutId);
			if (error instanceof Error && error.name === "AbortError") throw new RequestTimeoutError(`Request timeout after ${timeout$1}ms`, { cause: error });
			logger.error("[EmbedCredentialProvider] Request failed:", error);
			throw error;
		}
	}
	async authenticate() {
		return {
			token: await this.fetchSAT(),
			expiry_at: Date.now() + 3600 * 1e3
		};
	}
	async refresh() {
		return this.authenticate();
	}
};

//#endregion
//#region src/utils/embeddableCall.ts
/**
* Creates a call using an embed token for simple, embeddable integrations.
*
* Handles client creation, authentication, and dialing in a single call.
*
* @param options - Embed token, host, and destination.
* @returns The created {@link Call} instance.
*/
async function embeddableCall(options) {
	const { to, embedToken, host } = options;
	const requiredFailed = [];
	if (!to) requiredFailed.push("to");
	if (!embedToken) requiredFailed.push("embedToken");
	if (!host) requiredFailed.push("host");
	if (requiredFailed.length > 0) return Promise.reject(new DependencyError(`Missing required options: ${requiredFailed.join(", ")}`));
	const client = new SignalWire(new EmbedTokenCredentialProvider(host, embedToken));
	const call = await client.dial(to);
	call.status$.pipe(first((status) => status === "destroyed")).subscribe(() => {
		client.disconnect();
	});
	return call;
}

//#endregion
//#region src/dependencies/StaticCredentialProvider.ts
/**
* Credential provider that returns a fixed set of credentials.
*
* Use when the token is already available (e.g. from a backend endpoint).
*
* @example
* ```ts
* const provider = new StaticCredentialProvider({ token: 'my-sat-token' });
* const client = new SignalWire(provider);
* ```
*/
var StaticCredentialProvider = class {
	constructor(credentials) {
		this.credentials = credentials;
	}
	/** Returns the static credentials. */
	async authenticate() {
		return Promise.resolve(this.credentials);
	}
};

//#endregion
//#region src/index.ts
/**
* Library version from package.json, injected at build time.
*/
const version = "4.0.0-rc.0";
/**
* Flag indicating the library has been loaded and is ready to use.
* For UMD builds: `window.SignalWire.ready`
* For ES modules: `import { ready } from '@signalwire/js'`
*/
const ready = true;
/**
* Emits 'signalwire:js:ready' event when the library is loaded.
*
* Scripts that might load BEFORE the library (check flag first):
*    ```js
*    if (window.SignalWire?.ready) {
*      // Library already loaded, use it directly
*      initApp();
*    } else {
*      window.addEventListener('signalwire:js:ready', () => initApp());
*    }
*    ```
*/
const emitReadyEvent = () => {
	if (typeof window !== "undefined") {
		const event = new CustomEvent("signalwire:js:ready", { detail: { version: "4.0.0-rc.0" } });
		window.dispatchEvent(event);
	}
};
emitReadyEvent();

//#endregion
export { Address, CallCreateError, ClientPreferences, CollectionFetchError, DPoPInitError, DeviceTokenError, EmbedTokenCredentialProvider, InvalidCredentialsError, MediaTrackError, MessageParseError, OverconstrainedFallbackError, Participant, PreflightError, RecoveryError, SelfCapabilities, SelfParticipant, SignalWire, StaticCredentialProvider, TokenRefreshError, UnexpectedError, User, VertoPongError, WebRTCCall, embeddableCall, getLogger, isSelfParticipant, ready, setDebugOptions, setLogLevel, setLogger, version };
//# sourceMappingURL=index.mjs.map